// Anant Smart Marketplace — CityPro Connect blueprint v6.0
// Single-file lean tool · AT wallet top display · plan gating · guest feed · admin console.
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Sparkles, Store, ShoppingBag, Search, MapPin, Camera, Image as ImageIcon, Upload,
  MessageCircle, Share2, Shield, Star, Trash2, Edit3, Video, Youtube, AlertTriangle,
  Radio, Award, Users, RefreshCw
} from 'lucide-react';
import api from '../../utils/api';
import { useAuth } from '../../context/AuthContext';
import ProductReviewSection from './ProductReviewSection';
import RulesTermsTab from './RulesTermsTab';
import StoreCustomizeBlock from './StoreCustomizeBlock';
import VendorAppointmentsBlock from './VendorAppointmentsBlock';
import BuyerAppointmentsBlock from './BuyerAppointmentsBlock';
import AIWalletGuard from '../../components/AIWalletGuard';
import LivePopupBanner from '../../components/LivePopupBanner';
import { announce as sbAnnounce } from '../../utils/soundbox';

const TABS = [
  { key: 'feed',     label: '🛒 Marketplace' },
  { key: 'wallet',   label: '💰 AT Wallet' },
  { key: 'shops',    label: '🏪 All Shops' },
  { key: 'showrooms',label: '🚘 Showrooms' },
  { key: 'auctions', label: '🔨 Auctions' },
  { key: 'groupbuy', label: '🎁 Group Buys' },
  { key: 'tatkal',   label: '🚨 Tatkal Deals' },
  { key: 'lead360',  label: '🌟 Lead 360 CRM' },
  { key: 'franchisee', label: '🏢 Franchisee' },
  { key: 'wall',       label: '📢 Vyapar Wall' },
  { key: 'pulse',    label: '🔥 Demand Pulse' },
  { key: 'citydeals',label: '📰 Citywise Deals' },
  { key: 'cart',     label: '🛍️ Cart' },
  { key: 'rules',    label: '📜 Rules & Terms' },
  { key: 'store',    label: '🏪 My Store' },
  { key: 'listings', label: '📦 Listings' },
  { key: 'orders',   label: '📬 Orders' },
  { key: 'reviews',  label: '⭐ Reviews' },
  { key: 'analytics',label: '📊 Analytics' },
  { key: 'prospects',label: '🎯 Prospects & Leads' },
  { key: 'leads',    label: '🔔 Leads' },
  { key: 'clients',  label: '👥 Clients' },
  { key: 'vyapaar',  label: '🚀 Marketing Pro' },
  { key: 'plans',    label: '💎 Plans' },
];

const abs = (u) => (!u ? '' : u.startsWith('http') ? u : `${process.env.REACT_APP_BACKEND_URL || ''}${u}`);
const digits = (p) => String(p || '').replace(/[^0-9]/g, '');

export default function AnantMarketplace() {
  const location = useLocation();
  const initialTab = new URLSearchParams(location.search).has('category') || new URLSearchParams(location.search).has('search') ? 'feed' : 'feed';
  const [tab, setTab] = useState(initialTab);
  const [cfg, setCfg] = useState(null);
  const [traffic, setTraffic] = useState({ live: 100, footfall_today: 2450 });
  const [pulse, setPulse] = useState({ is_live: false, orders_5m: 0, orders_1h: 0 });
  const [newOrdersCount, setNewOrdersCount] = useState(0);
  const lastOrderIdsRef = useRef(new Set());
  const audioPlayedInitRef = useRef(false);
  const load = useCallback(async () => {
    try { const r = await api.get('/marketplace/config'); setCfg(r.data); } catch {}
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const tick = () => api.get('/marketplace/traffic').then((r) => setTraffic(r.data)).catch(() => {});
    tick();
    const t = setInterval(tick, 8000);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    const tick = () => api.get('/marketplace/live-pulse').then((r) => setPulse(r.data)).catch(() => {});
    tick();
    const t = setInterval(tick, 20000);
    return () => clearInterval(t);
  }, []);

  // Vendor incoming-orders polling every 5s — with browser push + chime + toast on new order
  useEffect(() => {
    if (!cfg || cfg.guest) return;
    let cancelled = false;
    const requestPushPermission = () => {
      try {
        if ('Notification' in window && Notification.permission === 'default') {
          Notification.requestPermission().catch(() => {});
        }
      } catch {}
    };
    requestPushPermission();
    const chimeEnabled = cfg.store?.chime_enabled !== false;
    const voiceEnabled = localStorage.getItem('mkt_voice_alerts') !== '0';
    const speakHindi = (text) => {
      if (!voiceEnabled) return;
      try {
        if (!('speechSynthesis' in window)) return;
        const u = new SpeechSynthesisUtterance(text);
        u.lang = 'hi-IN'; u.rate = 1; u.pitch = 1;
        window.speechSynthesis.cancel();
        window.speechSynthesis.speak(u);
      } catch {}
    };
    const playChime = () => {
      if (!chimeEnabled) return;
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return;
        const ctx = new Ctx();
        const now = ctx.currentTime;
        [880, 1174, 1568].forEach((freq, i) => {
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.type = 'sine';
          osc.frequency.value = freq;
          gain.gain.setValueAtTime(0.0001, now + i * 0.12);
          gain.gain.exponentialRampToValueAtTime(0.35, now + i * 0.12 + 0.02);
          gain.gain.exponentialRampToValueAtTime(0.0001, now + i * 0.12 + 0.25);
          osc.connect(gain).connect(ctx.destination);
          osc.start(now + i * 0.12);
          osc.stop(now + i * 0.12 + 0.3);
        });
        setTimeout(() => ctx.close(), 1200);
      } catch {}
    };
    const showPush = (order) => {
      try {
        if ('Notification' in window && Notification.permission === 'granted') {
          const n = new Notification(`🎉 New Order · ${Number(order.my_total_at || 0).toFixed(0)} AT`, {
            body: `${order.buyer_name || 'Buyer'} · ${order.item_count} item${order.item_count !== 1 ? 's' : ''} · ${order.buyer_city || ''}`,
            icon: '/favicon.ico', tag: order.id, requireInteraction: false,
          });
          n.onclick = () => { window.focus(); n.close(); };
          setTimeout(() => { try { n.close(); } catch {} }, 8000);
        }
      } catch {}
    };
    const tick = async () => {
      try {
        const r = await api.get('/marketplace/vendor/orders?limit=50');
        if (cancelled) return;
        const orders = r.data.orders || [];
        setNewOrdersCount(r.data.new_unread || 0);
        const currentIds = new Set(orders.map(o => o.id));
        // Detect brand-new orders (not seen in previous tick)
        if (audioPlayedInitRef.current) {
          const arrivals = orders.filter(o => !lastOrderIdsRef.current.has(o.id));
          if (arrivals.length > 0) {
            arrivals.forEach((o) => {
              toast.success(`🎉 New Order · ${Number(o.my_total_at || 0).toFixed(0)} AT · ${o.buyer_name || 'Buyer'}`, { duration: 6000 });
              showPush(o);
            });
            playChime();
            speakHindi(`Naya order aaya hai. ${Number(arrivals[0].my_total_at || 0).toFixed(0)} AT ka order ${arrivals[0].buyer_name || 'buyer'} se.`);
          }
        }
        lastOrderIdsRef.current = currentIds;
        audioPlayedInitRef.current = true;
      } catch {}
    };
    tick();
    const t = setInterval(tick, 5000);
    return () => { cancelled = true; clearInterval(t); };
  }, [cfg?.guest, cfg?.store?.chime_enabled]);

  // Group Buy notification poller (Wave 50) — plays soundbox announcement for shop owner
  useEffect(() => {
    if (!cfg || cfg.guest) return;
    let cancelled = false;
    const seen = new Set(JSON.parse(localStorage.getItem('gb_notif_seen') || '[]'));
    const tick = async () => {
      try {
        const r = await api.get('/notifications?limit=20');
        if (cancelled) return;
        const items = r.data.notifications || r.data.items || r.data || [];
        (Array.isArray(items) ? items : []).forEach((n) => {
          if (!n || !n.id || seen.has(n.id)) return;
          if (n.kind === 'marketplace_group_buy_confirmed') {
            const m = n.meta || {};
            sbAnnounce('group_buy_confirmed', { orders: m.orders, amount: Math.round(Number(m.amount || 0)) });
            toast.success(`🎁 Group Buy Confirmed · ${m.orders || 0} orders · +${Math.round(Number(m.amount || 0))} AT`, { duration: 8000 });
            seen.add(n.id);
          } else if (n.kind === 'marketplace_group_buy_join') {
            const m = n.meta || {};
            sbAnnounce('group_buy_join', { joined: m.joined, min_buyers: m.min_buyers });
            seen.add(n.id);
          }
        });
        localStorage.setItem('gb_notif_seen', JSON.stringify(Array.from(seen).slice(-100)));
      } catch {}
    };
    tick();
    const t = setInterval(tick, 12000);
    return () => { cancelled = true; clearInterval(t); };
  }, [cfg?.guest]);

  const isAdmin = cfg?.user_is_admin || false;
  const isGuest = cfg?.guest || false;
  const at = Number(cfg?.at_balance || 0);

  if (!cfg) return <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center">⏳ Loading Marketplace…</div>;

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-950 via-purple-950 to-slate-950 text-white pb-24">
      {/* Header: gradient hero + AT wallet + traffic */}
      <div className="p-3 sm:p-4" data-testid="mkt-header">
        <div className="rounded-2xl overflow-hidden relative" style={{ background: 'linear-gradient(135deg, #1E1B4B 0%, #4338CA 40%, #7C3AED 70%, #DB2777 100%)' }}>
          <div className="absolute inset-0" style={{ backdropFilter: 'blur(2px)' }} />
          <div className="relative p-4 space-y-2">
            <div className="flex items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <ShoppingBag size={22} className="text-yellow-300 drop-shadow" />
                <div>
                  <div className="text-lg font-black leading-tight tracking-widest" style={{letterSpacing:'0.12em'}}>ANANT MARKET</div>
                  <div className="text-[10px] opacity-90">Powered by CityPro Connect</div>
                </div>
              </div>
              <div className="flex flex-col items-end gap-1">
                {isGuest ? (
                  <a href="/login" className="px-2 py-1 rounded-full bg-white text-purple-800 text-[10px] font-black" data-testid="mkt-login-cta">🔐 Login to sell</a>
                ) : (
                  <div className="flex items-center gap-1.5">
                    <div className="px-2 py-1 rounded-full bg-yellow-400 text-slate-900 text-[10px] font-black" data-testid="mkt-at-balance">💰 {at.toFixed(0)} AT</div>
                    <GroupBuyBell />
                    <a href="/at-deposit" data-testid="mkt-buy-at-cta"
                       className="px-2 py-1 rounded-full bg-emerald-500 hover:bg-emerald-400 text-slate-900 text-[10px] font-black flex items-center gap-1 shadow-lg ring-2 ring-emerald-300/40"
                       title="AT wallet me balance add karein">
                      ➕ Buy AT
                    </a>
                    <button onClick={() => {
                      const v = localStorage.getItem('mkt_voice_alerts') !== '0';
                      localStorage.setItem('mkt_voice_alerts', v ? '0' : '1');
                      toast.success(v ? '🔇 Voice alerts OFF' : '🔊 Voice alerts ON (Hindi)');
                    }} data-testid="mkt-voice-toggle"
                       className="px-2 py-1 rounded-full bg-indigo-500 hover:bg-indigo-400 text-white text-[10px] font-black" title="Hindi voice alerts">
                      🔊
                    </button>
                  </div>
                )}
                {cfg.active && <div className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500/40 text-emerald-100 font-black">✅ {(cfg.plan?.name || 'Active').toUpperCase()}</div>}
              </div>
            </div>
            <div className="flex items-center gap-2 text-[11px]">
              <span className="flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-500/25 border border-emerald-400/60" data-testid="mkt-traffic-live">
                <span className="w-2 h-2 rounded-full bg-emerald-400 animate-pulse" /> Live: <b>{traffic.live}</b>
              </span>
              {pulse.is_live && (
                <span data-testid="mkt-live-pulse-indicator"
                      className="flex items-center gap-1 px-2 py-1 rounded-full bg-rose-500/90 text-white font-black shadow-lg"
                      style={{ animation: 'live-pulse-blink 1.1s ease-in-out infinite' }}>
                  <span className="w-2 h-2 rounded-full bg-white animate-ping" />
                  🔴 LIVE · {pulse.orders_5m + pulse.leads_5m} activity now
                </span>
              )}
              <span className="text-[10px] opacity-80">👁 Today: <b>{traffic.footfall_today?.toLocaleString('en-IN')}</b></span>
              <span className="ml-auto px-2 py-0.5 rounded bg-yellow-400 text-slate-900 text-[9px] font-black">⭐ FEATURED CORE ENGINE</span>
              <style>{`@keyframes live-pulse-blink { 0%,100% { transform: scale(1); box-shadow: 0 6px 20px -4px rgba(244,63,94,.7); } 50% { transform: scale(1.06); box-shadow: 0 10px 30px -4px rgba(244,63,94,1); } }`}</style>
            </div>
          </div>
        </div>

        {/* Tabs — guests only see Feed + Shops + Auctions */}
        <div className="flex gap-2 overflow-x-auto pb-1 mt-3">
          {(isGuest ? [{ key: 'feed', label: '🛒 Marketplace' }, { key: 'wallet', label: '💰 AT Wallet' }, { key: 'shops', label: '🏪 All Shops' }, { key: 'showrooms', label: '🚘 Showrooms' }, { key: 'auctions', label: '🔨 Auctions' }, { key: 'groupbuy', label: '🎁 Group Buys' }, { key: 'tatkal', label: '🚨 Tatkal Deals' }, { key: 'lead360', label: '🌟 Lead 360 CRM' }, { key: 'franchisee', label: '🏢 Franchisee' }, { key: 'wall', label: '📢 Vyapar Wall' }, { key: 'pulse', label: '🔥 Demand Pulse' }, { key: 'citydeals', label: '📰 Citywise Deals' }, { key: 'rules', label: '📜 Rules & Terms' }] : TABS).concat(isAdmin ? [{ key: 'admin', label: '👑 Admin' }] : []).map((t) => (
            <button key={t.key} onClick={() => setTab(t.key)} data-testid={`mkt-tab-${t.key}`}
              className={`relative px-3 py-1.5 rounded-lg text-xs font-black border-2 whitespace-nowrap ${tab === t.key ? 'bg-purple-500 border-purple-300 text-white' : 'bg-slate-900 border-slate-700 text-slate-300'}`}>
              {t.label}
              {t.key === 'leads' && cfg.unread_leads > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-rose-500 text-white text-[9px] flex items-center justify-center font-black" data-testid="mkt-leads-badge">{cfg.unread_leads > 99 ? '99+' : cfg.unread_leads}</span>
              )}
              {t.key === 'orders' && newOrdersCount > 0 && (
                <span className="absolute -top-1 -right-1 min-w-[16px] h-4 px-1 rounded-full bg-emerald-500 text-white text-[9px] flex items-center justify-center font-black animate-pulse" data-testid="mkt-orders-badge">{newOrdersCount > 99 ? '99+' : newOrdersCount}</span>
              )}
            </button>
          ))}
          {isGuest && (
            <a href="/login" data-testid="mkt-guest-login" className="px-3 py-1.5 rounded-lg text-xs font-black bg-white text-purple-800 whitespace-nowrap">
              🔐 Login to sell
            </a>
          )}
          <a href="/atc" data-testid="mkt-atc-link"
             className="px-3 py-1.5 rounded-lg text-xs font-black bg-gradient-to-r from-amber-400 to-orange-500 text-slate-900 whitespace-nowrap border-2 border-amber-300 shadow-lg shadow-amber-500/30 hover:scale-105 transition animate-pulse">
            🏬 ATC Trade Center →
          </a>
        </div>

        {/* Delivery Partner CTA — visible entry point on main marketplace page */}
        <a href="/delivery-partner" data-testid="mkt-delivery-partner-cta"
           className="mt-3 flex items-center justify-between gap-3 rounded-xl bg-gradient-to-r from-emerald-600/80 via-cyan-600/80 to-emerald-600/80 border border-emerald-300/50 px-3 py-2 hover:from-emerald-500 hover:to-cyan-500 transition group">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-xl">🛵</span>
            <div className="min-w-0">
              <div className="text-[13px] font-black text-white truncate">Delivery Partner Bano · Earn AT!</div>
              <div className="text-[10px] text-emerald-100 truncate opacity-90">Har delivery pe seller ka fee turant aapke AT wallet me · Admin approval baad start</div>
            </div>
          </div>
          <span className="text-[11px] font-black bg-white text-emerald-700 px-3 py-1.5 rounded-lg whitespace-nowrap shadow-lg group-hover:scale-105 transition">
            Apply Now →
          </span>
        </a>
      </div>

      <div className="px-3 sm:px-4">
        {tab === 'feed'      && <FeedTab cfg={cfg} />}
        {tab === 'wallet'    && <WalletTab cfg={cfg} />}
        {tab === 'shops'     && <ShopsTab />}
        {tab === 'showrooms' && <AllShowroomsTab />}
        {tab === 'auctions'  && <AuctionsTab isGuest={isGuest} />}
        {tab === 'groupbuy'  && <LiveGroupBuysTab />}
        {tab === 'tatkal'    && <TatkalDealsTab cfg={cfg} isAdmin={isAdmin} isGuest={isGuest} />}
        {tab === 'lead360'   && <Lead360CRMTab cfg={cfg} isAdmin={isAdmin} isGuest={isGuest} />}
        {tab === 'franchisee'&& <FranchiseeTab isAdmin={isAdmin} isGuest={isGuest} />}
        {tab === 'wall'      && <VyaparWallTab isAdmin={isAdmin} isGuest={isGuest} />}
        {tab === 'pulse'     && <DemandPulseTab />}
        {tab === 'citydeals' && <CityDealsTab isAdmin={isAdmin} />}
        {tab === 'rules'     && <RulesTermsTab />}
        {!isGuest && tab === 'cart'      && <CartTab />}
        {!isGuest && tab === 'store'     && <StoreTab cfg={cfg} onReload={load} />}
        {!isGuest && tab === 'listings'  && <ListingsTab cfg={cfg} onReload={load} />}
        {!isGuest && tab === 'orders'    && <VendorOrdersTab onReload={load} />}
        {!isGuest && tab === 'reviews'   && <VendorReviewsTab />}
        {!isGuest && tab === 'analytics' && <AnalyticsTab />}
        {!isGuest && tab === 'prospects' && <ProspectsTab isAdmin={isAdmin} />}
        {!isGuest && tab === 'leads'     && <LeadsTab onReload={load} />}
        {!isGuest && tab === 'clients'   && <ClientsTab />}
        {!isGuest && tab === 'vyapaar'   && <VyapaarProTab />}
        {!isGuest && tab === 'plans'     && <PlansTab cfg={cfg} onReload={load} />}
        {!isGuest && tab === 'admin'     && <AdminTab />}
      </div>
      <LivePopupBanner />
      <GracePeriodBanner cfg={cfg} onRenew={load} />
      <AdminLiveCounter isAdmin={isAdmin} />
    </div>
  );
}

// ============ FEED TAB (guest browsable) ============
function FeedTab({ cfg }) {
  const location = useLocation();
  const initial = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    return {
      search: sp.get('search') || '',
      city: sp.get('city') || '',
      category: sp.get('category') || '',
      kind: sp.get('kind') || '',
      min_at: sp.get('min_at') || '',
      max_at: sp.get('max_at') || '',
    };
  }, [location.search]);
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState(initial);
  const [page, setPage] = useState(1);
  const [pageInfo, setPageInfo] = useState({ pages: 1, total: 0 });
  const [loading, setLoading] = useState(false);
  const [catOpts, setCatOpts] = useState([]);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem('mkt_view') || 'grid'); // grid, list, compact, stories
  const [geo, setGeo] = useState(() => {
    try { return JSON.parse(localStorage.getItem('mkt_geo') || 'null'); } catch { return null; }
  });
  useEffect(() => { localStorage.setItem('mkt_view', viewMode); }, [viewMode]);
  useEffect(() => { if (geo) localStorage.setItem('mkt_geo', JSON.stringify(geo)); }, [geo]);
  const requestGps = () => {
    if (!navigator.geolocation) { toast.error('GPS not supported — city filter use karein'); return; }
    toast.message('📍 Detecting your location…');
    navigator.geolocation.getCurrentPosition(
      (pos) => { setGeo({ lat: +pos.coords.latitude.toFixed(4), lng: +pos.coords.longitude.toFixed(4) }); toast.success('✅ Nearby shops filter ON'); },
      (err) => toast.error('GPS denied — city filter se search karein'),
      { enableHighAccuracy: true, timeout: 8000 }
    );
  };
  const clearGeo = () => { setGeo(null); localStorage.removeItem('mkt_geo'); toast.success('GPS filter off'); };
  // Re-apply URL params whenever they change (deep-link from Home category tiles)
  useEffect(() => { setQ(initial); }, [initial]);
  // Any filter/GPS change goes back to page 1
  useEffect(() => { setPage(1); }, [q, geo?.lat, geo?.lng]);
  useEffect(() => {
    api.get('/marketplace/categories').then((r) => {
      const admin = (r.data?.categories || []).map((c) => c.name);
      const defaults = ['Electronics', 'Fashion', 'Grocery', 'Vehicle', 'Services', 'Home', 'Food', 'Property'];
      const merged = [...new Set([...defaults, ...admin])];
      setCatOpts(merged);
    }).catch(() => setCatOpts(['Electronics', 'Fashion', 'Grocery', 'Vehicle', 'Services', 'Home', 'Food', 'Property']));
  }, []);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { ...q, limit: 60, page };
      // Only pass numeric budget if set
      if (!params.min_at) delete params.min_at;
      if (!params.max_at) delete params.max_at;
      if (geo?.lat && geo?.lng) { params.user_lat = geo.lat; params.user_lng = geo.lng; }
      const r = await api.get('/marketplace/feed', { params });
      setRows(r.data.listings || []);
      setPageInfo({ pages: r.data.pages || 1, total: r.data.total ?? (r.data.listings || []).length });
      if (r.data.page && r.data.page !== page) setPage(r.data.page);
      // Wave 48 · log the search for Demand Pulse aggregation (fire & forget)
      if ((q.search || '').trim().length >= 2) {
        api.post('/marketplace/pulse/log-search', null,
          { params: { query: q.search, city: q.city || undefined, category: q.category || undefined } }
        ).catch(() => {});
      }
    } finally { setLoading(false); }
  }, [q, geo?.lat, geo?.lng, page]);
  useEffect(() => { load(); }, [load]);
  const [pick, setPick] = useState(null);

  const openWA = (l) => {
    api.post('/marketplace/track', { listing_id: l.id, kind: 'lead' }).catch(() => {});
    const shopWa = digits(l.shop_whatsapp || '');
    if (!shopWa) {
      toast.error('Iss shop ka WhatsApp number missing hai — listing kholein detail ke liye');
      return;
    }
    const shareOg = `${process.env.REACT_APP_BACKEND_URL || ''}/api/share/marketplace/listing/${l.id}`;
    const msg = `Namaste ${l.shop_name || ''}, maine aapka item "${l.title}" (Price: ${l.price_at} AT) Anant Marketplace par dekha. Mujhe ye chahiye: ${shareOg}`;
    window.open(`https://wa.me/${shopWa}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  return (
    <div className="space-y-3">
      {/* Search + filters */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2" data-testid="mkt-filter-bar">
        <input value={q.search} onChange={(e) => setQ({ ...q, search: e.target.value })} placeholder="🔍 Search" data-testid="mkt-search" className="col-span-2 sm:col-span-2 px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-sm" />
        <input value={q.city} onChange={(e) => setQ({ ...q, city: e.target.value })} placeholder="📍 City" data-testid="mkt-city" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-sm" />
        <select value={q.kind} onChange={(e) => setQ({ ...q, kind: e.target.value })} className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-sm">
          <option value="">All types</option>
          <option value="product">Products</option>
          <option value="service">Services</option>
          <option value="showroom">Showrooms</option>
        </select>
        <input value={q.category} onChange={(e) => setQ({ ...q, category: e.target.value })} placeholder="🏷️ Category (type or pick)" data-testid="mkt-category" list="mkt-cat-list" className="col-span-2 sm:col-span-4 px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-sm" />
        <datalist id="mkt-cat-list">
          {catOpts.map((c) => (<option key={c} value={c} />))}
        </datalist>
      </div>
      {/* Geo filter row */}
      <div className="flex items-center gap-2 text-[10px]" data-testid="mkt-geo-bar">
        {geo ? (
          <>
            <span className="px-2 py-1 rounded-full bg-emerald-500/25 border border-emerald-400/60 font-black text-emerald-300 flex items-center gap-1">
              📍 Near you · {geo.lat}, {geo.lng}
            </span>
            <button onClick={clearGeo} data-testid="mkt-geo-clear" className="px-2 py-1 rounded bg-slate-700 text-white font-black">✕ Clear GPS</button>
          </>
        ) : (
          <button onClick={requestGps} data-testid="mkt-geo-detect"
                  className="px-3 py-1 rounded-full bg-sky-600 hover:bg-sky-700 text-white font-black flex items-center gap-1">
            📍 Show shops nearby
          </button>
        )}
      </div>
      {/* Wave 52c · Investment range slider (AT) */}
      <div className="rounded-xl bg-slate-900/70 border border-emerald-400/30 p-2 flex items-center gap-2 flex-wrap" data-testid="mkt-feed-budget-slider">
        <div className="text-[11px] font-black text-emerald-300 uppercase">💰 Investment / Budget (AT)</div>
        <input type="number" min="0" placeholder="Min AT" value={q.min_at}
               onChange={(e) => setQ({ ...q, min_at: e.target.value })}
               data-testid="mkt-feed-min-at"
               className="w-24 px-2 py-1 rounded bg-slate-800 border border-slate-700 text-xs" />
        <input type="range" min="0" max="100000" step="100" value={q.max_at || 100000}
               onChange={(e) => setQ({ ...q, max_at: e.target.value })}
               data-testid="mkt-feed-max-at-range"
               className="flex-1 min-w-[120px]" />
        <input type="number" min="0" placeholder="Max AT" value={q.max_at}
               onChange={(e) => setQ({ ...q, max_at: e.target.value })}
               data-testid="mkt-feed-max-at"
               className="w-24 px-2 py-1 rounded bg-slate-800 border border-slate-700 text-xs" />
        {(q.min_at || q.max_at) && (
          <button onClick={() => setQ({ ...q, min_at: '', max_at: '' })} className="px-2 py-1 rounded bg-slate-700 text-white text-[10px] font-black">✕</button>
        )}
      </div>
      {(q.category || q.city || q.kind || q.search || q.min_at || q.max_at) && (
        <div className="flex flex-wrap items-center gap-1 text-[10px]" data-testid="mkt-active-filters">
          <span className="opacity-70 font-black">Active filters:</span>
          {q.category && <span className="px-2 py-0.5 rounded-full bg-purple-500/25 text-purple-200 font-black">🏷️ {q.category}</span>}
          {q.city && <span className="px-2 py-0.5 rounded-full bg-cyan-500/25 text-cyan-200 font-black">📍 {q.city}</span>}
          {q.kind && <span className="px-2 py-0.5 rounded-full bg-emerald-500/25 text-emerald-200 font-black">{q.kind}</span>}
          {q.search && <span className="px-2 py-0.5 rounded-full bg-amber-500/25 text-amber-200 font-black">🔍 {q.search}</span>}
          {(q.min_at || q.max_at) && <span className="px-2 py-0.5 rounded-full bg-emerald-500/25 text-emerald-200 font-black">💰 {q.min_at || 0} – {q.max_at || '∞'} AT</span>}
          <button onClick={() => setQ({ search: '', city: '', category: '', kind: '', min_at: '', max_at: '' })} data-testid="mkt-clear-filters"
                  className="ml-auto px-2 py-0.5 rounded bg-slate-700 text-white font-black">✕ Clear</button>
        </div>
      )}

      {loading && <div className="text-center opacity-60 py-8">⏳ Loading…</div>}
      {!loading && rows.length === 0 && <div className="text-center opacity-60 py-10 text-xs">No listings match. Filter change karein.</div>}

      {/* View mode switcher */}
      {rows.length > 0 && (
        <div className="flex items-center gap-1 text-[10px]" data-testid="mkt-view-switcher">
          <span className="opacity-70 font-black uppercase mr-1">View:</span>
          {[
            { k: 'grid',    lbl: '🔲 Grid' },
            { k: 'list',    lbl: '📋 List' },
            { k: 'compact', lbl: '🧾 Compact' },
            { k: 'stories', lbl: '📖 Stories' },
          ].map((v) => (
            <button key={v.k} onClick={() => setViewMode(v.k)} data-testid={`mkt-view-${v.k}`}
                    className={`px-2 py-1 rounded font-black ${viewMode === v.k ? 'bg-purple-500 text-white' : 'bg-slate-800 text-slate-300'}`}>
              {v.lbl}
            </button>
          ))}
        </div>
      )}

      {/* Listings — 4 layouts */}
      {viewMode === 'grid' && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
          {rows.map((l) => (
            <button key={l.id} id={`listing-${l.id}`} onClick={() => { setPick(l); api.post('/marketplace/track', { listing_id: l.id, kind: 'view' }).catch(() => {}); }}
                    data-testid={`mkt-listing-${l.id}`}
                    className={`text-left rounded-2xl overflow-hidden border-2 transition ${l.is_sponsor ? 'border-amber-400 bg-amber-500/10' : l.featured ? 'border-purple-400/70 bg-purple-500/10' : 'border-slate-700 bg-slate-900/70'} hover:border-purple-300`}>
              <div className="aspect-square bg-slate-800 relative">
                {l.images?.[0] ? <img src={abs(l.images[0])} alt="" className="w-full h-full object-contain" /> : <div className="w-full h-full flex items-center justify-center text-4xl">📦</div>}
                {l.is_sponsor && <span className="absolute top-1 left-1 px-1.5 py-0.5 rounded bg-amber-400 text-slate-900 text-[9px] font-black">⭐ SPONSORED</span>}
                {l.verified && <span className="absolute top-1 right-1 px-1.5 py-0.5 rounded bg-emerald-500 text-white text-[9px] font-black">✓ VERIFIED</span>}
              </div>
              <div className="p-2 space-y-0.5">
                <div className="text-xs font-black truncate">{l.title}</div>
                <div className="text-sm font-black text-yellow-300">{l.price_at} AT</div>
                <div className="text-[9px] opacity-70 truncate">🏪 {l.shop_name || '—'}{l.city ? ` · 📍 ${l.city}` : ''}</div>
                <div className="text-[8px] opacity-60">👁 {l.views || 0} · 💬 {l.leads || 0}</div>
              </div>
            </button>
          ))}
        </div>
      )}

      {viewMode === 'list' && (
        <div className="space-y-2">
          {rows.map((l) => (
            <button key={l.id} onClick={() => { setPick(l); api.post('/marketplace/track', { listing_id: l.id, kind: 'view' }).catch(() => {}); }}
                    data-testid={`mkt-listing-list-${l.id}`}
                    className={`w-full text-left rounded-xl overflow-hidden border-2 flex gap-2 p-2 transition ${l.is_sponsor ? 'border-amber-400 bg-amber-500/10' : 'border-slate-700 bg-slate-900/70'} hover:border-purple-300`}>
              {l.images?.[0] ? <img src={abs(l.images[0])} alt="" className="w-20 h-20 object-cover rounded-lg" /> : <div className="w-20 h-20 rounded-lg bg-slate-800 flex items-center justify-center text-3xl">📦</div>}
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-1 mb-0.5">
                  {l.is_sponsor && <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-400 text-slate-900 font-black">⭐</span>}
                  {l.verified && <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500 text-white font-black">✓</span>}
                  <div className="text-sm font-black truncate">{l.title}</div>
                </div>
                <div className="text-sm font-black text-yellow-300">{l.price_at} AT</div>
                <div className="text-[10px] opacity-70 truncate">🏪 {l.shop_name}{l.city ? ` · 📍 ${l.city}` : ''}</div>
                <div className="text-[9px] opacity-60">👁 {l.views || 0} · 💬 {l.leads || 0}</div>
              </div>
              <div className="text-emerald-300 self-center pr-1"><MessageCircle size={16} /></div>
            </button>
          ))}
        </div>
      )}

      {viewMode === 'compact' && (
        <div className="rounded-xl border-2 border-slate-700 overflow-hidden divide-y divide-slate-800">
          {rows.map((l) => (
            <button key={l.id} onClick={() => { setPick(l); api.post('/marketplace/track', { listing_id: l.id, kind: 'view' }).catch(() => {}); }}
                    data-testid={`mkt-listing-compact-${l.id}`}
                    className="w-full text-left flex items-center gap-2 py-1.5 px-2 hover:bg-slate-800/50 transition">
              {l.images?.[0] ? <img src={abs(l.images[0])} alt="" className="w-8 h-8 object-cover rounded" /> : <div className="w-8 h-8 rounded bg-slate-800 flex items-center justify-center text-lg">📦</div>}
              <div className="flex-1 min-w-0">
                <div className="text-[11px] font-black truncate">{l.title} {l.is_sponsor && <span className="text-amber-400">⭐</span>}</div>
                <div className="text-[9px] opacity-70 truncate">🏪 {l.shop_name}{l.city ? ` · ${l.city}` : ''}</div>
              </div>
              <div className="text-xs font-black text-yellow-300 whitespace-nowrap">{l.price_at} AT</div>
            </button>
          ))}
        </div>
      )}

      {viewMode === 'stories' && (
        <div className="flex gap-2 overflow-x-auto snap-x snap-mandatory pb-2" data-testid="mkt-stories-scroll">
          {rows.map((l) => (
            <button key={l.id} onClick={() => { setPick(l); api.post('/marketplace/track', { listing_id: l.id, kind: 'view' }).catch(() => {}); }}
                    data-testid={`mkt-listing-story-${l.id}`}
                    className={`shrink-0 snap-center w-64 rounded-2xl overflow-hidden border-2 ${l.is_sponsor ? 'border-amber-400' : 'border-slate-700'} bg-slate-900 hover:border-purple-300 transition`}>
              <div className="aspect-[9/12] bg-slate-800 relative">
                {l.images?.[0] ? <img src={abs(l.images[0])} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-6xl">📦</div>}
                <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black to-transparent">
                  <div className="flex items-center gap-1 mb-1">
                    {l.is_sponsor && <span className="text-[9px] px-1.5 py-0.5 rounded bg-amber-400 text-slate-900 font-black">⭐ SPONSORED</span>}
                    {l.verified && <span className="text-[9px] px-1.5 py-0.5 rounded bg-emerald-500 text-white font-black">✓</span>}
                  </div>
                  <div className="text-sm font-black text-white truncate">{l.title}</div>
                  <div className="text-lg font-black text-yellow-300">{l.price_at} AT</div>
                  <div className="text-[10px] text-white/80 truncate">🏪 {l.shop_name}{l.city ? ` · ${l.city}` : ''}</div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}

      {pageInfo.pages > 1 && (
        <div className="flex flex-wrap items-center justify-center gap-1 pt-2" data-testid="mkt-pagination">
          <button disabled={page <= 1} onClick={() => { setPage(page - 1); window.scrollTo({ top: 0, behavior: 'smooth' }); }} data-testid="mkt-page-prev"
                  className="px-3 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs font-black text-white disabled:opacity-40">‹ Prev</button>
          {Array.from({ length: pageInfo.pages }, (_, i) => i + 1)
            .filter((n) => n === 1 || n === pageInfo.pages || Math.abs(n - page) <= 2)
            .reduce((acc, n) => { if (acc.length && n - acc[acc.length - 1] > 1) acc.push('…'); acc.push(n); return acc; }, [])
            .map((n, i) => n === '…'
              ? <span key={`gap-${i}`} className="px-1 text-slate-400">…</span>
              : <button key={n} onClick={() => { setPage(n); window.scrollTo({ top: 0, behavior: 'smooth' }); }} data-testid={`mkt-page-${n}`}
                        className={`min-w-[2rem] px-2 py-1.5 rounded border text-xs font-black ${n === page ? 'bg-purple-600 border-purple-400 text-white' : 'bg-slate-800 border-slate-700 text-slate-200'}`}>{n}</button>)}
          <button disabled={page >= pageInfo.pages} onClick={() => { setPage(page + 1); window.scrollTo({ top: 0, behavior: 'smooth' }); }} data-testid="mkt-page-next"
                  className="px-3 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs font-black text-white disabled:opacity-40">Next ›</button>
          <span className="w-full text-center text-[10px] text-slate-400">Page {page} of {pageInfo.pages} · {pageInfo.total} listings</span>
        </div>
      )}

      {pick && <ListingModal l={pick} onClose={() => setPick(null)} openWA={openWA} />}
    </div>
  );
}

function ListingImageCarousel({ images }) {
  const imgs = (images || []).filter(Boolean);
  const [idx, setIdx] = useState(0);
  const touchX = useRef(null);
  const n = imgs.length;
  const go = (d) => setIdx((i) => (i + d + n) % n);
  if (n <= 1) return <img src={abs(imgs[0])} alt="" className="w-full aspect-square object-cover" />;
  return (
    <div className="relative w-full aspect-square overflow-hidden select-none" data-testid="mkt-modal-carousel"
         onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
         onTouchEnd={(e) => {
           if (touchX.current == null) return;
           const dx = e.changedTouches[0].clientX - touchX.current;
           touchX.current = null;
           if (Math.abs(dx) > 40) go(dx < 0 ? 1 : -1);
         }}>
      <div className="flex h-full transition-transform duration-300" style={{ transform: `translateX(-${idx * 100}%)` }}>
        {imgs.map((src, i) => <img key={i} src={abs(src)} alt="" draggable={false} className="w-full h-full object-cover shrink-0" />)}
      </div>
      <button onClick={() => go(-1)} data-testid="mkt-carousel-prev" aria-label="Previous image"
              className="hidden sm:flex absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/60 text-white font-black items-center justify-center">‹</button>
      <button onClick={() => go(1)} data-testid="mkt-carousel-next" aria-label="Next image"
              className="hidden sm:flex absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-black/60 text-white font-black items-center justify-center">›</button>
      <div className="absolute bottom-2 inset-x-0 flex justify-center gap-1.5">
        {imgs.map((_, i) => (
          <button key={i} onClick={() => setIdx(i)} aria-label={`Image ${i + 1}`}
                  className={`h-1.5 rounded-full transition-all ${i === idx ? 'w-5 bg-white' : 'w-1.5 bg-white/50'}`} />
        ))}
      </div>
      <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-black/60 text-white text-[10px] font-black">{idx + 1}/{n}</div>
    </div>
  );
}

function ListingModal({ l, onClose, openWA }) {
  const yt = useMemo(() => {
    if (!l.video_url) return null;
    const m = l.video_url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{6,})/);
    return m ? `https://www.youtube.com/embed/${m[1]}` : null;
  }, [l.video_url]);
  const [buyer, setBuyer] = useState({ name: '', phone: '', city: '', note: '' });
  const [shareImgIdx, setShareImgIdx] = useState(0);
  const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
  // OG-preview share URL — social scrapers hit this and get the product image tag
  const url = `${API_BASE}/api/share/marketplace/listing/${l.id}${shareImgIdx ? `?img=${shareImgIdx}` : ''}`;

  const logAndOpen = async (targetPhone, requestType, extraText) => {
    const p = String(targetPhone || '').replace(/[^0-9]/g, '');
    // Ensure a valid target — if missing, fall back to store wa
    const shopPhone = String(l.shop_whatsapp || '').replace(/[^0-9]/g, '');
    const finalPhone = p || shopPhone;
    if (!finalPhone) {
      toast.error('Shop owner ka WhatsApp number set nahi hai — abhi contact possible nahi');
      return;
    }
    try {
      await api.post('/marketplace/lead', {
        shop_slug: l.shop_slug, listing_id: l.id,
        buyer_name: buyer.name || null, buyer_phone: buyer.phone || null,
        buyer_city: buyer.city || null, request_type: requestType,
        note: buyer.note || null,
      });
    } catch {}
    const text = `${extraText || 'Namaste, mujhe ye chahiye:'}\n\n*${l.title}* · ${l.price_at} AT\n🏪 ${l.shop_name || ''}\n${url}${buyer.name ? `\n\n👤 ${buyer.name}` : ''}${buyer.phone ? `\n📞 ${buyer.phone}` : ''}${buyer.city ? `\n📍 ${buyer.city}` : ''}${buyer.note ? `\n📝 ${buyer.note}` : ''}`;
    // Direct chat with target number
    window.open(`https://wa.me/${finalPhone}?text=${encodeURIComponent(text)}`, '_blank');
  };

  const fetchImageFile = async () => {
    const imgs = l.images || [];
    if (!imgs.length) return null;
    const chosen = imgs[Math.min(shareImgIdx, imgs.length - 1)];
    // Try direct fetch first; if it fails (CORS), fall back to backend proxy which strips CORS + compresses
    const trySrc = async (src) => {
      try {
        const resp = await fetch(src, { credentials: 'omit', mode: 'cors' });
        if (!resp.ok) return null;
        const blob = await resp.blob();
        // Instagram needs image/jpeg or image/png
        if (!blob.type.startsWith('image/')) return null;
        return new File([blob], `${(l.title || 'product').replace(/\W+/g, '_')}.jpg`, { type: 'image/jpeg' });
      } catch { return null; }
    };
    const api = process.env.REACT_APP_BACKEND_URL || '';
    const absolute = chosen.startsWith('http') ? chosen : `${api}${chosen}`;
    // Direct first
    let f = await trySrc(absolute);
    if (f) return f;
    // Fallback via /api/share/img/{base64} (server-side fetches, strips CORS)
    try {
      const b64 = btoa(unescape(encodeURIComponent(absolute))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
      f = await trySrc(`${api}/api/share/img/${b64}.jpg`);
    } catch {}
    return f;
  };

  const share = async (kind) => {
    const text = `${l.title} · ${l.price_at} AT · ${l.shop_name || ''}\n${url}`;
    const file = await fetchImageFile();
    // ALWAYS try native share with file FIRST — this actually attaches the image bytes to WA/IG/FB
    if (file && navigator.canShare && navigator.canShare({ files: [file] })) {
      try { await navigator.share({ title: l.title, text, files: [file] }); toast.success('✅ Image share ho gaya'); return; }
      catch (e) { if (e && e.name === 'AbortError') return; }
    }
    // Native without file (URL only) — better than nothing for IG on mobile via Story sticker
    if (kind === 'instagram' && navigator.share && !file) {
      try { await navigator.share({ title: l.title, text, url }); toast.success('📸 Instagram me share karo'); return; }
      catch (e) { if (e && e.name === 'AbortError') return; }
    }
    // Fallback: download image + open target with OG-preview URL
    if (file) {
      const a = document.createElement('a'); a.href = URL.createObjectURL(file); a.download = file.name;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      toast.success('🖼 Image downloaded — target app me attach karein');
    } else {
      toast.warning('⚠️ Image fetch nahi hui — sirf link share ho raha hai');
    }
    if (kind === 'wa') window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(text)}`, '_blank');
    else if (kind === 'fb') window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
    else if (kind === 'instagram') {
      // Desktop → open IG create page; mobile web-share already handled above
      window.open('https://www.instagram.com/', '_blank');
    }
    else navigator.clipboard.writeText(text);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/85 flex items-end sm:items-center justify-center p-2" onClick={onClose} data-testid="mkt-listing-modal">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-lg rounded-2xl bg-slate-900 border-2 border-purple-400/60 max-h-[95vh] overflow-y-auto">
        <div className="relative">
          {l.images?.[0] && <ListingImageCarousel images={l.images} />}
          <button onClick={onClose} className="absolute top-2 right-2 w-9 h-9 rounded-full bg-black/70 text-white font-black">✕</button>
        </div>
        <div className="p-4 space-y-3">
          <div>
            <div className="text-lg font-black">{l.title}</div>
            <div className="text-xl font-black text-yellow-300 mt-1">{l.price_at} AT</div>
            <div className="text-[10px] opacity-70">🏪 {l.shop_name} {l.verified && '· ✓ Verified'} {l.city ? `· 📍 ${l.city}` : ''}</div>
          </div>
          {l.description && <div className="text-xs opacity-90 whitespace-pre-wrap">{l.description}</div>}
          {l.video_url && (
            <div className="rounded-xl overflow-hidden border-2 border-slate-700 bg-black">
              {yt ? <iframe title="video" src={yt} className="w-full aspect-video" allow="autoplay; encrypted-media" allowFullScreen /> : <video controls src={abs(l.video_url)} className="w-full aspect-video bg-black" />}
            </div>
          )}
          {/* Buyer info form */}
          <div className="rounded-xl bg-slate-800/40 border border-slate-700 p-2 space-y-2">
            <div className="text-[10px] font-black text-purple-300 uppercase">📝 Your details (optional but recommended)</div>
            <div className="grid grid-cols-2 gap-2">
              <input value={buyer.name} onChange={(e) => setBuyer({ ...buyer, name: e.target.value })} placeholder="Name" data-testid="mkt-buyer-name" className="px-2 py-1.5 rounded bg-slate-900 border border-slate-700 text-xs" />
              <input value={buyer.phone} onChange={(e) => setBuyer({ ...buyer, phone: e.target.value })} placeholder="Phone" data-testid="mkt-buyer-phone" className="px-2 py-1.5 rounded bg-slate-900 border border-slate-700 text-xs" />
              <input value={buyer.city} onChange={(e) => setBuyer({ ...buyer, city: e.target.value })} placeholder="City" className="px-2 py-1.5 rounded bg-slate-900 border border-slate-700 text-xs" />
              <input value={buyer.note} onChange={(e) => setBuyer({ ...buyer, note: e.target.value })} placeholder="Note" className="px-2 py-1.5 rounded bg-slate-900 border border-slate-700 text-xs" />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => logAndOpen(l.shop_whatsapp, 'order', 'Namaste, mujhe ye product order karna hai:')}
                    data-testid={`mkt-order-${l.id}`}
                    className="py-2.5 rounded-xl bg-green-600 hover:bg-green-700 text-white text-xs font-black flex items-center justify-center gap-1">
              <MessageCircle size={13} /> Order via WhatsApp
            </button>
            <button onClick={() => logAndOpen(l.shop_delivery_whatsapp || l.shop_whatsapp, 'delivery_request', '🚚 Delivery request:')}
                    data-testid={`mkt-delivery-${l.id}`}
                    className="py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 text-white text-xs font-black flex items-center justify-center gap-1">
              🚚 Request Delivery
            </button>
          </div>
          {/* Add to Cart (logged-in only) */}
          {localStorage.getItem('token') && (
            <button onClick={async () => {
              try { await api.post('/marketplace/cart/add', { listing_id: l.id, quantity: 1 });
                    toast.success('🛍️ Cart me add ho gaya — Cart tab me jaake bulk order karein'); }
              catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
            }} data-testid={`mkt-add-cart-${l.id}`}
                    className="w-full py-2 rounded-xl bg-amber-500 hover:bg-amber-600 text-slate-900 text-xs font-black flex items-center justify-center gap-1">
              🛍️ Add to Cart · Order later
            </button>
          )}

          {/* Group Buy CTA (Wave 50) — only if seller enabled it */}
          {l.group_buy_enabled && l.group_buy_min_buyers && l.group_buy_discount_pct ? (
            <GroupBuyStartBlock l={l} />
          ) : null}
          <div className="grid grid-cols-4 gap-1">
            <button onClick={() => share('wa')} data-testid="mkt-share-wa" className="py-1.5 rounded bg-green-600 text-white text-[10px] font-black">📱 WA</button>
            <button onClick={() => share('instagram')} data-testid="mkt-share-ig" className="py-1.5 rounded bg-gradient-to-r from-purple-500 to-pink-500 text-white text-[10px] font-black">📸 IG</button>
            <button onClick={() => share('fb')} data-testid="mkt-share-fb" className="py-1.5 rounded bg-blue-600 text-white text-[10px] font-black">📘 FB</button>
            <button onClick={() => share('native')} data-testid="mkt-share-native" className="py-1.5 rounded bg-slate-700 text-white text-[10px] font-black">📲 More</button>
          </div>
          {/* Image selector for share (only if listing has multiple images) */}
          {l.images && l.images.length > 1 && (
            <div className="rounded-lg bg-slate-800/40 border border-slate-700 p-2" data-testid="mkt-share-img-picker">
              <div className="text-[10px] font-black text-purple-300 uppercase mb-1">🖼️ Share which image?</div>
              <div className="flex gap-1 overflow-x-auto">
                {l.images.map((im, i) => (
                  <button key={i} onClick={() => setShareImgIdx(i)}
                          data-testid={`mkt-share-img-${i}`}
                          className={`shrink-0 w-14 h-14 rounded-lg overflow-hidden border-2 ${shareImgIdx === i ? 'border-purple-400' : 'border-slate-700'}`}>
                    <img src={abs(im)} alt={`img-${i}`} className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            </div>
          )}
          {/* Login CTA for guests */}
          {!localStorage.getItem('token') && (
            <a href="/login" data-testid="mkt-listing-login-cta"
               className="block text-center py-2 rounded-xl bg-gradient-to-r from-yellow-400 via-amber-400 to-orange-400 text-slate-900 text-xs font-black">
              🔐 Login for Safe Buying · Promo Offers · Local for Vocal Offers 🇮🇳
            </a>
          )}
          {/* Product/Service Reviews */}
          <ProductReviewSection listingId={l.id}
                                vendorId={l.user_id || l.seller_id} />
        </div>
      </div>
    </div>
  );
}

// ============ GROUP BUY (Wave 50) ============
function GroupBuyStartBlock({ l }) {
  const [qty, setQty] = useState(1);
  const [busy, setBusy] = useState(false);
  const [gb, setGb] = useState(null);
  const maxQty = Number(l.group_buy_max_qty_per_buyer || 1);
  const discPrice = Number(l.price_at) * (1 - Number(l.group_buy_discount_pct || 0) / 100);
  const start = async () => {
    if (!localStorage.getItem('token')) { window.location.href = '/login'; return; }
    setBusy(true);
    try {
      const r = await api.post('/group-buy/start', { listing_id: l.id, quantity: qty });
      setGb(r.data.group_buy);
      toast.success('🎁 Group Buy shuru! Link share karke buyers add karein');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to start group buy'); }
    finally { setBusy(false); }
  };
  if (gb) return <GroupBuyShareCard gb={gb} listing={l} />;
  return (
    <div className="rounded-xl border-2 border-fuchsia-400/50 bg-gradient-to-br from-fuchsia-600/20 to-purple-700/20 p-3 space-y-2" data-testid="mkt-gb-start-block">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[11px] font-black text-fuchsia-200">🎁 GROUP BUY UNLOCK</div>
          <div className="text-[16px] font-black text-white">
            {discPrice.toFixed(0)} AT <span className="text-[10px] font-bold text-fuchsia-300 line-through opacity-70">{Number(l.price_at).toFixed(0)} AT</span>
            <span className="ml-1 text-[10px] font-black text-emerald-300">−{Number(l.group_buy_discount_pct).toFixed(0)}%</span>
          </div>
        </div>
        <div className="text-right">
          <div className="text-[9px] font-black text-fuchsia-300 uppercase">Need</div>
          <div className="text-lg font-black text-white">{l.group_buy_min_buyers}</div>
          <div className="text-[9px] opacity-80">buyers</div>
        </div>
      </div>
      {maxQty > 1 && (
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-black text-fuchsia-200 uppercase">Qty</span>
          <button onClick={() => setQty(Math.max(1, qty - 1))} className="w-7 h-7 rounded bg-slate-800 text-white font-black">−</button>
          <span className="text-white font-black w-6 text-center" data-testid="mkt-gb-qty">{qty}</span>
          <button onClick={() => setQty(Math.min(maxQty, qty + 1))} className="w-7 h-7 rounded bg-slate-800 text-white font-black">+</button>
          <span className="text-[9px] opacity-70">max {maxQty}</span>
        </div>
      )}
      <button onClick={start} disabled={busy} data-testid={`mkt-gb-start-${l.id}`}
              className="w-full py-2.5 rounded-xl bg-gradient-to-r from-fuchsia-500 to-purple-600 text-white font-black text-sm disabled:opacity-50">
        {busy ? '...' : `🎁 Start Group Buy · ${(discPrice * qty).toFixed(0)} AT (24h)`}
      </button>
      <div className="text-[9px] opacity-75 text-fuchsia-100 text-center">
        Timer expire ho gaya to auto-refund. Min buyers hue to auto-confirm at discount price.
      </div>
    </div>
  );
}

function GroupBuyShareCard({ gb, listing }) {
  const shareUrl = `${window.location.origin}/gb/${gb.id}`;
  const copy = async () => {
    try { await navigator.clipboard.writeText(shareUrl); toast.success('📋 Link copied'); }
    catch { toast.error('Copy failed'); }
  };
  const waShare = () => {
    const title = listing?.title || gb.listing_title || 'this product';
    const msg = `🎁 Group Buy on *${title}* — Min ${gb.min_buyers} buyers · ${gb.discount_pct}% OFF · 24h only\n\nJoin: ${shareUrl}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
  };
  return (
    <div className="rounded-xl border-2 border-fuchsia-400/60 bg-gradient-to-br from-fuchsia-600/25 to-purple-700/25 p-3 space-y-2" data-testid="mkt-gb-share-card">
      <div className="text-[11px] font-black text-fuchsia-200">🎁 GROUP BUY ACTIVE · Share the link!</div>
      <div className="text-[13px] font-black text-white">Joined: {(gb.participants || []).length} / {gb.min_buyers}</div>
      <input readOnly value={shareUrl} onFocus={(e) => e.target.select()}
             data-testid="mkt-gb-share-url"
             className="w-full px-2 py-1.5 rounded bg-slate-900 border border-slate-700 text-xs text-fuchsia-100" />
      <div className="grid grid-cols-2 gap-2">
        <button onClick={copy} data-testid="mkt-gb-copy" className="py-2 rounded-lg bg-slate-700 text-white text-xs font-black">📋 Copy Link</button>
        <button onClick={waShare} data-testid="mkt-gb-wa" className="py-2 rounded-lg bg-green-600 text-white text-xs font-black">📱 Share on WhatsApp</button>
      </div>
    </div>
  );
}

function LiveGroupBuysTab() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(Date.now());
  const load = useCallback(async () => {
    try { const r = await api.get('/group-buy/live'); setRows(r.data.live || []); } catch {}
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  return (
    <div className="space-y-3" data-testid="mkt-live-gb-tab">
      <div className="flex items-center justify-between">
        <div className="text-sm font-black text-white">🎁 Live Group Buys</div>
        <button onClick={load} data-testid="mkt-live-gb-refresh" className="px-2 py-1 rounded bg-slate-800 text-xs font-black text-purple-300">↻ Refresh</button>
      </div>
      {loading && <div className="text-center opacity-60 py-6 text-xs">Loading…</div>}
      {!loading && rows.length === 0 && (
        <div className="text-center opacity-60 py-8 text-xs" data-testid="mkt-live-gb-empty">
          Abhi koi live group buy nahi hai. Kisi listing par "🎁 Start Group Buy" try karein.
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {rows.map((gb) => <LiveGroupBuyCard key={gb.id} gb={gb} now={now} />)}
      </div>
    </div>
  );
}

function LiveGroupBuyCard({ gb, now }) {
  const secondsLeft = Math.max(0, (gb.seconds_left || 0) - Math.floor((now - Date.now()) / 1000));
  const img = gb.listing_image;
  const h = Math.floor(secondsLeft / 3600);
  const m = Math.floor((secondsLeft % 3600) / 60);
  const s = secondsLeft % 60;
  return (
    <a href={`/gb/${gb.id}`} data-testid={`mkt-live-gb-${gb.id}`}
       className="block rounded-xl border-2 border-fuchsia-400/40 bg-slate-900 overflow-hidden hover:border-fuchsia-300 transition">
      <div className="relative">
        {img ? <img src={abs(img)} alt="" className="w-full h-32 object-contain bg-slate-800" />
             : <div className="w-full h-32 bg-slate-800 flex items-center justify-center text-3xl">🎁</div>}
        <div className="absolute top-1 right-1 bg-black/70 text-white text-[10px] font-black px-2 py-0.5 rounded">
          ⏱ {String(h).padStart(2,'0')}:{String(m).padStart(2,'0')}:{String(s).padStart(2,'0')}
        </div>
        <div className="absolute top-1 left-1 bg-fuchsia-500 text-white text-[10px] font-black px-2 py-0.5 rounded">
          −{Number(gb.discount_pct).toFixed(0)}%
        </div>
      </div>
      <div className="p-2 space-y-1">
        <div className="text-[12px] font-black text-white line-clamp-1">{gb.listing_title}</div>
        <div className="text-[10px] opacity-70">{gb.shop_name}</div>
        <div className="flex items-center gap-2">
          <div className="flex-1 h-2 bg-slate-800 rounded overflow-hidden">
            <div className="h-full bg-fuchsia-500" style={{ width: `${gb.progress_pct}%` }} />
          </div>
          <div className="text-[10px] font-black text-fuchsia-300">{gb.joined_count}/{gb.min_buyers}</div>
        </div>
        <div className="text-[10px] font-black text-emerald-300">{Number(gb.discounted_price).toFixed(0)} AT <span className="line-through opacity-60 text-[9px] text-slate-400">{Number(gb.original_price).toFixed(0)} AT</span></div>
      </div>
    </a>
  );
}

// ============ FRANCHISEE TAB (Wave 51) ============
function FranchiseeTab({ isAdmin, isGuest }) {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applyFor, setApplyFor] = useState(null);
  const [showAdmin, setShowAdmin] = useState(false);
  const [mine, setMine] = useState([]);
  const [minAt, setMinAt] = useState('');
  const [maxAt, setMaxAt] = useState('');
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (minAt) params.min_at = Number(minAt);
      if (maxAt) params.max_at = Number(maxAt);
      const r = await api.get('/franchisee', { params });
      setPosts(r.data.posts || []);
      if (!isGuest) {
        try { const m = await api.get('/franchisee/applications/mine'); setMine(m.data.applications || []); } catch {}
      }
    } catch (e) { toast.error('Load failed'); }
    finally { setLoading(false); }
  }, [isGuest, minAt, maxAt]);
  useEffect(() => { load(); }, [load]);

  const share = async (p) => {
    const url = `${window.location.origin}/franchisee/${p.id}`;
    const msg = `🏢 Franchise Opportunity: *${p.title}*\n${p.tagline || ''}\n${p.investment_range ? 'Investment: ' + p.investment_range : ''}\n\n${url}`;
    if (navigator.share) {
      try { await navigator.share({ title: p.title, text: msg, url }); return; } catch {}
    }
    try { await navigator.clipboard.writeText(url); toast.success('📋 Link copied'); } catch { toast.error('Copy failed'); }
  };
  const waShare = (p) => {
    const url = `${window.location.origin}/franchisee/${p.id}`;
    const msg = `🏢 Franchise Opportunity: *${p.title}*\n${p.tagline || ''}\n${p.investment_range ? 'Investment: ' + p.investment_range : ''}\n\nDetails: ${url}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
  };

  return (
    <div className="space-y-3" data-testid="mkt-franchisee-tab">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-lg font-black">🏢 Franchisee Opportunities</div>
          <div className="text-[11px] opacity-70">Verified franchise listings · apply with 1 tap · share with partners.</div>
        </div>
        {isAdmin && (
          <button onClick={() => setShowAdmin((v) => !v)} data-testid="mkt-franchisee-admin-toggle"
                  className="px-3 py-1.5 rounded-lg bg-amber-500 text-slate-900 text-xs font-black">
            {showAdmin ? 'Hide Admin' : '👑 Admin Panel'}
          </button>
        )}
      </div>

      {isAdmin && showAdmin && <FranchiseeAdminPanel onDone={load} />}

      {/* Wave 53 · Investment slider */}
      <div className="rounded-xl bg-slate-900/70 border border-emerald-400/30 p-2 flex items-center gap-2 flex-wrap" data-testid="mkt-fr-budget-slider">
        <div className="text-[11px] font-black text-emerald-300 uppercase">💰 Investment Budget (AT)</div>
        {/* 1-tap chip presets */}
        {[['<5L', 0, 500000], ['5-10L', 500000, 1000000], ['10-25L', 1000000, 2500000], ['25L+', 2500000, 99999999]].map(([label, min, max]) => (
          <button key={label}
                  onClick={() => { setMinAt(String(min)); setMaxAt(max >= 99999999 ? '' : String(max)); }}
                  data-testid={`mkt-fr-chip-${label}`}
                  className="px-2 py-0.5 rounded-full text-[10px] font-black bg-emerald-500/20 border border-emerald-400/40 text-emerald-200 hover:bg-emerald-500/40 transition">
            {label}
          </button>
        ))}
        <input type="number" min="0" placeholder="Min" value={minAt}
               onChange={(e) => setMinAt(e.target.value)}
               data-testid="mkt-fr-min-at"
               className="w-20 px-2 py-1 rounded bg-slate-800 border border-slate-700 text-xs" />
        <input type="range" min="0" max="100000" step="500" value={maxAt || 100000}
               onChange={(e) => setMaxAt(e.target.value)}
               data-testid="mkt-fr-max-at-range"
               className="flex-1 min-w-[100px]" />
        <input type="number" min="0" placeholder="Max" value={maxAt}
               onChange={(e) => setMaxAt(e.target.value)}
               data-testid="mkt-fr-max-at"
               className="w-20 px-2 py-1 rounded bg-slate-800 border border-slate-700 text-xs" />
        {(minAt || maxAt) && (
          <button onClick={() => { setMinAt(''); setMaxAt(''); }} className="px-2 py-1 rounded bg-slate-700 text-white text-[10px] font-black">✕</button>
        )}
      </div>

      {/* Public Leaderboard · Top 10 Fastest-Growing (last 30d) */}
      <FranchiseeLeaderboard />

      {loading && <div className="text-center opacity-60 py-6 text-xs">Loading…</div>}
      {!loading && posts.length === 0 && (
        <div className="text-center opacity-60 py-8 text-xs" data-testid="mkt-franchisee-empty">
          Abhi koi franchise opportunity listed nahi hai. {isAdmin ? 'Admin Panel se add karein.' : 'Jald hi listed hongi — baad me check karein.'}
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {posts.map((p) => (
          <div key={p.id} className="rounded-2xl bg-slate-900 border-2 border-amber-400/30 overflow-hidden" data-testid={`mkt-franchisee-card-${p.id}`}>
            {(p.images || [])[0] && (
              <img src={abs(p.images[0])} alt="" className="w-full h-40 object-contain bg-slate-800/60" />
            )}
            <div className="p-3 space-y-2">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <div className="text-sm font-black truncate">{p.title}</div>
                  {p.tagline && <div className="text-[11px] opacity-70">{p.tagline}</div>}
                </div>
                <div className="text-[10px] opacity-60 flex-shrink-0">👁 {p.views || 0}</div>
              </div>
              <div className="flex flex-wrap gap-1 text-[10px]">
                {p.category && <span className="px-1.5 py-0.5 rounded bg-slate-800">🏷 {p.category}</span>}
                {p.city && <span className="px-1.5 py-0.5 rounded bg-slate-800">📍 {p.city}</span>}
                {p.investment_range && <span className="px-1.5 py-0.5 rounded bg-emerald-600/20 text-emerald-300 font-black">💰 {p.investment_range}</span>}
                {p.roi && <span className="px-1.5 py-0.5 rounded bg-purple-600/20 text-purple-300 font-black">📈 {p.roi}</span>}
              </div>
              {p.description && <div className="text-[11px] opacity-80 line-clamp-3 whitespace-pre-wrap">{p.description}</div>}
              {(p.videos || []).slice(0, 1).map((v, i) => (
                <video key={i} src={abs(v)} controls className="w-full rounded" />
              ))}
              {(p.posts || []).length > 0 && (
                <details className="rounded border border-slate-800">
                  <summary className="px-2 py-1 text-[11px] opacity-80 cursor-pointer">📰 Posts & Updates ({p.posts.length})</summary>
                  <div className="p-2 space-y-2 max-h-60 overflow-y-auto">
                    {p.posts.map((pp, i) => (
                      <div key={i} className="rounded bg-slate-800/60 p-2 space-y-1">
                        {pp.title && <div className="text-[12px] font-black">{pp.title}</div>}
                        {pp.image && <img src={abs(pp.image)} alt="" className="w-full h-28 object-cover rounded" />}
                        {pp.video && <video src={abs(pp.video)} controls className="w-full rounded" />}
                        {pp.body && <div className="text-[11px] opacity-85 whitespace-pre-wrap">{pp.body}</div>}
                        {pp.url && <a href={pp.url} target="_blank" rel="noreferrer" className="text-[11px] text-sky-300 underline">🔗 Open link</a>}
                      </div>
                    ))}
                  </div>
                </details>
              )}
              <div className="grid grid-cols-4 gap-1">
                {!isGuest ? (
                  <button onClick={() => setApplyFor(p)} data-testid={`mkt-franchisee-apply-${p.id}`}
                          className="col-span-2 py-2 rounded-lg bg-emerald-500 text-slate-900 text-xs font-black">📝 Apply</button>
                ) : (
                  <a href="/login" className="col-span-2 py-2 rounded-lg bg-slate-700 text-white text-xs font-black text-center">🔐 Login to apply</a>
                )}
                {p.whatsapp && (
                  <a href={`https://wa.me/${String(p.whatsapp).replace(/[^0-9]/g, '')}`} target="_blank" rel="noreferrer"
                     data-testid={`mkt-franchisee-wa-${p.id}`}
                     className="py-2 rounded-lg bg-green-600 text-white text-xs font-black text-center">📱 WA</a>
                )}
                <button onClick={() => share(p)} data-testid={`mkt-franchisee-share-${p.id}`}
                        className="py-2 rounded-lg bg-sky-600 text-white text-xs font-black">🔗 Share</button>
              </div>
              <div className="grid grid-cols-2 gap-1">
                {p.url && (
                  <a href={p.url} target="_blank" rel="noreferrer"
                     className="py-1.5 rounded-lg bg-slate-700 text-white text-[11px] font-black text-center">🌍 Website</a>
                )}
                <button onClick={() => waShare(p)} data-testid={`mkt-franchisee-wa-share-${p.id}`}
                        className="py-1.5 rounded-lg bg-green-700 text-white text-[11px] font-black">📤 WA Share Link</button>
              </div>
              <a href={`/franchisee/${p.id}`} className="block text-center text-[10px] text-amber-300 underline">Open full page →</a>
            </div>
          </div>
        ))}
      </div>

      {!isGuest && mine.length > 0 && (
        <div className="rounded-xl bg-slate-900/70 border border-slate-700 p-3 mt-3" data-testid="mkt-franchisee-mine">
          <div className="text-[11px] font-black text-purple-300 uppercase mb-1">My Applications ({mine.length})</div>
          <div className="space-y-1">
            {mine.map((a) => (
              <div key={a.id} className="flex items-center justify-between text-[11px] py-1 border-b border-slate-800 last:border-0">
                <div className="min-w-0 flex-1 truncate">{a.post_title}</div>
                <span className={`px-2 py-0.5 rounded text-[10px] font-black ${a.status === 'approved' ? 'bg-emerald-600' : a.status === 'rejected' ? 'bg-rose-600' : a.status === 'contacted' ? 'bg-amber-600' : 'bg-slate-700'}`}>{a.status}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {applyFor && <FranchiseeApplyModal post={applyFor} onClose={() => { setApplyFor(null); load(); }} />}
    </div>
  );
}

// ============ Wave 52 · Grace banner + admin controls ============
function GracePeriodBanner({ cfg, onRenew }) {
  const ps = cfg?.plan_status;
  const [secondsLeft, setSecondsLeft] = useState(ps?.grace_seconds_left || 0);
  useEffect(() => {
    if (!ps || ps.status !== 'grace') return;
    setSecondsLeft(ps.grace_seconds_left || 0);
    const t = setInterval(() => setSecondsLeft((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(t);
  }, [ps?.status, ps?.grace_seconds_left]);
  if (!ps) return null;
  const expiredOrGrace = ps.status === 'grace' || ps.status === 'expired';
  if (!expiredOrGrace) return null;
  const h = Math.floor(secondsLeft / 3600);
  const m = Math.floor((secondsLeft % 3600) / 60);
  const s = secondsLeft % 60;
  const isGrace = ps.status === 'grace';
  return (
    <div className={`sticky top-0 z-40 px-3 py-2 ${isGrace ? 'bg-amber-500 text-slate-900' : 'bg-rose-600 text-white'} shadow-lg`} data-testid="mkt-grace-banner">
      <div className="max-w-7xl mx-auto flex flex-wrap items-center justify-between gap-2">
        <div className="text-xs font-black flex items-center gap-2">
          {isGrace ? '⏰' : '⛔'}
          {isGrace
            ? <>Plan expired · Grace period ends in <span className="tabular-nums bg-black/20 px-2 py-0.5 rounded">{String(h).padStart(2, '0')}:{String(m).padStart(2, '0')}:{String(s).padStart(2, '0')}</span></>
            : <>Display not available due to non-payment or renewal pending</>}
        </div>
        <a href="#plans" onClick={(e) => { e.preventDefault(); document.querySelector('[data-testid="mkt-tab-plans"]')?.click(); onRenew && onRenew(); }}
           data-testid="mkt-grace-renew-btn"
           className="px-3 py-1 rounded-full bg-white text-slate-900 text-xs font-black">💳 Renew Now</a>
      </div>
    </div>
  );
}

function AdminUserControlsBlock() {
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const search = async () => {
    if (q.trim().length < 2) { toast.error('Min 2 chars'); return; }
    setLoading(true);
    try {
      const r = await api.get('/admin/user-controls/search', { params: { q: q.trim() } });
      setResults(r.data.results || []);
    } catch (e) { toast.error('Search failed'); }
    finally { setLoading(false); }
  };
  const setLimit = async (userId, cur) => {
    const v = window.prompt(`New listing limit for user (current override: ${cur ?? 'none'}):`, cur ?? '');
    if (v === null) return;
    try {
      await api.post('/admin/user-controls/set-listing-limit', { user_id: userId, listing_limit: parseInt(v, 10) });
      toast.success('✅ Limit updated'); search();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const toggleHide = async (userId, nextHide) => {
    const reason = nextHide ? window.prompt('Reason for hiding (shown in logs):', '') : null;
    if (nextHide && reason === null) return;
    try {
      await api.post('/admin/user-controls/hide-store', { user_id: userId, hide: nextHide, reason });
      toast.success(nextHide ? '🙈 Hidden' : '👁 Unhidden'); search();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  return (
    <div className="rounded-2xl bg-indigo-500/10 border-2 border-indigo-400/40 p-3 space-y-2" data-testid="mkt-admin-user-controls">
      <div className="text-sm font-black text-indigo-200 uppercase">👑 User Controls (Search · Limit · Hide)</div>
      <div className="flex items-center gap-2">
        <input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && search()}
               placeholder="Search by name, email, username, mobile or shop name"
               data-testid="mkt-admin-user-search-input"
               className="flex-1 px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm" />
        <button onClick={search} disabled={loading} data-testid="mkt-admin-user-search-btn"
                className="px-3 py-2 rounded-lg bg-amber-500 text-slate-900 text-xs font-black disabled:opacity-50">{loading ? '…' : '🔎 Search'}</button>
      </div>
      <div className="space-y-1 max-h-96 overflow-y-auto">
        {results.length === 0 && q.trim().length >= 2 && !loading && (
          <div className="text-xs opacity-60 text-center py-3">No results</div>
        )}
        {results.map((row) => (
          <div key={row.user.id} className="rounded-lg bg-slate-900 border border-slate-800 p-2 flex flex-wrap gap-2 items-center" data-testid={`mkt-admin-user-row-${row.user.id}`}>
            <div className="flex-1 min-w-0">
              <div className="text-xs font-black truncate">{row.user.name || row.user.username || '-'} <span className="opacity-50">· {row.user.email || row.user.mobile || row.user.id?.slice(0, 8)}</span></div>
              {row.store ? (
                <div className="text-[10px] opacity-80">
                  🏪 <b>{row.store.shop_name || '(unnamed)'}</b> · slug: {row.store.slug || '-'} · plan: <b>{row.store.plan_tier || 'none'}</b> · exp: {row.store.plan_expires_at ? row.store.plan_expires_at.slice(0, 10) : '—'}
                  <br />
                  Listings cap: <b>{row.store.listing_limit_override ?? 'default'}</b> + {row.store.listing_credits} credits
                  {row.store.admin_force_hidden && <span className="ml-2 px-1.5 py-0.5 rounded bg-rose-600 text-white text-[9px] font-black">🙈 HIDDEN</span>}
                </div>
              ) : <div className="text-[10px] opacity-60">No store yet</div>}
            </div>
            {row.store && (
              <div className="flex gap-1">
                <button onClick={() => setLimit(row.user.id, row.store.listing_limit_override)}
                        data-testid={`mkt-admin-set-limit-${row.user.id}`}
                        className="px-2 py-1 rounded bg-sky-600 text-white text-[10px] font-black">📝 Limit</button>
                <button onClick={() => toggleHide(row.user.id, !row.store.admin_force_hidden)}
                        data-testid={`mkt-admin-toggle-hide-${row.user.id}`}
                        className={`px-2 py-1 rounded ${row.store.admin_force_hidden ? 'bg-emerald-600' : 'bg-rose-600'} text-white text-[10px] font-black`}>
                  {row.store.admin_force_hidden ? '👁 Unhide' : '🙈 Hide'}
                </button>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}

function FranchiseeLeaderboard() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try { const r = await api.get('/franchisee/leaderboard/top?limit=10'); setRows(r.data.leaderboard || []); }
      catch {}
      finally { setLoading(false); }
    })();
  }, []);
  if (loading) return null;
  if (rows.length === 0) return null;
  const medal = (i) => i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : `#${i + 1}`;
  return (
    <div className="rounded-2xl bg-gradient-to-br from-amber-900/40 via-orange-900/30 to-slate-900 border-2 border-amber-400/40 p-3 space-y-2" data-testid="mkt-fr-leaderboard">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <div className="text-sm font-black text-amber-200">🏆 Top 10 Fastest-Growing Franchises</div>
          <div className="text-[10px] opacity-80">Ranked by applications in last 30 days · Live FOMO</div>
        </div>
      </div>
      <div className="flex gap-2 overflow-x-auto pb-1 -mx-1 px-1">
        {rows.map((r) => (
          <a key={r.post_id} href={`/franchisee/${r.post_id}`}
             data-testid={`mkt-fr-leader-${r.rank}`}
             className="flex-shrink-0 w-44 rounded-xl bg-slate-900 border-2 border-amber-400/30 overflow-hidden hover:border-amber-300 transition">
            <div className="relative">
              {r.image ? <img src={abs(r.image)} alt="" className="w-full h-20 object-cover" />
                       : <div className="w-full h-20 bg-slate-800 flex items-center justify-center text-2xl">🏢</div>}
              <div className="absolute top-1 left-1 bg-amber-500 text-slate-900 text-[11px] font-black px-2 py-0.5 rounded shadow">
                {medal(r.rank - 1)}
              </div>
              <div className="absolute top-1 right-1 bg-emerald-500 text-slate-900 text-[10px] font-black px-1.5 py-0.5 rounded">
                🔥 {r.apps_30d}
              </div>
            </div>
            <div className="p-2 space-y-0.5">
              <div className="text-[11px] font-black truncate">{r.title}</div>
              {r.investment_range && <div className="text-[10px] text-emerald-300 font-black truncate">💰 {r.investment_range}</div>}
              <div className="text-[9px] opacity-70 truncate">{r.city || ''}{r.city && r.category ? ' · ' : ''}{r.category || ''}</div>
            </div>
          </a>
        ))}
      </div>
    </div>
  );
}

function FranchiseeApplyModal({ post, onClose }) {
  const [form, setForm] = useState({ name: '', phone: '', email: '', city: '', investment_budget: '', message: '' });
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!form.name.trim() || !form.phone.trim()) { toast.error('Name + phone required'); return; }
    setBusy(true);
    try {
      const r = await api.post('/franchisee/apply', { post_id: post.id, ...form });
      toast.success('✅ Application submitted');
      if (r.data?.whatsapp_link) {
        if (window.confirm('Franchise owner ko WhatsApp pe turant notify karna hai?')) {
          window.open(r.data.whatsapp_link, '_blank');
        }
      }
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Apply failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-end sm:items-center justify-center p-2" onClick={onClose} data-testid="mkt-franchisee-apply-modal">
      <div className="w-full max-w-md rounded-2xl bg-slate-900 border-2 border-amber-400/40 p-4 space-y-2" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div className="font-black">📝 Apply · {post.title}</div>
          <button onClick={onClose} className="text-xs opacity-60">✕</button>
        </div>
        <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Full Name *" data-testid="mkt-franchisee-apply-name" className="w-full px-2 py-2 rounded bg-slate-800 text-sm" />
        <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="Phone *" data-testid="mkt-franchisee-apply-phone" className="w-full px-2 py-2 rounded bg-slate-800 text-sm" />
        <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="w-full px-2 py-2 rounded bg-slate-800 text-sm" />
        <div className="grid grid-cols-2 gap-2">
          <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="City" className="px-2 py-2 rounded bg-slate-800 text-sm" />
          <input value={form.investment_budget} onChange={(e) => setForm({ ...form, investment_budget: e.target.value })} placeholder="Budget (e.g. 5-10L)" className="px-2 py-2 rounded bg-slate-800 text-sm" />
        </div>
        <textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} placeholder="Why do you want this franchise?" rows={3} className="w-full px-2 py-2 rounded bg-slate-800 text-sm" />
        <button onClick={submit} disabled={busy} data-testid="mkt-franchisee-apply-submit"
                className="w-full py-2.5 rounded-xl bg-emerald-500 text-slate-900 font-black disabled:opacity-50">
          {busy ? '...' : '🚀 Submit Application'}
        </button>
      </div>
    </div>
  );
}

function FranchiseeAdminPanel({ onDone }) {
  const empty = { id: null, title: '', tagline: '', description: '', whatsapp: '', url: '', city: '', category: '', investment_range: '', roi: '', images: [], videos: [], posts: [], active: true };
  const [form, setForm] = useState(empty);
  const [rows, setRows] = useState([]);
  const [apps, setApps] = useState([]);
  const [busy, setBusy] = useState(false);
  const load = async () => {
    try {
      const [r, a] = await Promise.all([api.get('/admin/franchisee'), api.get('/admin/franchisee/applications')]);
      setRows(r.data.posts || []); setApps(a.data.applications || []);
    } catch {}
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!form.title.trim()) { toast.error('Title required'); return; }
    setBusy(true);
    try {
      await api.post('/admin/franchisee', form);
      toast.success('✅ Saved');
      setForm(empty); load(); onDone && onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete this opportunity?')) return;
    try { await api.delete(`/admin/franchisee/${id}`); load(); onDone && onDone(); } catch {}
  };
  const edit = (p) => setForm({ ...empty, ...p, images: p.images || [], videos: p.videos || [], posts: p.posts || [] });
  const updateAppStatus = async (id, status) => {
    try { await api.patch(`/admin/franchisee/applications/${id}`, { status }); toast.success('Updated'); load(); } catch {}
  };

  const addArrItem = (field, value) => setForm({ ...form, [field]: [...(form[field] || []), value] });
  const rmArrItem = (field, i) => setForm({ ...form, [field]: form[field].filter((_, j) => j !== i) });
  const [uploading, setUploading] = useState(null);
  const uploadMedia = async (e, kind) => {
    const f = e.target.files?.[0]; if (!f) return;
    e.target.value = '';
    setUploading(kind);
    try {
      const fd = new FormData(); fd.append('file', f);
      const r = await api.post(`/admin/franchisee/upload/${kind}`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      const url = r.data.url;
      if (kind === 'image') addArrItem('images', url);
      else addArrItem('videos', url);
      toast.success(`✅ ${kind} uploaded`);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Upload failed'); }
    finally { setUploading(null); }
  };
  const downloadCsv = async (postId) => {
    try {
      const token = localStorage.getItem('token');
      const qs = postId ? `?post_id=${postId}` : '';
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/franchisee/applications.csv${qs}`;
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await resp.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = `franchise_applications_${postId ? postId.slice(0, 8) : 'all'}.csv`;
      a.click(); URL.revokeObjectURL(a.href);
      toast.success('✅ CSV downloaded');
    } catch (e) { toast.error('CSV download failed'); }
  };

  return (
    <div className="rounded-xl bg-amber-500/10 border-2 border-amber-400/40 p-3 space-y-3" data-testid="mkt-franchisee-admin-panel">
      <div className="text-[12px] font-black text-amber-200 uppercase">👑 Franchisee Admin Panel</div>

      {/* Create / Edit form */}
      <div className="rounded-lg bg-slate-900 p-2 space-y-2">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
          <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Title *" data-testid="mkt-fr-admin-title" className="px-2 py-2 rounded bg-slate-800 text-sm" />
          <input value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} placeholder="Tagline" className="px-2 py-2 rounded bg-slate-800 text-sm" />
          <input value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} placeholder="WhatsApp (with country code)" data-testid="mkt-fr-admin-wa" className="px-2 py-2 rounded bg-slate-800 text-sm" />
          <input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} placeholder="Website URL" data-testid="mkt-fr-admin-url" className="px-2 py-2 rounded bg-slate-800 text-sm" />
          <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="City" className="px-2 py-2 rounded bg-slate-800 text-sm" />
          <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Category (F&B, Retail, etc.)" className="px-2 py-2 rounded bg-slate-800 text-sm" />
          <input value={form.investment_range} onChange={(e) => setForm({ ...form, investment_range: e.target.value })} placeholder="Investment (e.g. ₹5-10L)" className="px-2 py-2 rounded bg-slate-800 text-sm" />
          <input value={form.roi} onChange={(e) => setForm({ ...form, roi: e.target.value })} placeholder="ROI (e.g. 18 months)" className="px-2 py-2 rounded bg-slate-800 text-sm" />
        </div>
        <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} placeholder="Full description…" className="w-full px-2 py-2 rounded bg-slate-800 text-sm" />

        {/* Images */}
        <div className="rounded bg-slate-800/50 p-2 space-y-1">
          <div className="flex items-center justify-between">
            <div className="text-[11px] font-black">🖼 Images ({(form.images || []).length})</div>
            <label className="px-2 py-0.5 rounded bg-emerald-500 text-slate-900 text-[10px] font-black cursor-pointer" data-testid="mkt-fr-admin-img-upload">
              {uploading === 'image' ? 'Uploading…' : '+ Upload image'}
              <input type="file" accept="image/*" className="hidden" onChange={(e) => uploadMedia(e, 'image')} disabled={!!uploading} />
            </label>
          </div>
          <div className="flex flex-wrap gap-1">
            {(form.images || []).map((img, i) => (
              <div key={i} className="relative w-16 h-16 rounded overflow-hidden border border-slate-700">
                <img src={abs(img)} alt="" className="w-full h-full object-cover" />
                <button type="button" onClick={() => rmArrItem('images', i)} className="absolute top-0 right-0 w-5 h-5 bg-rose-600 text-white text-[10px] font-black">✕</button>
              </div>
            ))}
          </div>
        </div>

        {/* Videos */}
        <div className="rounded bg-slate-800/50 p-2 space-y-1">
          <div className="flex items-center justify-between">
            <div className="text-[11px] font-black">🎬 Videos ({(form.videos || []).length})</div>
            <label className="px-2 py-0.5 rounded bg-emerald-500 text-slate-900 text-[10px] font-black cursor-pointer" data-testid="mkt-fr-admin-vid-upload">
              {uploading === 'video' ? 'Uploading…' : '+ Upload video'}
              <input type="file" accept="video/*" className="hidden" onChange={(e) => uploadMedia(e, 'video')} disabled={!!uploading} />
            </label>
          </div>
          {(form.videos || []).map((v, i) => (
            <div key={i} className="flex items-center gap-1 text-[11px]">
              <video src={abs(v)} className="w-20 h-14 object-cover rounded" />
              <span className="flex-1 truncate opacity-80">{v}</span>
              <button type="button" onClick={() => rmArrItem('videos', i)} className="px-1.5 py-0.5 rounded bg-rose-600 text-white text-[10px] font-black">✕</button>
            </div>
          ))}
        </div>

        {/* Posts */}
        <div className="rounded bg-slate-800/50 p-2 space-y-1">
          <div className="flex items-center justify-between">
            <div className="text-[11px] font-black">📰 Posts ({(form.posts || []).length})</div>
            <button type="button" onClick={() => addArrItem('posts', { title: '', body: '', image: '', video: '', url: '' })} className="px-2 py-0.5 rounded bg-emerald-500 text-slate-900 text-[10px] font-black">+ Add post</button>
          </div>
          {(form.posts || []).map((p, i) => (
            <div key={i} className="rounded bg-slate-900 p-1.5 space-y-1" data-testid={`mkt-fr-admin-post-${i}`}>
              <input value={p.title || ''} onChange={(e) => setForm({ ...form, posts: form.posts.map((x, j) => j === i ? { ...x, title: e.target.value } : x) })} placeholder="Post title" className="w-full px-2 py-1 rounded bg-slate-800 text-[11px]" />
              <textarea value={p.body || ''} onChange={(e) => setForm({ ...form, posts: form.posts.map((x, j) => j === i ? { ...x, body: e.target.value } : x) })} rows={2} placeholder="Post body" className="w-full px-2 py-1 rounded bg-slate-800 text-[11px]" />
              <div className="grid grid-cols-3 gap-1">
                <label className="px-1.5 py-1 rounded bg-slate-700 text-white text-[10px] font-black text-center cursor-pointer">
                  {p.image ? '🖼 ✓' : '+ Image'}
                  <input type="file" accept="image/*" className="hidden" onChange={async (e) => {
                    const f = e.target.files?.[0]; if (!f) return; e.target.value = '';
                    try {
                      const fd = new FormData(); fd.append('file', f);
                      const r = await api.post('/admin/franchisee/upload/image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                      setForm({ ...form, posts: form.posts.map((x, j) => j === i ? { ...x, image: r.data.url } : x) });
                      toast.success('✅ Uploaded');
                    } catch (err) { toast.error('Upload failed'); }
                  }} />
                </label>
                <label className="px-1.5 py-1 rounded bg-slate-700 text-white text-[10px] font-black text-center cursor-pointer">
                  {p.video ? '🎬 ✓' : '+ Video'}
                  <input type="file" accept="video/*" className="hidden" onChange={async (e) => {
                    const f = e.target.files?.[0]; if (!f) return; e.target.value = '';
                    try {
                      const fd = new FormData(); fd.append('file', f);
                      const r = await api.post('/admin/franchisee/upload/video', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                      setForm({ ...form, posts: form.posts.map((x, j) => j === i ? { ...x, video: r.data.url } : x) });
                      toast.success('✅ Uploaded');
                    } catch (err) { toast.error('Upload failed'); }
                  }} />
                </label>
                <input value={p.url || ''} onChange={(e) => setForm({ ...form, posts: form.posts.map((x, j) => j === i ? { ...x, url: e.target.value } : x) })} placeholder="External URL" className="px-2 py-1 rounded bg-slate-800 text-[11px]" />
              </div>
              {p.image && <img src={abs(p.image)} alt="" className="w-full h-20 object-cover rounded" />}
              <button type="button" onClick={() => rmArrItem('posts', i)} className="px-2 py-0.5 rounded bg-rose-600 text-white text-[10px] font-black">✕ Remove post</button>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-2 gap-2">
          <button onClick={() => setForm(empty)} className="py-2 rounded bg-slate-700 text-white text-xs font-black">✕ Reset</button>
          <button onClick={save} disabled={busy} data-testid="mkt-fr-admin-save" className="py-2 rounded bg-amber-500 text-slate-900 text-xs font-black disabled:opacity-50">
            {busy ? '...' : (form.id ? '💾 Update' : '➕ Add Opportunity')}
          </button>
        </div>
      </div>

      {/* Existing posts list */}
      <div className="rounded-lg bg-slate-900 p-2 space-y-1" data-testid="mkt-fr-admin-list">
        <div className="flex items-center justify-between">
          <div className="text-[11px] font-black text-amber-200 uppercase">All Opportunities ({rows.length})</div>
          <button onClick={() => downloadCsv(null)} data-testid="mkt-fr-admin-csv-all"
                  className="px-2 py-0.5 rounded bg-sky-600 text-white text-[10px] font-black">⬇ CSV (All apps)</button>
        </div>
        {rows.map((p) => (
          <div key={p.id} className="flex items-center justify-between gap-2 text-[11px] py-1 border-b border-slate-800 last:border-0">
            <div className="min-w-0 flex-1 truncate">{p.title} <span className="opacity-60">· 👁 {p.views || 0} · 📝 {p.applications || 0}</span></div>
            <button onClick={() => downloadCsv(p.id)} data-testid={`mkt-fr-admin-csv-${p.id}`}
                    className="px-2 py-0.5 rounded bg-sky-700 text-white text-[10px] font-black">⬇ CSV</button>
            <button onClick={() => edit(p)} className="px-2 py-0.5 rounded bg-sky-600 text-white text-[10px] font-black">✏️</button>
            <button onClick={() => del(p.id)} className="px-2 py-0.5 rounded bg-rose-600 text-white text-[10px] font-black">🗑</button>
          </div>
        ))}
      </div>

      {/* Applications list */}
      <div className="rounded-lg bg-slate-900 p-2 space-y-1" data-testid="mkt-fr-admin-apps">
        <div className="text-[11px] font-black text-amber-200 uppercase">Applications ({apps.length})</div>
        <div className="max-h-60 overflow-y-auto space-y-1">
          {apps.map((a) => (
            <div key={a.id} className="rounded bg-slate-800 p-1.5 text-[11px]">
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="font-black truncate">{a.name} · {a.phone}</div>
                  <div className="opacity-70">{a.post_title} · {a.city || '-'} · {a.investment_budget || '-'}</div>
                </div>
                <select value={a.status} onChange={(e) => updateAppStatus(a.id, e.target.value)}
                        data-testid={`mkt-fr-admin-app-status-${a.id}`}
                        className="px-1.5 py-0.5 rounded bg-slate-900 text-[10px] font-black">
                  <option value="new">new</option>
                  <option value="contacted">contacted</option>
                  <option value="approved">approved</option>
                  <option value="rejected">rejected</option>
                </select>
              </div>
              {a.message && <div className="opacity-70 mt-0.5">{a.message}</div>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ============ VYAPAR WALL (Wave 52b) ============
function VyaparWallTab({ isAdmin, isGuest }) {
  const [posts, setPosts] = useState([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [showComposer, setShowComposer] = useState(false);
  const [filter, setFilter] = useState('');
  const [cats, setCats] = useState([]);
  const [quota, setQuota] = useState(null);
  const [tick, setTick] = useState([]);
  const [now, setNow] = useState(Date.now());
  const LIMIT = 10;

  const load = useCallback(async (reset = true) => {
    setLoading(true);
    try {
      const q = reset ? 0 : offset;
      const r = await api.get('/vyapar-wall/feed', { params: { offset: q, limit: LIMIT, category: filter || undefined } });
      setPosts(reset ? r.data.posts : [...posts, ...r.data.posts]);
      setHasMore(r.data.has_more);
      setOffset(q + r.data.posts.length);
    } catch (e) { toast.error('Load failed'); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter]);

  const loadMeta = useCallback(async () => {
    try {
      const c = await api.get('/vyapar-wall/categories'); setCats(c.data.categories || []);
      const t = await api.get('/vyapar-wall/ticker'); setTick(t.data.ticker || []);
      if (!isGuest) { const q = await api.get('/vyapar-wall/quota'); setQuota(q.data); }
    } catch {}
  }, [isGuest]);

  useEffect(() => { load(true); loadMeta(); /* eslint-disable-next-line */ }, [filter]);
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);

  const onPostCreated = (p, newQuota) => {
    setPosts((prev) => [p, ...prev]);
    if (newQuota) setQuota(newQuota);
    loadMeta();
  };

  // Referral backlink from stored user profile
  const refLink = (() => {
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      const ref = u.referral_code || u.username || u.id;
      return ref ? `${window.location.origin}/signup?ref=${encodeURIComponent(ref)}` : window.location.origin;
    } catch { return window.location.origin; }
  })();

  return (
    <div className="space-y-3" data-testid="mkt-vyapar-wall-tab">
      {/* Ticker Marquee */}
      {tick.length > 0 && (
        <div className="rounded-xl bg-gradient-to-r from-fuchsia-700 to-indigo-800 border border-fuchsia-400/40 overflow-hidden" data-testid="mkt-wall-ticker">
          <div className="relative overflow-hidden">
            <div className="flex gap-6 py-2 px-3 whitespace-nowrap animate-[marquee_30s_linear_infinite]" style={{ animation: 'marquee 30s linear infinite' }}>
              {[...tick, ...tick].map((t, i) => (
                <span key={i} className="text-xs font-black text-white">
                  🔥 {t.offer_badge ? `[${t.offer_badge}] ` : ''}{t.title} · 👁 {t.views}
                </span>
              ))}
            </div>
          </div>
          <style>{`@keyframes marquee { 0% { transform: translateX(0); } 100% { transform: translateX(-50%); } }`}</style>
        </div>
      )}

      {/* Header + Compose */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-lg font-black">📢 Vyapar Wall</div>
          <div className="text-[11px] opacity-70">Business ki awaaz · 24-hour buzz · Zero paid promo cost.</div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {quota && (
            <div className="text-[11px] font-black px-2 py-1 rounded bg-slate-800" data-testid="mkt-wall-quota">
              {quota.tier?.toUpperCase()} · {quota.used_today}/{quota.limit === 9999 ? '∞' : quota.limit}
            </div>
          )}
          {!isGuest && (quota?.remaining > 0 || isAdmin) && (
            <button onClick={() => setShowComposer(true)} data-testid="mkt-wall-compose-btn"
                    className="px-3 py-1.5 rounded-lg bg-fuchsia-500 text-white text-xs font-black">+ New Post</button>
          )}
          {!isGuest && quota?.remaining === 0 && !isAdmin && (
            <button onClick={() => toast.error('Quota done · upgrade plan or wait till midnight')}
                    className="px-3 py-1.5 rounded-lg bg-slate-700 text-slate-400 text-xs font-black">🔒 Quota done</button>
          )}
          {isGuest && <a href="/login" className="px-3 py-1.5 rounded-lg bg-slate-700 text-white text-xs font-black">🔐 Login to post</a>}
        </div>
      </div>

      {/* Category filter */}
      <div className="flex items-center gap-1 overflow-x-auto pb-1">
        <button onClick={() => setFilter('')} data-testid="mkt-wall-cat-all"
                className={`px-2 py-1 rounded-full text-[11px] font-black whitespace-nowrap ${!filter ? 'bg-fuchsia-500 text-white' : 'bg-slate-800 text-slate-300'}`}>All</button>
        {cats.map((c) => (
          <button key={c.id} onClick={() => setFilter(c.id)} data-testid={`mkt-wall-cat-${c.id}`}
                  className={`px-2 py-1 rounded-full text-[11px] font-black whitespace-nowrap ${filter === c.id ? 'bg-fuchsia-500 text-white' : 'bg-slate-800 text-slate-300'}`}>{c.name}</button>
        ))}
      </div>

      {/* Feed */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {posts.map((p) => <WallPostCard key={p.id} p={p} now={now} isAdmin={isAdmin} refLink={refLink} onDelete={() => setPosts(posts.filter((x) => x.id !== p.id))} />)}
      </div>
      {loading && <div className="text-center opacity-60 py-3 text-xs">Loading…</div>}
      {!loading && posts.length === 0 && <div className="text-center opacity-60 py-10 text-xs" data-testid="mkt-wall-empty">Abhi koi post nahi · First mover ban jao!</div>}
      {hasMore && !loading && (
        <button onClick={() => load(false)} data-testid="mkt-wall-load-more"
                className="w-full py-2 rounded-xl bg-slate-800 text-white text-xs font-black">Load more →</button>
      )}

      {showComposer && <VyaparWallComposer cats={cats} onClose={() => setShowComposer(false)} onCreated={onPostCreated} />}
      {!isGuest && <VyaparWallAnalytics />}
    </div>
  );
}

function WallPostCard({ p, now, isAdmin, refLink, onDelete }) {
  const sec = Math.max(0, (p.seconds_left || 0) - Math.floor((now - Date.now()) / 1000));
  const d = Math.floor(sec / 86400);
  const h = Math.floor((sec % 86400) / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  const shareOgUrl = `${process.env.REACT_APP_BACKEND_URL || ''}/api/share/vyapar-wall/${p.id}`;
  const share = (kind) => {
    const txt = `${p.offer_badge ? '🔥 ' + p.offer_badge + ' · ' : ''}${p.title}\n\n${p.description || ''}\n\n👉 ${shareOgUrl}`;
    api.post(`/vyapar-wall/track/${p.id}?kind=share`).catch(() => {});
    if (kind === 'wa') window.open(`https://wa.me/?text=${encodeURIComponent(txt)}`, '_blank');
    else if (kind === 'fb') window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareOgUrl)}&quote=${encodeURIComponent(txt)}`, '_blank');
    else if (kind === 'ig') { navigator.clipboard.writeText(txt); toast.success('📋 Copied — paste in Instagram'); }
    else if (kind === 'copy') { navigator.clipboard.writeText(txt); toast.success('📋 Copied'); }
    else if (navigator.share) navigator.share({ title: p.title, text: txt, url: shareOgUrl }).catch(() => {});
  };
  const del = async () => {
    if (!window.confirm('Delete this post?')) return;
    try { await api.delete(`/vyapar-wall/post/${p.id}`); toast.success('🗑 Deleted'); onDelete && onDelete(); }
    catch { toast.error('Failed'); }
  };
  const togglePin = async () => {
    try { await api.post(`/admin/vyapar-wall/pin/${p.id}?pinned=${!p.pinned}`); toast.success(p.pinned ? '📌 Unpinned' : '📌 Pinned'); window.location.reload(); }
    catch { toast.error('Failed'); }
  };
  return (
    <div className={`rounded-2xl overflow-hidden border-2 ${p.pinned ? 'border-amber-400 bg-amber-500/5' : 'border-slate-700 bg-slate-900'}`} data-testid={`mkt-wall-post-${p.id}`}>
      {p.image_url && (
        <div className="w-full bg-slate-800/60 flex items-center justify-center overflow-hidden" style={{ aspectRatio: '4/3' }}>
          <img src={abs(p.image_url)} alt="" className="w-full h-full object-contain" loading="lazy" />
        </div>
      )}
      <div className="p-3 space-y-1.5">
        <div className="flex items-center gap-1 flex-wrap">
          {p.pinned && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-400 text-slate-900 font-black">📌 PINNED</span>}
          {p.tier === 'vip' && <span className="text-[10px] px-1.5 py-0.5 rounded bg-gradient-to-r from-amber-400 to-pink-500 text-slate-900 font-black">👑 VIP</span>}
          {p.offer_badge && <span className="text-[10px] px-1.5 py-0.5 rounded bg-rose-500 text-white font-black">🔥 {p.offer_badge}</span>}
        </div>
        <div className="text-sm font-black">{p.title}</div>
        {p.description && <div className="text-[11px] opacity-85 line-clamp-3">{p.description}</div>}
        <div className="text-[10px] opacity-70">by {p.shop_name || p.username || 'user'}</div>
        <div className="flex items-center justify-between text-[11px]">
          <span className="text-amber-300 tabular-nums font-black" data-testid={`mkt-wall-countdown-${p.id}`}>
            ⏱ {d > 0 ? `${d}d ` : ''}{String(h).padStart(2, '0')}:{String(m).padStart(2, '0')}:{String(s).padStart(2, '0')}
          </span>
          <span className="opacity-60">👁 {p.views || 0} · 🔄 {p.shares || 0}</span>
        </div>
        <div className="flex gap-1 pt-1">
          <button onClick={() => share('wa')} data-testid={`mkt-wall-share-wa-${p.id}`} className="flex-1 py-1.5 rounded bg-green-600 text-white text-[10px] font-black">📱 WA</button>
          <button onClick={() => share('fb')} className="flex-1 py-1.5 rounded bg-blue-600 text-white text-[10px] font-black">📘 FB</button>
          <button onClick={() => share('ig')} className="flex-1 py-1.5 rounded bg-pink-600 text-white text-[10px] font-black">📸 IG</button>
          <button onClick={() => share('copy')} className="flex-1 py-1.5 rounded bg-slate-700 text-white text-[10px] font-black">📋</button>
          {isAdmin && (
            <>
              <button onClick={togglePin} data-testid={`mkt-wall-pin-${p.id}`} className="py-1.5 px-2 rounded bg-amber-600 text-white text-[10px] font-black">{p.pinned ? '📌-' : '📌+'}</button>
              <button onClick={del} className="py-1.5 px-2 rounded bg-rose-600 text-white text-[10px] font-black">🗑</button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function VyaparWallComposer({ cats, onClose, onCreated }) {
  const [form, setForm] = useState({ title: '', description: '', offer_badge: '', offer_badge_custom: '', validity_days: 7, category: '' });
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const BADGES = ['Flat 50% Off', 'Flat 30% Off', 'Flash Deal', 'Combo Offer', 'Buy 1 Get 1', 'Clearance'];
  const onFile = (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    setFile(f);
    const r = new FileReader(); r.onload = (ev) => setPreview(ev.target.result); r.readAsDataURL(f);
  };
  const submit = async () => {
    if (!form.title.trim()) { toast.error('Title required'); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => fd.append(k, v == null ? '' : String(v)));
      if (file) fd.append('image', file);
      const r = await api.post('/vyapar-wall/post', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('✅ Post published');
      onCreated && onCreated(r.data.post, r.data.quota);
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-end sm:items-center justify-center p-2" onClick={onClose} data-testid="mkt-wall-composer">
      <div className="w-full max-w-md rounded-2xl bg-slate-900 border-2 border-fuchsia-400/40 p-3 space-y-2 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div className="font-black">📢 New Vyapar Wall Post</div>
          <button onClick={onClose} className="text-xs opacity-60">✕</button>
        </div>
        <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Catchy title *" data-testid="mkt-wall-title" className="w-full px-2 py-2 rounded bg-slate-800 text-sm" />
        <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} placeholder="Offer details" className="w-full px-2 py-2 rounded bg-slate-800 text-sm" />
        <div className="grid grid-cols-2 gap-2">
          <select value={form.offer_badge} onChange={(e) => setForm({ ...form, offer_badge: e.target.value })} data-testid="mkt-wall-badge" className="px-2 py-2 rounded bg-slate-800 text-sm">
            <option value="">— Offer badge —</option>
            {BADGES.map((b) => <option key={b} value={b}>{b}</option>)}
          </select>
          <input value={form.offer_badge_custom} onChange={(e) => setForm({ ...form, offer_badge_custom: e.target.value })} placeholder="Or custom badge" className="px-2 py-2 rounded bg-slate-800 text-sm" />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} className="px-2 py-2 rounded bg-slate-800 text-sm">
            <option value="">— Category —</option>
            {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
          <select value={form.validity_days} onChange={(e) => setForm({ ...form, validity_days: Number(e.target.value) })} data-testid="mkt-wall-validity" className="px-2 py-2 rounded bg-slate-800 text-sm">
            {[1, 2, 3, 5, 7, 10, 14, 21, 30].map((d) => <option key={d} value={d}>{d} day{d > 1 ? 's' : ''}</option>)}
          </select>
        </div>
        <label className="block w-full px-2 py-2 rounded bg-slate-800 text-sm cursor-pointer text-center">
          {preview ? 'Change image' : '🖼 Add image (auto WebP)'}
          <input type="file" accept="image/*" onChange={onFile} className="hidden" data-testid="mkt-wall-image" />
        </label>
        {preview && <img src={preview} alt="" className="w-full h-40 object-cover rounded" />}
        <button onClick={submit} disabled={busy} data-testid="mkt-wall-submit"
                className="w-full py-2.5 rounded-xl bg-fuchsia-500 text-white font-black disabled:opacity-50">
          {busy ? '...' : '🚀 Publish'}
        </button>
      </div>
    </div>
  );
}

// ============ WALLET TAB (Wave 52c · top-level) ============
function WalletTab({ cfg }) {
  const [tx, setTx] = useState([]);
  const [loading, setLoading] = useState(true);
  const [panel, setPanel] = useState(null); // null | 'send' | 'receive' | 'withdraw'
  const [wSettings, setWSettings] = useState({ withdraw_fee_pct: 2, withdraw_min_at: 5000 });
  const [myQR, setMyQR] = useState(null);
  const at = Number(cfg?.at_balance || 0);

  const load = async () => {
    try {
      const [txRes, wsRes, qrRes] = await Promise.all([
        api.get('/at-wallet/ledger?limit=20').catch(() => ({ data: { ledger: [] } })),
        api.get('/at-wallet/withdraw/settings').catch(() => ({ data: { withdraw_fee_pct: 2, withdraw_min_at: 5000 } })),
        api.get('/at-wallet/qr').catch(() => ({ data: null })),
      ]);
      setTx(txRes.data?.ledger || []);
      setWSettings(wsRes.data || { withdraw_fee_pct: 2, withdraw_min_at: 5000 });
      setMyQR(qrRes.data);
    } catch {}
    finally { setLoading(false); }
  };
  useEffect(() => { if (!cfg?.guest) load(); }, [cfg?.guest]);

  if (cfg?.guest) {
    return (
      <div className="rounded-2xl bg-gradient-to-br from-amber-500/15 to-yellow-500/10 border-2 border-amber-400/40 p-6 text-center" data-testid="mkt-wallet-guest">
        <div className="text-5xl mb-2">💰</div>
        <div className="text-sm font-black text-amber-200">Login karein AT Wallet dekhne ke liye</div>
        <a href="/login" className="inline-block mt-3 px-4 py-2 rounded-xl bg-amber-500 text-slate-900 text-xs font-black">🔐 Login</a>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="mkt-wallet-tab">
      {/* Hero balance card */}
      <div className="rounded-2xl p-5 bg-gradient-to-br from-amber-500 via-yellow-500 to-orange-500 text-slate-900 shadow-xl relative overflow-hidden" data-testid="mkt-wallet-balance-card">
        <div className="absolute -top-10 -right-10 w-44 h-44 bg-white/20 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-6 -left-6 w-32 h-32 bg-white/10 rounded-full blur-2xl"></div>
        <div className="relative">
          <div className="text-[11px] font-black uppercase opacity-90 tracking-widest">⚡ AT Pay Wallet</div>
          <div className="text-5xl font-black mt-1 tabular-nums" data-testid="mkt-wallet-balance">{at.toFixed(0)}</div>
          <div className="text-xs font-black opacity-80">Anant Tokens</div>
          <div className="mt-2 text-[11px] opacity-85">P2P transfer fee: <b>0 AT</b> · zero-fee ✨ · Withdraw fee: <b>{wSettings.withdraw_fee_pct}%</b></div>
        </div>
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-2 gap-2">
        <button onClick={() => { window.location.href='/at-deposit'; }} data-testid="mkt-wallet-action-topup"
                className="py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 text-slate-900 text-sm font-black flex items-center justify-center gap-2">
          ➕ Top Up AT
        </button>
        <button onClick={() => setPanel('send')} data-testid="mkt-wallet-action-send"
                className="py-3 rounded-xl bg-gradient-to-r from-sky-600 to-blue-600 text-white text-sm font-black flex items-center justify-center gap-2">
          💸 Send AT
        </button>
        <button onClick={() => setPanel('receive')} data-testid="mkt-wallet-action-receive"
                className="py-3 rounded-xl bg-gradient-to-r from-fuchsia-600 to-purple-600 text-white text-sm font-black flex items-center justify-center gap-2">
          📥 Receive AT
        </button>
        <button onClick={() => setPanel('withdraw')} data-testid="mkt-wallet-action-withdraw"
                className="py-3 rounded-xl bg-gradient-to-r from-rose-600 to-red-600 text-white text-sm font-black flex items-center justify-center gap-2">
          💵 Withdraw
        </button>
      </div>

      {/* Info strip */}
      <div className="rounded-xl bg-slate-900 border border-amber-400/30 px-3 py-2 text-[11px] text-amber-200 flex gap-2 flex-wrap">
        <span>Min Withdraw: <b>{Number(wSettings.withdraw_min_at).toFixed(0)} AT</b></span>
        <span>·</span>
        <span>Fee: <b>{wSettings.withdraw_fee_pct}%</b> (platform processing)</span>
        <span>·</span>
        <span>P2P Transfer: <b>FREE</b></span>
      </div>

      {/* Recent transactions */}
      <div className="rounded-2xl bg-slate-900 border border-slate-800 p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="text-sm font-black">Recent Transactions</div>
          <button onClick={load} className="text-[11px] opacity-70">↻ Refresh</button>
        </div>
        {loading && <div className="text-xs opacity-60 py-3 text-center">Loading…</div>}
        {!loading && tx.length === 0 && <div className="text-xs opacity-60 py-6 text-center">No transactions yet</div>}
        <div className="space-y-1">
          {tx.slice(0, 20).map((t, i) => {
            const amt = Number(t.amount ?? t.at_amount ?? 0);
            const isCr = (t.direction || t.type || '').toLowerCase().includes('cred') || (t.direction === 'credit');
            return (
              <div key={t.id || i} className="flex items-center justify-between py-1.5 border-b border-slate-800 last:border-0" data-testid={`mkt-wallet-tx-${i}`}>
                <div className="min-w-0 flex-1">
                  <div className="text-xs font-black truncate">{t.description || t.tx_type || t.type || 'Transaction'}</div>
                  <div className="text-[10px] opacity-60">{(t.created_at || '').slice(0, 16).replace('T', ' ')}</div>
                </div>
                <div className={`text-xs font-black tabular-nums ml-2 ${isCr ? 'text-emerald-300' : 'text-rose-300'}`}>
                  {isCr ? '+' : '−'}{Math.abs(amt).toFixed(0)} AT
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Modals */}
      {panel === 'send'     && <ATSendModal at={at} onClose={() => { setPanel(null); load(); }} />}
      {panel === 'receive'  && <ATReceiveModal qrData={myQR} onClose={() => setPanel(null)} />}
      {panel === 'withdraw' && <ATWithdrawModal at={at} settings={wSettings} onClose={() => { setPanel(null); load(); }} />}
    </div>
  );
}

// ─── INLINE AT SEND MODAL ───────────────────────────────────────────
// Reads every QR format the app issues:
//  - Receive QR link:   anant://at-pay?u=<username>&id=<user id>
//  - /at-wallet/qr JSON: {"type":"at_wallet","to_user_id":..,"to_username":..,"name":..}
//  - legacy:            anantpay://anant.<username>@wallet  (or a UPI link with pa=)
// Returns { id, username, name } or null if it is not an Anant wallet QR.
function parseATWalletQR(raw) {
  const data = (raw || '').trim();
  if (data.startsWith('anant://at-pay')) {
    try {
      const u = new URL(data.replace('anant://', 'https://x/'));
      return { id: u.searchParams.get('id') || '', username: u.searchParams.get('u') || '' };
    } catch { return null; }
  }
  if (data.startsWith('{')) {
    try {
      const p = JSON.parse(data);
      if (p && (p.to_user_id || p.to_username)) return { id: p.to_user_id || '', username: p.to_username || '', name: p.name };
    } catch { /* not JSON */ }
    return null;
  }
  let ident = data.replace('anantpay://', '');
  if (ident.includes('upi://pay')) {
    const m = ident.match(/pa=([^&]+)/);
    if (m) ident = decodeURIComponent(m[1]);
  }
  const legacy = ident.match(/^anant\.(.+)@wallet$/i);
  const username = legacy ? legacy[1] : ident;
  return username ? { id: '', username } : null;
}

function ATSendModal({ at, onClose }) {
  const [mode, setMode] = useState('search'); // 'search' | 'scan'
  const [query, setQuery] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [recipient, setRecipient] = useState(null);
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const animRef = useRef(null);

  useEffect(() => {
    if (query.length < 2) { setSuggestions([]); return; }
    const t = setTimeout(async () => {
      try { const r = await api.get('/at-wallet/lookup', { params: { q: query } }); setSuggestions(r.data?.results || []); }
      catch {}
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => { return () => stopCamera(); }, []);

  const stopCamera = () => {
    if (animRef.current) cancelAnimationFrame(animRef.current);
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
  };

  const startCamera = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
      streamRef.current = stream;
      if (videoRef.current) { videoRef.current.srcObject = stream; videoRef.current.play(); }
      requestAnimationFrame(scanLoop);
    } catch { toast.error('Camera access denied. Manual search use karo.'); setMode('search'); }
  };

  const scanLoop = () => {
    const video = videoRef.current;
    if (!video || !streamRef.current) return;
    if (video.readyState === video.HAVE_ENOUGH_DATA) {
      const canvas = document.createElement('canvas');
      canvas.width = video.videoWidth; canvas.height = video.videoHeight;
      const ctx = canvas.getContext('2d'); ctx.drawImage(video, 0, 0);
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
      import('jsqr').then(({ default: jsQR }) => {
        const code = jsQR(imageData.data, imageData.width, imageData.height);
        if (code?.data) handleQRResult(code.data);
        else animRef.current = requestAnimationFrame(scanLoop);
      }).catch(() => { animRef.current = requestAnimationFrame(scanLoop); });
    } else { animRef.current = requestAnimationFrame(scanLoop); }
  };

  const handleQRResult = async (data) => {
    stopCamera();
    const qr = parseATWalletQR(data);
    if (!qr || !qr.username) { toast.error('Ye Anant AT Wallet QR nahi hai. Manual search try karo.'); setMode('search'); return; }
    try {
      const r = await api.get('/at-wallet/lookup', { params: { q: qr.username } });
      const results = r.data?.results || [];
      // Exact username, and the QR's user id too when present, so a QR can't show one name and pay another
      const match = results.find((u) => (u.username || '').toLowerCase() === qr.username.toLowerCase() && (!qr.id || u.id === qr.id));
      if (match) {
        setRecipient(match); setQuery(match.name || match.username); setSuggestions([]);
        setMode('search'); toast.success(`✅ QR Scanned! @${match.username}`);
      } else { toast.error('User not found. Manual search try karo.'); setMode('search'); }
    } catch { toast.error('Lookup failed'); setMode('search'); }
  };

  useEffect(() => {
    if (mode === 'scan') startCamera(); else stopCamera();
  }, [mode]);

  const pickRecipient = (r) => { setRecipient(r); setQuery(r.name || r.username); setSuggestions([]); };

  const send = async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { toast.error('Valid amount daalo'); return; }
    if (!recipient) { toast.error('Recipient choose karo'); return; }
    if (amt > at) { toast.error(`Insufficient AT · balance ${at.toFixed(0)} AT`); return; }
    setBusy(true);
    try {
      await api.post('/at-wallet/send', { to_user_id: recipient.id, amount: amt, note });
      toast.success(`✅ ${amt} AT sent to @${recipient.username} · Transfer fee: 0 AT`);
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Send failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-end sm:items-center justify-center p-2" data-testid="mkt-wallet-send-modal">
      <div className="w-full max-w-md rounded-2xl bg-slate-900 border-2 border-sky-500/60 p-4 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div className="font-black text-sky-300 text-lg">💸 Send AT · <span className="text-emerald-300 text-sm">0 Fee</span></div>
          <button onClick={onClose} className="w-7 h-7 rounded-full bg-slate-800 text-xs flex items-center justify-center">✕</button>
        </div>
        <div className="flex gap-2">
          <button onClick={() => setMode('search')} className={`flex-1 py-2 rounded-xl text-xs font-black border-2 transition ${mode === 'search' ? 'bg-sky-600 border-sky-400 text-white' : 'bg-slate-800 border-slate-700 text-slate-300'}`}>
            🔍 Search
          </button>
          <button onClick={() => setMode('scan')} data-testid="mkt-wallet-scan-toggle"
                  className={`flex-1 py-2 rounded-xl text-xs font-black border-2 transition ${mode === 'scan' ? 'bg-fuchsia-600 border-fuchsia-400 text-white' : 'bg-slate-800 border-slate-700 text-slate-300'}`}>
            📷 Scan QR
          </button>
        </div>
        {mode === 'scan' && (
          <div className="space-y-2">
            <div className="text-[11px] opacity-70 text-center">Recipient ka AT Wallet QR scan karo</div>
            <div className="relative rounded-xl overflow-hidden bg-black aspect-[4/3]">
              <video ref={videoRef} autoPlay playsInline muted className="w-full h-full object-cover" />
              <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                <div className="w-40 h-40 border-4 border-fuchsia-400 rounded-xl" style={{ boxShadow: '0 0 0 9999px rgba(0,0,0,0.5)' }} />
              </div>
              <div className="absolute bottom-2 left-0 right-0 text-center text-[10px] text-fuchsia-200 font-black animate-pulse">QR frame mein rakhein...</div>
            </div>
            <button onClick={() => setMode('search')} className="w-full py-2 rounded-xl bg-slate-700 text-slate-300 text-xs font-black">← Manual Search</button>
          </div>
        )}
        {mode === 'search' && (
          <>
            <div>
              <div className="text-[11px] opacity-70 mb-1">Username / Mobile / Name se search karein</div>
              <div className="relative">
                <input value={query} onChange={(e) => { setQuery(e.target.value); setRecipient(null); }}
                       placeholder="@username ya mobile number" data-testid="mkt-wallet-send-search"
                       className="w-full px-3 py-2 rounded-xl bg-slate-800 border border-slate-700 text-sm" />
                {suggestions.length > 0 && (
                  <div className="absolute top-full left-0 right-0 z-50 bg-slate-800 rounded-xl border border-sky-500/40 shadow-xl mt-1 max-h-40 overflow-y-auto">
                    {suggestions.map((s) => (
                      <button key={s.id} onClick={() => pickRecipient(s)} className="w-full text-left px-3 py-2 hover:bg-sky-500/20 text-sm">
                        <span className="font-black">@{s.username}</span> <span className="opacity-70 text-xs">{s.name}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              {recipient && (
                <div className="mt-1 px-2 py-1 rounded-lg bg-sky-500/20 border border-sky-400/40 text-xs font-black text-sky-200" data-testid="mkt-wallet-send-recipient">
                  ✓ @{recipient.username} · {recipient.name}
                </div>
              )}
            </div>
            <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={`Amount AT (max ${at.toFixed(0)})`}
                   data-testid="mkt-wallet-send-amount" className="w-full px-3 py-2 rounded-xl bg-slate-800 border border-slate-700 text-sm" />
            <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (optional)"
                   className="w-full px-3 py-2 rounded-xl bg-slate-800 border border-slate-700 text-sm" />
            <div className="text-[10px] text-emerald-300 font-black text-center">✨ P2P Transfer Fee: 0 AT (FREE)</div>
            <button onClick={send} disabled={busy || !recipient || !amount} data-testid="mkt-wallet-send-btn"
                    className="w-full py-3 rounded-xl bg-gradient-to-r from-sky-600 to-blue-600 text-white font-black text-sm disabled:opacity-40">
              {busy ? '…' : `💸 Send ${amount || '?'} AT FREE`}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

// ─── INLINE AT RECEIVE MODAL ─────────────────────────────────────────
function ATReceiveModal({ qrData, onClose }) {
  const { user } = useAuth();
  const qrUrl = qrData
    ? `https://api.qrserver.com/v1/create-qr-code/?size=200x200&data=${encodeURIComponent(qrData.text || qrData.payload || '')}`
    : null;
  const copy = async (text) => {
    try { await navigator.clipboard.writeText(text); toast.success('Copied!'); }
    catch { toast.error('Copy failed'); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-end sm:items-center justify-center p-2" data-testid="mkt-wallet-receive-modal">
      <div className="w-full max-w-sm rounded-2xl bg-slate-900 border-2 border-fuchsia-500/60 p-4 space-y-3 text-center" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div className="font-black text-fuchsia-300 text-lg">📥 Receive AT</div>
          <button onClick={onClose} className="w-7 h-7 rounded-full bg-slate-800 text-xs flex items-center justify-center">✕</button>
        </div>
        <div className="text-xs opacity-70">Ye QR share karo — koi bhi AT bhejna chahiye to scan kare</div>
        {qrUrl ? (
          <div className="flex justify-center">
            <div className="p-3 bg-white rounded-2xl inline-block shadow-lg">
              <img src={qrUrl} alt="AT Wallet QR" className="w-40 h-40" data-testid="mkt-wallet-qr-img" />
            </div>
          </div>
        ) : (
          <div className="text-xs opacity-60 py-6">QR load ho raha hai…</div>
        )}
        <div className="rounded-xl bg-slate-800 px-3 py-2 text-xs font-black text-fuchsia-200">
          @{user?.username || '—'}
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button onClick={() => copy(user?.username || '')} className="py-2 rounded-xl bg-fuchsia-600/30 border border-fuchsia-500/50 text-fuchsia-200 text-xs font-black">
            📋 Copy Username
          </button>
          <button onClick={() => {
            const text = `Mujhe AT bhejo: @${user?.username} · Anant Pay`;
            const wa = `https://wa.me/?text=${encodeURIComponent(text)}`;
            window.open(wa, '_blank');
          }} className="py-2 rounded-xl bg-green-700/40 border border-green-500/50 text-green-200 text-xs font-black">
            💬 WhatsApp Share
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── INLINE AT WITHDRAW MODAL ─────────────────────────────────────────
function ATWithdrawModal({ at, settings, onClose }) {
  const [form, setForm] = useState({ amount: '', method: 'upi', upi_id: '', bank_account: '', bank_ifsc: '', bank_name: '', note: '' });
  const [busy, setBusy] = useState(false);
  const [myReqs, setMyReqs] = useState([]);
  const minAt = Number(settings?.withdraw_min_at || 5000);
  const feePct = Number(settings?.withdraw_fee_pct || 2);
  const feeAT = form.amount ? (parseFloat(form.amount) * feePct / 100).toFixed(2) : 0;
  const totalDebit = form.amount ? (parseFloat(form.amount) + Number(feeAT)).toFixed(2) : 0;

  useEffect(() => {
    api.get('/at-wallet/withdraw/my').then((r) => setMyReqs(r.data?.requests || [])).catch(() => {});
  }, []);

  const submit = async () => {
    const amt = parseFloat(form.amount);
    if (!amt || amt < minAt) { toast.error(`Minimum ${minAt} AT withdraw karna hoga`); return; }
    if (form.method === 'upi' && !form.upi_id) { toast.error('UPI ID required'); return; }
    if (form.method === 'bank' && (!form.bank_account || !form.bank_ifsc)) { toast.error('Bank details required'); return; }
    if (Number(totalDebit) > at) { toast.error(`Insufficient AT · need ${totalDebit} AT (including fee)`); return; }
    setBusy(true);
    try {
      await api.post('/at-wallet/withdraw', { ...form, amount: amt });
      toast.success('✅ Withdraw request submit ho gaya. Admin 24-48h mein process karega.');
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-end sm:items-center justify-center p-2" data-testid="mkt-wallet-withdraw-modal">
      <div className="w-full max-w-md rounded-2xl bg-slate-900 border-2 border-rose-500/60 p-4 space-y-3 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div className="font-black text-rose-300 text-lg">💵 Withdraw AT → INR</div>
          <button onClick={onClose} className="w-7 h-7 rounded-full bg-slate-800 text-xs flex items-center justify-center">✕</button>
        </div>
        {/* Info card */}
        <div className="rounded-xl bg-rose-900/30 border border-rose-500/40 p-3 text-[11px] space-y-1 text-rose-200">
          <div>Minimum: <b>{minAt} AT</b> · Fee: <b>{feePct}% deducted</b></div>
          {form.amount && <div className="font-black text-white">You receive: <span className="text-emerald-300">{form.amount} AT</span> · Fee: <span className="text-rose-300">{feeAT} AT</span> · Total debit: <span className="text-amber-300">{totalDebit} AT</span></div>}
        </div>
        <input type="number" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })}
               placeholder={`Amount AT (min ${minAt})`} data-testid="mkt-wallet-withdraw-amount"
               className="w-full px-3 py-2 rounded-xl bg-slate-800 border border-slate-700 text-sm" />
        {/* Method */}
        <div className="flex gap-2">
          {['upi', 'bank'].map((m) => (
            <button key={m} onClick={() => setForm({ ...form, method: m })}
                    className={`flex-1 py-2 rounded-xl text-xs font-black border-2 transition ${form.method === m ? 'bg-rose-600 border-rose-400 text-white' : 'bg-slate-800 border-slate-700 text-slate-400'}`}>
              {m === 'upi' ? '📱 UPI' : '🏦 Bank Transfer'}
            </button>
          ))}
        </div>
        {form.method === 'upi' && (
          <input value={form.upi_id} onChange={(e) => setForm({ ...form, upi_id: e.target.value })}
                 placeholder="UPI ID (e.g. name@bank)" data-testid="mkt-wallet-withdraw-upi"
                 className="w-full px-3 py-2 rounded-xl bg-slate-800 border border-slate-700 text-sm" />
        )}
        {form.method === 'bank' && (
          <div className="space-y-2">
            <input value={form.bank_account} onChange={(e) => setForm({ ...form, bank_account: e.target.value })}
                   placeholder="Account Number" className="w-full px-3 py-2 rounded-xl bg-slate-800 border border-slate-700 text-sm" />
            <input value={form.bank_ifsc} onChange={(e) => setForm({ ...form, bank_ifsc: e.target.value })}
                   placeholder="IFSC Code" className="w-full px-3 py-2 rounded-xl bg-slate-800 border border-slate-700 text-sm" />
            <input value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })}
                   placeholder="Bank Name" className="w-full px-3 py-2 rounded-xl bg-slate-800 border border-slate-700 text-sm" />
          </div>
        )}
        <input value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
               placeholder="Note (optional)" className="w-full px-3 py-2 rounded-xl bg-slate-800 border border-slate-700 text-sm" />
        <button onClick={submit} disabled={busy}
                data-testid="mkt-wallet-withdraw-submit"
                className="w-full py-3 rounded-xl bg-gradient-to-r from-rose-600 to-red-600 text-white font-black text-sm disabled:opacity-40">
          {busy ? '…' : `💵 Request Withdraw ${form.amount ? `(${totalDebit} AT total)` : ''}`}
        </button>
        {/* My past requests */}
        {myReqs.length > 0 && (
          <div className="pt-2 border-t border-slate-800">
            <div className="text-xs font-black text-rose-300 mb-1">My Requests</div>
            {myReqs.slice(0, 5).map((r) => (
              <div key={r.id} className="flex items-center justify-between text-[11px] py-1 border-b border-slate-800 last:border-0">
                <span>{r.amount} AT · {r.method}</span>
                <span className={`font-black ${r.status === 'paid' ? 'text-emerald-300' : r.status === 'rejected' ? 'text-rose-300' : 'text-amber-300'}`}>{r.status}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ============ GROUP BUY BELL (header-level) ============
function GroupBuyBell() {
  const [data, setData] = useState({ pending: 0, recent: [] });
  const [open, setOpen] = useState(false);
  const tick = useCallback(async () => {
    try { const r = await api.get('/group-buy/bell-summary'); setData(r.data); } catch {}
  }, []);
  useEffect(() => { tick(); const t = setInterval(tick, 30000); return () => clearInterval(t); }, [tick]);
  if (!localStorage.getItem('token')) return null;
  return (
    <div className="relative" data-testid="mkt-gb-bell">
      <button onClick={() => setOpen((v) => !v)} data-testid="mkt-gb-bell-btn"
              className="relative px-2 py-1 rounded-full bg-slate-800 hover:bg-slate-700 text-white text-sm">
        🔔
        {data.pending > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] px-1 rounded-full bg-rose-500 text-white text-[10px] font-black flex items-center justify-center" data-testid="mkt-gb-bell-count">
            {data.pending > 9 ? '9+' : data.pending}
          </span>
        )}
      </button>
      {open && (
        <div className="absolute right-0 top-9 w-72 rounded-2xl bg-slate-900 border-2 border-slate-700 shadow-2xl z-50 p-2" data-testid="mkt-gb-bell-dropdown">
          <div className="text-xs font-black text-fuchsia-300 uppercase mb-1">🎁 My Pending Group Buys</div>
          {data.recent.length === 0 && <div className="text-xs opacity-60 py-4 text-center">No pending group buys</div>}
          <div className="space-y-1 max-h-80 overflow-y-auto">
            {data.recent.map((r) => (
              <a key={r.id} href={`/gb/${r.id}`} className="block p-2 rounded bg-slate-800 hover:bg-slate-700" data-testid={`mkt-gb-bell-item-${r.id}`}>
                <div className="text-xs font-black truncate">{r.title}</div>
                <div className="text-[10px] opacity-80">Joined {r.joined}/{r.min_buyers}</div>
              </a>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ============ ADMIN LIVE COUNTER ============
function AdminLiveCounter({ isAdmin }) {
  useEffect(() => {
    if (!isAdmin) return;
    let since = new Date().toISOString();
    const tick = async () => {
      try {
        const r = await api.get('/admin/franchisee/activity-ping', { params: { since_iso: since } });
        const apps = r.data.new_applications || 0;
        const stores = r.data.new_stores || 0;
        if (apps > 0) {
          toast.success(`🎉 ${apps} new application${apps > 1 ? 's' : ''} received!`, { duration: 7000 });
          try { sbAnnounce('new_order', { amount: apps }); } catch {}
        }
        if (stores > 0) {
          toast.success(`🏪 ${stores} new store signup${stores > 1 ? 's' : ''}!`, { duration: 7000 });
          try { sbAnnounce('new_order', { amount: stores }); } catch {}
        }
        since = r.data.now || since;
      } catch {}
    };
    const t = setInterval(tick, 20000);
    return () => clearInterval(t);
  }, [isAdmin]);
  return null;
}

// ============ TATKAL DEALS (Wave 53) ============
function TatkalDealsTab({ cfg, isAdmin, isGuest }) {
  const [deals, setDeals] = useState([]);
  const [mine, setMine] = useState([]);
  const [vendorDeals, setVendorDeals] = useState([]);
  const [refunds, setRefunds] = useState([]);
  const [tick, setTick] = useState(Date.now());
  const [showCreate, setShowCreate] = useState(false);
  const [booking, setBooking] = useState(null);
  const canVendor = !isGuest && ['standard', 'prime', 'vip'].includes((cfg?.plan_status?.status === 'active' ? (cfg?.plan?.parent_tier || cfg?.plan?.id) : null) || '');
  const load = useCallback(async () => {
    try { const r = await api.get('/tatkal/live'); 
      const newDeals = r.data.deals || [];
      // Push alert for new deals (soundbox + toast)
      if (deals.length > 0 && newDeals.length > deals.length) {
        const latestDeal = newDeals[0];
        toast.success(`🚨 New Tatkal Deal! ${latestDeal.title} · ₹${latestDeal.tatkal_price}`);
        try { sbAnnounce('new_order', { amount: latestDeal.booking_fee_at }); } catch {}
      }
      setDeals(newDeals);
    } catch {}
    if (!isGuest) {
      try { const r = await api.get('/tatkal/my-bookings'); setMine(r.data.bookings || []); } catch {}
      try { const r = await api.get('/tatkal/vendor/deals'); setVendorDeals(r.data.deals || []); } catch {}
    }
    if (isAdmin) {
      try { const r = await api.get('/admin/tatkal/refunds'); setRefunds(r.data.refunds || []); } catch {}
    }
  }, [isGuest, isAdmin, deals.length]);
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [load]);
  useEffect(() => { const t = setInterval(() => setTick(Date.now()), 1000); return () => clearInterval(t); }, []);

  const book = async (deal) => {
    if (isGuest) { window.location.href = '/login'; return; }
    const bal = Number(cfg?.at_balance || 0);
    const fee = deal.booking_fee_at || 50;
    if (bal < fee) { toast.error(`Need ${fee} AT · have ${Math.floor(bal)}. Top-up karein.`); return; }
    if (!window.confirm(`${fee} AT booking fee pay karna hai (non-refundable unless missed+approved). Continue?`)) return;
    try {
      const r = await api.post(`/tatkal/book/${deal.deal_id}`);
      setBooking(r.data.booking);
      toast.success('🎉 Booked! VIP code generated.');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Booking failed'); }
  };

  const requestRefund = async (bk) => {
    if (!window.confirm(`Refund request bhejna hai? Admin approve karega to 40% (${bk.refund_amount} AT) wapas milega.`)) return;
    try { await api.post(`/tatkal/bookings/${bk.booking_id}/request-refund`); toast.success('Refund requested'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  return (
    <div className="space-y-3" data-testid="mkt-tatkal-tab">
      {/* FOMO Header */}
      <div className="rounded-2xl p-4 bg-gradient-to-r from-rose-700 via-red-600 to-orange-600 text-white shadow-xl relative overflow-hidden">
        <div className="absolute inset-0 opacity-20 animate-pulse bg-gradient-to-r from-yellow-400 via-rose-500 to-yellow-400"></div>
        <div className="relative">
          <div className="text-xs font-black uppercase tracking-widest opacity-90">⚡ URGENT · LIVE NOW</div>
          <div className="text-2xl font-black mt-1">Anant Tatkal Zone</div>
          <div className="text-[11px] opacity-90">Flash deals · Max 6h window · AT Wallet only · 1-click lock-in</div>
        </div>
      </div>

      {/* Vendor "panic button" */}
      {canVendor && (
        <button onClick={() => setShowCreate(true)} data-testid="mkt-tatkal-launch-btn"
                className="w-full py-3 rounded-2xl bg-gradient-to-r from-rose-600 to-red-700 text-white font-black text-sm animate-pulse">
          🚨 LAUNCH TATKAL DEAL (vendor only)
        </button>
      )}
      {!canVendor && !isGuest && (
        <div className="text-[11px] opacity-70 text-center">Tatkal vendor = Standard/Prime/VIP plan only</div>
      )}

      {/* Live deals grid */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
        {deals.map((d) => <TatkalDealCard key={d.deal_id} d={d} tick={tick} onBook={() => book(d)} />)}
      </div>
      {deals.length === 0 && <div className="text-xs opacity-60 py-10 text-center" data-testid="mkt-tatkal-empty">⏳ Abhi koi Tatkal deal live nahi. Vendors ki wait karein — ya khud launch karein!</div>}

      {/* My bookings */}
      {mine.length > 0 && (
        <div className="rounded-xl bg-slate-900 border border-slate-700 p-3 space-y-2" data-testid="mkt-tatkal-mine">
          <div className="text-xs font-black uppercase text-rose-300">🎟 My Tatkal Bookings</div>
          {mine.map((bk) => (
            <div key={bk.booking_id} className="flex items-center justify-between gap-2 py-1 border-b border-slate-800 last:border-0">
              <div className="min-w-0 flex-1">
                <div className="text-xs font-black truncate">{bk.deal_title}</div>
                <div className="text-[10px] opacity-70">Code: <b className="text-amber-300">{bk.tatkal_code}</b> · {bk.status}</div>
              </div>
              {bk.status === 'Missed' && (
                <button onClick={() => requestRefund(bk)} className="px-2 py-1 rounded bg-amber-500 text-slate-900 text-[10px] font-black">💰 Refund 40%</button>
              )}
              {bk.status === 'Refund_Requested' && <span className="text-[10px] text-amber-300 font-black">⏳ Pending</span>}
              {bk.status === 'Refund_Approved' && <span className="text-[10px] text-emerald-300 font-black">✅ Refunded</span>}
              {bk.status === 'Completed' && <span className="text-[10px] text-emerald-300 font-black">✅ Done</span>}
            </div>
          ))}
        </div>
      )}

      {/* Vendor's own deals */}
      {canVendor && vendorDeals.length > 0 && (
        <div className="rounded-xl bg-slate-900 border border-rose-500/40 p-3" data-testid="mkt-tatkal-vendor-list">
          <div className="text-xs font-black uppercase text-rose-300 mb-2">⚡ My Tatkal Deals</div>
          {vendorDeals.map((d) => (
            <div key={d.deal_id} className="py-1.5 border-b border-slate-800 last:border-0 text-[11px]">
              <div className="font-black">{d.title} — {d.status} · {d.available_slots}/{d.total_slots} slots</div>
              <div className="opacity-70">Codes to verify: {(d.bookings || []).filter((b) => b.status === 'Booked').map((b) => b.tatkal_code).join(', ') || '—'}</div>
            </div>
          ))}
        </div>
      )}

      {/* Admin refund center + City Heatmap + Settings */}
      {isAdmin && (
        <div className="space-y-3">
          {/* City Heatmap */}
          <TatkalCityHeatmap />

          {/* Admin Settings */}
          <TatkalAdminSettings />

          {/* Refund center */}
          <div className="rounded-xl bg-amber-500/10 border-2 border-amber-400/40 p-3 space-y-2" data-testid="mkt-tatkal-admin-refunds">
            <div className="text-xs font-black uppercase text-amber-200">👑 Admin · Refund Resolution Center ({refunds.length})</div>
            {refunds.map((r) => (
              <div key={r.booking_id} className="rounded bg-slate-900 p-2 flex flex-wrap items-center gap-2 text-[11px]">
                <div className="min-w-0 flex-1">
                  <div className="font-black">{r.username} · {r.deal_title}</div>
                  <div className="opacity-70">Shop: {r.vendor_shop_name || '—'} · Code: {r.tatkal_code} · Refund: {r.refund_amount} AT</div>
                </div>
                {r.vendor_wa_link && <a href={r.vendor_wa_link} target="_blank" rel="noreferrer" className="px-2 py-1 rounded bg-green-600 text-white text-[10px] font-black">📱 Verify</a>}
                <button onClick={async () => { if (window.confirm(`Approve refund of ${r.refund_amount} AT?`)) { try { await api.post(`/admin/tatkal/refunds/${r.booking_id}/approve`, {}); toast.success('✅ Refunded'); load(); } catch (e) { toast.error('Failed'); } } }}
                        data-testid={`mkt-tatkal-approve-${r.booking_id}`}
                        className="px-2 py-1 rounded bg-emerald-500 text-slate-900 text-[10px] font-black">✅ Approve</button>
                <button onClick={async () => { const n = window.prompt('Reject reason?'); if (n === null) return; try { await api.post(`/admin/tatkal/refunds/${r.booking_id}/reject`, { note: n }); toast.success('Rejected'); load(); } catch { toast.error('Failed'); } }}
                        className="px-2 py-1 rounded bg-rose-600 text-white text-[10px] font-black">✕ Reject</button>
              </div>
            ))}
            {refunds.length === 0 && <div className="text-[11px] opacity-60">No pending refunds</div>}
          </div>
        </div>
      )}

      {showCreate && <TatkalCreateModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); load(); }} />}
      {booking && <TatkalVipPass bk={booking} onClose={() => setBooking(null)} />}
    </div>
  );
}

function TatkalDealCard({ d, tick, onBook }) {
  const sec = Math.max(0, (d.seconds_left || 0) - Math.floor((tick - Date.now()) / 1000));
  const h = Math.floor(sec / 3600);
  const m = Math.floor((sec % 3600) / 60);
  const s = sec % 60;
  const sold = d.total_slots - d.available_slots;
  const pct = Math.round((sold / Math.max(1, d.total_slots)) * 100);
  const shareOgUrl = `${process.env.REACT_APP_BACKEND_URL || ''}/api/share/tatkal/${d.deal_id}`;
  return (
    <div className="rounded-2xl bg-slate-900 border-2 border-rose-500/50 overflow-hidden" data-testid={`mkt-tatkal-card-${d.deal_id}`}>
      {/* Media — aspect-video preserves ratio, object-contain shows full image */}
      {d.media_url && d.media_type === 'video' && (
        <video src={d.media_url} className="w-full aspect-video object-cover bg-black" autoPlay muted loop playsInline />
      )}
      {d.media_url && d.media_type !== 'video' && (
        <div className="w-full aspect-video bg-slate-800 flex items-center justify-center overflow-hidden">
          <img src={d.media_url} alt="" className="w-full h-full object-contain" />
        </div>
      )}
      <div className="p-3 space-y-2">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="text-sm font-black truncate">{d.title}</div>
            <div className="text-[10px] opacity-70">{d.shop_name}{d.city ? ' · ' + d.city : ''}</div>
          </div>
          <div className="text-right">
            <div className="text-[9px] opacity-70">ONLY</div>
            <div className="text-xl font-black text-emerald-300 tabular-nums">₹{d.tatkal_price}</div>
            <div className="text-[10px] line-through opacity-60">₹{d.original_price}</div>
          </div>
        </div>
        {d.description && <div className="text-[11px] opacity-80 line-clamp-2">{d.description}</div>}
        <div className="rounded bg-black/40 p-2 text-center">
          <div className="text-[9px] opacity-70 uppercase">Ends in</div>
          <div className="text-3xl font-black text-rose-400 tabular-nums" data-testid={`mkt-tatkal-timer-${d.deal_id}`}>
            {String(h).padStart(2, '0')}:{String(m).padStart(2, '0')}:{String(s).padStart(2, '0')}
          </div>
        </div>
        <div>
          <div className="flex items-center justify-between text-[10px] font-black mb-1">
            <span>{sold} / {d.total_slots} booked</span>
            <span className={d.available_slots <= 2 ? 'text-rose-400 animate-pulse' : 'text-amber-300'}>
              {d.available_slots <= 2 ? `🔥 Only ${d.available_slots} left!` : `${d.available_slots} slots`}
            </span>
          </div>
          <div className="h-2 bg-slate-800 rounded overflow-hidden">
            <div className="h-full bg-gradient-to-r from-amber-400 to-rose-500" style={{ width: `${pct}%` }} />
          </div>
        </div>
        <button onClick={onBook} disabled={d.available_slots <= 0 || sec === 0}
                data-testid={`mkt-tatkal-book-${d.deal_id}`}
                className="w-full py-2.5 rounded-xl bg-gradient-to-r from-rose-600 to-red-700 text-white font-black text-sm disabled:opacity-40">
          🎟 Book Now · {d.booking_fee_at} AT lock-in
        </button>
        {/* Social Share buttons — use OG backend URL so WhatsApp shows image preview */}
        <div className="flex gap-1">
          <button onClick={(e) => { e.stopPropagation(); const msg = `🚨 Tatkal Deal LIVE: *${d.title}* · ₹${d.tatkal_price} (was ₹${d.original_price}) · ${d.available_slots} slots left!\n\n👉 ${shareOgUrl}`; window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank'); }}
                  data-testid={`mkt-tatkal-wa-${d.deal_id}`}
                  className="flex-1 py-1.5 rounded-lg bg-green-700/50 text-green-200 text-[10px] font-black text-center">
            💬 WA
          </button>
          <button onClick={(e) => { e.stopPropagation(); const msg = `🚨 Tatkal Deal: ${d.title} at ₹${d.tatkal_price}!`; window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareOgUrl)}&quote=${encodeURIComponent(msg)}`, '_blank'); }}
                  data-testid={`mkt-tatkal-fb-${d.deal_id}`}
                  className="flex-1 py-1.5 rounded-lg bg-blue-700/50 text-blue-200 text-[10px] font-black text-center">
            📘 FB
          </button>
          <button onClick={(e) => { e.stopPropagation(); const caption = `🚨 Tatkal Deal: ${d.title} at ₹${d.tatkal_price}! Book now: ${shareOgUrl}`; navigator.clipboard.writeText(caption).then(() => toast.success('📋 Caption copied — paste in Instagram Story!')).catch(() => {}); }}
                  data-testid={`mkt-tatkal-ig-${d.deal_id}`}
                  className="flex-1 py-1.5 rounded-lg bg-gradient-to-br from-purple-700/50 to-pink-700/50 text-pink-200 text-[10px] font-black text-center">
            📸 IG
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── TATKAL ADMIN: CITY HEATMAP ───────────────────────────────────────
function TatkalCityHeatmap() {
  const [data, setData] = useState([]);
  const [days, setDays] = useState(7);
  const [loading, setLoading] = useState(false);
  const load = async () => {
    setLoading(true);
    try { const r = await api.get('/admin/tatkal/city-heatmap', { params: { days } }); setData(r.data.cities || []); }
    catch { toast.error('Heatmap load failed'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [days]);
  const max = Math.max(1, ...data.map((c) => c.deals));

  return (
    <div className="rounded-xl bg-slate-900 border border-amber-400/30 p-3 space-y-2" data-testid="mkt-tatkal-heatmap">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-xs font-black text-amber-200 uppercase">🗺️ City Deals Heatmap</div>
        <div className="flex gap-1">
          {[7, 15, 30].map((d) => (
            <button key={d} onClick={() => setDays(d)}
                    className={`px-2 py-0.5 rounded text-[10px] font-black ${days === d ? 'bg-amber-500 text-slate-900' : 'bg-slate-700 text-slate-300'}`}>
              {d}d
            </button>
          ))}
        </div>
      </div>
      {loading && <div className="text-xs opacity-60 text-center py-2">Loading…</div>}
      {!loading && data.length === 0 && <div className="text-xs opacity-60 text-center py-2">No data for period</div>}
      <div className="space-y-1 max-h-64 overflow-y-auto">
        {data.map((city) => {
          const pct = Math.round((city.deals / max) * 100);
          return (
            <div key={city.city} className="flex items-center gap-2" data-testid={`mkt-tatkal-heatmap-${city.city}`}>
              <div className="w-20 text-[10px] font-black truncate capitalize">{city.city}</div>
              <div className="flex-1 rounded-full bg-slate-800 h-3 overflow-hidden">
                <div style={{ width: `${pct}%` }} className="h-full bg-gradient-to-r from-amber-500 to-rose-500 rounded-full transition-all" />
              </div>
              <div className="w-16 text-right text-[10px] text-slate-300">
                <span className="font-black text-amber-200">{city.deals}</span> deals · {city.bookings} bkd
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── TATKAL ADMIN: SETTINGS ───────────────────────────────────────────
function TatkalAdminSettings() {
  const [settings, setSettings] = useState({ max_window_hours: 6, default_booking_fee: 50 });
  const [saving, setSaving] = useState(false);
  const load = async () => {
    try { const r = await api.get('/admin/tatkal/settings'); setSettings(r.data); } catch {}
  };
  useEffect(() => { load(); }, []);
  const save = async () => {
    setSaving(true);
    try {
      await api.post('/admin/tatkal/settings', settings);
      toast.success('✅ Tatkal settings saved');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };
  return (
    <div className="rounded-xl bg-slate-900 border border-rose-400/30 p-3 space-y-2" data-testid="mkt-tatkal-admin-settings">
      <div className="text-xs font-black text-rose-200 uppercase">⚙️ Tatkal Settings</div>
      <div className="flex items-center gap-2 flex-wrap">
        <label className="text-[11px] opacity-70">Max Window (hours)</label>
        <input type="number" min={1} max={72} value={settings.max_window_hours || 6}
               onChange={(e) => setSettings({ ...settings, max_window_hours: Number(e.target.value) })}
               className="w-20 px-2 py-1 rounded bg-slate-800 border border-slate-700 text-sm" />
        <label className="text-[11px] opacity-70">Default Fee AT</label>
        <input type="number" min={10} max={1000} value={settings.default_booking_fee || 50}
               onChange={(e) => setSettings({ ...settings, default_booking_fee: Number(e.target.value) })}
               className="w-20 px-2 py-1 rounded bg-slate-800 border border-slate-700 text-sm" />
        <button onClick={save} disabled={saving}
                className="px-3 py-1 rounded-lg bg-rose-600 text-white text-xs font-black disabled:opacity-40">
          {saving ? '…' : '💾 Save'}
        </button>
      </div>
    </div>
  );
}

function TatkalCreateModal({ onClose, onCreated }) {
  const nowLocal = new Date(Date.now() + 60000).toISOString().slice(0, 16);
  const defEnd = new Date(Date.now() + 2 * 3600 * 1000).toISOString().slice(0, 16);
  const [form, setForm] = useState({ title: '', description: '', original_price: '', tatkal_price: '', total_slots: 2, start_time: nowLocal, end_time: defEnd, booking_fee_at: 50, media_url: '', media_type: 'image' });
  const [busy, setBusy] = useState(false);
  const [maxHours, setMaxHours] = useState(6);
  useEffect(() => {
    api.get('/admin/tatkal/settings').then((r) => setMaxHours(r.data?.max_window_hours || 6)).catch(() => {});
  }, []);
  const submit = async () => {
    if (!form.title.trim() || !form.original_price || !form.tatkal_price) { toast.error('Fill all required'); return; }
    setBusy(true);
    try {
      const payload = { ...form,
        original_price: Number(form.original_price),
        tatkal_price: Number(form.tatkal_price),
        total_slots: Number(form.total_slots),
        booking_fee_at: Number(form.booking_fee_at),
        start_time: new Date(form.start_time).toISOString(),
        end_time: new Date(form.end_time).toISOString(),
        media_url: form.media_url || undefined,
        media_type: form.media_url ? (form.media_type || 'image') : undefined,
      };
      await api.post('/tatkal/vendor/create', payload);
      toast.success('🚨 Tatkal deal launched!');
      // Soundbox alert
      try { sbAnnounce('new_order', { amount: 1 }); } catch {}
      onCreated && onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-end sm:items-center justify-center p-2" onClick={onClose} data-testid="mkt-tatkal-create-modal">
      <div className="w-full max-w-md rounded-2xl bg-slate-900 border-2 border-rose-500 p-4 space-y-2 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="font-black text-rose-300">🚨 Launch Tatkal Deal · 30 sec</div>
        <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Deal title *" data-testid="mkt-tatkal-title" className="w-full px-2 py-2 rounded bg-slate-800 text-sm" />
        <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} placeholder="Short description" className="w-full px-2 py-2 rounded bg-slate-800 text-sm" />
        <div className="grid grid-cols-2 gap-2">
          <input type="number" value={form.original_price} onChange={(e) => setForm({ ...form, original_price: e.target.value })} placeholder="Original ₹" data-testid="mkt-tatkal-orig" className="px-2 py-2 rounded bg-slate-800 text-sm" />
          <input type="number" value={form.tatkal_price} onChange={(e) => setForm({ ...form, tatkal_price: e.target.value })} placeholder="Tatkal ₹" data-testid="mkt-tatkal-price" className="px-2 py-2 rounded bg-slate-800 text-sm" />
          <input type="number" min={1} max={100} value={form.total_slots} onChange={(e) => setForm({ ...form, total_slots: e.target.value })} placeholder="Slots" data-testid="mkt-tatkal-slots" className="px-2 py-2 rounded bg-slate-800 text-sm" />
          <input type="number" min={10} max={500} value={form.booking_fee_at} onChange={(e) => setForm({ ...form, booking_fee_at: e.target.value })} placeholder="Fee AT" className="px-2 py-2 rounded bg-slate-800 text-sm" />
          <input type="datetime-local" value={form.start_time} onChange={(e) => setForm({ ...form, start_time: e.target.value })} className="px-2 py-2 rounded bg-slate-800 text-sm" />
          <input type="datetime-local" value={form.end_time} onChange={(e) => setForm({ ...form, end_time: e.target.value })} data-testid="mkt-tatkal-end" className="px-2 py-2 rounded bg-slate-800 text-sm" />
        </div>
        {/* Media Upload */}
        <div className="space-y-1">
          <div className="text-[11px] font-black text-rose-200">📸 Deal Media (optional)</div>
          <div className="flex gap-2">
            <input value={form.media_url} onChange={(e) => setForm({ ...form, media_url: e.target.value })}
                   placeholder="Image/Video URL ya Gallery se upload"
                   className="flex-1 px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
            <select value={form.media_type} onChange={(e) => setForm({ ...form, media_type: e.target.value })}
                    className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs">
              <option value="image">Image</option>
              <option value="video">Video</option>
            </select>
          </div>
          {/* Camera input option */}
          <label className="cursor-pointer inline-flex items-center gap-1 px-2 py-1 rounded bg-slate-700 text-[10px] font-black text-slate-200">
            📷 Camera/Gallery
            <input type="file" accept="image/*,video/*" capture="environment" className="hidden"
                   onChange={async (e) => {
                     const file = e.target.files?.[0];
                     if (!file) return;
                     const fd = new FormData(); fd.append('file', file);
                     try {
                       const r = await api.post('/webshop/upload/image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                       const url = r.data?.url || r.data?.file_url || '';
                       setForm((f) => ({ ...f, media_url: url, media_type: file.type.startsWith('video') ? 'video' : 'image' }));
                       toast.success('Media uploaded!');
                     } catch { toast.error('Upload failed'); }
                   }} />
          </label>
          {form.media_url && (
            <div className="rounded overflow-hidden max-h-20">
              {form.media_type === 'video'
                ? <video src={form.media_url} className="w-full max-h-20 object-cover" />
                : <img src={form.media_url} alt="" className="w-full max-h-20 object-cover" />}
            </div>
          )}
        </div>
        <div className="text-[10px] opacity-70">⚠ Max {maxHours}h window. Buyers pay AT booking-fee locked immediately.</div>
        <button onClick={submit} disabled={busy} data-testid="mkt-tatkal-submit"
                className="w-full py-2.5 rounded-xl bg-rose-600 text-white font-black disabled:opacity-50">
          {busy ? '…' : '🚨 Launch Tatkal Deal'}
        </button>
      </div>
    </div>
  );
}

function TatkalVipPass({ bk, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-3" onClick={onClose} data-testid="mkt-tatkal-vip-pass">
      <div className="w-full max-w-sm rounded-3xl bg-gradient-to-br from-amber-400 to-rose-500 p-5 text-slate-900 text-center shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="text-xs font-black uppercase tracking-widest opacity-80">Anant VIP Pass</div>
        <div className="text-sm font-black mt-1">{bk.deal_title}</div>
        <div className="my-3 py-3 rounded-xl bg-black text-amber-300 font-black tracking-widest text-3xl tabular-nums" data-testid="mkt-tatkal-vip-code">{bk.tatkal_code}</div>
        <div className="text-[11px] opacity-80">Show this at the shop. Non-refundable unless Missed + Admin-approved.</div>
        <button onClick={onClose} className="mt-3 w-full py-2 rounded-xl bg-black/80 text-white text-xs font-black">Close</button>
      </div>
    </div>
  );
}

// ============ WALL ANALYTICS (Wave 53) ============
function VyaparWallAnalytics() {
  const [data, setData] = useState(null);
  const [open, setOpen] = useState(false);
  const load = async () => {
    try { const r = await api.get('/vyapar-wall/analytics'); setData(r.data); } catch {}
  };
  useEffect(() => { if (open) load(); }, [open]);
  return (
    <div className="rounded-xl bg-emerald-500/10 border border-emerald-400/40 p-2" data-testid="mkt-wall-analytics">
      <div className="flex items-center justify-between">
        <div className="text-[11px] font-black text-emerald-300 uppercase">📊 My Wall Analytics</div>
        <button onClick={() => setOpen((v) => !v)} data-testid="mkt-wall-analytics-toggle" className="px-2 py-0.5 rounded bg-emerald-500 text-slate-900 text-[10px] font-black">{open ? 'Hide' : 'Show'}</button>
      </div>
      {open && data && (
        <div className="mt-2 space-y-2">
          <div className="grid grid-cols-4 gap-1 text-center">
            <div className="rounded bg-slate-900 p-1"><div className="text-[9px] opacity-70">Posts</div><div className="font-black">{data.totals.posts}</div></div>
            <div className="rounded bg-slate-900 p-1"><div className="text-[9px] opacity-70">Views</div><div className="font-black">{data.totals.views}</div></div>
            <div className="rounded bg-slate-900 p-1"><div className="text-[9px] opacity-70">Shares</div><div className="font-black">{data.totals.shares}</div></div>
            <div className="rounded bg-slate-900 p-1"><div className="text-[9px] opacity-70">Ref Signups</div><div className="font-black text-emerald-300">{data.totals.ref_signups}</div></div>
          </div>
          <div className="max-h-56 overflow-y-auto space-y-1">
            {(data.posts || []).map((p) => (
              <div key={p.id} className="flex items-center justify-between gap-2 text-[11px] py-1 border-b border-slate-800 last:border-0" data-testid={`mkt-wall-analytics-row-${p.id}`}>
                <div className="min-w-0 flex-1 truncate">{p.title}</div>
                <div className="opacity-80">👁 {p.views || 0}</div>
                <div className="opacity-80">🔄 {p.shares || 0}</div>
                <div className="opacity-80">🖱 {p.clicks || 0}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── VENDOR ORDERS DASHBOARD ──────────────────────────────────────────
function VendorOrdersDashboard() {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState('pending,confirmed');
  const [loading, setLoading] = useState(false);
  const [unread, setUnread] = useState(0);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/marketplace/vendor/orders', { params: filter ? { status: filter, limit: 100 } : { limit: 100 } });
      setOrders(r.data.orders || []);
      setUnread(r.data.new_unread || r.data.unread_count || 0);
    } catch { toast.error('Orders load failed'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [filter]);

  const STATUS_COLORS = {
    pending: 'bg-amber-500/20 text-amber-300',
    confirmed: 'bg-sky-500/20 text-sky-300',
    shipped: 'bg-blue-500/20 text-blue-300',
    delivered: 'bg-emerald-500/20 text-emerald-300',
    cancelled: 'bg-rose-500/20 text-rose-300',
    refund_requested: 'bg-orange-500/20 text-orange-300',
  };

  const totalAt = orders.reduce((s, o) => s + Number(o.my_total_at || 0), 0);

  return (
    <div className="space-y-3" data-testid="mkt-vendor-orders">
      {/* Summary */}
      <div className="rounded-2xl bg-gradient-to-r from-emerald-900/60 to-teal-900/60 border-2 border-emerald-400/40 p-4 flex flex-wrap gap-3 items-center">
        <div className="flex-1 min-w-0">
          <div className="text-xs font-black text-emerald-300 uppercase tracking-wide">📦 Incoming Orders</div>
          <div className="text-[11px] opacity-80 mt-0.5">Showing <b>{orders.length}</b> orders · Total: <b className="text-emerald-300">{totalAt.toFixed(0)} AT</b></div>
        </div>
        {unread > 0 && (
          <div className="px-3 py-1 rounded-full bg-rose-500 text-white text-xs font-black">{unread} New</div>
        )}
        <button onClick={load} className="px-2 py-1 rounded-lg bg-slate-700 text-slate-200 text-[10px] font-black">↻</button>
      </div>

      {/* Filters */}
      <div className="flex gap-1 flex-wrap">
        {[
          ['pending,confirmed', '🔥 New'],
          ['shipped', '🚚 Shipped'],
          ['delivered', '✅ Delivered'],
          ['cancelled,refund_requested', '❌ Issues'],
          ['', '📋 All'],
        ].map(([val, label]) => (
          <button key={val} onClick={() => setFilter(val)}
                  className={`px-3 py-1.5 rounded-full text-[11px] font-black border-2 transition ${filter === val ? 'bg-emerald-600 border-emerald-400 text-white' : 'bg-slate-800 border-slate-700 text-slate-300'}`}>
            {label}
          </button>
        ))}
      </div>

      {loading && <div className="text-center py-8 text-xs opacity-60">Loading orders…</div>}
      {!loading && orders.length === 0 && (
        <div className="text-center py-10 text-xs opacity-60">
          {filter.includes('pending') ? 'Koi naya order nahi abhi — share karo apna store!' : 'No orders found for this filter.'}
        </div>
      )}

      <div className="space-y-2">
        {orders.map((order) => (
          <div key={order.id} className="rounded-2xl bg-slate-900 border border-slate-700 p-3 space-y-2"
               data-testid={`mkt-vendor-order-${order.id}`}>
            {/* Order header */}
            <div className="flex items-start justify-between gap-2 flex-wrap">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[10px] font-mono opacity-60">#{(order.id || '').slice(0, 8)}</span>
                  <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${STATUS_COLORS[order.status] || 'bg-slate-700 text-slate-300'}`}>
                    {order.status}
                  </span>
                  <span className="text-[10px] opacity-50">{(order.created_at || '').slice(0, 16).replace('T', ' ')}</span>
                </div>
              </div>
              <div className="font-black text-emerald-300 text-base shrink-0">{Number(order.my_total_at || 0).toFixed(0)} AT</div>
            </div>

            {/* Buyer info */}
            <div className="grid grid-cols-2 gap-1 text-[11px]">
              <div className="rounded-lg bg-slate-800 px-2 py-1">
                <span className="opacity-60">Buyer: </span><b>{order.buyer_name || '—'}</b>
              </div>
              <div className="rounded-lg bg-slate-800 px-2 py-1">
                <span className="opacity-60">City: </span><b>{order.buyer_city || '—'}</b>
              </div>
              {order.buyer_address && (
                <div className="col-span-2 rounded-lg bg-slate-800 px-2 py-1">
                  <span className="opacity-60">Address: </span>{order.buyer_address}
                </div>
              )}
            </div>

            {/* Items */}
            <div className="space-y-1">
              {(order.my_items || []).map((item, i) => (
                <div key={i} className="flex items-center justify-between text-[11px] border-b border-slate-800 last:border-0 py-0.5">
                  <span className="truncate flex-1 font-black">{item.title}</span>
                  <span className="opacity-70 ml-2">×{item.quantity}</span>
                  <span className="text-amber-200 font-black ml-2">{Number(item.line_total_at || 0).toFixed(0)} AT</span>
                </div>
              ))}
            </div>

            {/* Contact + Note */}
            <div className="flex gap-2 flex-wrap items-center">
              {order.buyer_phone && (
                <>
                  <a href={`tel:${order.buyer_phone}`} className="px-2 py-1 rounded-lg bg-sky-700/40 text-sky-200 text-[10px] font-black" data-testid={`mkt-vo-call-${order.id}`}>
                    📞 Call
                  </a>
                  <a href={`https://wa.me/${(order.buyer_phone).replace(/\D/g,'')}?text=${encodeURIComponent(`Namaste! Aapka order #${(order.id||'').slice(0,8)} mujhe mila. Confirm karta hoon.`)}`}
                     target="_blank" rel="noreferrer"
                     className="px-2 py-1 rounded-lg bg-green-700/40 text-green-200 text-[10px] font-black" data-testid={`mkt-vo-wa-${order.id}`}>
                    💬 WhatsApp
                  </a>
                </>
              )}
              {order.note && <span className="text-[10px] opacity-60 italic">"{order.note}"</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============ STORE CATEGORY PICKER ============
// Multi-select chips for the store's category. Options = fixed shop types + admin categories.
// Value stays a comma-separated string so the existing /shops category + kind filters keep working.
const STORE_FIXED_CATS = ['Products', 'Services', 'Showrooms'];
const splitCats = (v) => (v || '').split(',').map((x) => x.trim()).filter(Boolean);

function StoreCategoryPicker({ value, onChange }) {
  const [adminCats, setAdminCats] = useState([]);
  const [custom, setCustom] = useState('');
  useEffect(() => {
    api.get('/marketplace/categories')
      .then((r) => setAdminCats((r.data?.categories || []).map((c) => (c.name || '').trim()).filter(Boolean)))
      .catch(() => setAdminCats([]));
  }, []);
  const selected = splitCats(value);
  const isSel = (name) => selected.some((x) => x.toLowerCase() === name.toLowerCase());
  // Options: fixed + admin + any saved value not in the list (so nothing saved is ever hidden)
  const options = [];
  const seen = new Set();
  [...STORE_FIXED_CATS, ...adminCats, ...selected].forEach((n) => {
    const k = n.toLowerCase();
    if (!seen.has(k)) { seen.add(k); options.push(n); }
  });
  const toggle = (name) => {
    const next = isSel(name) ? selected.filter((x) => x.toLowerCase() !== name.toLowerCase()) : [...selected, name];
    onChange(next.join(', '));
  };
  const addCustom = () => {
    const n = custom.trim();
    if (!n) return;
    if (!isSel(n)) onChange([...selected, n].join(', '));
    setCustom('');
  };
  return (
    <div className="col-span-2 rounded-lg bg-slate-800 border border-slate-700 p-2 space-y-1.5" data-testid="mkt-store-category-picker">
      <div className="text-[10px] font-black text-purple-300 uppercase">Store categories (tap to select)</div>
      <div className="flex flex-wrap gap-1.5">
        {options.map((n) => (
          <button type="button" key={n} onClick={() => toggle(n)} data-testid={`mkt-store-cat-${n.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
                  className={`px-2 py-1 rounded-full text-[11px] font-bold border ${isSel(n) ? 'bg-purple-500 border-purple-300 text-white' : 'bg-slate-900 border-slate-600 text-slate-300'}`}>
            {isSel(n) ? '✓ ' : ''}{n}
          </button>
        ))}
      </div>
      <div className="flex gap-1.5">
        <input value={custom} onChange={(e) => setCustom(e.target.value)}
               onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addCustom(); } }}
               placeholder="+ Add other category" data-testid="mkt-store-cat-custom"
               className="flex-1 px-2 py-1 rounded bg-slate-900 border border-slate-700 text-xs" />
        <button type="button" onClick={addCustom} className="px-2 py-1 rounded bg-purple-600 text-white text-xs font-black">Add</button>
      </div>
    </div>
  );
}

// ============ STORE TAB ============
function StoreTab({ cfg, onReload }) {
  const s = cfg.store || {};
  const [form, setForm] = useState({
    shop_name: s.shop_name || '', tagline: s.tagline || '', logo_url: s.logo_url || '',
    banner_url: s.banner_url || '', city: s.city || '', category: s.category || '',
    whatsapp: s.whatsapp || '', delivery_whatsapp: s.delivery_whatsapp || '', address: s.address || '',
    is_in_stock: s.is_in_stock !== false, service_radius_km: s.service_radius_km || 3,
    commission_percent: s.commission_percent ?? 5,
    chime_enabled: s.chime_enabled !== false,
    lat: s.lat ?? null, lng: s.lng ?? null,
    maps_link: s.maps_link || '',                                  // Wave 47
    description: s.description || '', hours_text: s.hours_text || '',
    close_days: s.close_days || '', terms_text: s.terms_text || '',
    disclaimer_text: s.disclaimer_text || '',                      // Wave 48
    social_links: s.social_links || {},                            // Wave 51
    custom_buttons: Array.isArray(s.custom_buttons) ? s.custom_buttons : [],
    telegram_chat_id: s.telegram_chat_id || '',                   // Wave 52: Instant order alerts
    // Wave 57: Display Screen & High-Conversion features
    promo_videos: Array.isArray(s.promo_videos) ? s.promo_videos : [],
    promo_images: Array.isArray(s.promo_images) ? s.promo_images : [],
    delivery_eta: s.delivery_eta || '',
    flash_deal_text: s.flash_deal_text || '',
    flash_deal_expires: s.flash_deal_expires ? s.flash_deal_expires.slice(0, 16) : '',
    vocal_for_local: s.vocal_for_local || false,
    vocal_story: s.vocal_story || '',
    vip_club_text: s.vip_club_text || '',
    display_screen_title: s.display_screen_title || '',
  });
  // Wave 47 · GPS accuracy from last read (metres). null = never captured / typed manually.
  const [gpsAccuracy, setGpsAccuracy] = useState(null);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(null);
  const [stockBusy, setStockBusy] = useState(false);
  const uploadPromoVideo = async (ev, key) => {
    const f = ev.target.files?.[0]; ev.target.value = ''; if (!f) return;
    if (f.size > 100 * 1024 * 1024) { toast.error('Video max 100MB — YouTube URL use karo'); return; }
    setUploading(key);
    try {
      const fd = new FormData(); fd.append('file', f);
      const r = await api.post('/marketplace/upload/video', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 300000 });
      const url = r.data.url;
      setForm((p) => ({ ...p, promo_videos: [...(p.promo_videos || []), url] }));
      toast.success('Video uploaded! Save Display Screen to publish.');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Upload failed — YouTube URL try karo'); }
    finally { setUploading(null); }
  };
  const logoRef = useRef(null);
  const bannerRef = useRef(null);

  const detectLocation = () => {
    if (!navigator.geolocation) { toast.error('GPS not supported on this device. Type Latitude/Longitude below or paste a Google Maps link.'); return; }
    toast.message('📍 Detecting GPS…');
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        const acc = pos.coords.accuracy || 9999;
        // Coarse fix (>1 km) = network/IP guess, often far from the shop. Never auto-fill it.
        if (acc > 1000) {
          setGpsAccuracy(acc);
          toast.error(`⚠️ Only an approximate location was found (±${(acc / 1000).toFixed(1)} km). It was NOT saved. Use your phone with GPS on, or type Latitude/Longitude below, or paste your Google Maps link.`, { duration: 9000 });
          return;
        }
        // Wave 47 — reject silent-save if accuracy is worse than 100m; force manual confirm.
        if (acc > 100) {
          setGpsAccuracy(acc);
          const proceed = window.confirm(
            `⚠️ GPS accuracy is ±${Math.round(acc)}m (worse than 100m)\n\n` +
            `Point may be wrong. Options:\n` +
            `• OK = Use it anyway (you can drag/type to correct below)\n` +
            `• Cancel = Discard and try again outdoors`
          );
          if (!proceed) return;
        } else {
          setGpsAccuracy(acc);
        }
        setForm((p) => ({ ...p, lat: +pos.coords.latitude.toFixed(6), lng: +pos.coords.longitude.toFixed(6) }));
        toast.success(`✅ Location set (±${Math.round(acc)}m accuracy)`);
      },
      (err) => {
        const msg = {
          1: 'Location permission is blocked. Allow location for this site in your browser settings, then try again.',
          2: 'Location unavailable. Turn on GPS/Location on your device, or type Latitude/Longitude below.',
          3: 'Location timed out. Try again near a window or outdoors, or type Latitude/Longitude below.',
        }[err?.code] || ('Could not get location: ' + (err?.message || 'unknown error'));
        toast.error('📍 ' + msg, { duration: 8000 });
      },
      { enableHighAccuracy: true, timeout: 20000, maximumAge: 0 }
    );
  };

  // Pull lat/lng out of a pasted Google Maps link (@lat,lng · q=lat,lng · ll=lat,lng · !3dLAT!4dLNG).
  const coordsFromMapsLink = (url) => {
    const u = decodeURIComponent(url || '');
    const pats = [/!3d(-?\d+(?:\.\d+)?)!4d(-?\d+(?:\.\d+)?)/, /@(-?\d+(?:\.\d+)?),\s*(-?\d+(?:\.\d+)?)/,
                  /[?&](?:q|query|ll|destination)=(-?\d+(?:\.\d+)?),\s*(-?\d+(?:\.\d+)?)/, /^\s*(-?\d+(?:\.\d+)?),\s*(-?\d+(?:\.\d+)?)\s*$/];
    for (const re of pats) {
      const m = u.match(re);
      if (m) {
        const lat = Number(m[1]); const lng = Number(m[2]);
        if (Math.abs(lat) <= 90 && Math.abs(lng) <= 180) return { lat: +lat.toFixed(6), lng: +lng.toFixed(6) };
      }
    }
    return null;
  };
  const onMapsLinkChange = (val) => {
    const c = coordsFromMapsLink(val);
    setForm((p) => ({ ...p, maps_link: val, ...(c || {}) }));
    if (c) { setGpsAccuracy(null); toast.success(`✅ Location set from Maps link (${c.lat}, ${c.lng})`); }
  };

  const toggleStock = async () => {
    const next = !form.is_in_stock;
    if (!window.confirm(next ? 'Turn stock ON (all listings active)?' : 'Turn stock OFF (all listings paused)?')) return;
    setStockBusy(true);
    try {
      const r = await api.post('/marketplace/store/stock-toggle', { is_in_stock: next });
      setForm((p) => ({ ...p, is_in_stock: next }));
      toast.success(`✅ Stock ${next ? 'ON' : 'OFF'} · ${r.data.listings_synced} listings synced`);
      onReload && onReload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setStockBusy(false); }
  };

  const uploadTo = async (e, key) => {
    const f = e.target.files?.[0]; if (!f) return;
    setUploading(key);
    try {
      const fd = new FormData(); fd.append('file', f);
      const r = await api.post('/webshop/upload/image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm((prev) => ({ ...prev, [key]: r.data.url }));
      toast.success('🖼 Uploaded');
    } catch (err) { toast.error(err?.response?.data?.detail || 'Upload failed'); }
    finally {
      setUploading(null);
      if (logoRef.current) logoRef.current.value = '';
      if (bannerRef.current) bannerRef.current.value = '';
    }
  };

  const save = async () => {
    if (!form.shop_name.trim()) { toast.error('Shop name required'); return; }
    // Nudge vendor to verify GPS once — helps buyers find their shop on Maps.
    if (form.address && form.address.trim() && (!form.lat || !form.lng)) {
      const wantsGPS = window.confirm(
        '📍 Address set but GPS location not verified.\n\n' +
        'Auto-fill lat/lng now from your current location? ' +
        '(Buyers will get a working Google Maps link on your storefront.)\n\n' +
        'OK = Detect now  ·  Cancel = Save without GPS'
      );
      if (wantsGPS) { detectLocation(); return; /* vendor taps Save again after GPS locks */ }
    }
    setBusy(true);
    try { await api.post('/marketplace/store', form); toast.success('✅ Store saved'); onReload && onReload(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const [storePanel, setStorePanel] = useState('settings'); // 'settings' | 'display' | 'orders'

  if (!cfg.active) return <ActiveGate />;
  return (
    <div className="space-y-3">
      {/* Panel switcher */}
      <div className="flex gap-2">
        <button onClick={() => setStorePanel('settings')}
                className={`px-4 py-2 rounded-xl text-xs font-black border-2 transition ${storePanel === 'settings' ? 'bg-purple-600 border-purple-400 text-white' : 'bg-slate-800 border-slate-700 text-slate-300'}`}
                data-testid="mkt-store-tab-settings">
          ⚙️ Store Settings
        </button>
        <button onClick={() => setStorePanel('display')}
                className={`px-4 py-2 rounded-xl text-xs font-black border-2 transition ${storePanel === 'display' ? 'bg-rose-600 border-rose-400 text-white' : 'bg-slate-800 border-slate-700 text-slate-300'}`}
                data-testid="mkt-store-tab-display">
          🖥️ Display Screen
        </button>
        <button onClick={() => setStorePanel('orders')}
                className={`px-4 py-2 rounded-xl text-xs font-black border-2 transition ${storePanel === 'orders' ? 'bg-emerald-600 border-emerald-400 text-white' : 'bg-slate-800 border-slate-700 text-slate-300'}`}
                data-testid="mkt-store-tab-orders">
          📦 My Orders
        </button>
      </div>

      {storePanel === 'orders' && <VendorOrdersDashboard />}

      {storePanel === 'display' && (() => {
        const isAdv = ['standard', 'prime', 'vip', 'standard_yearly', 'prime_yearly', 'vip_yearly'].includes(cfg.store?.plan_tier) || cfg.store?.advanced_features_enabled === true;
        if (!isAdv) return (
          <div className="rounded-2xl bg-slate-900 border-2 border-amber-400/40 p-6 text-center space-y-3">
            <div className="text-5xl">🔒</div>
            <div className="text-sm font-black text-amber-300">Advanced Display Features Locked</div>
            <div className="text-xs opacity-70 leading-relaxed">
              Ye features <b className="text-amber-300">600 AT Standard plan</b> mein auto-unlock hote hain.<br />
              Ya phir Admin se request karo ki aapke liye "Allow Advanced Features" enable kar den.<br />
              Basic (300 AT) plan mein Admin manually unlock kar sakta hai.
            </div>
          </div>
        );
        return (
          <div className="space-y-3">
            <div className="p-3 rounded-2xl bg-slate-900 border border-rose-400/40 space-y-3">
              <div className="text-xs font-black text-rose-300">🖥️ Display Screen · High-Conversion Features</div>

              {/* ⚡ Flash Deal */}
              <div className="rounded-xl bg-rose-900/30 border border-rose-400/30 p-3 space-y-2">
                <div className="text-[10px] font-black text-rose-300 uppercase">⚡ Flash Deal Banner (Countdown Timer)</div>
                <input value={form.flash_deal_text} onChange={(e) => setForm({ ...form, flash_deal_text: e.target.value })}
                       placeholder="Flash deal offer (e.g. 50% OFF on all sarees today!)"
                       data-testid="mkt-display-flash-text"
                       className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
                <div>
                  <div className="text-[10px] opacity-60 mb-1">Deal Expiry Time (countdown will tick live)</div>
                  <input type="datetime-local" value={form.flash_deal_expires} onChange={(e) => setForm({ ...form, flash_deal_expires: e.target.value })}
                         data-testid="mkt-display-flash-expires"
                         className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
                </div>
              </div>

              {/* 🚚 Delivery ETA */}
              <div className="rounded-xl bg-amber-900/30 border border-amber-400/30 p-3 space-y-2">
                <div className="text-[10px] font-black text-amber-300 uppercase">🚚 Delivery ETA Banner</div>
                <select value={form.delivery_eta} onChange={(e) => setForm({ ...form, delivery_eta: e.target.value })}
                        data-testid="mkt-display-delivery-eta"
                        className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs">
                  <option value="">No delivery ETA banner</option>
                  <option value="Fast Delivery Today">⚡ Fast Delivery Today</option>
                  <option value="Delivery in 1 Hour">🚀 Delivery in 1 Hour</option>
                  <option value="Delivery in 2 Hours">🛵 Delivery in 2 Hours</option>
                  <option value="Delivery in 3 Hours">🛵 Delivery in 3 Hours</option>
                  <option value="Delivery in 4 Hours">🚚 Delivery in 4 Hours</option>
                  <option value="Delivery in 5 Hours">🚚 Delivery in 5 Hours</option>
                </select>
              </div>

              {/* 🇮🇳 Vocal for Local */}
              <div className="rounded-xl bg-orange-900/30 border border-orange-400/30 p-3 space-y-2">
                <div className="text-[10px] font-black text-orange-300 uppercase">🇮🇳 Vocal for Local Founder Badge</div>
                <label className="flex items-center gap-2 text-xs cursor-pointer">
                  <input type="checkbox" checked={!!form.vocal_for_local} onChange={(e) => setForm({ ...form, vocal_for_local: e.target.checked })}
                         data-testid="mkt-display-vocal-local" className="w-4 h-4 accent-orange-400" />
                  Show "Vocal for Local · Made in India" badge on my store
                </label>
                {form.vocal_for_local && (
                  <input value={form.vocal_story} onChange={(e) => setForm({ ...form, vocal_story: e.target.value })}
                         placeholder="Your story (e.g. Handmade in Gujarat · Supporting 5 local artisans)"
                         className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
                )}
              </div>

              {/* 👑 VIP Club */}
              <div className="rounded-xl bg-purple-900/30 border border-purple-400/30 p-3 space-y-2">
                <div className="text-[10px] font-black text-purple-300 uppercase">👑 VIP Club &amp; Instant Cashback Banner</div>
                <textarea value={form.vip_club_text} onChange={(e) => setForm({ ...form, vip_club_text: e.target.value })}
                          placeholder="Offer text (e.g. WhatsApp pe JOIN likhkar bhejo, pao 10% cashback har order pe!)"
                          rows={2} maxLength={300} data-testid="mkt-display-vip-text"
                          className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs resize-none" />
              </div>

              {/* 🎬 Promo Videos */}
              <div className="rounded-xl bg-rose-900/30 border border-rose-400/30 p-3 space-y-2">
                <div className="text-[10px] font-black text-rose-300 uppercase">🎬 Promo Videos (YouTube / Reels / MP4)</div>
                <div className="text-[9px] opacity-60">Har line mein ek URL daalo (YouTube, Instagram Reel, ya direct .mp4 link)</div>
                <textarea value={(form.promo_videos || []).join('\n')}
                          onChange={(e) => setForm({ ...form, promo_videos: e.target.value.split('\n').map((v) => v.trim()).filter(Boolean) })}
                          placeholder={'https://youtube.com/watch?v=...\nhttps://youtu.be/...'}
                          rows={3} data-testid="mkt-display-promo-videos"
                          className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs font-mono resize-none" />
                {(form.promo_videos || []).length > 0 && (
                  <div className="text-[9px] text-emerald-300">{form.promo_videos.length} video URL(s) saved</div>
                )}
                <div className="flex flex-wrap gap-2">
                  <label className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg ${uploading === 'promo_video_camera' ? 'bg-slate-700 opacity-60' : 'bg-rose-700 hover:bg-rose-600'} text-white text-[10px] font-black cursor-pointer transition`}>
                    {uploading === 'promo_video_camera' ? 'Uploading…' : 'Record from Camera'}
                    <input type="file" accept="video/*" capture="environment" className="hidden" disabled={!!uploading} data-testid="mkt-display-promo-video-camera"
                           onChange={(ev) => uploadPromoVideo(ev, 'promo_video_camera')} />
                  </label>
                  <label className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg ${uploading === 'promo_video_gallery' ? 'bg-slate-700 opacity-60' : 'bg-rose-700 hover:bg-rose-600'} text-white text-[10px] font-black cursor-pointer transition`}>
                    {uploading === 'promo_video_gallery' ? 'Uploading…' : 'Upload from Gallery (max 100MB)'}
                    <input type="file" accept="video/*" className="hidden" disabled={!!uploading} data-testid="mkt-display-promo-video-gallery"
                           onChange={(ev) => uploadPromoVideo(ev, 'promo_video_gallery')} />
                  </label>
                </div>
                {(form.promo_videos || []).map((v, i) => (
                  <div key={i} className="flex items-center gap-2 text-[9px] text-slate-300">
                    <span className="truncate flex-1 font-mono">{v}</span>
                    <button type="button" onClick={() => setForm({ ...form, promo_videos: form.promo_videos.filter((_, j) => j !== i) })} className="px-1.5 py-0.5 rounded bg-slate-700 hover:bg-red-700 font-black">x</button>
                  </div>
                ))}
              </div>

              {/* 🖼️ Promo / Offer Images */}
              <div className="rounded-xl bg-amber-900/30 border border-amber-400/30 p-3 space-y-2">
                <div className="text-[10px] font-black text-amber-300 uppercase">🖼️ Offer / Promo Images</div>
                <div className="text-[9px] opacity-60">Offer banners, discount images — URL paste karo ya upload karo</div>
                <textarea value={(form.promo_images || []).join('\n')}
                          onChange={(e) => setForm({ ...form, promo_images: e.target.value.split('\n').map((v) => v.trim()).filter(Boolean) })}
                          placeholder={'https://img.example.com/offer1.jpg'}
                          rows={2} data-testid="mkt-display-promo-images"
                          className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs font-mono resize-none" />
                <label className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-lg ${uploading === 'promo_img' ? 'bg-slate-700 opacity-60' : 'bg-amber-600 hover:bg-amber-500'} text-white text-[10px] font-black cursor-pointer transition`}>
                  {uploading === 'promo_img' ? '⏳ Uploading…' : '📤 Upload Offer Image'}
                  <input type="file" accept="image/*" className="hidden" disabled={!!uploading}
                         onChange={async (ev) => {
                           const f = ev.target.files?.[0]; if (!f) return;
                           setUploading('promo_img');
                           try {
                             const fd = new FormData(); fd.append('file', f);
                             const r = await api.post('/webshop/upload/image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
                             const url = (r.data.url || '').startsWith('http') ? r.data.url : `${process.env.REACT_APP_BACKEND_URL || ''}${r.data.url}`;
                             setForm((p) => ({ ...p, promo_images: [...(p.promo_images || []), url] }));
                             toast.success('✅ Offer image uploaded!');
                           } catch { toast.error('Upload failed'); }
                           finally { setUploading(null); }
                         }} />
                </label>
                {(form.promo_images || []).length > 0 && (
                  <div className="flex gap-2 overflow-x-auto py-1">
                    {form.promo_images.map((url, i) => (
                      <div key={i} className="relative shrink-0">
                        <img src={url} alt="" className="h-16 w-auto rounded border border-slate-600 object-contain bg-slate-800" />
                        <button onClick={() => setForm((p) => ({ ...p, promo_images: p.promo_images.filter((_, j) => j !== i) }))}
                                className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-red-500 text-white text-[9px] flex items-center justify-center font-black">✕</button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* 📲 Social Share */}
              {cfg.store?.slug && (
                <div className="rounded-xl bg-emerald-900/30 border border-emerald-400/30 p-3 space-y-2">
                  <div className="text-[10px] font-black text-emerald-300 uppercase">📲 Share Apna Display Screen</div>
                  <div className="flex flex-wrap gap-2">
                    {[['wa', '📱 WhatsApp', 'bg-green-600'], ['fb', '📘 Facebook', 'bg-blue-600'], ['ig', '📸 Instagram', 'bg-gradient-to-r from-purple-500 to-pink-500']].map(([kind, label, cls]) => (
                      <button key={kind} onClick={() => {
                        const url = `${window.location.origin}/marketplace/store/${cfg.store.slug}`;
                        const text = `${cfg.store.shop_name || ''} — ${cfg.store.tagline || ''}\n🛍️ ANANT MARKET pe dekhein: ${url}`;
                        if (kind === 'wa') window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(text)}`, '_blank');
                        else if (kind === 'fb') window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
                        else { navigator.clipboard.writeText(url); toast.success('Link copied — Instagram Stories pe paste karo!'); }
                      }} className={`px-3 py-1.5 rounded-xl ${cls} text-white text-[10px] font-black`}>{label}</button>
                    ))}
                  </div>
                </div>
              )}

              <button onClick={save} disabled={busy} data-testid="mkt-store-display-save"
                      className="w-full py-2.5 rounded-xl bg-gradient-to-r from-rose-500 to-pink-600 text-white text-xs font-black disabled:opacity-60 hover:from-rose-400 hover:to-pink-500 transition">
                {busy ? '⏳ Saving…' : '💾 Save Display Screen Settings'}
              </button>
            </div>
          </div>
        );
      })()}

            {storePanel === 'settings' && (<>
      {cfg.store?.slug && <StoreCustomizeBlock store={cfg.store} onReload={onReload} />}
      {cfg.store?.slug && <VendorAppointmentsBlock />}
      <BuyerAppointmentsBlock />
      <div className="p-3 rounded-2xl bg-slate-900 border border-purple-400/40 space-y-2" data-testid="mkt-store-form">
        <div className="text-xs font-black text-purple-300 flex items-center gap-1"><Store size={12} /> My Store</div>
        {cfg.store?.slug && <div className="text-[10px] opacity-70">Public URL: <a href={`/marketplace/store/${cfg.store.slug}`} className="text-emerald-300 underline">/marketplace/store/{cfg.store.slug}</a></div>}
        <input value={form.shop_name} onChange={(e) => setForm({ ...form, shop_name: e.target.value })} placeholder="Shop name *" data-testid="mkt-shop-name" className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm" />
        <input value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} placeholder="Tagline" className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm" />
        <div className="grid grid-cols-2 gap-2">
          <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="City" className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm" />
          <StoreCategoryPicker value={form.category} onChange={(v) => setForm((p) => ({ ...p, category: v }))} />
          <input value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} placeholder="WhatsApp (10 digits)" data-testid="mkt-shop-wa" className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm" />
          <input value={form.delivery_whatsapp} onChange={(e) => setForm({ ...form, delivery_whatsapp: e.target.value })} placeholder="Delivery WA (optional)" data-testid="mkt-shop-delivery-wa" className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm" />
          <div className="col-span-2 flex items-stretch gap-1.5">
            <input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Address" data-testid="mkt-shop-address" className="flex-1 px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm" />
            <button type="button" onClick={detectLocation} data-testid="mkt-shop-address-gps"
                    title={form.lat && form.lng ? `📍 Verified · ${form.lat}, ${form.lng} — tap to re-detect` : 'Tap to auto-fill GPS from your current location'}
                    className={`shrink-0 px-2.5 rounded-lg text-xs font-black flex items-center gap-1 transition ${
                      form.lat && form.lng
                        ? 'bg-emerald-500 text-slate-900 hover:bg-emerald-400'
                        : 'bg-sky-600 text-white hover:bg-sky-500 animate-pulse'
                    }`}>
              {form.lat && form.lng ? '✅ GPS' : '📍 GPS'}
            </button>
          </div>
        </div>

        {/* Vendor Dashboard: Stock Toggle + Radius + GPS + Commission */}
        <div className="rounded-xl bg-slate-800/40 border border-slate-700 p-2 space-y-2" data-testid="mkt-vendor-dash">
          <div className="flex items-center gap-3">
            <button onClick={toggleStock} disabled={stockBusy} data-testid="mkt-stock-toggle"
                    className={`px-4 py-2 rounded-full font-black text-xs flex items-center gap-2 transition ${form.is_in_stock ? 'bg-emerald-500 text-slate-900' : 'bg-slate-700 text-slate-300'}`}>
              <span className={`w-2 h-2 rounded-full ${form.is_in_stock ? 'bg-white animate-pulse' : 'bg-slate-500'}`} />
              {stockBusy ? '⏳' : (form.is_in_stock ? '🟢 Master LIVE' : '⏸️ Master OUT OF STOCK')}
            </button>
            <div className="text-[10px] opacity-70">One tap → all listings sync</div>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <label className="text-[10px] font-black text-purple-300 uppercase flex flex-col gap-1">Service Radius (km)
              <input type="number" min={0} max={100} step={0.5} value={form.service_radius_km}
                     onChange={(e) => setForm({ ...form, service_radius_km: Number(e.target.value) })}
                     data-testid="mkt-shop-radius" className="px-2 py-1.5 rounded bg-slate-900 border border-slate-700 text-xs" />
            </label>
            <label className="text-[10px] font-black text-purple-300 uppercase flex flex-col gap-1">Commission %
              <input type="number" min={0} max={50} step={0.5} value={form.commission_percent}
                     onChange={(e) => setForm({ ...form, commission_percent: Number(e.target.value) })}
                     data-testid="mkt-shop-commission" className="px-2 py-1.5 rounded bg-slate-900 border border-slate-700 text-xs" />
            </label>
          </div>
          {/* New-order sound chime toggle */}
          <button onClick={() => setForm((p) => ({ ...p, chime_enabled: !p.chime_enabled }))}
                  data-testid="mkt-chime-toggle"
                  className={`w-full px-3 py-2 rounded-lg text-xs font-black flex items-center justify-between border ${
                    form.chime_enabled ? 'bg-emerald-500/20 border-emerald-400 text-emerald-200' : 'bg-slate-800 border-slate-700 text-slate-400'
                  }`}>
            <span className="flex items-center gap-2">{form.chime_enabled ? '🔔' : '🔕'} New-order chime</span>
            <span className="text-[10px] opacity-80">{form.chime_enabled ? 'ON — audio + popup' : 'OFF — popup only'}</span>
          </button>
          <div className="flex items-center gap-2">
            <button onClick={detectLocation} data-testid="mkt-shop-detect-gps"
                    className="px-3 py-1.5 rounded bg-sky-600 hover:bg-sky-700 text-white text-xs font-black">📍 Detect My Location</button>
            <div className="text-[10px] opacity-80">
              {form.lat !== null && form.lng !== null
                ? <>Lat {form.lat}, Lng {form.lng}{gpsAccuracy != null && (
                    <span className={gpsAccuracy > 100 ? 'text-amber-300 font-black ml-1' : 'text-emerald-300 ml-1'}>
                      · ±{Math.round(gpsAccuracy)}m {gpsAccuracy > 100 ? '⚠️ low accuracy' : '✓'}
                    </span>)}</>
                : 'Location not set — GPS filter disabled'}
            </div>
          </div>
          {/* Wave 47 · type-to-correct lat/lng + map preview + optional Google Maps link */}
          <div className="grid grid-cols-2 gap-2">
            <label className="text-[10px] font-black text-purple-300 uppercase flex flex-col gap-1">
              Latitude
              <input type="number" step="0.000001" value={form.lat ?? ''}
                     onChange={(e) => setForm({ ...form, lat: e.target.value === '' ? null : Number(e.target.value) })}
                     placeholder="e.g. 28.6139" data-testid="mkt-shop-lat"
                     className="px-2 py-1.5 rounded bg-slate-900 border border-slate-700 text-xs" />
            </label>
            <label className="text-[10px] font-black text-purple-300 uppercase flex flex-col gap-1">
              Longitude
              <input type="number" step="0.000001" value={form.lng ?? ''}
                     onChange={(e) => setForm({ ...form, lng: e.target.value === '' ? null : Number(e.target.value) })}
                     placeholder="e.g. 77.2090" data-testid="mkt-shop-lng"
                     className="px-2 py-1.5 rounded bg-slate-900 border border-slate-700 text-xs" />
            </label>
          </div>
          {form.lat !== null && form.lng !== null && (
            <div className="rounded-lg overflow-hidden border border-slate-700" data-testid="mkt-shop-map-preview">
              <iframe title="Store map preview" width="100%" height="150" loading="lazy" referrerPolicy="no-referrer-when-downgrade"
                      src={`https://www.google.com/maps?q=${form.lat},${form.lng}&hl=en&z=16&output=embed`}
                      style={{ border: 0 }} />
              <div className="text-[9px] opacity-60 px-1 py-0.5 bg-slate-900/70 text-center">
                📍 Pin preview · type lat/lng above to correct, or open in Maps to fine-tune and paste the link below
              </div>
            </div>
          )}
          <label className="text-[10px] font-black text-purple-300 uppercase flex flex-col gap-1">
            Google Maps Location Link (optional — overrides pin on public page)
            <input type="url" value={form.maps_link || ''}
                   onChange={(e) => onMapsLinkChange(e.target.value)}
                   placeholder="https://maps.app.goo.gl/... or https://www.google.com/maps/place/..."
                   data-testid="mkt-shop-maps-link"
                   className="px-2 py-1.5 rounded bg-slate-900 border border-slate-700 text-xs" />
          </label>
        </div>
        {/* Wave 48 · Storefront details block (shown on public page) */}
        <div className="rounded-xl bg-slate-800/40 border border-purple-400/30 p-2 space-y-2" data-testid="mkt-store-details">
          <div className="text-[11px] font-black text-purple-300 flex items-center gap-1">📝 Storefront details (shown on your public page)</div>
          <label className="text-[10px] font-black text-purple-300 uppercase flex flex-col gap-1">
            About / Description
            <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                      placeholder="Owner details, USPs, brand story…" rows={3}
                      data-testid="mkt-shop-description"
                      className="px-2 py-1.5 rounded bg-slate-900 border border-slate-700 text-xs" />
          </label>
          <div className="grid grid-cols-2 gap-2">
            <label className="text-[10px] font-black text-purple-300 uppercase flex flex-col gap-1">
              Timings
              <input value={form.hours_text} onChange={(e) => setForm({ ...form, hours_text: e.target.value })}
                     placeholder="10:00 AM – 9:00 PM" data-testid="mkt-shop-hours"
                     className="px-2 py-1.5 rounded bg-slate-900 border border-slate-700 text-xs" />
            </label>
            <label className="text-[10px] font-black text-purple-300 uppercase flex flex-col gap-1">
              Close Day(s)
              <input value={form.close_days} onChange={(e) => setForm({ ...form, close_days: e.target.value })}
                     placeholder="Sunday" data-testid="mkt-shop-close-days"
                     className="px-2 py-1.5 rounded bg-slate-900 border border-slate-700 text-xs" />
            </label>
          </div>
          <label className="text-[10px] font-black text-purple-300 uppercase flex flex-col gap-1">
            Store Terms & Conditions
            <textarea value={form.terms_text} onChange={(e) => setForm({ ...form, terms_text: e.target.value })}
                      placeholder="Returns policy, payment, exchange, warranty…" rows={2}
                      data-testid="mkt-shop-terms"
                      className="px-2 py-1.5 rounded bg-slate-900 border border-slate-700 text-xs" />
          </label>
          <label className="text-[10px] font-black text-purple-300 uppercase flex flex-col gap-1">
            Formal Disclaimer (optional)
            <textarea value={form.disclaimer_text} onChange={(e) => setForm({ ...form, disclaimer_text: e.target.value })}
                      placeholder="Legal disclaimer that appears at the bottom of your public page" rows={2}
                      data-testid="mkt-shop-disclaimer"
                      className="px-2 py-1.5 rounded bg-slate-900 border border-slate-700 text-xs" />
          </label>
        </div>
        <div className="grid grid-cols-2 gap-2">
          <button onClick={() => logoRef.current?.click()} disabled={!!uploading} className="flex items-center justify-center gap-1 px-2 py-2 rounded-lg bg-slate-700 text-xs font-black disabled:opacity-50" data-testid="mkt-logo-upload">
            <ImageIcon size={13} /> {uploading === 'logo_url' ? 'Uploading…' : (form.logo_url ? 'Change Logo' : 'Upload Logo')}
          </button>
          <button onClick={() => bannerRef.current?.click()} disabled={!!uploading} className="flex items-center justify-center gap-1 px-2 py-2 rounded-lg bg-slate-700 text-xs font-black disabled:opacity-50" data-testid="mkt-banner-upload">
            <ImageIcon size={13} /> {uploading === 'banner_url' ? 'Uploading…' : (form.banner_url ? 'Change Banner' : 'Upload Banner')}
          </button>
          <input ref={logoRef} type="file" accept="image/*" onChange={(e) => uploadTo(e, 'logo_url')} className="hidden" />
          <input ref={bannerRef} type="file" accept="image/*" onChange={(e) => uploadTo(e, 'banner_url')} className="hidden" />
        </div>
        {(form.logo_url || form.banner_url) && (
          <div className="grid grid-cols-2 gap-2">
            {form.logo_url && <img src={abs(form.logo_url)} alt="logo" className="w-full h-20 object-cover rounded" />}
            {form.banner_url && <img src={abs(form.banner_url)} alt="banner" className="w-full h-20 object-cover rounded" />}
          </div>
        )}

        {/* Wave 51 · Social Media Links */}
        <div className="rounded-xl bg-pink-500/10 border border-pink-400/40 p-2 space-y-2" data-testid="mkt-shop-social">
          <div className="text-[11px] font-black text-pink-300 uppercase">🌐 Social Media & Delivery Platform Links</div>
          <div className="grid grid-cols-2 gap-2">
            {[
              { k: 'zomato', label: '🍽️ Zomato', ph: 'https://www.zomato.com/your-shop' },
              { k: 'swiggy', label: '🥘 Swiggy', ph: 'https://www.swiggy.com/your-shop' },
              { k: 'blinkit', label: '⚡ Blinkit', ph: 'https://blinkit.com/your-shop' },
              { k: 'zepto', label: '🚀 Zepto', ph: 'https://www.zeptonow.com/your-shop' },
              { k: 'instagram', label: '📸 Instagram', ph: 'https://instagram.com/your-handle' },
              { k: 'facebook', label: '📘 Facebook', ph: 'https://facebook.com/your-page' },
              { k: 'youtube', label: '▶️ YouTube', ph: 'https://youtube.com/@your-channel' },
              { k: 'website', label: '🌍 Website', ph: 'https://your-website.com' },
              { k: 'google_maps', label: '📍 Google Maps', ph: 'https://maps.google.com/?q=...' },
              { k: 'justdial', label: '📒 JustDial', ph: 'https://www.justdial.com/your-shop' },
            ].map((f) => (
              <label key={f.k} className="text-[10px] font-black text-pink-200 flex flex-col gap-0.5">
                {f.label}
                <input value={(form.social_links || {})[f.k] || ''}
                       onChange={(e) => setForm({ ...form, social_links: { ...(form.social_links || {}), [f.k]: e.target.value } })}
                       placeholder={f.ph}
                       data-testid={`mkt-shop-social-${f.k}`}
                       className="px-2 py-1.5 rounded bg-slate-900 border border-slate-700 text-[11px] normal-case" />
              </label>
            ))}
          </div>
        </div>

        {/* Wave 52 · Telegram Order Alerts */}
        <div className="rounded-xl bg-blue-500/10 border border-blue-400/40 p-3 space-y-2" data-testid="mkt-shop-telegram">
          <div className="text-[11px] font-black text-blue-300 uppercase">📱 Telegram Order Alerts (FREE)</div>
          <div className="text-[10px] opacity-70 space-y-1">
            <div>Naye order aane pe seedha Telegram pe notification aayega — bilkul FREE!</div>
            <div>Steps: 1) <a href="https://t.me/Anant_admin_bot" target="_blank" rel="noreferrer" className="text-blue-300 underline">@Anant_admin_bot</a> ko message karo · 2) /start bhejo · 3) Apna Chat ID neeche daalo</div>
          </div>
          <div className="flex gap-2 items-center">
            <input value={form.telegram_chat_id || ''} onChange={(e) => setForm({ ...form, telegram_chat_id: e.target.value })}
                   placeholder="Telegram Chat ID (e.g. 123456789)" type="text" inputMode="numeric"
                   data-testid="mkt-shop-telegram-id"
                   className="flex-1 px-2 py-1.5 rounded bg-slate-900 border border-blue-400/30 text-sm" />
            <a href="https://t.me/Anant_admin_bot" target="_blank" rel="noreferrer"
               className="px-2 py-1.5 rounded-lg bg-blue-600/40 border border-blue-400/50 text-blue-200 text-[10px] font-black whitespace-nowrap">
              Get ID →
            </a>
          </div>
          {form.telegram_chat_id && (
            <div className="text-[10px] text-emerald-300 font-black">
              ✓ Alerts jaayenge Chat ID: {form.telegram_chat_id} pe
            </div>
          )}
        </div>

        {/* Wave 51 · Custom Buttons */}
        <div className="rounded-xl bg-cyan-500/10 border border-cyan-400/40 p-2 space-y-2" data-testid="mkt-shop-custom-btns">
          <div className="flex items-center justify-between">
            <div className="text-[11px] font-black text-cyan-300 uppercase">🔘 Custom Buttons (max 10)</div>
            <button type="button"
                    disabled={(form.custom_buttons || []).length >= 10}
                    onClick={() => setForm({ ...form, custom_buttons: [...(form.custom_buttons || []), { label: '', url: '', icon: '' }] })}
                    data-testid="mkt-shop-custom-add"
                    className="px-2 py-0.5 rounded bg-emerald-500 text-slate-900 text-[10px] font-black disabled:opacity-40">+ Add</button>
          </div>
          {(form.custom_buttons || []).map((b, i) => (
            <div key={i} className="grid grid-cols-9 gap-1 items-center" data-testid={`mkt-shop-custom-row-${i}`}>
              <input value={b.icon || ''} onChange={(e) => setForm({ ...form, custom_buttons: form.custom_buttons.map((x, j) => j === i ? { ...x, icon: e.target.value } : x) })}
                     placeholder="🏷️" className="col-span-1 px-1 py-1 rounded bg-slate-900 border border-slate-700 text-[11px] text-center" />
              <input value={b.label} onChange={(e) => setForm({ ...form, custom_buttons: form.custom_buttons.map((x, j) => j === i ? { ...x, label: e.target.value } : x) })}
                     placeholder="Button label" className="col-span-3 px-1.5 py-1 rounded bg-slate-900 border border-slate-700 text-[11px]" />
              <input value={b.url} onChange={(e) => setForm({ ...form, custom_buttons: form.custom_buttons.map((x, j) => j === i ? { ...x, url: e.target.value } : x) })}
                     placeholder="https://..." className="col-span-4 px-1.5 py-1 rounded bg-slate-900 border border-slate-700 text-[11px] normal-case" />
              <button type="button" onClick={() => setForm({ ...form, custom_buttons: form.custom_buttons.filter((_, j) => j !== i) })}
                      data-testid={`mkt-shop-custom-del-${i}`}
                      className="col-span-1 px-1.5 py-1 rounded bg-rose-600 text-white text-[10px] font-black">✕</button>
            </div>
          ))}
          {(form.custom_buttons || []).length === 0 && (
            <div className="text-[10px] opacity-60">No custom buttons yet. Add anything: appointment link, menu PDF, catalog, review page etc.</div>
          )}
        </div>

        <button onClick={save} disabled={busy} data-testid="mkt-store-save"
                className="w-full py-2 rounded-xl bg-purple-500 text-white text-sm font-black disabled:opacity-50">
          {busy ? 'Saving…' : '💾 Save Store'}
        </button>
      </div>

      {cfg.store?.slug && <ShopShareBlock store={cfg.store} />}
      </>)}
    </div>
  );
}

// ============ SHOP SHARE / QR / CATALOGUE ============
function ShopShareBlock({ store }) {
  const { user } = useAuth() || {};
  const cvsRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [pdfBusy, setPdfBusy] = useState(false);
  const [platformSettings, setPlatformSettings] = useState({});
  // Public URL with referral + location auto-appended for organic tracking
  const referralParams = new URLSearchParams();
  if (user?.username) referralParams.set('ref', user.username);
  if (store.city) referralParams.set('loc', store.city);
  const paramStr = referralParams.toString();
  const publicUrl = `${window.location.origin}/marketplace/store/${store.slug}${paramStr ? `?${paramStr}` : ''}`;
  // OG-preview URL — this is what social scrapers hit to grab shop banner/logo
  const shareOgUrl = `${process.env.REACT_APP_BACKEND_URL || ''}/api/share/marketplace/store/${store.slug}`;
  const qrUrl = `https://api.qrserver.com/v1/create-qr-code/?size=600x600&margin=5&ecc=H&data=${encodeURIComponent(shareOgUrl)}`;

  useEffect(() => {
    api.get('/platform/settings').then((r) => setPlatformSettings(r.data || {})).catch(() => {});
  }, []);

  const shareToChannel = () => {
    const tagline = platformSettings.default_share_tagline || `Visit my store on Anant Marketplace! 🛍️`;
    const msg = `${tagline}\n\n🏪 ${store.shop_name}\n📍 ${store.city || ''}\n👉 ${publicUrl}`;
    if (platformSettings.whatsapp_channel_url) {
      // Copy message + open channel — user pastes into channel
      navigator.clipboard.writeText(msg);
      toast.success('📋 Store link copied · Opening WhatsApp Channel');
      window.open(platformSettings.whatsapp_channel_url, '_blank');
    } else {
      toast.error('Admin ne WhatsApp Channel set nahi kiya');
    }
  };
  const shareToGroup = () => {
    const tagline = platformSettings.default_share_tagline || `Visit my store on Anant Marketplace! 🛍️`;
    const msg = `${tagline}\n\n🏪 ${store.shop_name}\n📍 ${store.city || ''}\n👉 ${publicUrl}`;
    if (platformSettings.whatsapp_group_url) {
      navigator.clipboard.writeText(msg);
      toast.success('📋 Store link copied · Opening WhatsApp Group');
      window.open(platformSettings.whatsapp_group_url, '_blank');
    } else {
      toast.error('Admin ne WhatsApp Group set nahi kiya');
    }
  };

  const renderCatalogue = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/marketplace/store/public/${store.slug}`);
      const products = (r.data.listings || []).slice(0, 4);
      const c = cvsRef.current; if (!c) return null;
      c.width = 1080; c.height = 1350;
      const ctx = c.getContext('2d');
      ctx.fillStyle = '#0f172a'; ctx.fillRect(0, 0, c.width, c.height);
      // Banner strip
      if (store.banner_url) {
        const bimg = new Image(); bimg.crossOrigin = 'anonymous';
        await new Promise((res) => { bimg.onload = () => { ctx.drawImage(bimg, 0, 0, c.width, 300); res(); }; bimg.onerror = res; bimg.src = abs(store.banner_url); });
      } else {
        const g = ctx.createLinearGradient(0, 0, c.width, 300); g.addColorStop(0, '#4338CA'); g.addColorStop(1, '#DB2777');
        ctx.fillStyle = g; ctx.fillRect(0, 0, c.width, 300);
      }
      ctx.fillStyle = 'rgba(0,0,0,0.55)'; ctx.fillRect(0, 200, c.width, 100);
      ctx.fillStyle = '#fff'; ctx.font = 'bold 60px sans-serif'; ctx.textAlign = 'center';
      ctx.fillText((store.shop_name || 'Marketplace Shop').slice(0, 28), c.width / 2, 268);
      // Product grid 2x2 (450x450 each)
      const positions = [[60, 340], [570, 340], [60, 810], [570, 810]];
      for (let i = 0; i < 4; i++) {
        const [x, y] = positions[i];
        ctx.fillStyle = '#1e293b'; ctx.fillRect(x, y, 450, 450);
        const p = products[i];
        if (p?.images?.[0]) {
          try {
            const img = new Image(); img.crossOrigin = 'anonymous';
            await new Promise((res) => { img.onload = () => { ctx.drawImage(img, x, y, 450, 380); res(); }; img.onerror = res; img.src = abs(p.images[0]); });
          } catch {}
        } else if (p) {
          ctx.fillStyle = '#334155'; ctx.font = '120px sans-serif'; ctx.textAlign = 'center';
          ctx.fillText('📦', x + 225, y + 240);
        }
        if (p) {
          ctx.fillStyle = '#0f172a'; ctx.fillRect(x, y + 380, 450, 70);
          ctx.fillStyle = '#facc15'; ctx.font = 'bold 32px sans-serif'; ctx.textAlign = 'left';
          ctx.fillText(`${p.price_at} AT`, x + 15, y + 425);
          ctx.fillStyle = '#fff'; ctx.font = '20px sans-serif';
          ctx.fillText((p.title || '').slice(0, 22), x + 15, y + 445);
        }
      }
      // QR + URL footer
      const qimg = new Image(); qimg.crossOrigin = 'anonymous';
      await new Promise((res) => { qimg.onload = () => { ctx.drawImage(qimg, 40, 1280 - 220, 220, 220); res(); }; qimg.onerror = res; qimg.src = qrUrl; });
      ctx.fillStyle = '#fff'; ctx.font = 'bold 42px sans-serif'; ctx.textAlign = 'left';
      ctx.fillText('📷 Scan · Order via WhatsApp', 290, 1180);
      ctx.font = '26px monospace'; ctx.fillStyle = '#a78bfa';
      ctx.fillText(publicUrl.slice(0, 40), 290, 1230);
      ctx.font = '22px sans-serif'; ctx.fillStyle = 'rgba(255,255,255,0.5)';
      ctx.fillText('Powered by Anant Smart Marketplace', 290, 1270);
      return await new Promise((res) => c.toBlob(res, 'image/jpeg', 0.9));
    } finally { setBusy(false); }
  };

  const shareCatalogue = async (kind) => {
    const blob = await renderCatalogue();
    if (!blob) return;
    const file = new File([blob], `${store.slug}-catalogue.jpg`, { type: 'image/jpeg' });
    const text = `${store.shop_name} · ${store.tagline || ''}\n${shareOgUrl}`;
    // Try native share first (attaches actual image to WA/Instagram/etc)
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      try { await navigator.share({ title: store.shop_name, text, files: [file] }); toast.success('✅ Shared with image'); return; }
      catch (e) { if (e && e.name === 'AbortError') return; }
    }
    // Fallback: download the image + open target
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = file.name;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    if (kind === 'wa') window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(text)}`, '_blank');
    else if (kind === 'fb') window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareOgUrl)}`, '_blank');
    else if (kind === 'instagram') {
      // No web-share for IG; download prompts user to open Instagram and attach the JPG
      window.open('https://www.instagram.com/', '_blank');
      toast.success('🖼 Catalogue downloaded — attach in Instagram post/reel');
    }
  };

  const downloadQR = () => {
    const a = document.createElement('a'); a.href = qrUrl; a.download = `${store.slug}-qr.png`; a.click();
  };
  const copyUrl = () => { navigator.clipboard.writeText(shareOgUrl); toast.success('✅ Share link copied (with image preview)'); };

  // Multi-page PDF of all listings — jsPDF + autoTable, zero server cost
  const exportPdf = async (mode) => {
    setPdfBusy(true);
    try {
      const [{ default: jsPDF }, autoTableMod] = await Promise.all([
        import('jspdf'),
        import('jspdf-autotable'),
      ]);
      const autoTable = autoTableMod.default || autoTableMod.autoTable || autoTableMod;
      const r = await api.get(`/marketplace/store/public/${store.slug}`);
      const listings = r.data.listings || [];
      if (!listings.length) { toast.error('No active listings to export'); return; }

      const doc = new jsPDF({ unit: 'pt', format: 'a4' });
      const pageW = doc.internal.pageSize.getWidth();

      // Header block with shop logo + WA number
      const drawHeader = async () => {
        doc.setFillColor(30, 27, 75); doc.rect(0, 0, pageW, 80, 'F');
        doc.setTextColor(255, 255, 255);
        // Optional logo
        if (store.logo_url) {
          try {
            const src = store.logo_url.startsWith('http') ? store.logo_url : `${process.env.REACT_APP_BACKEND_URL || ''}${store.logo_url}`;
            const b = await fetch(src, { credentials: 'omit' }).then((x) => x.blob());
            const dataUrl = await new Promise((res) => { const fr = new FileReader(); fr.onload = () => res(fr.result); fr.readAsDataURL(b); });
            doc.addImage(dataUrl, 'JPEG', 20, 15, 50, 50);
          } catch {}
        }
        doc.setFontSize(20); doc.setFont('helvetica', 'bold');
        doc.text(store.shop_name || 'Shop Catalogue', 85, 35);
        doc.setFontSize(11); doc.setFont('helvetica', 'normal');
        const sub = [store.tagline, store.city && `📍 ${store.city}`, store.whatsapp && `📞 ${store.whatsapp}`].filter(Boolean).join(' · ');
        if (sub) doc.text(sub, 85, 55);
        doc.setFontSize(9); doc.setTextColor(180, 180, 180);
        doc.text(`Exported: ${new Date().toLocaleDateString('en-GB')} · ${shareOgUrl}`, 85, 70);
      };
      await drawHeader();

      // Prefetch product images as base64
      const imgMap = {};
      await Promise.all(listings.slice(0, 60).map(async (l) => {
        if (!l.images?.[0]) return;
        try {
          const src = l.images[0].startsWith('http') ? l.images[0] : `${process.env.REACT_APP_BACKEND_URL || ''}${l.images[0]}`;
          const b = await fetch(src, { credentials: 'omit' }).then((x) => x.blob());
          imgMap[l.id] = await new Promise((res) => { const fr = new FileReader(); fr.onload = () => res(fr.result); fr.readAsDataURL(b); });
        } catch {}
      }));

      // Table with autoTable — image column via didDrawCell
      autoTable(doc, {
        startY: 100,
        head: [['#', 'Photo', 'Product / Service', 'Price', 'Order via WhatsApp']],
        body: listings.map((l, i) => [
          String(i + 1),
          '',
          `${l.title}${l.category ? `\n${l.category}` : ''}${l.description ? `\n${(l.description || '').slice(0, 80)}` : ''}`,
          `${l.price_at} AT`,
          `wa.me/${(store.whatsapp || '').replace(/[^0-9]/g, '')}\nRef: ${l.id.slice(0, 8)}`,
        ]),
        theme: 'grid',
        styles: { fontSize: 9, cellPadding: 6, valign: 'middle' },
        headStyles: { fillColor: [124, 58, 237], textColor: 255, fontStyle: 'bold' },
        columnStyles: {
          0: { cellWidth: 25, halign: 'center' },
          1: { cellWidth: 65 },
          3: { cellWidth: 60, halign: 'right', textColor: [180, 83, 9], fontStyle: 'bold' },
          4: { cellWidth: 130, fontSize: 8 },
        },
        didDrawCell: (data) => {
          if (data.section === 'body' && data.column.index === 1) {
            const l = listings[data.row.index];
            const img = imgMap[l?.id];
            if (img) {
              try { doc.addImage(img, 'JPEG', data.cell.x + 3, data.cell.y + 3, 55, data.cell.height - 6); } catch {}
            } else {
              doc.setFontSize(18); doc.text('📦', data.cell.x + 22, data.cell.y + data.cell.height / 2 + 6);
            }
          }
        },
        margin: { top: 100, bottom: 40 },
        didDrawPage: () => {
          doc.setFontSize(8); doc.setTextColor(120, 120, 120);
          doc.text(`Page ${doc.internal.getNumberOfPages()} · Powered by Anant Smart Marketplace`,
                   pageW / 2, doc.internal.pageSize.getHeight() - 15, { align: 'center' });
        },
      });

      const pdfBlob = doc.output('blob');
      const fileName = `${store.slug}-catalog.pdf`;

      if (mode === 'share' && navigator.canShare) {
        const file = new File([pdfBlob], fileName, { type: 'application/pdf' });
        if (navigator.canShare({ files: [file] })) {
          try { await navigator.share({ title: store.shop_name, text: `${store.shop_name} · Catalog`, files: [file] });
                toast.success('✅ Catalog PDF shared'); return; }
          catch (e) { if (e && e.name === 'AbortError') return; }
        }
      }
      // Fallback: download + open WA
      const link = document.createElement('a'); link.href = URL.createObjectURL(pdfBlob); link.download = fileName;
      document.body.appendChild(link); link.click(); document.body.removeChild(link);
      if (mode === 'share') {
        window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(`${store.shop_name} · Catalog PDF · ${shareOgUrl}`)}`, '_blank');
        toast.success('📎 PDF downloaded — attach in WhatsApp chat');
      } else {
        toast.success('✅ Catalog PDF downloaded');
      }
    } catch (e) {
      toast.error(e?.message || 'PDF export failed');
    } finally { setPdfBusy(false); }
  };

  return (
    <div className="p-3 rounded-2xl bg-emerald-500/10 border-2 border-emerald-400/40 space-y-3" data-testid="mkt-share-block">
      <div className="text-xs font-black text-emerald-300 flex items-center gap-1">🎯 Share your shop</div>
      <div className="grid grid-cols-2 gap-3">
        <div className="text-center bg-white rounded-xl p-2" data-testid="mkt-shop-qr">
          <img src={qrUrl} alt="QR" className="w-full aspect-square" />
          <button onClick={downloadQR} className="mt-1 text-[10px] text-purple-700 font-black">⬇️ QR</button>
        </div>
        <div className="text-[10px] space-y-2">
          <div className="opacity-70 break-all">{publicUrl}</div>
          <button onClick={copyUrl} className="w-full py-1 rounded bg-slate-700 text-white text-[10px] font-black">📋 Copy Link</button>
          <a href={publicUrl} target="_blank" rel="noreferrer" className="block text-center py-1 rounded bg-emerald-500 text-slate-900 text-[10px] font-black">👁 Preview Public Shop</a>
        </div>
      </div>
      <div className="text-[10px] font-black text-emerald-300 uppercase">📸 Full Catalogue Share (with photo preview)</div>
      <div className="grid grid-cols-4 gap-1">
        <button onClick={() => shareCatalogue('wa')} disabled={busy} data-testid="mkt-share-cat-wa" className="py-2 rounded-lg bg-green-600 text-white text-[10px] font-black disabled:opacity-50">📱 WA</button>
        <button onClick={() => shareCatalogue('instagram')} disabled={busy} data-testid="mkt-share-cat-ig" className="py-2 rounded-lg bg-gradient-to-r from-purple-500 to-pink-500 text-white text-[10px] font-black disabled:opacity-50">📸 IG</button>
        <button onClick={() => shareCatalogue('fb')} disabled={busy} data-testid="mkt-share-cat-fb" className="py-2 rounded-lg bg-blue-600 text-white text-[10px] font-black disabled:opacity-50">📘 FB</button>
        <button onClick={() => shareCatalogue('native')} disabled={busy} data-testid="mkt-share-cat-native" className="py-2 rounded-lg bg-slate-700 text-white text-[10px] font-black disabled:opacity-50">📲 More</button>
      </div>

      {/* WhatsApp Channel & Group broadcast — admin-configured platform-wide links */}
      {(platformSettings.whatsapp_channel_url || platformSettings.whatsapp_group_url) && (
        <>
          <div className="text-[10px] font-black text-cyan-300 uppercase mt-2">📢 Broadcast via Anant Community</div>
          <div className="grid grid-cols-2 gap-1">
            {platformSettings.whatsapp_channel_url && (
              <button onClick={shareToChannel} data-testid="mkt-share-wa-channel"
                      className="py-2 rounded-lg bg-gradient-to-r from-green-600 to-emerald-600 text-white text-[10px] font-black">
                📣 WA Channel
              </button>
            )}
            {platformSettings.whatsapp_group_url && (
              <button onClick={shareToGroup} data-testid="mkt-share-wa-group"
                      className="py-2 rounded-lg bg-gradient-to-r from-teal-600 to-cyan-600 text-white text-[10px] font-black">
                👥 WA Group
              </button>
            )}
          </div>
        </>
      )}

      {/* PDF Catalog export — multi-page, jsPDF, zero server cost */}
      <div className="text-[10px] font-black text-emerald-300 uppercase mt-2">📄 Catalog PDF (all listings)</div>
      <div className="grid grid-cols-2 gap-1">
        <button onClick={() => exportPdf('download')} disabled={pdfBusy} data-testid="mkt-pdf-download"
                className="py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-white text-[10px] font-black disabled:opacity-50">
          {pdfBusy ? '⏳ Composing…' : '⬇️ Download PDF'}
        </button>
        <button onClick={() => exportPdf('share')} disabled={pdfBusy} data-testid="mkt-pdf-share"
                className="py-2 rounded-lg bg-emerald-500 hover:bg-emerald-600 text-slate-900 text-[10px] font-black disabled:opacity-50">
          {pdfBusy ? '⏳ Composing…' : '📱 WhatsApp PDF'}
        </button>
      </div>
      <canvas ref={cvsRef} className="hidden" />
      {busy && <div className="text-center text-[10px] opacity-70">⏳ Composing catalogue image…</div>}
    </div>
  );
}

// ============ LISTINGS TAB ============
function ListingsTab({ cfg, onReload }) {
  const [rows, setRows] = useState([]);
  const [catOptions, setCatOptions] = useState([]);
  const empty = { id: null, title: '', kind: 'product', description: '', price_at: '', category: '', city: cfg.store?.city || '', images: [], video_url: '', stock: '', active: true, commission_percent_override: null, delivery_fee_at: 0, free_delivery_min_qty: null, free_delivery_min_amount_at: null, group_buy_enabled: false, group_buy_min_buyers: null, group_buy_discount_pct: null, group_buy_max_qty_per_buyer: 1, group_buy_announce_on_join: false, group_buy_announce_on_confirm: true };
  const [form, setForm] = useState(empty);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);
  const load = useCallback(async () => {
    try { const r = await api.get('/marketplace/listings'); setRows(r.data.listings || []); } catch {}
  }, []);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    api.get('/marketplace/categories').then((r) => {
      const defaults = ['Electronics', 'Fashion', 'Grocery', 'Vehicle', 'Services', 'Home Decor', 'Food', 'Property'];
      const admin = (r.data?.categories || []).map((c) => ({ name: c.name, parent: c.parent }));
      const merged = [...defaults.map((n) => ({ name: n })), ...admin];
      // Dedupe by name
      const seen = new Set(); const out = [];
      merged.forEach((c) => { if (!seen.has(c.name.toLowerCase())) { seen.add(c.name.toLowerCase()); out.push(c); } });
      setCatOptions(out);
    }).catch(() => setCatOptions([]));
  }, []);
  const remaining = (cfg.plan?.listing_limit || 0) + (cfg.store?.listing_credits || 0) - rows.length;

  const uploadImgs = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const urls = [];
      for (const f of files) {
        const fd = new FormData(); fd.append('file', f);
        const r = await api.post('/webshop/upload/image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
        urls.push(r.data.url);
      }
      setForm((prev) => ({ ...prev, images: [...prev.images, ...urls].slice(0, 6) }));
      toast.success(`🖼 ${urls.length} uploaded`);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Upload failed'); }
    finally {
      setUploading(false);
      if (cameraRef.current) cameraRef.current.value = '';
      if (galleryRef.current) galleryRef.current.value = '';
    }
  };

  const save = async () => {
    if (!form.title.trim() || !form.price_at) { toast.error('Title + price required'); return; }
    setBusy(true);
    try {
      await api.post('/marketplace/listing', {
        ...form,
        price_at: Number(form.price_at),
        stock: form.stock ? Number(form.stock) : null,
        delivery_fee_at: Number(form.delivery_fee_at || 0),
        free_delivery_min_qty: form.free_delivery_min_qty === '' || form.free_delivery_min_qty == null ? null : Number(form.free_delivery_min_qty),
        free_delivery_min_amount_at: form.free_delivery_min_amount_at === '' || form.free_delivery_min_amount_at == null ? null : Number(form.free_delivery_min_amount_at),
        group_buy_enabled: !!form.group_buy_enabled,
        group_buy_min_buyers: form.group_buy_enabled && form.group_buy_min_buyers ? Number(form.group_buy_min_buyers) : null,
        group_buy_discount_pct: form.group_buy_enabled && form.group_buy_discount_pct ? Number(form.group_buy_discount_pct) : null,
        group_buy_max_qty_per_buyer: form.group_buy_enabled ? Number(form.group_buy_max_qty_per_buyer || 1) : 1,
        group_buy_announce_on_join: !!form.group_buy_announce_on_join,
        group_buy_announce_on_confirm: form.group_buy_announce_on_confirm !== false,
      });
      toast.success('✅ Listing saved');
      setForm(empty); load(); onReload && onReload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm('Delete this listing?')) return;
    await api.delete(`/marketplace/listing/${id}`); load(); onReload && onReload();
  };
  const edit = (l) => setForm({ ...empty, ...l, images: l.images || [], stock: l.stock ?? '' });

  if (!cfg.active) return <ActiveGate />;
  if (!cfg.store?.slug) return (
    <div className="rounded-2xl bg-amber-500/15 border-2 border-amber-400/60 p-4 text-center text-sm">
      <AlertTriangle className="inline w-5 h-5 text-amber-300 mr-1" />
      Pehle "My Store" tab me shop name save karein, phir listings add karein.
    </div>
  );
  return (
    <div className="space-y-3">
      <div className="rounded-xl bg-slate-900 border border-slate-700 p-2 flex items-center gap-2 text-xs">
        <div className="flex-1">
          Listings: <b className="text-emerald-300">{rows.length}</b> / <b>{(cfg.plan?.listing_limit || 0) + (cfg.store?.listing_credits || 0)}</b>
          {(remaining <= 0) && <span className="ml-2 text-rose-300 font-black">🚫 Limit reached</span>}
        </div>
      </div>

      <div className="p-3 rounded-2xl bg-slate-900 border-2 border-purple-400/50 space-y-2" data-testid="mkt-listing-form">
        <div className="text-xs font-black text-purple-300">{form.id ? '✏️ Edit Listing' : '➕ Add Listing'}</div>
        <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Title *" data-testid="mkt-title" className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm" />
        <div className="grid grid-cols-2 gap-2">
          <select value={form.kind} onChange={(e) => setForm({ ...form, kind: e.target.value })} className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm" data-testid="mkt-kind">
            <option value="product">📦 Product</option>
            <option value="service">🔧 Service</option>
            <option value="showroom">🚘 Showroom</option>
          </select>
          <input type="number" value={form.price_at} onChange={(e) => setForm({ ...form, price_at: e.target.value })} placeholder="Price (AT) *" data-testid="mkt-price" className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm" />
          <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="City" className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm" />
          <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="mkt-listing-category" className="px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm">
            <option value="">🏷️ Category / Sub-category</option>
            {/* Keep an older listing's saved category visible even if it is no longer in the list */}
            {form.category && !catOptions.some((c) => (c.name || '').toLowerCase() === form.category.toLowerCase()) && (
              <option value={form.category}>{form.category}</option>
            )}
            {catOptions.map((c) => (
              <option key={c.name} value={c.name}>{c.parent ? `↳ ${c.name} (under ${c.parent})` : c.name}</option>
            ))}
          </select>
          <label className="col-span-2 text-[10px] font-black text-purple-300 uppercase flex items-center gap-2">
            Commission % override
            <input type="number" min={0} max={50} step={0.5}
                   value={form.commission_percent_override ?? ''}
                   onChange={(e) => setForm({ ...form, commission_percent_override: e.target.value === '' ? null : Number(e.target.value) })}
                   placeholder="(default: use store setting)"
                   data-testid="mkt-listing-commission"
                   className="flex-1 px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs normal-case" />
          </label>
        </div>
        <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} placeholder="Description" className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm resize-y" />

        {/* Multi-source media */}
        <div className="rounded-lg bg-slate-800/50 border border-slate-700 p-2 space-y-2">
          <div className="text-[10px] font-black text-purple-300 uppercase">🖼 Images (max 6) · Camera / Gallery / URL</div>
          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => cameraRef.current?.click()} disabled={uploading} className="flex items-center justify-center gap-1 px-2 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-black disabled:opacity-50" data-testid="mkt-camera">
              <Camera size={13} /> {uploading ? 'Uploading…' : '📸 Camera'}
            </button>
            <button onClick={() => galleryRef.current?.click()} disabled={uploading} className="flex items-center justify-center gap-1 px-2 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-xs font-black disabled:opacity-50" data-testid="mkt-gallery">
              <ImageIcon size={13} /> {uploading ? 'Uploading…' : '🖼 Gallery'}
            </button>
            <input ref={cameraRef} type="file" accept="image/*" capture="environment" onChange={uploadImgs} className="hidden" />
            <input ref={galleryRef} type="file" accept="image/*" multiple onChange={uploadImgs} className="hidden" />
          </div>
          <input placeholder="🔗 Or paste image URL — press Enter"
                 onKeyDown={(e) => {
                   if (e.key === 'Enter' && e.target.value.trim()) {
                     setForm((prev) => ({ ...prev, images: [...prev.images, e.target.value.trim()].slice(0, 6) }));
                     e.target.value = '';
                   }
                 }}
                 className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          {form.images.length > 0 && (
            <div className="grid grid-cols-3 gap-1" data-testid="mkt-images-preview">
              {form.images.map((im, i) => (
                <div key={i} className="relative">
                  <img src={abs(im)} alt="" className="w-full aspect-square object-cover rounded" />
                  <button onClick={() => setForm((prev) => ({ ...prev, images: prev.images.filter((_, j) => j !== i) }))} className="absolute top-0 right-0 w-5 h-5 rounded-bl bg-black/70 text-white text-xs">✕</button>
                </div>
              ))}
            </div>
          )}
        </div>

        <input value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })} placeholder="🎬 Video URL (YouTube / direct .mp4 / Reel)" data-testid="mkt-video" className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-xs" />
        <label className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg ${uploading === 'listing_video' ? 'bg-slate-700 opacity-60' : 'bg-rose-700 hover:bg-rose-600'} text-white text-[10px] font-black cursor-pointer transition w-fit`}>
          {uploading === 'listing_video' ? '⏳ Uploading…' : '📹 Upload Video (MP4 · max 100MB)'}
          <input type="file" accept="video/*" className="hidden" disabled={!!uploading}
                 onChange={async (ev) => {
                   const f = ev.target.files?.[0]; if (!f) return;
                   if (f.size > 100 * 1024 * 1024) { toast.error('Video max 100MB — YouTube URL use karo'); return; }
                   setUploading('listing_video');
                   try {
                     const fd = new FormData(); fd.append('file', f);
                     const r = await api.post('/marketplace/upload/video', fd, { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 300000 });
                     const url = (r.data.url || '').startsWith('http') ? r.data.url : `${process.env.REACT_APP_BACKEND_URL || ''}${r.data.url}`;
                     setForm((p) => ({ ...p, video_url: url }));
                     toast.success('✅ Video uploaded!');
                   } catch (err) { toast.error(err?.response?.data?.detail || 'Upload failed — YouTube URL try karo'); }
                   finally { setUploading(null); }
                 }} />
        </label>
        <input type="number" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} placeholder="Stock (optional)" className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm" />

        {/* Delivery config — per-listing */}
        <div className="rounded-lg bg-sky-500/10 border border-sky-400/40 p-2 space-y-2" data-testid="mkt-listing-delivery">
          <div className="text-[10px] font-black text-sky-300 uppercase flex items-center gap-1">🚚 Delivery Configuration</div>
          <div className="grid grid-cols-3 gap-2">
            <label className="text-[10px] font-black text-sky-200 flex flex-col gap-1 uppercase">Delivery Fee (AT)
              <input type="number" min={0} step={0.5} value={form.delivery_fee_at || 0}
                     onChange={(e) => setForm({ ...form, delivery_fee_at: e.target.value === '' ? 0 : Number(e.target.value) })}
                     data-testid="mkt-listing-delivery-fee"
                     className="px-2 py-1.5 rounded bg-slate-900 border border-slate-700 text-xs normal-case" />
            </label>
            <label className="text-[10px] font-black text-sky-200 flex flex-col gap-1 uppercase">Free @ Min Qty
              <input type="number" min={1} value={form.free_delivery_min_qty ?? ''}
                     onChange={(e) => setForm({ ...form, free_delivery_min_qty: e.target.value === '' ? null : Number(e.target.value) })}
                     placeholder="optional"
                     data-testid="mkt-listing-free-qty"
                     className="px-2 py-1.5 rounded bg-slate-900 border border-slate-700 text-xs normal-case" />
            </label>
            <label className="text-[10px] font-black text-sky-200 flex flex-col gap-1 uppercase">Free @ Min AT
              <input type="number" min={0} step={0.5} value={form.free_delivery_min_amount_at ?? ''}
                     onChange={(e) => setForm({ ...form, free_delivery_min_amount_at: e.target.value === '' ? null : Number(e.target.value) })}
                     placeholder="optional"
                     data-testid="mkt-listing-free-amt"
                     className="px-2 py-1.5 rounded bg-slate-900 border border-slate-700 text-xs normal-case" />
            </label>
          </div>
          <div className="text-[9px] opacity-80">
            {Number(form.delivery_fee_at || 0) <= 0 && !form.free_delivery_min_qty && !form.free_delivery_min_amount_at ? (
              <span className="text-amber-300">📍 <b>Self-Pickup only</b> — no delivery partner needed.</span>
            ) : Number(form.delivery_fee_at || 0) <= 0 ? (
              <span className="text-emerald-300">🎉 Delivery <b>always FREE</b> (fee = 0 with threshold set).</span>
            ) : (
              <span>Buyer pays <b className="text-yellow-300">{Number(form.delivery_fee_at).toFixed(0)} AT</b>
                {form.free_delivery_min_qty ? <> · Free at <b>{form.free_delivery_min_qty}+ qty</b></> : null}
                {form.free_delivery_min_amount_at ? <> · Free at <b>{Number(form.free_delivery_min_amount_at).toFixed(0)}+ AT</b></> : null}
              </span>
            )}
          </div>
        </div>

        {/* Group Buy config — per-listing (Wave 50) */}
        <div className="rounded-lg bg-fuchsia-500/10 border border-fuchsia-400/40 p-2 space-y-2" data-testid="mkt-listing-groupbuy">
          <div className="flex items-center justify-between">
            <div className="text-[10px] font-black text-fuchsia-300 uppercase flex items-center gap-1">🎁 Group Buy</div>
            <label className="flex items-center gap-1 text-[10px] font-black text-fuchsia-200">
              <input type="checkbox" checked={!!form.group_buy_enabled}
                     onChange={(e) => setForm({ ...form, group_buy_enabled: e.target.checked })}
                     data-testid="mkt-listing-gb-enable" />
              Enable
            </label>
          </div>
          {form.group_buy_enabled && (
            <>
              <div className="grid grid-cols-3 gap-2">
                <label className="text-[10px] font-black text-fuchsia-200 flex flex-col gap-1 uppercase">Min Buyers
                  <input type="number" min={2} value={form.group_buy_min_buyers ?? ''}
                         onChange={(e) => setForm({ ...form, group_buy_min_buyers: e.target.value === '' ? null : Number(e.target.value) })}
                         placeholder="e.g. 5" data-testid="mkt-listing-gb-min"
                         className="px-2 py-1.5 rounded bg-slate-900 border border-slate-700 text-xs normal-case" />
                </label>
                <label className="text-[10px] font-black text-fuchsia-200 flex flex-col gap-1 uppercase">Discount %
                  <input type="number" min={0} step={0.5} value={form.group_buy_discount_pct ?? ''}
                         onChange={(e) => setForm({ ...form, group_buy_discount_pct: e.target.value === '' ? null : Number(e.target.value) })}
                         placeholder="e.g. 20" data-testid="mkt-listing-gb-disc"
                         className="px-2 py-1.5 rounded bg-slate-900 border border-slate-700 text-xs normal-case" />
                </label>
                <label className="text-[10px] font-black text-fuchsia-200 flex flex-col gap-1 uppercase">Max Qty/Buyer
                  <input type="number" min={1} value={form.group_buy_max_qty_per_buyer ?? 1}
                         onChange={(e) => setForm({ ...form, group_buy_max_qty_per_buyer: e.target.value === '' ? 1 : Number(e.target.value) })}
                         data-testid="mkt-listing-gb-maxqty"
                         className="px-2 py-1.5 rounded bg-slate-900 border border-slate-700 text-xs normal-case" />
                </label>
              </div>
              <div className="flex flex-wrap gap-3 pt-1">
                <label className="flex items-center gap-1 text-[10px] font-black text-fuchsia-100">
                  <input type="checkbox" checked={!!form.group_buy_announce_on_join}
                         onChange={(e) => setForm({ ...form, group_buy_announce_on_join: e.target.checked })}
                         data-testid="mkt-listing-gb-ann-join" />
                  🔊 Announce on each Join
                </label>
                <label className="flex items-center gap-1 text-[10px] font-black text-fuchsia-100">
                  <input type="checkbox" checked={form.group_buy_announce_on_confirm !== false}
                         onChange={(e) => setForm({ ...form, group_buy_announce_on_confirm: e.target.checked })}
                         data-testid="mkt-listing-gb-ann-confirm" />
                  🎉 Announce on Confirm
                </label>
              </div>
              <div className="text-[9px] opacity-80 text-fuchsia-100">
                Buyers pay <b>{form.price_at ? (Number(form.price_at) * (1 - Number(form.group_buy_discount_pct || 0)/100)).toFixed(0) : '—'} AT</b> instead of {form.price_at || '—'} AT · 24h timer · Auto-refund if quorum not met.
              </div>
            </>
          )}
        </div>

        <div className="grid grid-cols-2 gap-2">
          <button onClick={() => setForm(empty)} className="py-2 rounded-lg bg-slate-700 text-white text-xs font-black">✕ Reset</button>
          <button onClick={save} disabled={busy || remaining <= 0} data-testid="mkt-listing-save"
                  className="py-2 rounded-lg bg-purple-500 text-white text-sm font-black disabled:opacity-50">
            {busy ? '…' : (form.id ? '💾 Update' : '➕ Add Listing')}
          </button>
        </div>
      </div>

      <div className="space-y-2">
        {rows.map((l) => (
          <div key={l.id} className="rounded-xl bg-slate-900 border border-slate-700 p-2 flex items-center gap-2" data-testid={`mkt-my-listing-${l.id}`}>
            {l.images?.[0] ? <img src={abs(l.images[0])} alt="" className="w-14 h-14 object-cover rounded" /> : <div className="w-14 h-14 rounded bg-slate-800 flex items-center justify-center text-xl">📦</div>}
            <div className="flex-1 min-w-0">
              <div className="text-sm font-black truncate">{l.title} {l.featured && '⭐'} {l.verified && '✓'}</div>
              <div className="text-[10px] opacity-80">{l.price_at} AT · {l.kind} · 👁 {l.views || 0} · 💬 {l.leads || 0}</div>
              <div className="text-[9px] opacity-60">Exp: {l.expires_at?.slice(0, 10)}</div>
            </div>
            <button onClick={() => edit(l)} title="Edit" className="p-1.5 rounded bg-amber-500 text-slate-900"><Edit3 size={12} /></button>
            <button onClick={() => del(l.id)} className="p-1.5 rounded bg-red-900/60"><Trash2 size={12} /></button>
          </div>
        ))}
        {rows.length === 0 && <div className="text-center opacity-60 py-6 text-xs">Koi listing nahi. Upar add karein.</div>}
      </div>
    </div>
  );
}

// ============ PLANS TAB ============
function PlansTab({ cfg, onReload }) {
  const plans = cfg.plans || {};
  const active = cfg.active;
  const at = Number(cfg.at_balance || 0);
  const [cycle, setCycle] = useState('monthly');
  const subscribe = async (id) => {
    const p = plans[id];
    if (!p) return;
    if (at < p.at_cost) { toast.error(`Sirf ${at} AT — ${p.at_cost} chahiye. Top up karo!`); return; }
    if (!window.confirm(`${p.name} — ${p.at_cost} AT deduct hoga. Continue?`)) return;
    try {
      await api.post('/marketplace/subscribe', { plan_id: id });
      toast.success('✅ Subscribed');
      onReload && onReload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const monthlyMain = ['basic', 'standard', 'prime', 'vip'];
  const yearlyMain = ['basic_yearly', 'standard_yearly', 'prime_yearly', 'vip_yearly'];
  const mainPlans = cycle === 'yearly' ? yearlyMain : monthlyMain;
  const addons = ['single_listing', 'bundle10', 'sponsor_week'];
  const styles = (id) => {
    if (id.startsWith('vip')) return 'bg-gradient-to-br from-amber-500/25 via-yellow-500/15 to-pink-500/20 border-amber-300 text-amber-100';
    if (id.startsWith('prime')) return 'bg-yellow-500/15 border-yellow-400/70';
    if (id.startsWith('standard')) return 'bg-sky-500/15 border-sky-400/60';
    return 'bg-slate-900 border-slate-700';
  };
  const label = (id) => {
    if (id.startsWith('vip')) return '👑 VIP SHOWROOM';
    if (id.startsWith('prime')) return '⭐ PRIME GOLD';
    if (id.startsWith('standard')) return '🚀 STANDARD';
    return '🛒 BASIC';
  };
  return (
    <div className="space-y-3">
      <AIWalletGuard atBalance={at} threshold={300} tool="Marketplace" testId="mkt-wallet-guard" />
      {active && cfg.plan && (
        <div className="rounded-xl bg-emerald-500/15 border-2 border-emerald-400/60 p-3 text-sm" data-testid="mkt-active-banner">
          <div className="font-black text-emerald-300">✅ Active: {cfg.plan.name}</div>
          <div className="text-[10px] opacity-80">Expires: {cfg.store?.plan_expires_at?.slice(0, 10)} · Repeat plan buy blocked until expiry</div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div className="text-xs font-black opacity-80 uppercase">🏪 Store Plans</div>
        {/* Monthly / Yearly toggle */}
        <div className="inline-flex rounded-full bg-slate-900 border border-slate-700 p-0.5 text-[11px] font-black" data-testid="mkt-plans-cycle-toggle">
          <button onClick={() => setCycle('monthly')} data-testid="mkt-plans-cycle-monthly"
                  className={`px-3 py-1 rounded-full ${cycle === 'monthly' ? 'bg-purple-500 text-white' : 'text-slate-400'}`}>Monthly</button>
          <button onClick={() => setCycle('yearly')} data-testid="mkt-plans-cycle-yearly"
                  className={`px-3 py-1 rounded-full ${cycle === 'yearly' ? 'bg-emerald-500 text-slate-900' : 'text-slate-400'}`}>Yearly · Save 20%</button>
        </div>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
        {mainPlans.map((id) => {
          const p = plans[id]; if (!p) return null;
          const monthlyEquiv = cycle === 'yearly' ? Math.round(p.at_cost / 12) : null;
          return (
            <div key={id} className={`p-3 rounded-2xl border-2 ${styles(id)}`}>
              <div className="text-[11px] font-black opacity-90">{label(id)}</div>
              <div className="text-sm font-black truncate">{p.name}</div>
              <div className="text-2xl font-black text-yellow-300 mt-1">{p.at_cost} AT</div>
              {monthlyEquiv && <div className="text-[10px] text-emerald-300 font-black">≈ {monthlyEquiv} AT/mo</div>}
              <div className="text-[10px] opacity-70 mb-2">
                {p.days} days · {p.listing_limit} listings
                {p.vip && <> · 👑 VIP badge · Priority rank · 1 free sponsor/week · 10 Wall posts/day</>}
                {!p.vip && p.badge === 'PRIME_GOLD' && ' · ⭐ Verified'}
              </div>
              <button onClick={() => subscribe(id)} disabled={active} data-testid={`mkt-subscribe-${id}`}
                      className={`w-full py-1.5 rounded-lg text-xs font-black ${id.startsWith('vip') ? 'bg-gradient-to-r from-amber-400 to-pink-500 text-slate-900' : id.startsWith('prime') ? 'bg-yellow-400 text-slate-900' : 'bg-purple-500 text-white'} disabled:opacity-50`}>
                {active ? '🔒 Plan active' : `Subscribe`}
              </button>
            </div>
          );
        })}
      </div>

      <div className="text-xs font-black opacity-80 uppercase">⚡ Add-ons</div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {addons.map((id) => {
          const p = plans[id]; if (!p) return null;
          return (
            <div key={id} className="p-3 rounded-2xl bg-slate-900 border border-slate-700">
              <div className="text-sm font-black">{p.name}</div>
              <div className="text-2xl font-black text-yellow-300 mt-1">{p.at_cost} AT</div>
              <div className="text-[10px] opacity-70 mb-2">
                {p.credits ? `${p.credits} listing credit(s) · ${p.days}d` : `${p.days} days sponsored banner`}
              </div>
              <button onClick={() => subscribe(id)} data-testid={`mkt-subscribe-${id}`}
                      className="w-full py-1.5 rounded-lg bg-fuchsia-500 text-white text-xs font-black">
                Buy
              </button>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============ VENDOR ORDERS TAB (real-time incoming orders + status update) ============
const VENDOR_STATUSES = [
  { key: 'confirmed',        label: '✅ Accept',        color: 'bg-cyan-500 text-white' },
  { key: 'preparing',        label: '👨‍🍳 Preparing',    color: 'bg-sky-500 text-white' },
  { key: 'packed',           label: '📦 Packed',        color: 'bg-indigo-500 text-white' },
  { key: 'out_for_delivery', label: '🛵 Out for Del.',  color: 'bg-purple-500 text-white' },
  { key: 'delivered',        label: '🏠 Delivered',     color: 'bg-emerald-500 text-slate-900' },
  { key: 'rejected',         label: '✗ Reject',         color: 'bg-rose-600 text-white' },
];

const V_STATUS_LABELS = {
  pending: 'Placed', confirmed: 'Confirmed', preparing: 'Preparing',
  packed: 'Packed', shipped: 'Shipped', out_for_delivery: 'Out for delivery',
  delivered: 'Delivered', rejected: 'Rejected', completed: 'Delivered',
};

function VendorOrdersTab({ onReload }) {
  const [orders, setOrders] = useState([]);
  const [filter, setFilter] = useState('active'); // active | all | delivered | rejected
  const [busy, setBusy] = useState(false);
  const [busyIds, setBusyIds] = useState({});
  const [expanded, setExpanded] = useState(null);

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await api.get('/marketplace/vendor/orders?limit=100');
      setOrders(r.data.orders || []);
    } catch (e) { /* silent — polling on top-level handles chime */ }
    finally { setBusy(false); }
  }, []);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    const t = setInterval(load, 5000); // 5-second real-time refresh
    return () => clearInterval(t);
  }, [load]);

  const filtered = orders.filter((o) => {
    if (filter === 'all') return true;
    if (filter === 'active') return !['delivered', 'rejected', 'completed'].includes(o.status);
    if (filter === 'delivered') return ['delivered', 'completed'].includes(o.status);
    if (filter === 'rejected') return o.status === 'rejected';
    return true;
  });

  const updateStatus = async (orderId, newStatus) => {
    if (newStatus === 'rejected' && !window.confirm('Reject this order? (Buyer will be notified & refunded is manual)')) return;
    setBusyIds((p) => ({ ...p, [orderId]: true }));
    try {
      await api.post(`/marketplace/order/${orderId}/status`, { status: newStatus });
      toast.success(`✅ Order → ${V_STATUS_LABELS[newStatus] || newStatus}`);
      load();
      onReload && onReload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Update failed');
    } finally {
      setBusyIds((p) => ({ ...p, [orderId]: false }));
    }
  };

  const counts = orders.reduce((a, o) => {
    a.all = (a.all || 0) + 1;
    if (['delivered', 'completed'].includes(o.status)) a.delivered = (a.delivered || 0) + 1;
    else if (o.status === 'rejected') a.rejected = (a.rejected || 0) + 1;
    else a.active = (a.active || 0) + 1;
    return a;
  }, {});

  return (
    <div className="space-y-2" data-testid="mkt-orders-tab">
      {/* Header */}
      <div className="rounded-xl bg-gradient-to-r from-emerald-600/20 to-purple-600/20 border border-emerald-400/40 p-3 flex items-center gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <span className="relative">
            <span className="text-xl">📬</span>
            {counts.active > 0 && <span className="absolute -top-1 -right-2 w-3 h-3 rounded-full bg-rose-500 animate-ping" />}
          </span>
          <div>
            <div className="text-sm font-black text-emerald-200">Incoming Orders (live · 5s)</div>
            <div className="text-[10px] opacity-80">Real-time · Browser push enabled · Chime on new order</div>
          </div>
        </div>
        <div className="ml-auto flex gap-1">
          {[
            { key: 'active',    label: `🔥 Active (${counts.active || 0})` },
            { key: 'all',       label: `All (${counts.all || 0})` },
            { key: 'delivered', label: `✅ Delivered (${counts.delivered || 0})` },
            { key: 'rejected',  label: `✗ Rejected (${counts.rejected || 0})` },
          ].map((f) => (
            <button key={f.key} onClick={() => setFilter(f.key)} data-testid={`mkt-orders-filter-${f.key}`}
                    className={`px-2 py-1 rounded text-[10px] font-black ${filter === f.key ? 'bg-purple-500 text-white' : 'bg-slate-800 text-slate-300'}`}>
              {f.label}
            </button>
          ))}
          <button onClick={load} data-testid="mkt-orders-refresh"
                  className="px-2 py-1 rounded bg-emerald-500 text-slate-900 text-[10px] font-black">↻</button>
        </div>
      </div>

      {/* Orders list */}
      {filtered.length === 0 && (
        <div className="text-center py-12 opacity-60 text-sm" data-testid="mkt-orders-empty">
          {busy ? '⏳ Loading…' : filter === 'active' ? '🎉 No active orders — sab up-to-date!' : 'No orders here yet'}
        </div>
      )}

      {filtered.map((o) => {
        const isBusy = !!busyIds[o.id];
        const isOpen = expanded === o.id;
        const isNew = o.status === 'pending';
        const created = new Date(o.created_at || Date.now());
        const ageMin = Math.floor((Date.now() - created.getTime()) / 60000);
        const ageLabel = ageMin < 1 ? 'just now' : ageMin < 60 ? `${ageMin}m ago` : `${Math.floor(ageMin/60)}h ${ageMin%60}m ago`;
        return (
          <div key={o.id} data-testid={`mkt-order-card-${o.id.slice(0,8)}`}
               className={`rounded-2xl border p-3 space-y-2 transition ${
                 isNew ? 'bg-gradient-to-r from-emerald-900/40 to-slate-900 border-emerald-400/60 shadow-lg shadow-emerald-500/20 animate-pulse-slow' :
                 'bg-slate-900 border-slate-700'
               }`}>
            {/* Row 1: buyer + amount + status */}
            <div className="flex items-start gap-2 flex-wrap">
              <div className="flex-1 min-w-[160px]">
                <div className="text-sm font-black flex items-center gap-2">
                  {isNew && <span className="w-2 h-2 rounded-full bg-emerald-400 animate-ping" />}
                  {o.buyer_name || <span className="opacity-60 italic">Buyer</span>}
                </div>
                <div className="text-[10px] opacity-70">
                  📞 <a href={`tel:${o.buyer_phone}`} className="hover:text-emerald-300">{o.buyer_phone || '—'}</a> · 📍 {o.buyer_city || '—'}
                </div>
                <div className="text-[9px] opacity-50 font-mono">#{o.id.slice(0, 8)} · {ageLabel}</div>
              </div>
              <div className="text-right">
                <div className="text-2xl font-black text-yellow-300">{Number(o.my_total_at || 0).toFixed(0)}<span className="text-[10px]"> AT</span></div>
                <div className="text-[9px] opacity-70">{o.item_count} item{o.item_count !== 1 ? 's' : ''}</div>
                <div className={`text-[9px] px-1.5 py-0.5 rounded-full inline-block mt-0.5 font-black uppercase ${
                  isNew ? 'bg-amber-500 text-slate-900 animate-pulse' :
                  o.status === 'confirmed' ? 'bg-cyan-500 text-slate-900' :
                  o.status === 'preparing' ? 'bg-sky-500 text-white' :
                  o.status === 'packed' ? 'bg-indigo-500 text-white' :
                  o.status === 'shipped' ? 'bg-blue-500 text-white' :
                  o.status === 'out_for_delivery' ? 'bg-purple-500 text-white' :
                  ['delivered','completed'].includes(o.status) ? 'bg-emerald-500 text-slate-900' :
                  'bg-rose-500 text-white'
                }`}>
                  {V_STATUS_LABELS[o.status] || o.status}
                </div>
              </div>
            </div>

            {/* Items */}
            <div className="text-xs opacity-90 line-clamp-2">
              🛍️ {(o.my_items || []).map(it => `${it.title} × ${it.quantity}`).join(' · ') || '—'}
            </div>

            {/* Action buttons */}
            {!['delivered', 'completed', 'rejected'].includes(o.status) && (
              <div className="flex flex-wrap gap-1 pt-2 border-t border-slate-800">
                {VENDOR_STATUSES.filter(s => s.key !== o.status).map((s) => (
                  <button key={s.key} disabled={isBusy}
                          onClick={() => updateStatus(o.id, s.key)}
                          data-testid={`mkt-order-${o.id.slice(0,8)}-${s.key}`}
                          className={`text-[10px] px-2 py-1.5 rounded-lg font-black ${s.color} disabled:opacity-40 hover:scale-105 transition`}>
                    {s.label}
                  </button>
                ))}
              </div>
            )}

            {/* Expand */}
            <div className="flex items-center justify-between text-[10px]">
              <a href={`/order/${o.id}`} target="_blank" rel="noopener noreferrer" data-testid={`mkt-order-${o.id.slice(0,8)}-track`}
                 className="text-emerald-300 hover:underline">🔗 Track link</a>
              <button onClick={() => setExpanded(isOpen ? null : o.id)} data-testid={`mkt-order-${o.id.slice(0,8)}-expand`}
                      className="opacity-70 hover:opacity-100">
                {isOpen ? '↑ Hide' : '↓ Details'}
              </button>
            </div>

            {isOpen && (
              <div className="mt-2 p-2 rounded-lg bg-slate-950/60 space-y-1.5 text-[10px]" data-testid={`mkt-order-${o.id.slice(0,8)}-details`}>
                {o.buyer_address && <div>📍 <b>Address:</b> {o.buyer_address}</div>}
                {o.note && <div>💬 <b>Note:</b> {o.note}</div>}
                <div className="pt-1 border-t border-slate-800">
                  <div className="opacity-80 mb-1 font-black">📋 Timeline</div>
                  {[...(o.tracking_updates || [])].reverse().map((t, i) => (
                    <div key={i} className="flex gap-2">
                      <span className="text-emerald-400">●</span>
                      <div className="flex-1">
                        <b>{V_STATUS_LABELS[t.status] || t.status}</b> · <span className="opacity-70">{t.text}</span>
                        <div className="opacity-50 text-[9px]">{new Date(t.at).toLocaleString('en-IN')}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}
      <style>{`@keyframes pulse-slow { 0%,100% { box-shadow: 0 0 20px -4px rgba(16,185,129,0.4); } 50% { box-shadow: 0 0 30px -2px rgba(16,185,129,0.9); } } .animate-pulse-slow { animation: pulse-slow 2s ease-in-out infinite; }`}</style>
    </div>
  );
}


// ============ VENDOR REVIEWS TAB (read-only + reply button) ============
function VendorReviewsTab() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [replyingId, setReplyingId] = useState(null);
  const [replyText, setReplyText] = useState('');

  const load = useCallback(async () => {
    setBusy(true);
    try {
      const r = await api.get('/marketplace/vendor/reviews?limit=200');
      setData(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Load failed'); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const postReply = async (rid) => {
    if (!replyText.trim()) { toast.error('Reply khali'); return; }
    try {
      await api.post(`/marketplace/review/${rid}/reply`, { text: replyText });
      toast.success('Reply posted');
      setReplyingId(null); setReplyText(''); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const reviews = data?.reviews || [];
  return (
    <div className="space-y-2" data-testid="mkt-vendor-reviews-tab">
      <div className="rounded-xl bg-gradient-to-r from-yellow-500/20 to-orange-500/20 border border-yellow-400/40 p-3 flex items-center gap-3">
        <span className="text-2xl">⭐</span>
        <div className="flex-1">
          <div className="text-sm font-black text-yellow-200">Reviews on your listings</div>
          <div className="text-[10px] opacity-80">{data?.count || 0} reviews · Avg <b>{Number(data?.avg || 0).toFixed(1)}</b> ★ · <b>{data?.verified || 0}</b> verified · <b className="text-rose-300">{data?.unreplied || 0}</b> unreplied</div>
        </div>
        <button onClick={load} className="p-2 rounded bg-slate-800"><RefreshCw size={12} className={busy ? 'animate-spin' : ''} /></button>
      </div>
      {reviews.length === 0 && (
        <div className="text-center py-10 opacity-60 text-sm">
          {busy ? '⏳ Loading…' : 'No reviews yet — jab buyers delivery ke baad rate karenge, yahaan dikhega'}
        </div>
      )}
      {reviews.map((r) => (
        <div key={r.id} className="rounded-xl bg-slate-900 border border-slate-700 p-3 space-y-1.5" data-testid={`vendor-review-${r.id.slice(0,8)}`}>
          <div className="flex items-start gap-2 flex-wrap">
            <div className="w-8 h-8 rounded-full bg-purple-500 flex items-center justify-center text-xs font-black">{(r.buyer_name || '?')[0]?.toUpperCase()}</div>
            <div className="flex-1 min-w-0">
              <div className="text-sm font-black flex items-center gap-1 flex-wrap">
                {r.buyer_name}
                {r.verified_purchase && <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500/25 text-emerald-300 border border-emerald-400/60 font-black">✓ Verified</span>}
              </div>
              <div className="flex items-center gap-0.5 mt-0.5">
                {[1,2,3,4,5].map((n) => <span key={n} className={r.stars >= n ? 'text-yellow-300' : 'text-slate-600'}>★</span>)}
                <span className="ml-2 text-[9px] opacity-60">on <b>{r.listing_title || '—'}</b> · {new Date(r.created_at).toLocaleDateString('en-IN')}</span>
              </div>
            </div>
            {!r.vendor_reply && (
              <button onClick={() => { setReplyingId(r.id); setReplyText(''); }} data-testid={`vendor-reply-btn-${r.id.slice(0,8)}`}
                      className="text-[10px] px-2 py-1 rounded bg-cyan-500 text-slate-900 font-black">💬 Reply</button>
            )}
            {r.vendor_reply && (
              <button onClick={() => { setReplyingId(r.id); setReplyText(r.vendor_reply.text); }} data-testid={`vendor-editreply-${r.id.slice(0,8)}`}
                      className="text-[10px] px-2 py-1 rounded bg-slate-700 font-black">✏️ Edit Reply</button>
            )}
          </div>
          {r.comment && <div className="text-[11px] opacity-90">{r.comment}</div>}
          {r.vendor_reply && (
            <div className="mt-1 pl-2 border-l-2 border-cyan-400 bg-slate-800/40 p-1.5 rounded">
              <div className="text-[9px] font-black text-cyan-300">💬 Your reply</div>
              <div className="text-[10px] opacity-90">{r.vendor_reply.text}</div>
            </div>
          )}
          {replyingId === r.id && (
            <div className="mt-1 space-y-1 border-t border-slate-700 pt-2">
              <textarea value={replyText} onChange={(e) => setReplyText(e.target.value)} rows={2} maxLength={800}
                        placeholder="Reply likhein…"
                        data-testid={`vendor-reply-textarea-${r.id.slice(0,8)}`}
                        className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
              <div className="flex gap-1">
                <button onClick={() => setReplyingId(null)} className="flex-1 py-1.5 rounded bg-slate-700 text-[10px] font-black">Cancel</button>
                <button onClick={() => postReply(r.id)} data-testid={`vendor-reply-submit-${r.id.slice(0,8)}`}
                        className="flex-1 py-1.5 rounded bg-cyan-500 text-slate-900 text-[10px] font-black">Post Reply</button>
              </div>
            </div>
          )}
        </div>
      ))}
    </div>
  );
}


// ============ LEADS TAB (WA-order click notifications) ============
function LeadsTab({ onReload }) {
  const [rows, setRows] = useState([]);
  const load = useCallback(() => api.get('/marketplace/leads').then((r) => setRows(r.data.leads || [])).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const seen = async (id) => { await api.post('/marketplace/leads/seen', { lead_id: id }); load(); onReload && onReload(); };
  const seenAll = async () => { await api.post('/marketplace/leads/seen', { all: true }); toast.success('✅ All seen'); load(); onReload && onReload(); };
  const unread = rows.filter((l) => !l.seen).length;
  return (
    <div className="space-y-2" data-testid="mkt-leads-tab">
      <div className="rounded-xl bg-slate-900 border border-slate-700 p-2.5 flex items-center gap-2 text-xs">
        <MessageCircle size={13} className="text-purple-300" />
        <div className="flex-1"><b className="text-purple-300">{rows.length}</b> leads · {unread ? <span className="text-rose-300 font-black">{unread} unread</span> : <span className="opacity-60">All seen</span>}</div>
        {unread > 0 && <button onClick={seenAll} data-testid="mkt-leads-mark-all" className="px-2 py-1 rounded bg-purple-500 text-white text-[10px] font-black">Mark all seen</button>}
        <button onClick={load} className="px-2 py-1 rounded bg-slate-700 text-white text-[10px] font-black">🔄</button>
      </div>
      {rows.length === 0 && <div className="text-center opacity-60 py-10 text-xs">Abhi tak koi lead nahi.</div>}
      {rows.map((l) => (
        <div key={l.id} className={`p-3 rounded-xl border-2 ${l.seen ? 'bg-slate-900/60 border-slate-700' : 'bg-purple-500/10 border-purple-400/60'}`} data-testid={`mkt-lead-${l.id}`}>
          <div className="flex items-center gap-2">
            <div className="flex-1 min-w-0">
              <div className="text-sm font-black truncate">{l.listing_title} <span className="opacity-70 text-[10px]">· {l.listing_price} AT</span></div>
              <div className="text-[10px] opacity-80">
                <span className="px-1.5 py-0.5 rounded bg-slate-800 mr-1 text-[9px] font-black uppercase">{l.request_type}</span>
                {l.buyer_name && <>👤 <b>{l.buyer_name}</b> · </>}
                {l.buyer_phone && <>📞 <a href={`https://wa.me/${String(l.buyer_phone).replace(/[^0-9]/g,'')}`} target="_blank" rel="noreferrer" className="text-emerald-300 underline">{l.buyer_phone}</a> · </>}
                {l.buyer_city && <>📍 {l.buyer_city}</>}
              </div>
              {l.note && <div className="text-[10px] italic opacity-70 mt-0.5">📝 {l.note}</div>}
              <div className="text-[9px] opacity-60 mt-0.5">{l.created_at?.slice(0, 16).replace('T', ' ')}</div>
            </div>
            {!l.seen && <button onClick={() => seen(l.id)} data-testid={`mkt-lead-seen-${l.id}`} className="px-2 py-1 rounded bg-purple-500 text-white text-[10px] font-black">✓ Seen</button>}
          </div>
        </div>
      ))}
    </div>
  );
}

// ============ CLIENTS TAB (per-shop CRM database) ============
function ClientsTab() {
  const [rows, setRows] = useState([]);
  const empty = { id: null, name: '', mobile: '', whatsapp: '', email: '', group: 'Regular', city: '', dob: '', anniversary: '', tags: '', notes: '' };
  const [form, setForm] = useState(empty);
  const load = useCallback(() => api.get('/marketplace/clients').then((r) => setRows(r.data.clients || [])).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);
  const save = async () => {
    if (!form.name.trim()) { toast.error('Name required'); return; }
    try { await api.post('/marketplace/clients', form); toast.success(form.id ? '✅ Updated' : '✅ Added'); setForm(empty); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const del = async (id) => { if (window.confirm('Delete?')) { await api.delete(`/marketplace/clients/${id}`); load(); } };
  return (
    <div className="space-y-3" data-testid="mkt-clients-tab">
      <div className={`p-3 rounded-2xl bg-slate-900 border-2 ${form.id ? 'border-amber-400/70' : 'border-purple-400/40'} space-y-2`}>
        <div className="text-xs font-black text-purple-300 flex items-center gap-1"><Users size={12} /> {form.id ? '✏️ Edit Client' : '➕ Add Client'}</div>
        <div className="grid grid-cols-2 gap-2">
          <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Name *" data-testid="mkt-client-name" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-sm" />
          <input value={form.mobile} onChange={(e) => setForm({ ...form, mobile: e.target.value })} placeholder="Mobile" data-testid="mkt-client-mobile" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-sm" />
          <input value={form.whatsapp} onChange={(e) => setForm({ ...form, whatsapp: e.target.value })} placeholder="WhatsApp" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-sm" />
          <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="Email" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-sm" />
          <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="City" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-sm" />
          <input value={form.group} onChange={(e) => setForm({ ...form, group: e.target.value })} placeholder="Group (VIP/Regular)" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-sm" />
          <input type="date" value={form.dob} onChange={(e) => setForm({ ...form, dob: e.target.value })} className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          <input type="date" value={form.anniversary} onChange={(e) => setForm({ ...form, anniversary: e.target.value })} className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
        </div>
        <textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Notes" className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs resize-y" />
        <div className="grid grid-cols-2 gap-2">
          {form.id && <button onClick={() => setForm(empty)} className="py-2 rounded-lg bg-slate-700 text-white text-xs font-black">✕ Cancel</button>}
          <button onClick={save} data-testid="mkt-client-save" className={`${form.id ? '' : 'col-span-2'} py-2 rounded-lg bg-purple-500 text-white text-xs font-black`}>{form.id ? '💾 Update' : '➕ Add Client'}</button>
        </div>
      </div>
      <div className="text-[10px] font-black opacity-70 uppercase">Clients ({rows.length})</div>
      {rows.map((c) => (
        <div key={c.id} className="p-2 rounded-xl bg-slate-900 border border-slate-700 flex items-center gap-2 text-xs" data-testid={`mkt-client-${c.id}`}>
          <div className="flex-1 min-w-0">
            <div className="font-black truncate">{c.name} <span className="text-[10px] opacity-70">· {c.group || 'Regular'}</span></div>
            <div className="text-[10px] opacity-70">{c.mobile || c.whatsapp || ''} {c.city ? `· 📍 ${c.city}` : ''}</div>
          </div>
          {(c.whatsapp || c.mobile) && <a href={`https://wa.me/${String(c.whatsapp || c.mobile).replace(/[^0-9]/g,'')}`} target="_blank" rel="noreferrer" className="p-1.5 rounded bg-green-600 text-white"><MessageCircle size={12} /></a>}
          <button onClick={() => setForm({ ...empty, ...c })} className="p-1.5 rounded bg-amber-500 text-slate-900"><Edit3 size={12} /></button>
          <button onClick={() => del(c.id)} className="p-1.5 rounded bg-red-900/60"><Trash2 size={12} /></button>
        </div>
      ))}
      {rows.length === 0 && <div className="text-center opacity-60 py-6 text-xs">No clients yet.</div>}
    </div>
  );
}

// ─── LEAD360 DETAIL MODAL — Professional Lead Profile ─────────────────
function LeadDetailModal({ lead: initialLead, cats, onClose, onSaved }) {
  const [lead, setLead] = useState(initialLead);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ ...initialLead });
  const [saving, setSaving] = useState(false);
  const [newFamDate, setNewFamDate] = useState({ label: '', date: '', person_name: '' });
  const [listening, setListening] = useState(false);
  const recogRef = useRef(null);

  const STATUS_COLORS = { new: 'bg-sky-500 text-white', contacted: 'bg-amber-500 text-black', interested: 'bg-emerald-500 text-white', converted: 'bg-purple-500 text-white', lost: 'bg-rose-600 text-white' };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/lead360/leads/${lead.id}`, form);
      toast.success('✅ Lead updated!');
      setLead({ ...lead, ...form }); setEditing(false); onSaved();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setSaving(false); }
  };

  const startVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error('Voice not supported'); return; }
    const r = new SR(); r.lang = 'hi-IN'; r.continuous = false;
    r.onresult = (e) => { const t = Array.from(e.results).map((x) => x[0].transcript).join(' '); setForm((f) => ({ ...f, extra_notes: (f.extra_notes ? f.extra_notes + ' ' : '') + t })); setListening(false); };
    r.onerror = r.onend = () => setListening(false);
    recogRef.current = r; r.start(); setListening(true);
  };

  const addFamilyDate = () => {
    if (!newFamDate.label || !newFamDate.date) return;
    const updated = [...(form.family_dates || []), { ...newFamDate }];
    setForm({ ...form, family_dates: updated });
    setNewFamDate({ label: '', date: '', person_name: '' });
  };

  const F = ({ label, val }) => val ? <div><span className="opacity-60 text-[10px]">{label}: </span><span className="text-xs font-black">{val}</span></div> : null;

  const catName = cats.find((c) => c.id === lead.category_id)?.name || '';
  const waUrl = lead.mobile ? `https://wa.me/${(lead.mobile).replace(/\D/g, '')}` : null;

  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex items-start justify-center p-2 pt-4 overflow-y-auto" data-testid="mkt-lead360-detail-modal">
      <div className="w-full max-w-lg rounded-2xl bg-slate-950 border-2 border-emerald-500/50 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        {/* Header */}
        <div className="relative bg-gradient-to-br from-emerald-800 to-teal-900 rounded-t-2xl p-5">
          <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_70%_30%,gold,transparent)] rounded-t-2xl"></div>
          <div className="relative flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              {!editing ? (
                <div className="text-xl font-black text-white truncate">{lead.name}</div>
              ) : (
                <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                       className="w-full px-2 py-1 rounded-lg bg-white/10 text-white font-black text-lg border border-white/20" />
              )}
              <div className="text-[11px] opacity-80 text-emerald-200 mt-0.5">{lead.business_type || '—'} {lead.city ? `· 📍 ${lead.city}` : ''}</div>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <span className={`px-2 py-0.5 rounded-full text-[10px] font-black ${STATUS_COLORS[lead.status] || 'bg-slate-600 text-white'}`}>{lead.status}</span>
                {catName && <span className="text-[10px] bg-white/20 px-2 py-0.5 rounded-full">{catName}</span>}
                {lead.days_old > 0 && <span className="text-[10px] opacity-70">{lead.days_old}d ago</span>}
              </div>
            </div>
            <div className="flex gap-1 shrink-0">
              <button onClick={() => setEditing(!editing)} className={`px-2 py-1 rounded-lg text-[10px] font-black ${editing ? 'bg-amber-500 text-black' : 'bg-white/20 text-white'}`}>
                {editing ? '✕ Cancel' : '✏️ Edit'}
              </button>
              <button onClick={onClose} className="w-7 h-7 rounded-full bg-white/20 text-white text-xs flex items-center justify-center">✕</button>
            </div>
          </div>
        </div>

        <div className="p-4 space-y-4 max-h-[75vh] overflow-y-auto">
          {/* Contact actions */}
          <div className="flex gap-2 flex-wrap">
            {lead.mobile && <a href={`tel:${lead.mobile}`} className="flex-1 py-2 rounded-xl bg-sky-700/50 text-sky-200 text-xs font-black text-center" data-testid={`mkt-ld-call`}>📞 Call</a>}
            {waUrl && <a href={waUrl} target="_blank" rel="noreferrer" className="flex-1 py-2 rounded-xl bg-green-700/50 text-green-200 text-xs font-black text-center" data-testid={`mkt-ld-wa`}>💬 WhatsApp</a>}
            {lead.email && <a href={`mailto:${lead.email}`} className="flex-1 py-2 rounded-xl bg-blue-700/50 text-blue-200 text-xs font-black text-center">📧 Email</a>}
            {lead.linkedin && <a href={lead.linkedin} target="_blank" rel="noreferrer" className="flex-1 py-2 rounded-xl bg-blue-800/50 text-blue-200 text-xs font-black text-center">🔗 LinkedIn</a>}
          </div>

          {/* Section 1: Basic Info */}
          <Section title="📋 Contact & Business">
            {!editing ? (
              <div className="grid grid-cols-2 gap-1.5 text-xs">
                <F label="Mobile" val={lead.mobile} /><F label="Email" val={lead.email} />
                <F label="Business" val={lead.business_type} /><F label="Category" val={catName} />
                <F label="City" val={lead.city} /><F label="State" val={lead.state} />
                <F label="Website" val={lead.website} /><F label="Referral" val={lead.referral_name} />
                <F label="Employees" val={lead.employee_count} /><F label="Turnover" val={lead.annual_turnover} />
                <F label="Deal Value" val={lead.deal_value ? `₹${lead.deal_value}` : null} /><F label="Expected Close" val={lead.expected_closure} />
                <F label="Source" val={lead.source} />
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                {[['Mobile', 'mobile', 'tel'], ['Email', 'email', 'email'], ['Business Type', 'business_type', 'text'], ['City', 'city', 'text'], ['State', 'state', 'text'], ['Website', 'website', 'url'], ['LinkedIn', 'linkedin', 'url'], ['Referral By', 'referral_name', 'text'], ['Employees', 'employee_count', 'text'], ['Annual Turnover', 'annual_turnover', 'text'], ['Deal Value ₹', 'deal_value', 'number'], ['Expected Close', 'expected_closure', 'date']].map(([label, key, type]) => (
                  <label key={key} className="space-y-0.5">
                    <div className="text-[9px] opacity-60 uppercase tracking-wide">{label}</div>
                    <input type={type} value={form[key] || ''} onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                           className="w-full px-2 py-1.5 rounded-lg bg-slate-800 border border-slate-700 text-xs" />
                  </label>
                ))}
                <label className="space-y-0.5 col-span-2">
                  <div className="text-[9px] opacity-60 uppercase tracking-wide">Category</div>
                  <select value={form.category_id || ''} onChange={(e) => setForm({ ...form, category_id: e.target.value })}
                          className="w-full px-2 py-1.5 rounded-lg bg-slate-800 border border-slate-700 text-xs">
                    <option value="">Select</option>
                    {cats.map((c) => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
                  </select>
                </label>
                <label className="col-span-2 space-y-0.5">
                  <div className="text-[9px] opacity-60 uppercase tracking-wide">Status</div>
                  <select value={form.status || 'new'} onChange={(e) => setForm({ ...form, status: e.target.value })}
                          className="w-full px-2 py-1.5 rounded-lg bg-slate-800 border border-slate-700 text-xs">
                    {['new','contacted','interested','converted','lost'].map((s) => <option key={s} value={s}>{s}</option>)}
                  </select>
                </label>
              </div>
            )}
          </Section>

          {/* Section 2: Family 360 */}
          <Section title="👨‍👩‍👧‍👦 Family 360 — Personal Dates">
            {!editing ? (
              <div className="space-y-1">
                <div className="grid grid-cols-2 gap-1.5 text-xs">
                  <F label="🎂 Birthday" val={lead.dob} /><F label="💍 Anniversary" val={lead.anniversary} />
                  <F label="Spouse" val={lead.spouse_name} />
                </div>
                {(lead.family_dates || []).length > 0 && (
                  <div className="mt-2 space-y-1">
                    <div className="text-[10px] font-black opacity-70 uppercase">Important Family Dates</div>
                    {(lead.family_dates || []).map((fd, i) => (
                      <div key={i} className="flex items-center gap-2 text-[11px] rounded-lg bg-slate-800 px-2 py-1">
                        <span>🗓</span><span className="font-black flex-1">{fd.label}</span>
                        <span className="opacity-70">{fd.person_name}</span>
                        <span className="text-emerald-300">{fd.date}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ) : (
              <div className="space-y-2">
                <div className="grid grid-cols-2 gap-2">
                  {[['🎂 Birthday (YYYY-MM-DD)', 'dob', 'date'], ['💍 Anniversary', 'anniversary', 'date'], ['Spouse Name', 'spouse_name', 'text']].map(([label, key, type]) => (
                    <label key={key} className="space-y-0.5">
                      <div className="text-[9px] opacity-60 uppercase tracking-wide">{label}</div>
                      <input type={type} value={form[key] || ''} onChange={(e) => setForm({ ...form, [key]: e.target.value })}
                             className="w-full px-2 py-1.5 rounded-lg bg-slate-800 border border-slate-700 text-xs" />
                    </label>
                  ))}
                </div>
                {/* Add Family Date */}
                <div className="rounded-lg bg-slate-800 border border-slate-700 p-2 space-y-1.5">
                  <div className="text-[10px] font-black text-emerald-300">+ Add Important Family Date</div>
                  <div className="grid grid-cols-3 gap-1">
                    <input value={newFamDate.label} onChange={(e) => setNewFamDate({ ...newFamDate, label: e.target.value })}
                           placeholder="Label (e.g. Beta ka Bday)" className="col-span-2 px-2 py-1 rounded-lg bg-slate-700 border border-slate-600 text-[10px]" />
                    <input type="date" value={newFamDate.date} onChange={(e) => setNewFamDate({ ...newFamDate, date: e.target.value })}
                           className="px-2 py-1 rounded-lg bg-slate-700 border border-slate-600 text-[10px]" />
                    <input value={newFamDate.person_name} onChange={(e) => setNewFamDate({ ...newFamDate, person_name: e.target.value })}
                           placeholder="Person name" className="col-span-2 px-2 py-1 rounded-lg bg-slate-700 border border-slate-600 text-[10px]" />
                    <button onClick={addFamilyDate} className="py-1 rounded-lg bg-emerald-600 text-white text-[10px] font-black">+ Add</button>
                  </div>
                  {(form.family_dates || []).length > 0 && (
                    <div className="space-y-0.5">
                      {(form.family_dates || []).map((fd, i) => (
                        <div key={i} className="flex items-center gap-1 text-[10px]">
                          <span className="flex-1">{fd.label} · {fd.person_name} · {fd.date}</span>
                          <button onClick={() => setForm({ ...form, family_dates: (form.family_dates || []).filter((_, j) => j !== i) })} className="text-rose-400">✕</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </Section>

          {/* Section 3: Notes */}
          <Section title="📝 Notes & Follow-up">
            {!editing ? (
              <div className="space-y-2 text-xs">
                {lead.notes && <div className="rounded-lg bg-slate-800 px-3 py-2"><div className="text-[9px] opacity-60 mb-0.5">Quick Note</div>{lead.notes}</div>}
                {lead.extra_notes && <div className="rounded-lg bg-slate-800 px-3 py-2"><div className="text-[9px] opacity-60 mb-0.5">Detailed Notes</div>{lead.extra_notes}</div>}
                <div className="grid grid-cols-2 gap-1">
                  <F label="Last Followup" val={lead.last_followup} />
                  <F label="Followup Count" val={lead.followup_count} />
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <textarea value={form.notes || ''} onChange={(e) => setForm({ ...form, notes: e.target.value })}
                          rows={2} placeholder="Quick note"
                          className="w-full px-3 py-2 rounded-xl bg-slate-800 border border-slate-700 text-xs" />
                <div className="relative">
                  <textarea value={form.extra_notes || ''} onChange={(e) => setForm({ ...form, extra_notes: e.target.value })}
                            rows={3} placeholder="Detailed notes (ya 🎤 voice)"
                            className="w-full px-3 py-2 rounded-xl bg-slate-800 border border-slate-700 text-xs pr-10" />
                  <button onClick={listening ? (() => { recogRef.current?.stop(); setListening(false); }) : startVoice}
                          className={`absolute right-2 top-2 w-7 h-7 rounded-full text-xs flex items-center justify-center ${listening ? 'bg-rose-500 animate-pulse' : 'bg-slate-600'}`}>
                    {listening ? '⏹' : '🎤'}
                  </button>
                </div>
                <label className="space-y-0.5">
                  <div className="text-[9px] opacity-60 uppercase">Next Followup Date</div>
                  <input type="date" value={form.followup_date || form.last_followup || ''} onChange={(e) => setForm({ ...form, followup_date: e.target.value })}
                         className="w-full px-2 py-1.5 rounded-lg bg-slate-800 border border-slate-700 text-xs" />
                </label>
              </div>
            )}
          </Section>

          {editing && (
            <button onClick={save} disabled={saving} data-testid="mkt-ld-save"
                    className="w-full py-3 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-black text-sm disabled:opacity-40">
              {saving ? '…' : '💾 Save Profile'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div className="rounded-xl bg-slate-900 border border-slate-700 overflow-hidden">
      <div className="px-3 py-2 bg-slate-800 text-[11px] font-black text-slate-300 uppercase tracking-wide">{title}</div>
      <div className="p-3">{children}</div>
    </div>
  );
}

// ─── AT WITHDRAW ADMIN PANEL
function ATWithdrawAdminPanel() {
  const [requests, setRequests] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(null); // req_id being processed

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get('/admin/at-wallet/withdraw', { params: filter ? { status: filter } : {} });
      setRequests(r.data.requests || []);
    } catch { toast.error('Load failed'); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [filter]);

  const doAction = async (req, action) => {
    let payment_ref = '';
    let reason = '';
    if (action === 'approve') {
      payment_ref = window.prompt(`✅ Approve ${req.amount} AT withdraw for @${req.username}\n\nPayment reference / UTR number dalo (optional):`) ?? '';
      if (payment_ref === null) return; // user cancelled
    } else {
      reason = window.prompt(`✕ Reject reason?`) ?? '';
      if (reason === null) return;
    }
    setBusy(req.id);
    try {
      await api.post(`/admin/at-wallet/withdraw/${req.id}/action`, { action, reason, payment_ref });
      toast.success(action === 'approve' ? `✅ Approved & paid — @${req.username}` : `✕ Rejected — AT refunded to @${req.username}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Action failed'); }
    finally { setBusy(null); }
  };

  const STATUS_STYLE = {
    pending: 'bg-amber-500/20 text-amber-300',
    paid: 'bg-emerald-500/20 text-emerald-300',
    rejected: 'bg-rose-500/20 text-rose-300',
  };

  const total_pending_at = requests
    .filter((r) => r.status === 'pending')
    .reduce((s, r) => s + Number(r.total_debit || r.amount || 0), 0);

  return (
    <div className="space-y-3" data-testid="mkt-admin-withdrawals">
      {/* Summary bar */}
      <div className="rounded-2xl bg-gradient-to-r from-rose-900/60 to-red-900/60 border-2 border-rose-400/40 p-3 flex flex-wrap gap-3 items-center">
        <div className="flex-1 min-w-0">
          <div className="text-xs font-black text-rose-200 uppercase tracking-wide">💵 AT Withdraw Requests</div>
          <div className="text-[11px] opacity-80">Pending AT hold: <b className="text-amber-300">{total_pending_at.toFixed(1)} AT</b></div>
        </div>
        <div className="flex gap-1">
          {['pending', 'paid', 'rejected', ''].map((s) => (
            <button key={s} onClick={() => setFilter(s)}
                    className={`px-2 py-1 rounded-lg text-[10px] font-black border transition ${filter === s ? 'bg-rose-500 border-rose-300 text-white' : 'bg-slate-800 border-slate-700 text-slate-300'}`}>
              {s || 'All'}
            </button>
          ))}
          <button onClick={load} className="px-2 py-1 rounded-lg bg-slate-700 text-slate-200 text-[10px] font-black">↻</button>
        </div>
      </div>

      {loading && <div className="text-center py-6 text-xs opacity-60">Loading…</div>}
      {!loading && requests.length === 0 && (
        <div className="text-center py-10 text-xs opacity-60">
          {filter === 'pending' ? '✅ Koi pending request nahi!' : 'No requests found.'}
        </div>
      )}

      <div className="space-y-2">
        {requests.map((req) => {
          const feeAt = req.fee_at ?? (req.amount * 0.02);
          return (
            <div key={req.id} className="rounded-2xl bg-slate-900 border border-slate-700 p-3 space-y-2"
                 data-testid={`mkt-admin-wd-${req.id}`}>
              {/* Header */}
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-black text-sm">@{req.username}</span>
                    <span className={`text-[10px] font-black px-2 py-0.5 rounded-full ${STATUS_STYLE[req.status] || 'bg-slate-700 text-slate-300'}`}>
                      {req.status}
                    </span>
                    <span className="text-[10px] opacity-60">{(req.created_at || '').slice(0, 16).replace('T', ' ')}</span>
                  </div>
                </div>
                {/* Amount */}
                <div className="text-right shrink-0">
                  <div className="font-black text-rose-200 text-base">{Number(req.amount).toFixed(0)} AT</div>
                  <div className="text-[10px] opacity-60">+{Number(feeAt).toFixed(1)} fee · {Number(req.total_debit ?? req.amount).toFixed(1)} total</div>
                </div>
              </div>

              {/* Payment details */}
              <div className="rounded-lg bg-slate-800 px-2 py-1.5 text-[11px] space-y-0.5">
                <div className="flex gap-2 flex-wrap">
                  <span className="font-black text-slate-300">Method:</span>
                  <span className="uppercase font-black text-amber-200">{req.method}</span>
                  {req.upi_id && <span className="text-slate-300">UPI: <b>{req.upi_id}</b></span>}
                  {req.bank_account && <span className="text-slate-300">Acc: <b>{req.bank_account}</b></span>}
                  {req.bank_ifsc && <span className="text-slate-300">IFSC: <b>{req.bank_ifsc}</b></span>}
                  {req.bank_name && <span className="text-slate-300">{req.bank_name}</span>}
                </div>
                {req.note && <div className="opacity-70">Note: {req.note}</div>}
                {req.payment_ref && <div className="text-emerald-300 font-black">Payment Ref: {req.payment_ref}</div>}
                {req.reason && <div className="text-rose-300">Reason: {req.reason}</div>}
              </div>

              {/* Action buttons - only for pending */}
              {req.status === 'pending' && (
                <div className="flex gap-2">
                  <button onClick={() => doAction(req, 'approve')} disabled={busy === req.id}
                          data-testid={`mkt-admin-wd-approve-${req.id}`}
                          className="flex-1 py-2 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 text-white text-xs font-black disabled:opacity-40">
                    {busy === req.id ? '…' : `✅ Approve · Pay ₹ to UPI/Bank`}
                  </button>
                  <button onClick={() => doAction(req, 'reject')} disabled={busy === req.id}
                          data-testid={`mkt-admin-wd-reject-${req.id}`}
                          className="px-4 py-2 rounded-xl bg-rose-700/60 border border-rose-500/60 text-rose-200 text-xs font-black disabled:opacity-40">
                    ✕ Reject
                  </button>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── ADMIN TELEGRAM STATUS BLOCK ─────────────────────────────────────
function AdminTelegramStatusBlock() {
  const [status, setStatus] = useState(null);
  const [testing, setTesting] = useState(false);
  const load = async () => {
    try { const r = await api.get('/telegram/status'); setStatus(r.data); } catch {}
  };
  useEffect(() => { load(); }, []);
  const testAlert = async () => {
    setTesting(true);
    try {
      const r = await api.post('/telegram/test-notification?notification_type=product_order');
      if (r.data?.success) toast.success('✅ Telegram test message sent!');
      else toast.error(r.data?.error || 'Failed');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setTesting(false); }
  };
  return (
    <div className="rounded-2xl bg-slate-900 border border-blue-400/40 p-4 space-y-2" data-testid="mkt-admin-telegram-status">
      <div className="flex items-center justify-between">
        <div className="text-sm font-black text-blue-300">📱 Telegram Bot Status</div>
        <div className={`text-[11px] font-black px-2 py-0.5 rounded-full ${status?.configured ? 'bg-emerald-500/20 text-emerald-300' : 'bg-rose-500/20 text-rose-300'}`}>
          {status?.configured ? '✓ Connected' : '✕ Not configured'}
        </div>
      </div>
      {status?.bot_info && (
        <div className="text-[11px] opacity-80">Bot: <b>@{status.bot_info.username}</b> · {status.bot_info.first_name}</div>
      )}
      <div className="text-[11px] opacity-70 space-y-0.5">
        <div>Admin Chat ID: <b>{status?.admin_chat_id || '—'}</b></div>
        <div>Vendor tatkal bookings → Telegram alert (vendor ka chat ID store settings mein set karo)</div>
        <div>Lead360 milestones → Daily 9 AM IST alert (cron active)</div>
      </div>
      <button onClick={testAlert} disabled={testing || !status?.configured}
              data-testid="mkt-admin-telegram-test"
              className="px-3 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-black disabled:opacity-40">
        {testing ? '…' : '📱 Test Telegram Alert'}
      </button>
    </div>
  );
}

// ============ ADMIN TAB ============
function AdminTab() {
  const [s, setS] = useState(null);
  const [ls, setLs] = useState([]);
  const [stores, setStores] = useState([]);
  const [cats, setCats] = useState([]);
  const [stats, setStats] = useState(null);
  const [view, setView] = useState('overview'); // overview | listings | stores | categories | leads
  const [leads, setLeads] = useState([]);
  const [editL, setEditL] = useState(null);   // listing edit
  const [editSt, setEditSt] = useState(null); // store edit
  const [catForm, setCatForm] = useState({ id: null, name: '', parent: '', icon: '🏷️', tint: 'from-indigo-500 to-purple-600' });
  const [popups, setPopups] = useState([]);
  const [popForm, setPopForm] = useState({ id: null, text: '', icon: '🎉', tint: 'from-emerald-500 to-teal-600', active: true });
  const [auctions, setAuctions] = useState([]);
  const [auctionAction, setAuctionAction] = useState(null); // {aid, action, reason}
  const [taxWallet, setTaxWallet] = useState(null); // {balance, ledger, ...}
  // Super admin check — matches backend SUPER_ADMIN_EMAILS
  const currentUser = useMemo(() => JSON.parse(localStorage.getItem('user') || '{}'), []);
  const isSuperAdmin = (currentUser?.email || '').toLowerCase() === 'freedomanil@gmail.com';

  const [walletSettings, setWalletSettings] = useState({ withdraw_fee_pct: 2, withdraw_min_at: 5000 });
  const [tatkalSettings, setTatkalSettings] = useState({ max_window_hours: 6, default_booking_fee: 50 });

  const load = useCallback(async () => {
    try {
      const [a, b, c, d, e, f, g, h, ws, ts] = await Promise.all([
        api.get('/admin/marketplace/settings'),
        api.get('/admin/marketplace/listings'),
        api.get('/admin/marketplace/stores'),
        api.get('/marketplace/categories'),
        api.get('/admin/marketplace/stats'),
        api.get('/admin/marketplace/leads').catch(() => ({ data: { leads: [] } })),
        api.get('/admin/marketplace/popups').catch(() => ({ data: { popups: [] } })),
        api.get('/admin/marketplace/auctions').catch(() => ({ data: { auctions: [] } })),
        api.get('/admin/at-wallet/settings').catch(() => ({ data: { withdraw_fee_pct: 2, withdraw_min_at: 5000 } })),
        api.get('/admin/tatkal/settings').catch(() => ({ data: { max_window_hours: 6, default_booking_fee: 50 } })),
      ]);
      setS(a.data.settings); setLs(b.data.listings || []); setStores(c.data.stores || []);
      setCats(d.data.categories || []); setStats(e.data); setLeads(f.data.leads || []);
      setPopups(g.data.popups || []); setAuctions(h.data.auctions || []);
      setWalletSettings(ws.data || { withdraw_fee_pct: 2, withdraw_min_at: 5000 });
      setTatkalSettings(ts.data || { max_window_hours: 6, default_booking_fee: 50 });
      // Tax wallet — super admin only
      if (isSuperAdmin) {
        try { const tw = await api.get('/admin/marketplace/tax-wallet'); setTaxWallet(tw.data); }
        catch { setTaxWallet(null); }
      }
    } catch { toast.error('Load failed'); }
  }, [isSuperAdmin]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try { const r = await api.post('/admin/marketplace/settings', s); setS(r.data.settings); toast.success('✅ Saved'); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const doAction = async (body) => {
    try { await api.post('/admin/marketplace/action', body); toast.success('✅ Done'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const saveCat = async () => {
    if (!catForm.name.trim()) return toast.error('Name required');
    try { await api.post('/admin/marketplace/categories', catForm); toast.success('✅ Category saved');
          setCatForm({ id: null, name: '', parent: '', icon: '🏷️', tint: 'from-indigo-500 to-purple-600' }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const delCat = async (cid) => {
    if (!window.confirm('Delete this category?')) return;
    try { await api.delete(`/admin/marketplace/categories/${cid}`); toast.success('🗑️ Deleted'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const saveListingEdit = async () => {
    try { await api.post('/admin/marketplace/listing/edit', editL); toast.success('✅ Listing updated'); setEditL(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const saveStoreEdit = async () => {
    try { await api.post('/admin/marketplace/store/edit', editSt); toast.success('✅ Store updated'); setEditSt(null); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const savePopup = async () => {
    if (!popForm.text.trim()) return toast.error('Text required');
    try { await api.post('/admin/marketplace/popups', popForm); toast.success('✅ Popup saved');
          setPopForm({ id: null, text: '', icon: '🎉', tint: 'from-emerald-500 to-teal-600', active: true }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const delPopup = async (pid) => {
    if (!window.confirm('Delete this popup?')) return;
    try { await api.delete(`/admin/marketplace/popups/${pid}`); toast.success('🗑️ Deleted'); load(); }
    catch (e) { toast.error('Failed'); }
  };

  if (!s) return <div className="text-center opacity-60 py-6">⏳ Loading Admin Console…</div>;

  const SubTab = ({ k, label, badge }) => (
    <button onClick={() => setView(k)} data-testid={`mkt-admin-tab-${k}`}
      className={`px-3 py-1.5 rounded-lg text-xs font-black border-2 whitespace-nowrap relative ${view === k ? 'bg-purple-500 border-purple-300 text-white' : 'bg-slate-900 border-slate-700 text-slate-300'}`}>
      {label}{badge != null && <span className="ml-1 px-1 rounded bg-yellow-400 text-slate-900 text-[9px]">{badge}</span>}
    </button>
  );

  return (
    <div className="space-y-3" data-testid="mkt-admin-panel">
      {/* Sub-tab bar */}
      <div className="flex gap-2 overflow-x-auto pb-1">
        <SubTab k="overview"   label="👑 Overview" />
        <SubTab k="users"      label="👥 User Controls" />
        <SubTab k="listings"   label="📦 Listings" badge={ls.length} />
        <SubTab k="stores"     label="🏪 Stores" badge={stores.length} />
        <SubTab k="categories" label="🏷️ Categories" badge={cats.length} />
        <SubTab k="popups"     label="💬 Popups" badge={popups.length} />
        <SubTab k="auctions"   label="🔨 Auctions" badge={auctions.length} />
        <SubTab k="leads"      label="📞 Leads" badge={leads.length} />
        <SubTab k="withdrawals" label="💵 Withdrawals" />
        <SubTab k="platform-settings" label="⚙️ Platform Settings" />
        <SubTab k="mlm-guide" label="🌐 MLM Guide" />
        {isSuperAdmin && <SubTab k="tax" label="🔒 Tax Reserve" />}
      </div>

      {/* Overview: settings + stats */}
      {view === 'users' && <AdminUserControlsBlock />}

      {view === 'overview' && (
        <>
          {stats && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs" data-testid="mkt-admin-stats">
              {[['Stores', stats.stores_total, 'purple'], ['Active', stats.stores_active, 'emerald'], ['Banned', stats.stores_banned, 'rose'],
                ['Listings', stats.listings_total, 'cyan'], ['Active L.', stats.listings_active, 'yellow'], ['Leads (all)', stats.leads_total, 'sky'],
                ['Leads today', stats.leads_today, 'pink'], ['Custom cats', stats.categories_custom, 'orange']].map(([lbl, v, tint]) => (
                <div key={lbl} className={`rounded-xl bg-${tint}-500/15 border border-${tint}-400/50 p-2`}>
                  <div className="text-[10px] opacity-80">{lbl}</div>
                  <div className="text-lg font-black">{v}</div>
                </div>
              ))}
            </div>
          )}
          <div className="p-3 rounded-2xl bg-purple-500/10 border-2 border-purple-400/50 space-y-2">
            <div className="text-xs font-black text-purple-300 uppercase">⚙️ Marketplace Controls</div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              <label className="text-xs flex flex-col">Traffic baseline
                <input type="number" value={s.traffic_baseline} onChange={(e) => setS({ ...s, traffic_baseline: Number(e.target.value) })} data-testid="mkt-admin-baseline" className="px-2 py-1 rounded bg-slate-800 border border-slate-700" />
              </label>
              <label className="text-xs flex items-center gap-2 mt-4">
                <input type="checkbox" checked={!!s.fluctuation} onChange={(e) => setS({ ...s, fluctuation: e.target.checked })} data-testid="mkt-admin-fluct" /> Fluctuation
              </label>
              <label className="text-xs flex items-center gap-2 mt-4">
                <input type="checkbox" checked={!!s.enabled} onChange={(e) => setS({ ...s, enabled: e.target.checked })} data-testid="mkt-admin-enabled" /> Enabled
              </label>
            </div>
            <button onClick={save} data-testid="mkt-admin-save" className="px-3 py-1.5 rounded-lg bg-purple-500 text-white text-xs font-black">💾 Save Settings</button>
          </div>
        </>
      )}

      {/* Listings management */}
      {view === 'listings' && (
        <div className="space-y-1 max-h-[600px] overflow-y-auto">
          {ls.map((l) => (
            <div key={l.id} className="rounded bg-slate-900/70 p-2 flex items-center gap-2 text-xs" data-testid={`mkt-admin-listing-${l.id}`}>
              {l.images?.[0] ? <img src={abs(l.images[0])} alt="" className="w-10 h-10 object-cover rounded" /> : <div className="w-10 h-10 bg-slate-800 rounded flex items-center justify-center text-lg">📦</div>}
              <div className="flex-1 min-w-0">
                <div className="text-[11px] font-black truncate">{l.title} · <span className="text-yellow-300">{l.price_at} AT</span> {l.featured && '⭐'}</div>
                <div className="text-[9px] opacity-70 truncate">🏪 {l.shop_name} · slug: {l.shop_slug} · 👁 {l.views || 0} · 💬 {l.leads || 0}{l.active ? '' : ' · 🚫 HELD'}</div>
              </div>
              <button onClick={() => setEditL({ id: l.id, title: l.title, description: l.description, price_at: l.price_at, category: l.category, city: l.city, stock: l.stock, active: l.active, featured: l.featured })}
                      data-testid={`mkt-admin-edit-listing-${l.id}`} className="p-1 rounded bg-sky-500 text-white" title="Edit"><Edit3 size={10} /></button>
              {!l.featured && <button onClick={() => doAction({ listing_id: l.id, action: 'feature' })} className="p-1 rounded bg-purple-500 text-white" title="Feature"><Star size={10} /></button>}
              {l.featured && <button onClick={() => doAction({ listing_id: l.id, action: 'unfeature' })} className="p-1 rounded bg-slate-600" title="Unfeature">⭐</button>}
              <button onClick={() => doAction({ store_slug: l.shop_slug, action: 'verify' })} className="p-1 rounded bg-emerald-500 text-slate-900" title="Verify shop"><Shield size={10} /></button>
              <button onClick={() => doAction({ listing_id: l.id, action: l.active ? 'hold_listing' : 'release_listing' })} className="text-[9px] px-1 py-0.5 rounded bg-amber-500 text-slate-900 font-black">{l.active ? 'Hold' : 'Release'}</button>
              <button onClick={() => window.confirm('Force delete?') && doAction({ listing_id: l.id, action: 'delete_listing' })} className="p-1 rounded bg-red-900/60" title="Force delete"><Trash2 size={10} /></button>
            </div>
          ))}
          {ls.length === 0 && <div className="text-center opacity-60 py-6 text-xs">No listings yet</div>}
        </div>
      )}

      {/* Stores management */}
      {view === 'stores' && (
        <div className="space-y-1 max-h-[600px] overflow-y-auto">
          {stores.map((st) => (
            <div key={st.slug} className={`rounded p-2 flex items-center gap-2 text-xs ${st.banned ? 'bg-red-900/40 border border-red-500/50' : 'bg-slate-900/70'}`} data-testid={`mkt-admin-store-${st.slug}`}>
              {st.logo_url ? <img src={abs(st.logo_url)} alt="" className="w-10 h-10 object-cover rounded" /> : <div className="w-10 h-10 bg-slate-800 rounded flex items-center justify-center text-lg">🏪</div>}
              <div className="flex-1 min-w-0">
                <div className="text-[11px] font-black truncate">{st.shop_name || '—'} {st.verified && '✓'} {st.featured && '⭐'} {st.banned && '🚫'}</div>
                <div className="text-[9px] opacity-70 truncate">
                  slug: {st.slug} · plan: {st.plan_tier || '—'} · items: {st.listings_count || 0} · credits: {st.listing_credits || 0} ·&nbsp;
                  <button onClick={() => {
                    const v = window.prompt(`Set visitor count for "${st.shop_name}":`, st.visitor_count || 0);
                    if (v === null) return;
                    api.post('/admin/marketplace/store/visitor-count', { slug: st.slug, visitor_count: parseInt(v) || 0 })
                      .then(() => { load(); toast.success('✅ Visitor count updated'); })
                      .catch(() => toast.error('Failed'));
                  }} className="text-emerald-300 hover:text-emerald-100 cursor-pointer underline" title="Click to edit visitor count">
                    👁 {(st.visitor_count || 0).toLocaleString('en-IN')} ✎
                  </button>
                </div>
              </div>
              <button onClick={() => setEditSt({ slug: st.slug, shop_name: st.shop_name, tagline: st.tagline, city: st.city, category: st.category, whatsapp: st.whatsapp, delivery_whatsapp: st.delivery_whatsapp, verified: st.verified, featured: st.featured, banned: st.banned, listing_credits: st.listing_credits, plan_expires_at: st.plan_expires_at })}
                      data-testid={`mkt-admin-edit-store-${st.slug}`} className="p-1 rounded bg-sky-500 text-white"><Edit3 size={10} /></button>
              {!st.verified && <button onClick={() => doAction({ store_slug: st.slug, action: 'verify' })} className="p-1 rounded bg-emerald-500 text-slate-900" title="Verify"><Shield size={10} /></button>}
              {/* Advanced features toggle — for basic plan stores */}
              <button onClick={() => api.post('/admin/marketplace/store/toggle-advanced', { slug: st.slug, advanced_features_enabled: !st.advanced_features_enabled }).then(() => load()).catch(() => toast.error('Failed'))}
                      data-testid={`mkt-admin-toggle-adv-${st.slug}`}
                      className={`text-[9px] px-1.5 py-0.5 rounded font-black transition ${st.advanced_features_enabled ? 'bg-purple-600 text-white' : 'bg-slate-700 text-slate-400 hover:bg-slate-600'}`}
                      title={st.advanced_features_enabled ? 'Advanced features: ALLOWED — click to lock' : 'Advanced features: LOCKED — click to unlock for basic plan'}>
                {st.advanced_features_enabled ? '🔓 ADV' : '🔒 ADV'}
              </button>
              <button onClick={() => doAction({ store_slug: st.slug, action: st.banned ? 'unban_store' : 'ban_store' })}
                      className={`text-[9px] px-1 py-0.5 rounded font-black ${st.banned ? 'bg-emerald-500 text-slate-900' : 'bg-red-600 text-white'}`}>
                {st.banned ? 'Unban' : 'Ban'}
              </button>
            </div>
          ))}
          {stores.length === 0 && <div className="text-center opacity-60 py-6 text-xs">No stores yet</div>}
        </div>
      )}

      {/* Categories CRUD */}
      {view === 'categories' && (
        <div className="space-y-3">
          <div className="p-3 rounded-2xl bg-purple-500/10 border-2 border-purple-400/50 space-y-2">
            <div className="text-xs font-black text-purple-300 uppercase">🏷️ {catForm.id ? 'Edit' : 'Add'} Category</div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
              <input value={catForm.name} onChange={(e) => setCatForm({ ...catForm, name: e.target.value })} placeholder="Name" data-testid="mkt-admin-cat-name" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
              <input value={catForm.parent} onChange={(e) => setCatForm({ ...catForm, parent: e.target.value })} placeholder="Parent (optional)" data-testid="mkt-admin-cat-parent" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
              <input value={catForm.icon} onChange={(e) => setCatForm({ ...catForm, icon: e.target.value })} placeholder="Icon emoji" data-testid="mkt-admin-cat-icon" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" maxLength={4} />
              <select value={catForm.tint} onChange={(e) => setCatForm({ ...catForm, tint: e.target.value })} data-testid="mkt-admin-cat-tint" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs">
                <option value="from-indigo-500 to-purple-600">Indigo→Purple</option>
                <option value="from-emerald-500 to-cyan-600">Emerald→Cyan</option>
                <option value="from-orange-500 to-yellow-600">Orange→Yellow</option>
                <option value="from-pink-500 to-rose-600">Pink→Rose</option>
                <option value="from-sky-500 to-blue-600">Sky→Blue</option>
              </select>
            </div>
            <div className="flex gap-2">
              <button onClick={saveCat} data-testid="mkt-admin-cat-save" className="px-3 py-1.5 rounded-lg bg-purple-500 text-white text-xs font-black">💾 Save</button>
              {catForm.id && <button onClick={() => setCatForm({ id: null, name: '', parent: '', icon: '🏷️', tint: 'from-indigo-500 to-purple-600' })} className="px-3 py-1.5 rounded-lg bg-slate-700 text-white text-xs font-black">✕ Cancel</button>}
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {cats.map((c) => (
              <div key={c.id} data-testid={`mkt-admin-cat-${c.id}`}
                   className={`rounded-2xl bg-gradient-to-br ${c.tint || 'from-indigo-500 to-purple-600'} p-2 text-white shadow border border-white/20 relative`}>
                <div className="text-3xl leading-none">{c.icon || '🏷️'}</div>
                <div className="text-xs font-black mt-1 truncate">{c.name}</div>
                {c.parent && <div className="text-[9px] opacity-80 truncate">under {c.parent}</div>}
                <div className="absolute top-1 right-1 flex gap-1">
                  <button onClick={() => setCatForm({ id: c.id, name: c.name, parent: c.parent || '', icon: c.icon || '🏷️', tint: c.tint || 'from-indigo-500 to-purple-600' })} className="p-1 rounded bg-white/20 hover:bg-white/40" title="Edit"><Edit3 size={10} /></button>
                  <button onClick={() => delCat(c.id)} className="p-1 rounded bg-red-500/70 hover:bg-red-600" title="Delete"><Trash2 size={10} /></button>
                </div>
              </div>
            ))}
            {cats.length === 0 && <div className="col-span-full text-center opacity-60 py-6 text-xs">No custom categories yet. Add above ↑</div>}
          </div>
        </div>
      )}

      {/* All leads across shops */}
      {view === 'leads' && (
        <div className="space-y-1 max-h-[600px] overflow-y-auto">
          {leads.map((ld) => (
            <div key={ld.id} className="rounded bg-slate-900/70 p-2 text-xs" data-testid={`mkt-admin-lead-${ld.id}`}>
              <div className="text-[11px] font-black truncate">{ld.listing_title} · <span className="text-yellow-300">{ld.listing_price} AT</span> · <span className="text-emerald-300">{ld.request_type}</span></div>
              <div className="text-[9px] opacity-80 truncate">🏪 {ld.shop_name} ({ld.shop_slug}) · 👤 {ld.buyer_name || '—'} · 📞 {ld.buyer_phone || '—'} · 📍 {ld.buyer_city || '—'}</div>
              <div className="text-[9px] opacity-60">{(ld.created_at || '').slice(0, 19).replace('T', ' ')}</div>
            </div>
          ))}
          {leads.length === 0 && <div className="text-center opacity-60 py-6 text-xs">No leads yet</div>}
        </div>
      )}

      {/* Popups CRUD */}
      {view === 'popups' && (
        <div className="space-y-3" data-testid="mkt-admin-popups">
          <div className="p-3 rounded-2xl bg-purple-500/10 border-2 border-purple-400/50 space-y-2">
            <div className="text-xs font-black text-purple-300 uppercase">💬 {popForm.id ? 'Edit' : 'Add'} Live Activity Popup</div>
            <input value={popForm.text} onChange={(e) => setPopForm({ ...popForm, text: e.target.value })} placeholder="Sachin ne abhi X order kiya hai" data-testid="mkt-admin-popup-text"
                   className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" maxLength={200} />
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              <input value={popForm.icon} onChange={(e) => setPopForm({ ...popForm, icon: e.target.value })} placeholder="Icon" data-testid="mkt-admin-popup-icon"
                     className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" maxLength={4} />
              <select value={popForm.tint} onChange={(e) => setPopForm({ ...popForm, tint: e.target.value })} data-testid="mkt-admin-popup-tint"
                      className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs">
                <option value="from-emerald-500 to-teal-600">Emerald→Teal</option>
                <option value="from-pink-500 to-rose-600">Pink→Rose</option>
                <option value="from-amber-500 to-yellow-600">Amber→Yellow</option>
                <option value="from-indigo-500 to-purple-600">Indigo→Purple</option>
                <option value="from-sky-500 to-blue-600">Sky→Blue</option>
              </select>
              <label className="text-xs flex items-center gap-1 px-2 py-1.5 rounded bg-slate-800 border border-slate-700">
                <input type="checkbox" checked={!!popForm.active} onChange={(e) => setPopForm({ ...popForm, active: e.target.checked })} data-testid="mkt-admin-popup-active" /> Active
              </label>
            </div>
            <div className="flex gap-2">
              <button onClick={savePopup} data-testid="mkt-admin-popup-save" className="px-3 py-1.5 rounded-lg bg-purple-500 text-white text-xs font-black">💾 Save</button>
              {popForm.id && <button onClick={() => setPopForm({ id: null, text: '', icon: '🎉', tint: 'from-emerald-500 to-teal-600', active: true })} className="px-3 py-1.5 rounded-lg bg-slate-700 text-white text-xs font-black">✕ Cancel</button>}
            </div>
            {popForm.text && (
              <div className="mt-2">
                <div className="text-[10px] opacity-70 mb-1">Preview:</div>
                <div className={`inline-flex items-center gap-2 rounded-full pl-2 pr-4 py-2 bg-gradient-to-r ${popForm.tint} text-white text-xs font-black border border-white/40 shadow-lg`}>
                  <span className="text-lg">{popForm.icon}</span>
                  <span>{popForm.text}</span>
                </div>
              </div>
            )}
          </div>
          <div className="space-y-1 max-h-[400px] overflow-y-auto">
            {popups.map((p) => (
              <div key={p.id} className="rounded bg-slate-900/70 p-2 flex items-center gap-2 text-xs" data-testid={`mkt-admin-popup-${p.id}`}>
                <div className={`inline-flex items-center gap-1 rounded-full px-2 py-1 bg-gradient-to-r ${p.tint} text-white font-black shrink-0`}><span>{p.icon}</span></div>
                <div className="flex-1 min-w-0 truncate">{p.text}</div>
                <span className={`text-[9px] px-1.5 py-0.5 rounded font-black ${p.active ? 'bg-emerald-500 text-slate-900' : 'bg-slate-700 text-slate-300'}`}>{p.active ? 'ON' : 'OFF'}</span>
                <button onClick={() => setPopForm({ id: p.id, text: p.text, icon: p.icon || '🎉', tint: p.tint || 'from-emerald-500 to-teal-600', active: p.active })} className="p-1 rounded bg-sky-500 text-white"><Edit3 size={10} /></button>
                <button onClick={() => delPopup(p.id)} className="p-1 rounded bg-red-900/60"><Trash2 size={10} /></button>
              </div>
            ))}
            {popups.length === 0 && <div className="text-center opacity-60 py-6 text-xs">No popups yet. Add above ↑</div>}
          </div>
        </div>
      )}

      {/* Auctions management */}
      {view === 'auctions' && (
        <div className="space-y-2" data-testid="mkt-admin-auctions">
          {auctions.map((a) => (
            <div key={a.id} className="rounded-xl bg-slate-900/70 border border-slate-700 p-3 space-y-1 text-xs" data-testid={`mkt-admin-auction-${a.id}`}>
              <div className="flex items-center gap-2">
                {a.images?.[0] ? <img src={abs(a.images[0])} alt="" className="w-10 h-10 rounded object-cover" /> : <div className="w-10 h-10 rounded bg-slate-800 flex items-center justify-center text-lg">🔨</div>}
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] font-black truncate">{a.title} · <span className="text-yellow-300">{a.current_bid_at} AT</span></div>
                  <div className="text-[9px] opacity-70 truncate">Status: <b className="uppercase">{a.status}</b> · Winner: {a.winner_name || '—'} · Ends: {(a.end_at || '').slice(0, 16).replace('T', ' ')}</div>
                  {a.status_reason && <div className="text-[9px] text-amber-300">Reason: {a.status_reason}</div>}
                </div>
              </div>
              {a.status === 'live' && (
                <div className="flex gap-1 flex-wrap">
                  {['complete', 'hold', 'forwarded', 'postponed', 'cancelled'].map((act) => (
                    <button key={act} onClick={() => setAuctionAction({ aid: a.id, action: act, reason: '' })}
                            data-testid={`mkt-admin-auction-${act}-${a.id}`}
                            className={`text-[9px] px-2 py-1 rounded font-black ${act === 'complete' ? 'bg-emerald-500 text-slate-900' : act === 'cancelled' ? 'bg-red-600 text-white' : 'bg-amber-500 text-slate-900'}`}>
                      {act.toUpperCase()}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
          {auctions.length === 0 && <div className="text-center opacity-60 py-6 text-xs">No auctions yet</div>}
        </div>
      )}

      {/* Auction action modal (reason box) */}
      {auctionAction && (
        <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-3" onClick={() => setAuctionAction(null)}>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-sm rounded-2xl bg-slate-900 border-2 border-purple-400/60 p-4 space-y-2">
            <div className="text-sm font-black text-purple-300">Action: <span className="uppercase">{auctionAction.action}</span></div>
            <textarea rows={3} value={auctionAction.reason} onChange={(e) => setAuctionAction({ ...auctionAction, reason: e.target.value })} placeholder="Reason (optional but recommended)" data-testid="mkt-admin-auction-reason"
                      className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
            <div className="flex gap-2">
              <button onClick={async () => {
                try { const r = await api.post(`/admin/marketplace/auction/${auctionAction.aid}/action`, { action: auctionAction.action, reason: auctionAction.reason });
                      toast.success(r.data.winner_name ? `✅ Winner: ${r.data.winner_name}` : '✅ Done'); setAuctionAction(null); load(); }
                catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
              }} data-testid="mkt-admin-auction-confirm" className="flex-1 py-2 rounded bg-emerald-500 text-slate-900 text-xs font-black">Confirm</button>
              <button onClick={() => setAuctionAction(null)} className="flex-1 py-2 rounded bg-slate-700 text-white text-xs font-black">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Tax Reserve Wallet (Super Admin only) */}
      {view === 'tax' && isSuperAdmin && (
        <div className="space-y-3" data-testid="mkt-admin-tax">
          <div className="rounded-2xl bg-gradient-to-br from-emerald-500/20 via-teal-500/15 to-amber-500/20 border-2 border-emerald-400/60 p-4 shadow-lg relative overflow-hidden">
            <div className="text-[10px] font-black text-emerald-300 uppercase tracking-widest">🔒 Tax Reserve Wallet (Super Admin only)</div>
            <div className="text-4xl font-black text-emerald-200 mt-1" style={{ textShadow: '0 0 20px rgba(52,211,153,.4)' }}>{taxWallet ? Number(taxWallet.balance || 0).toFixed(2) : '—'} <span className="text-sm">AT</span></div>
            <div className="text-[10px] opacity-80 mt-1">
              Total credited: <b className="text-emerald-300">{Number(taxWallet?.total_credited || 0).toFixed(0)} AT</b> ·
              Total withdrawn: <b className="text-rose-300">{Number(taxWallet?.total_withdrawn || 0).toFixed(0)} AT</b>
            </div>
            <div className="text-[9px] opacity-70 mt-1">Auto-credited @ 10% of every completed order's commission. Sub-admins & vendors have zero access.</div>
          </div>

          {/* Monthly Tax Collected — line chart (last 12 months) */}
          {taxWallet?.monthly_series && (() => {
            const series = taxWallet.monthly_series || [];
            const maxV = Math.max(1, ...series.map((m) => m.amount));
            const W = 560, H = 130, P = 24;
            const stepX = (W - P * 2) / Math.max(1, series.length - 1);
            const pts = series.map((m, i) => ({
              x: P + i * stepX, y: H - P - (m.amount / maxV) * (H - P * 2), m: m.month, v: m.amount,
            }));
            const path = pts.map((p, i) => `${i === 0 ? 'M' : 'L'}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(' ');
            const area = `${path} L${pts[pts.length - 1].x},${H - P} L${pts[0].x},${H - P} Z`;
            const total = series.reduce((s, m) => s + m.amount, 0);
            return (
              <div className="rounded-2xl bg-slate-900/70 border border-emerald-400/40 p-3" data-testid="mkt-admin-tax-chart">
                <div className="flex items-center justify-between mb-1">
                  <div className="text-xs font-black text-emerald-300 uppercase">📈 Monthly Tax Collected · Last 12 Months</div>
                  <div className="text-[10px] opacity-80">Total: <b className="text-emerald-200">{total.toFixed(2)} AT</b></div>
                </div>
                <svg viewBox={`0 0 ${W} ${H}`} className="w-full h-32">
                  <defs>
                    <linearGradient id="taxg" x1="0" x2="0" y1="0" y2="1">
                      <stop offset="0%" stopColor="#34d399" stopOpacity="0.6" />
                      <stop offset="100%" stopColor="#0f766e" stopOpacity="0.05" />
                    </linearGradient>
                  </defs>
                  {[0, 0.5, 1].map((r, i) => (<line key={i} x1={P} x2={W - P} y1={P + r * (H - P * 2)} y2={P + r * (H - P * 2)} stroke="#475569" strokeDasharray="2 3" strokeWidth="0.5" />))}
                  <path d={area} fill="url(#taxg)" />
                  <path d={path} fill="none" stroke="#34d399" strokeWidth="2" style={{ filter: 'drop-shadow(0 0 4px rgba(52,211,153,.6))' }} />
                  {pts.map((p, i) => (
                    <g key={i}>
                      <circle cx={p.x} cy={p.y} r={3} fill="#34d399" />
                      {p.v > 0 && <text x={p.x} y={p.y - 6} fontSize="8" fill="#a7f3d0" textAnchor="middle" fontWeight="bold">{p.v.toFixed(1)}</text>}
                    </g>
                  ))}
                  {series.map((m, i) => (
                    <text key={m.month} x={P + i * stepX} y={H - 6} fontSize="8" fill="#94a3b8" textAnchor="middle">{m.month.slice(5)}/{m.month.slice(2, 4)}</text>
                  ))}
                </svg>
              </div>
            );
          })()}

          {/* Withdraw form */}
          <TaxWithdrawForm onDone={() => load()} />

          {/* Ledger */}
          <div className="rounded-2xl bg-slate-900/70 border border-slate-700 p-3" data-testid="mkt-admin-tax-ledger">
            <div className="text-xs font-black text-emerald-300 uppercase mb-2">📜 Ledger (last {taxWallet?.count || 0} entries)</div>
            <div className="max-h-96 overflow-y-auto space-y-1">
              {(taxWallet?.ledger || []).map((t) => (
                <div key={t.id} className="rounded bg-slate-800/60 p-2 text-xs flex items-center gap-2">
                  <div className={`w-2 h-2 rounded-full ${Number(t.amount) >= 0 ? 'bg-emerald-400' : 'bg-rose-400'}`} />
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] font-black truncate">{t.type} · {t.source || '—'}{t.source_id ? ` · ${String(t.source_id).slice(0, 8)}` : ''}</div>
                    <div className="text-[9px] opacity-70">{(t.created_at || '').slice(0, 19).replace('T', ' ')}{t.note ? ` · ${t.note}` : ''}</div>
                  </div>
                  <div className={`text-sm font-black ${Number(t.amount) >= 0 ? 'text-emerald-300' : 'text-rose-300'}`}>
                    {Number(t.amount) >= 0 ? '+' : ''}{Number(t.amount).toFixed(2)} AT
                  </div>
                </div>
              ))}
              {(!taxWallet?.ledger || taxWallet.ledger.length === 0) && <div className="text-center opacity-60 py-6 text-xs">No transactions yet</div>}
            </div>
          </div>
        </div>
      )}
      {view === 'tax' && !isSuperAdmin && (
        <div className="rounded-2xl bg-rose-500/10 border border-rose-500/60 p-6 text-center text-sm text-rose-200 font-black">🚫 Super Admin access only</div>
      )}

      {/* AT Withdraw Approval Center */}
      {view === 'withdrawals' && <ATWithdrawAdminPanel />}

      {/* MLM / Community Rewards Master Guide */}
      {view === 'mlm-guide' && (
        <div className="space-y-4" data-testid="mkt-admin-mlm-guide">
          <div className="rounded-2xl bg-gradient-to-br from-purple-500/20 via-indigo-500/15 to-fuchsia-500/20 border-2 border-purple-400/60 p-4">
            <div className="text-sm font-black text-purple-200 uppercase tracking-widest mb-1">🌐 Community Rewards — Admin Master Guide</div>
            <div className="text-[11px] opacity-80">Yeh guide aapko explain karta hai ki platform ke kaun se tools 4-level MLM Community Rewards use karte hain. Har level pe kya % milta hai, yeh neeche clearly diya gaya hai.</div>
          </div>

          {/* 4-Level Structure Explainer */}
          <div className="rounded-xl bg-slate-900 border border-purple-400/30 p-3 space-y-2">
            <div className="text-xs font-black text-purple-300 uppercase">4-Level Community Reward Structure</div>
            <div className="grid grid-cols-4 gap-1 text-center">
              {[['L1', '15%', 'Direct Referrer', 'from-purple-500 to-indigo-600'], ['L2', '7%', "Referrer's Referrer", 'from-indigo-500 to-blue-600'], ['L3', '5%', 'Level 3 Upline', 'from-blue-500 to-cyan-600'], ['L4', '3%', 'Level 4 Upline', 'from-cyan-500 to-teal-600']].map(([lv, pct, lbl, grad]) => (
                <div key={lv} className={`rounded-lg bg-gradient-to-b ${grad} p-2`}>
                  <div className="text-[10px] font-black text-white">{lv}</div>
                  <div className="text-xl font-black text-white">{pct}</div>
                  <div className="text-[8px] text-white/80 leading-tight">{lbl}</div>
                </div>
              ))}
            </div>
            <div className="text-[9px] opacity-70 text-center">Total distributed: 30% of source amount · Remaining 70% stays in platform</div>
          </div>

          {/* Tools Using MLM */}
          <div className="space-y-2">
            {[
              {
                icon: '🌟', title: 'Lead360 CRM — Subscription', color: 'emerald',
                source: 'Subscription payment (300 AT Basic / 600 AT Standard)',
                chain: 'Subscriber ka referral chain (upar 4 levels)',
                example: '300 AT subscription → L1=45 AT, L2=21 AT, L3=15 AT, L4=9 AT',
                note: 'Fire hota hai jab koi Lead360 subscription kharidta hai',
              },
              {
                icon: '💵', title: 'AT Wallet — Bank Withdraw Fee', color: 'amber',
                source: '2% of withdrawal amount (Admin ke approve karne par)',
                chain: 'Withdrawer ka referral chain (upar 4 levels)',
                example: '10,000 AT withdraw → Fee=200 AT → L1=30 AT, L2=14 AT, L3=10 AT, L4=6 AT',
                note: 'Admin jab withdraw approve karta hai tab distribute hota hai. Total fee amount ka 30% community mein jaata hai.',
              },
              {
                icon: '🔨', title: 'Auction — Lister Commission', color: 'rose',
                source: 'Lister-defined Commission % of final winning bid',
                chain: 'Auction seller/lister ka referral chain (upar 4 levels)',
                example: '10,000 AT winning bid · 10% commission → Pool=1000 AT → L1=150 AT, L2=70 AT, L3=50 AT, L4=30 AT · Seller gets 9,000 AT',
                note: 'Sirf tab apply hota hai jab lister ne auction create karte waqt commission % set kiya ho. 0% = no distribution.',
              },
              {
                icon: '🛒', title: 'Marketplace Orders — Order Commission', color: 'cyan',
                source: 'Platform commission % on each order (default 5%)',
                chain: "Buyer ka referral chain (upar 3 levels — L1=20%, L2=10%, L3=5%)",
                example: '1000 AT order · 5% commission=50 AT → Vendor gets 950 AT (approx) · Buyer L1=10 AT, L2=5 AT, L3=2.5 AT · Platform remainder',
                note: 'Marketplace order settlement system — slightly different percentages from standard 4-level. Yeh market standard hai.',
              },
            ].map((t) => (
              <div key={t.title} className={`rounded-xl bg-slate-900 border border-${t.color}-400/30 p-3 space-y-1.5`}>
                <div className="flex items-center gap-2">
                  <span className="text-lg">{t.icon}</span>
                  <div className="text-xs font-black text-white">{t.title}</div>
                  <span className={`ml-auto px-2 py-0.5 rounded-full text-[9px] font-black bg-${t.color}-500/20 text-${t.color}-300 border border-${t.color}-400/30`}>ACTIVE</span>
                </div>
                <div className="grid grid-cols-1 gap-1 text-[10px]">
                  <div><span className="text-slate-400">Source Amount:</span> <span className="text-white">{t.source}</span></div>
                  <div><span className="text-slate-400">Referral Chain:</span> <span className="text-white">{t.chain}</span></div>
                  <div className={`rounded bg-${t.color}-500/10 p-1.5 text-${t.color}-200 font-black`}>📊 {t.example}</div>
                  <div className="text-[9px] opacity-60 italic">{t.note}</div>
                </div>
              </div>
            ))}
          </div>

          {/* Summary Table */}
          <div className="rounded-xl bg-slate-900 border border-slate-700 overflow-hidden">
            <div className="bg-slate-800 px-3 py-2 text-[10px] font-black text-purple-300 uppercase">Quick Reference Table</div>
            <div className="overflow-x-auto">
              <table className="w-full text-[10px]">
                <thead>
                  <tr className="bg-slate-800/50">
                    <th className="text-left px-2 py-1.5 text-slate-400">Tool</th>
                    <th className="text-center px-2 py-1.5 text-purple-300">L1</th>
                    <th className="text-center px-2 py-1.5 text-indigo-300">L2</th>
                    <th className="text-center px-2 py-1.5 text-blue-300">L3</th>
                    <th className="text-center px-2 py-1.5 text-cyan-300">L4</th>
                    <th className="text-left px-2 py-1.5 text-slate-400">Trigger</th>
                  </tr>
                </thead>
                <tbody>
                  {[
                    ['Lead360 Sub.', '15%', '7%', '5%', '3%', 'Subscription buy'],
                    ['Withdraw Fee', '15%', '7%', '5%', '3%', 'Admin approve'],
                    ['Auction Comm.', '15%', '7%', '5%', '3%', 'Admin complete'],
                    ['Mkt. Orders', '20%*', '10%*', '5%*', '—', 'Order placed'],
                  ].map(([tool, l1, l2, l3, l4, trigger]) => (
                    <tr key={tool} className="border-t border-slate-800 hover:bg-slate-800/30">
                      <td className="px-2 py-1.5 font-black">{tool}</td>
                      <td className="px-2 py-1.5 text-center text-purple-300 font-black">{l1}</td>
                      <td className="px-2 py-1.5 text-center text-indigo-300 font-black">{l2}</td>
                      <td className="px-2 py-1.5 text-center text-blue-300 font-black">{l3}</td>
                      <td className="px-2 py-1.5 text-center text-cyan-300 font-black">{l4}</td>
                      <td className="px-2 py-1.5 opacity-70">{trigger}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="px-3 py-1.5 text-[9px] opacity-60">*Marketplace orders mein buyer ke 3-level referral chain pe commission split hota hai — alag structure hai.</div>
          </div>
        </div>
      )}

      {/* Platform Settings: AT Wallet + Tatkal Admin Controls */}
      {view === 'platform-settings' && (
        <div className="space-y-4" data-testid="mkt-admin-platform-settings">
          {/* AT Wallet Settings */}
          <div className="rounded-2xl bg-amber-500/10 border-2 border-amber-400/40 p-4 space-y-3">
            <div className="text-sm font-black text-amber-200">💰 AT Wallet — Withdraw Settings</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="space-y-1">
                <div className="text-[11px] text-amber-300 font-black">Withdraw Fee % <span className="opacity-60">(platform cut)</span></div>
                <div className="flex items-center gap-2">
                  <input type="number" min={0} max={20} step={0.5}
                         value={walletSettings.withdraw_fee_pct ?? 2}
                         onChange={(e) => setWalletSettings({ ...walletSettings, withdraw_fee_pct: parseFloat(e.target.value) })}
                         data-testid="mkt-admin-wallet-fee"
                         className="w-24 px-3 py-2 rounded-xl bg-slate-800 border border-amber-400/40 text-sm font-black" />
                  <span className="text-amber-300 font-black text-sm">%</span>
                </div>
                <div className="text-[10px] opacity-60">e.g. 2 means user withdraws 1000 AT → platform deducts 20 AT</div>
              </label>
              <label className="space-y-1">
                <div className="text-[11px] text-amber-300 font-black">Minimum Withdraw Amount <span className="opacity-60">(AT)</span></div>
                <div className="flex items-center gap-2">
                  <input type="number" min={100} max={100000} step={100}
                         value={walletSettings.withdraw_min_at ?? 5000}
                         onChange={(e) => setWalletSettings({ ...walletSettings, withdraw_min_at: parseFloat(e.target.value) })}
                         data-testid="mkt-admin-wallet-min"
                         className="w-32 px-3 py-2 rounded-xl bg-slate-800 border border-amber-400/40 text-sm font-black" />
                  <span className="text-amber-300 font-black text-sm">AT</span>
                </div>
              </label>
            </div>
            <div className="rounded-xl bg-amber-900/30 border border-amber-500/30 p-2 text-[11px] text-amber-200">
              Preview: User withdraws {walletSettings.withdraw_min_at ?? 5000} AT →
              fee = {((walletSettings.withdraw_min_at ?? 5000) * ((walletSettings.withdraw_fee_pct ?? 2) / 100)).toFixed(1)} AT →
              user gets {((walletSettings.withdraw_min_at ?? 5000) - (walletSettings.withdraw_min_at ?? 5000) * ((walletSettings.withdraw_fee_pct ?? 2) / 100)).toFixed(1)} AT equivalent in INR
            </div>
            <button onClick={async () => {
              try {
                await api.post('/admin/at-wallet/settings', walletSettings);
                toast.success('✅ AT Wallet settings saved!');
              } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
            }} data-testid="mkt-admin-wallet-save"
            className="px-4 py-2 rounded-xl bg-amber-500 text-slate-900 text-sm font-black">
              💾 Save Wallet Settings
            </button>
          </div>

          {/* Tatkal Settings */}
          <div className="rounded-2xl bg-rose-500/10 border-2 border-rose-400/40 p-4 space-y-3">
            <div className="text-sm font-black text-rose-200">🚨 Tatkal Deals — Time & Fee Settings</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <label className="space-y-1">
                <div className="text-[11px] text-rose-300 font-black">Max Deal Window (hours) <span className="opacity-60">(urgency cap)</span></div>
                <div className="flex items-center gap-2">
                  <input type="number" min={1} max={72} step={1}
                         value={tatkalSettings.max_window_hours ?? 6}
                         onChange={(e) => setTatkalSettings({ ...tatkalSettings, max_window_hours: parseFloat(e.target.value) })}
                         data-testid="mkt-admin-tatkal-window"
                         className="w-24 px-3 py-2 rounded-xl bg-slate-800 border border-rose-400/40 text-sm font-black" />
                  <span className="text-rose-300 font-black text-sm">hours</span>
                </div>
                <div className="text-[10px] opacity-60">Vendor max {tatkalSettings.max_window_hours ?? 6}h window set kar sakta hai deal ke liye</div>
              </label>
              <label className="space-y-1">
                <div className="text-[11px] text-rose-300 font-black">Default Booking Fee <span className="opacity-60">(AT)</span></div>
                <div className="flex items-center gap-2">
                  <input type="number" min={10} max={1000}
                         value={tatkalSettings.default_booking_fee ?? 50}
                         onChange={(e) => setTatkalSettings({ ...tatkalSettings, default_booking_fee: parseInt(e.target.value) })}
                         data-testid="mkt-admin-tatkal-fee"
                         className="w-24 px-3 py-2 rounded-xl bg-slate-800 border border-rose-400/40 text-sm font-black" />
                  <span className="text-rose-300 font-black text-sm">AT</span>
                </div>
              </label>
            </div>
            <button onClick={async () => {
              try {
                await api.post('/admin/tatkal/settings', tatkalSettings);
                toast.success('✅ Tatkal settings saved!');
              } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
            }} data-testid="mkt-admin-tatkal-save"
            className="px-4 py-2 rounded-xl bg-rose-600 text-white text-sm font-black">
              💾 Save Tatkal Settings
            </button>
          </div>

          {/* Telegram Status */}
          <AdminTelegramStatusBlock />
        </div>
      )}

      {/* Listing Edit Modal */}
      {editL && (
        <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-3" onClick={() => setEditL(null)} data-testid="mkt-admin-edit-listing-modal">
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-2xl bg-slate-900 border-2 border-purple-400/60 p-4 space-y-2 max-h-[90vh] overflow-y-auto">
            <div className="text-sm font-black text-purple-300">✏️ Edit Listing</div>
            <input value={editL.title || ''} onChange={(e) => setEditL({ ...editL, title: e.target.value })} placeholder="Title" data-testid="mkt-admin-editL-title" className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
            <textarea rows={3} value={editL.description || ''} onChange={(e) => setEditL({ ...editL, description: e.target.value })} placeholder="Description" data-testid="mkt-admin-editL-desc" className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
            <div className="grid grid-cols-2 gap-2">
              <input type="number" value={editL.price_at ?? ''} onChange={(e) => setEditL({ ...editL, price_at: Number(e.target.value) })} placeholder="Price (AT)" data-testid="mkt-admin-editL-price" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
              <input value={editL.category || ''} onChange={(e) => setEditL({ ...editL, category: e.target.value })} placeholder="Category" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
              <input value={editL.city || ''} onChange={(e) => setEditL({ ...editL, city: e.target.value })} placeholder="City" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
              <input type="number" value={editL.stock ?? ''} onChange={(e) => setEditL({ ...editL, stock: Number(e.target.value) })} placeholder="Stock" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
            </div>
            <div className="flex gap-3 text-xs">
              <label className="flex items-center gap-1"><input type="checkbox" checked={!!editL.active} onChange={(e) => setEditL({ ...editL, active: e.target.checked })} /> Active</label>
              <label className="flex items-center gap-1"><input type="checkbox" checked={!!editL.featured} onChange={(e) => setEditL({ ...editL, featured: e.target.checked })} /> Featured</label>
            </div>
            <div className="flex gap-2">
              <button onClick={saveListingEdit} data-testid="mkt-admin-editL-save" className="flex-1 py-2 rounded bg-emerald-500 text-slate-900 text-xs font-black">💾 Save</button>
              <button onClick={() => setEditL(null)} className="flex-1 py-2 rounded bg-slate-700 text-white text-xs font-black">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Store Edit Modal */}
      {editSt && (
        <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-3" onClick={() => setEditSt(null)} data-testid="mkt-admin-edit-store-modal">
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-2xl bg-slate-900 border-2 border-purple-400/60 p-4 space-y-2 max-h-[90vh] overflow-y-auto">
            <div className="text-sm font-black text-purple-300">✏️ Edit Store</div>
            <input value={editSt.shop_name || ''} onChange={(e) => setEditSt({ ...editSt, shop_name: e.target.value })} placeholder="Shop name" data-testid="mkt-admin-editS-name" className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
            <input value={editSt.tagline || ''} onChange={(e) => setEditSt({ ...editSt, tagline: e.target.value })} placeholder="Tagline" className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
            <div className="grid grid-cols-2 gap-2">
              <input value={editSt.city || ''} onChange={(e) => setEditSt({ ...editSt, city: e.target.value })} placeholder="City" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
              <input value={editSt.category || ''} onChange={(e) => setEditSt({ ...editSt, category: e.target.value })} placeholder="Category" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
              <input value={editSt.whatsapp || ''} onChange={(e) => setEditSt({ ...editSt, whatsapp: e.target.value })} placeholder="WhatsApp" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
              <input value={editSt.delivery_whatsapp || ''} onChange={(e) => setEditSt({ ...editSt, delivery_whatsapp: e.target.value })} placeholder="Delivery WA" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
              <input type="number" value={editSt.listing_credits ?? ''} onChange={(e) => setEditSt({ ...editSt, listing_credits: Number(e.target.value) })} placeholder="Extra listing credits" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
              <input value={editSt.plan_expires_at || ''} onChange={(e) => setEditSt({ ...editSt, plan_expires_at: e.target.value })} placeholder="Plan expires (ISO)" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
            </div>
            <div className="flex gap-3 text-xs flex-wrap">
              <label className="flex items-center gap-1"><input type="checkbox" checked={!!editSt.verified} onChange={(e) => setEditSt({ ...editSt, verified: e.target.checked })} /> Verified</label>
              <label className="flex items-center gap-1"><input type="checkbox" checked={!!editSt.featured} onChange={(e) => setEditSt({ ...editSt, featured: e.target.checked })} /> Featured</label>
              <label className="flex items-center gap-1"><input type="checkbox" checked={!!editSt.banned} onChange={(e) => setEditSt({ ...editSt, banned: e.target.checked })} /> Banned</label>
            </div>
            <div className="flex gap-2">
              <button onClick={saveStoreEdit} data-testid="mkt-admin-editS-save" className="flex-1 py-2 rounded bg-emerald-500 text-slate-900 text-xs font-black">💾 Save</button>
              <button onClick={() => setEditSt(null)} className="flex-1 py-2 rounded bg-slate-700 text-white text-xs font-black">Cancel</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============ AUCTIONS TAB (public feed + detail + bid + chat) ============
function AuctionsTab({ isGuest }) {
  const [rows, setRows] = useState([]);
  const [pick, setPick] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/marketplace/auctions', { params: { status: 'live', limit: 60 } }); setRows(r.data.auctions || []); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="space-y-3" data-testid="mkt-auctions-tab">
      <div className="rounded-2xl bg-gradient-to-r from-purple-600 via-pink-600 to-amber-600 p-3 flex items-center justify-between gap-2 shadow-lg">
        <div>
          <div className="text-sm font-black text-white">🔨 Anant Smart Auction Deals</div>
          <div className="text-[10px] text-white/90">Listing pack 1000 AT · 30-day max · 100 AT to participate · Highest bidder wins</div>
        </div>
        {!isGuest && (
          <button onClick={() => setShowCreate(true)} data-testid="mkt-auction-create-btn"
                  className="px-3 py-1.5 rounded-lg bg-white text-purple-700 text-xs font-black whitespace-nowrap">
            🔨 List Auction
          </button>
        )}
      </div>
      {loading && <div className="text-center opacity-60 py-8 text-xs">⏳ Loading auctions…</div>}
      {!loading && rows.length === 0 && <div className="text-center opacity-60 py-10 text-xs">Koi live auction nahi hai abhi</div>}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {rows.map((a) => (
          <button key={a.id} onClick={() => setPick(a.id)} data-testid={`mkt-auction-card-${a.id}`}
                  className="group text-left rounded-2xl overflow-hidden bg-gradient-to-br from-slate-900 via-purple-950 to-slate-900 hover:scale-[1.02] transition-transform relative"
                  style={{ boxShadow: '0 8px 32px -4px rgba(168,85,247,.4), 0 0 0 2px rgba(250,204,21,.4) inset' }}>
            {/* Golden shine sweep animation */}
            <span className="absolute inset-0 pointer-events-none overflow-hidden rounded-2xl">
              <span className="absolute -inset-y-8 -left-full w-1/2 rotate-12 bg-gradient-to-r from-transparent via-yellow-300/25 to-transparent"
                    style={{ animation: 'auction-shine 3.5s ease-in-out infinite' }} />
            </span>
            <div className="aspect-video bg-slate-800 relative">
              {a.images?.[0] ? <img src={abs(a.images[0])} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-6xl">🔨</div>}
              <div className="absolute inset-0 bg-gradient-to-t from-slate-900 via-transparent to-transparent" />
              <div className="absolute top-2 left-2 px-2.5 py-1 rounded-full bg-rose-500 text-white text-[10px] font-black flex items-center gap-1 shadow-lg"
                   style={{ animation: 'auction-live-blink 1.2s ease-in-out infinite' }}>
                <span className="w-1.5 h-1.5 rounded-full bg-white animate-ping" /> LIVE
              </div>
              {(a.video_url) && <span className="absolute top-2 right-2 px-2 py-0.5 rounded bg-black/70 text-white text-[9px] font-black">🎬 Video</span>}
              <AuctionCountdown endAt={a.end_at} compact />
            </div>
            <div className="p-3 space-y-1 relative z-10">
              <div className="text-sm font-black truncate bg-gradient-to-r from-yellow-300 via-amber-200 to-yellow-300 bg-clip-text text-transparent"
                   style={{ textShadow: '0 0 12px rgba(250,204,21,.3)' }}>
                🔨 {a.title}
              </div>
              <div className="flex items-baseline gap-1">
                <span className="text-[9px] opacity-70 font-black uppercase">Current Bid</span>
                <span className="text-lg font-black text-yellow-300" style={{ textShadow: '0 0 10px rgba(250,204,21,.6)' }}>{a.current_bid_at} AT</span>
              </div>
              <div className="text-[9px] opacity-70 truncate">👥 {a.participant_count || 0} bidders · 🔨 {a.bid_count || 0} bids{a.city ? ` · 📍 ${a.city}` : ''}</div>
            </div>
          </button>
        ))}
      </div>
      <style>{`
        @keyframes auction-shine {
          0% { transform: translateX(-100%) rotate(12deg); }
          60% { transform: translateX(250%) rotate(12deg); }
          100% { transform: translateX(250%) rotate(12deg); }
        }
        @keyframes auction-live-blink {
          0%,100% { transform: scale(1); box-shadow: 0 4px 20px -2px rgba(244,63,94,.6); }
          50% { transform: scale(1.05); box-shadow: 0 6px 30px -2px rgba(244,63,94,1); }
        }
      `}</style>
      {pick && <AuctionModal aid={pick} onClose={() => { setPick(null); load(); }} isGuest={isGuest} />}
      {showCreate && <AuctionCreateModal onClose={(refresh) => { setShowCreate(false); if (refresh) load(); }} />}
    </div>
  );
}

function AuctionCountdown({ endAt, compact }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const t = setInterval(() => setNow(Date.now()), 1000); return () => clearInterval(t); }, []);
  const remaining = Math.max(0, new Date(endAt).getTime() - now);
  const d = Math.floor(remaining / (1000 * 60 * 60 * 24));
  const h = Math.floor((remaining / (1000 * 60 * 60)) % 24);
  const m = Math.floor((remaining / (1000 * 60)) % 60);
  const s = Math.floor((remaining / 1000) % 60);
  const pad = (n) => String(n).padStart(2, '0');
  const expired = remaining === 0;
  if (compact) {
    return (
      <div className={`absolute bottom-1 right-1 px-2 py-1 rounded font-mono font-black text-[10px] text-white ${expired ? 'bg-slate-700' : 'bg-black/70 border border-yellow-400/60'}`}>
        {expired ? '⌛ ENDED' : `${pad(d)}:${pad(h)}:${pad(m)}:${pad(s)}`}
      </div>
    );
  }
  return (
    <div className="text-center" data-testid="mkt-auction-timer">
      <div className={`inline-flex gap-1 sm:gap-2 font-mono font-black ${expired ? 'text-slate-500' : 'text-yellow-300'}`}
           style={{ textShadow: expired ? 'none' : '0 0 10px rgba(250, 204, 21, .6)' }}>
        {[[d, 'D'], [h, 'H'], [m, 'M'], [s, 'S']].map(([v, lbl], i) => (
          <div key={i} className="rounded-lg bg-slate-900 border-2 border-yellow-400/60 px-2 sm:px-3 py-1 sm:py-2">
            <div className="text-2xl sm:text-4xl leading-none">{pad(v)}</div>
            <div className="text-[8px] sm:text-[10px] opacity-80 mt-0.5">{lbl}</div>
          </div>
        ))}
      </div>
      {expired && <div className="text-[10px] text-rose-300 font-black mt-1">⌛ Auction ENDED</div>}
    </div>
  );
}

function AuctionCreateModal({ onClose }) {
  const [form, setForm] = useState({ title: '', description: '', base_price_at: '', min_increment_at: 50, category: '', city: '', images: [], video_url: '', duration_days: 7, commission_percent: '' });
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const cameraRef = useRef(null);
  const galleryRef = useRef(null);

  const uploadFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const f of files.slice(0, 6 - form.images.length)) {
        const fd = new FormData(); fd.append('file', f);
        try {
          const r = await api.post('/webshop/upload/image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
          if (r.data?.url) uploaded.push(r.data.url);
        } catch {}
      }
      setForm((p) => ({ ...p, images: [...p.images, ...uploaded].slice(0, 6) }));
      if (uploaded.length) toast.success(`✅ ${uploaded.length} uploaded`);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const submit = async () => {
    if (!form.title || !form.base_price_at) { toast.error('Title + base price required'); return; }
    if (!window.confirm('Auction listing = 500 AT. Proceed?')) return;
    setBusy(true);
    try {
      const payload = { ...form, base_price_at: Number(form.base_price_at), min_increment_at: Number(form.min_increment_at || 50), duration_days: Number(form.duration_days) };
      if (form.commission_percent !== '' && form.commission_percent !== null) payload.commission_percent = Number(form.commission_percent);
      await api.post('/marketplace/auction/create', payload);
      toast.success('✅ Auction live · 500 AT deducted'); onClose(true);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/85 flex items-center justify-center p-3" onClick={() => onClose(false)} data-testid="mkt-auction-create-modal">
      <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md rounded-2xl bg-slate-900 border-2 border-purple-400/60 p-4 space-y-2 max-h-[95vh] overflow-y-auto">
        <div className="text-sm font-black text-purple-300">🔨 New Auction · 500 AT + max 30 days</div>
        <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Auction title *" data-testid="mkt-auction-title" className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
        <textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="Description" className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
        <div className="grid grid-cols-2 gap-2">
          <input type="number" value={form.base_price_at} onChange={(e) => setForm({ ...form, base_price_at: e.target.value })} placeholder="Base price (AT) *" data-testid="mkt-auction-base" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          <input type="number" value={form.min_increment_at} onChange={(e) => setForm({ ...form, min_increment_at: e.target.value })} placeholder="Min bid increment (AT)" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="City" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Category" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          <input type="number" value={form.duration_days} min={1} max={30} onChange={(e) => setForm({ ...form, duration_days: e.target.value })} placeholder="Days (1-30)" data-testid="mkt-auction-days" className="col-span-2 px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
        </div>
        {/* Lister Community Commission % */}
        <div className="rounded-lg bg-purple-500/10 border border-purple-400/30 p-2 space-y-1">
          <div className="text-[10px] font-black text-purple-300 uppercase">🌐 Community Commission % (Optional)</div>
          <div className="text-[9px] opacity-70">Winning bid ka yeh % aapki referral chain ke 4 levels mein distribute hoga (L1=15%, L2=7%, L3=5%, L4=3%)</div>
          <input type="number" min={0} max={50} step={0.5} value={form.commission_percent} onChange={(e) => setForm({ ...form, commission_percent: e.target.value })}
                 placeholder="Commission % (e.g. 10) — 0 to skip" data-testid="mkt-auction-commission"
                 className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          {form.commission_percent > 0 && form.base_price_at > 0 && (
            <div className="text-[9px] text-emerald-300">
              Example: {form.base_price_at} AT bid → Seller gets {(form.base_price_at * (1 - form.commission_percent / 100)).toFixed(0)} AT · Community pool = {(form.base_price_at * form.commission_percent / 100).toFixed(0)} AT
            </div>
          )}
        </div>

        {/* Media uploads — Camera / Gallery / URL / Video */}
        <div className="rounded-lg bg-slate-800/50 border border-slate-700 p-2 space-y-2">
          <div className="text-[10px] font-black text-purple-300 uppercase">🖼 Photos (max 6) · Camera / Gallery / URL</div>
          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => cameraRef.current?.click()} disabled={uploading} data-testid="mkt-auction-camera"
                    className="flex items-center justify-center gap-1 px-2 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-black disabled:opacity-50">
              <Camera size={13} /> {uploading ? '⏳' : '📸 Camera'}
            </button>
            <button onClick={() => galleryRef.current?.click()} disabled={uploading} data-testid="mkt-auction-gallery"
                    className="flex items-center justify-center gap-1 px-2 py-2 rounded-lg bg-slate-700 hover:bg-slate-600 text-xs font-black disabled:opacity-50">
              <ImageIcon size={13} /> {uploading ? '⏳' : '🖼 Gallery'}
            </button>
            <input ref={cameraRef} type="file" accept="image/*" capture="environment" onChange={uploadFiles} className="hidden" />
            <input ref={galleryRef} type="file" accept="image/*" multiple onChange={uploadFiles} className="hidden" />
          </div>
          <input placeholder="🔗 Or paste image URL — press Enter"
                 onKeyDown={(e) => { if (e.key === 'Enter' && e.target.value.trim()) { setForm((p) => ({ ...p, images: [...p.images, e.target.value.trim()].slice(0, 6) })); e.target.value = ''; } }}
                 className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          {form.images.length > 0 && (
            <div className="grid grid-cols-3 gap-1" data-testid="mkt-auction-images-preview">
              {form.images.map((im, i) => (
                <div key={i} className="relative">
                  <img src={abs(im)} alt="" className="w-full aspect-square object-cover rounded" />
                  <button onClick={() => setForm((p) => ({ ...p, images: p.images.filter((_, j) => j !== i) }))} className="absolute top-0 right-0 w-5 h-5 rounded-bl bg-black/70 text-white text-xs">✕</button>
                </div>
              ))}
            </div>
          )}
          <input value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })} data-testid="mkt-auction-video"
                 placeholder="🎬 Video URL (YouTube / direct .mp4)" className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
        </div>

        <div className="flex gap-2">
          <button onClick={submit} disabled={busy} data-testid="mkt-auction-submit" className="flex-1 py-2 rounded bg-emerald-500 text-slate-900 text-xs font-black disabled:opacity-50">{busy ? '⏳' : '💰 Pay 500 AT & Launch'}</button>
          <button onClick={() => onClose(false)} className="flex-1 py-2 rounded bg-slate-700 text-white text-xs font-black">Cancel</button>
        </div>
      </div>
    </div>
  );
}

function AuctionModal({ aid, onClose, isGuest }) {
  const [a, setA] = useState(null);
  const [busy, setBusy] = useState(false);
  const [bidAmt, setBidAmt] = useState('');
  const [chat, setChat] = useState('');
  const load = useCallback(async () => {
    try { const r = await api.get(`/marketplace/auction/${aid}`); setA(r.data.auction); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Load failed'); onClose(); }
  }, [aid, onClose]);
  useEffect(() => { load(); const t = setInterval(load, 6000); return () => clearInterval(t); }, [load]);
  if (!a) return null;
  const isEnded = a.status !== 'live' || (a.end_at && new Date(a.end_at) <= new Date());
  const participate = async () => {
    if (!window.confirm('Participate = 100 AT. Proceed?')) return;
    setBusy(true);
    try { await api.post(`/marketplace/auction/${aid}/participate`); toast.success('✅ Joined! Ab bid lagao'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  const bid = async () => {
    const amt = Number(bidAmt);
    if (!amt) return;
    setBusy(true);
    try { await api.post(`/marketplace/auction/${aid}/bid`, { amount_at: amt }); toast.success(`✅ Bid placed: ${amt} AT`); setBidAmt(''); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  const send = async () => {
    if (!chat.trim()) return;
    try { await api.post(`/marketplace/auction/${aid}/chat`, { text: chat }); setChat(''); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const minBid = Number(a.current_bid_at || 0) + Number(a.min_increment_at || 50);
  const myUsername = (JSON.parse(localStorage.getItem('user') || '{}') || {}).username;
  const iAmWinner = a.status === 'completed' && a.winner_name && myUsername === a.winner_name;

  // Social share via OG-preview URL — WA/IG/FB scrape and show actual image
  const shareOgUrl = `${process.env.REACT_APP_BACKEND_URL || ''}/api/share/marketplace/auction/${aid}`;
  const shareText = `🔨 LIVE AUCTION · ${a.title}\n💰 Current bid: ${a.current_bid_at} AT · ⏰ Ends: ${(a.end_at || '').slice(0, 16).replace('T', ' ')}\n${shareOgUrl}`;
  const shareAuction = async (kind) => {
    // Try native share with image file first (attaches photo directly)
    if (a.images?.[0]) {
      try {
        const src = a.images[0].startsWith('http') ? a.images[0] : `${process.env.REACT_APP_BACKEND_URL || ''}${a.images[0]}`;
        const resp = await fetch(src, { credentials: 'omit' });
        if (resp.ok) {
          const blob = await resp.blob();
          const file = new File([blob], `${(a.title || 'auction').replace(/\W+/g, '_')}.jpg`, { type: blob.type || 'image/jpeg' });
          if (navigator.canShare && navigator.canShare({ files: [file] })) {
            try { await navigator.share({ title: a.title, text: shareText, files: [file] }); toast.success('✅ Shared'); return; }
            catch (e) { if (e && e.name === 'AbortError') return; }
          }
        }
      } catch {}
    }
    if (kind === 'wa') window.open(`https://api.whatsapp.com/send?text=${encodeURIComponent(shareText)}`, '_blank');
    else if (kind === 'fb') window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareOgUrl)}`, '_blank');
    else if (kind === 'ig') window.open('https://www.instagram.com/', '_blank');
    else navigator.clipboard.writeText(shareText).then(() => toast.success('Copied'));
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/90 flex items-start sm:items-center justify-center p-2 overflow-y-auto" onClick={onClose} data-testid="mkt-auction-modal">
      <div onClick={(e) => e.stopPropagation()}
           className="w-full max-w-2xl my-2 rounded-2xl bg-gradient-to-br from-slate-900 via-purple-950 to-slate-900 max-h-[95vh] overflow-y-auto relative"
           style={{ boxShadow: '0 25px 80px -10px rgba(250,204,21,.5), 0 0 0 2px rgba(250,204,21,.6) inset' }}>
        {/* Golden shine sweep on modal */}
        <span className="absolute inset-0 pointer-events-none overflow-hidden rounded-2xl">
          <span className="absolute -inset-y-16 -left-full w-1/3 rotate-12 bg-gradient-to-r from-transparent via-yellow-300/15 to-transparent"
                style={{ animation: 'auction-shine 5s ease-in-out infinite' }} />
        </span>
        <div className="relative">
          {a.images?.[0] && <img src={abs(a.images[0])} alt="" className="w-full aspect-video object-cover" />}
          <div className="absolute inset-0 bg-gradient-to-t from-slate-900/80 via-transparent to-transparent" />
          <button onClick={onClose} className="absolute top-2 right-2 w-9 h-9 rounded-full bg-black/70 text-white font-black" data-testid="mkt-auction-close">✕</button>
          <div className="absolute top-2 left-2 px-3 py-1 rounded-full bg-rose-500 text-white text-xs font-black flex items-center gap-1"
               style={{ animation: 'auction-live-blink 1.2s ease-in-out infinite' }}>
            <span className="w-2 h-2 rounded-full bg-white animate-ping" /> LIVE AUCTION
          </div>
        </div>
        <div className="p-4 space-y-3 relative z-10">
          <div>
            <div className="text-2xl font-black bg-gradient-to-r from-yellow-300 via-amber-200 to-yellow-300 bg-clip-text text-transparent"
                 style={{ textShadow: '0 0 20px rgba(250,204,21,.4)' }}>🔨 {a.title}</div>
            <div className="text-[11px] opacity-70">{a.city && `📍 ${a.city}`} {a.category && ` · 🏷 ${a.category}`}</div>
          </div>
          {a.description && <div className="text-xs opacity-90 whitespace-pre-wrap">{a.description}</div>}

          {/* Video display */}
          {a.video_url && (() => {
            const yt = (a.video_url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{6,})/) || [])[1];
            return (
              <div className="rounded-xl overflow-hidden border-2 border-yellow-400/40 bg-black" data-testid="mkt-auction-video">
                {yt ? (
                  <iframe title="video" src={`https://www.youtube.com/embed/${yt}`} className="w-full aspect-video" allow="autoplay; encrypted-media" allowFullScreen />
                ) : (
                  <video controls src={abs(a.video_url)} className="w-full aspect-video bg-black" />
                )}
              </div>
            );
          })()}

          {/* Image gallery */}
          {a.images?.length > 1 && (
            <div className="grid grid-cols-4 gap-1" data-testid="mkt-auction-gallery">
              {a.images.slice(1, 9).map((im, i) => <img key={i} src={abs(im)} alt="" className="w-full aspect-square object-cover rounded" />)}
            </div>
          )}

          <AuctionCountdown endAt={a.end_at} />

          {/* Social Share row */}
          <div className="rounded-xl bg-slate-800/60 border border-yellow-400/40 p-2" data-testid="mkt-auction-share">
            <div className="text-[10px] font-black text-yellow-300 uppercase mb-1">📢 Share this auction</div>
            <div className="grid grid-cols-4 gap-1">
              <button onClick={() => shareAuction('wa')} data-testid="mkt-auction-share-wa" className="py-1.5 rounded bg-green-600 text-white text-[10px] font-black">📱 WA</button>
              <button onClick={() => shareAuction('ig')} data-testid="mkt-auction-share-ig" className="py-1.5 rounded bg-gradient-to-r from-purple-500 to-pink-500 text-white text-[10px] font-black">📸 IG</button>
              <button onClick={() => shareAuction('fb')} data-testid="mkt-auction-share-fb" className="py-1.5 rounded bg-blue-600 text-white text-[10px] font-black">📘 FB</button>
              <button onClick={() => shareAuction('copy')} data-testid="mkt-auction-share-copy" className="py-1.5 rounded bg-slate-700 text-white text-[10px] font-black">🔗 Copy</button>
            </div>
          </div>

          {/* Winner banner */}
          {a.status === 'completed' && a.winner_name && (
            <div className={`rounded-xl p-3 text-center font-black relative overflow-hidden ${iAmWinner ? 'bg-gradient-to-r from-emerald-400 via-yellow-300 to-emerald-400 text-slate-900' : 'bg-slate-800 text-yellow-300'}`}
                 style={iAmWinner ? { boxShadow: '0 0 40px rgba(52,211,153,.8)', animation: 'winner-pulse 1.4s ease-in-out infinite' } : {}}
                 data-testid="mkt-auction-winner-banner">
              {iAmWinner ? '🏆 AUCTION GRABBED SUCCESS! 🎉' : `🏆 Winner: ${a.winner_name} · ${a.current_bid_at} AT`}
              {a.settlement?.status === 'settled' && <div className="text-[10px] mt-1 opacity-90">💰 {a.settlement.transferred_at} AT auto-transferred to seller ✅</div>}
            </div>
          )}
          {['hold', 'forwarded', 'postponed', 'cancelled'].includes(a.status) && (
            <div className="rounded-xl bg-amber-500/20 border border-amber-400/60 p-2 text-xs" data-testid="mkt-auction-status-banner">
              Status: <b className="uppercase">{a.status}</b>{a.status_reason ? ` · Reason: ${a.status_reason}` : ''}
            </div>
          )}

          {/* Current bid — dramatic golden card */}
          <div className="rounded-xl p-3 flex items-center justify-between relative overflow-hidden"
               style={{ background: 'linear-gradient(135deg, rgba(250,204,21,.15), rgba(217,119,6,.15))',
                        boxShadow: '0 0 30px rgba(250,204,21,.25), 0 0 0 2px rgba(250,204,21,.5) inset' }}>
            <div>
              <div className="text-[10px] opacity-70 uppercase font-black">Highest Bid</div>
              <div className="text-3xl font-black text-yellow-300" style={{ textShadow: '0 0 15px rgba(250,204,21,.7)' }}>{a.current_bid_at} AT</div>
              <div className="text-[10px] opacity-80">by <b>{a.current_bidder_name || '—'}</b></div>
            </div>
            <div className="text-right text-[10px] opacity-80">
              <div>👥 {(a.participants || []).length} bidders</div>
              <div>🔨 {(a.bids || []).length} bids</div>
              <div>+ min inc: {a.min_increment_at} AT</div>
            </div>
          </div>
          <style>{`@keyframes winner-pulse { 0%,100% { transform: scale(1); } 50% { transform: scale(1.02); } }`}</style>

          {/* Actions */}
          {!isEnded && !isGuest && !a.is_seller && (
            !a.is_participant ? (
              <button onClick={participate} disabled={busy} data-testid="mkt-auction-participate"
                      className="w-full py-3 rounded-2xl bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-black disabled:opacity-50">
                💰 Participate in Bidding · Pay 100 AT
              </button>
            ) : (
              <div className="grid grid-cols-3 gap-2 items-center">
                <input type="number" value={bidAmt} onChange={(e) => setBidAmt(e.target.value)} placeholder={`Min ${minBid} AT`} data-testid="mkt-auction-bid-input"
                       className="col-span-2 px-3 py-2 rounded-xl bg-slate-800 border border-yellow-400/60 text-sm font-black" />
                <button onClick={bid} disabled={busy || !bidAmt} data-testid="mkt-auction-bid-btn"
                        className="py-2 rounded-xl bg-emerald-500 text-slate-900 text-xs font-black disabled:opacity-50">
                  🔨 Bid
                </button>
              </div>
            )
          )}
          {isGuest && <a href="/login" className="block text-center py-2 rounded-xl bg-yellow-400 text-slate-900 text-xs font-black">🔐 Login to participate</a>}

          {/* Bids table (big window) */}
          <div className="rounded-xl bg-slate-800/60 border border-slate-700 p-2" data-testid="mkt-auction-bids-list">
            <div className="text-[10px] font-black text-purple-300 uppercase mb-1">🔨 All Bids ({(a.bids || []).length})</div>
            <div className="max-h-40 overflow-y-auto space-y-1">
              {(a.bids || []).slice().reverse().map((b, i) => (
                <div key={i} className="flex items-center justify-between text-xs">
                  <span><b className="text-yellow-300">{b.amount_at} AT</b> — {b.user_name}</span>
                  <span className="opacity-60 text-[9px]">{(b.at || '').slice(11, 19)}</span>
                </div>
              ))}
              {(!a.bids || a.bids.length === 0) && <div className="text-center opacity-60 py-3 text-[10px]">No bids yet — pehla banno!</div>}
            </div>
          </div>

          {/* Chat box */}
          {(a.is_participant || a.is_seller) && !isEnded && (
            <div className="rounded-xl bg-slate-800/60 border border-slate-700 p-2" data-testid="mkt-auction-chat">
              <div className="text-[10px] font-black text-emerald-300 uppercase mb-1">💬 Auction Chat</div>
              <div className="max-h-32 overflow-y-auto space-y-1 mb-2">
                {(a.chat || []).map((m, i) => (
                  <div key={i} className="text-xs"><b className="text-purple-300">{m.user_name}:</b> {m.text}</div>
                ))}
                {(!a.chat || a.chat.length === 0) && <div className="text-center opacity-60 py-2 text-[10px]">No messages yet</div>}
              </div>
              <div className="flex gap-1">
                <input value={chat} onChange={(e) => setChat(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && send()} placeholder="Type message…"
                       data-testid="mkt-auction-chat-input" className="flex-1 px-2 py-1.5 rounded bg-slate-900 border border-slate-700 text-xs" />
                <button onClick={send} data-testid="mkt-auction-chat-send" className="px-3 py-1.5 rounded bg-emerald-500 text-slate-900 text-xs font-black">Send</button>
              </div>
            </div>
          )}

          {/* Re-auction after expiry (seller only) */}
          {isEnded && a.is_seller && a.status !== 'completed' && (
            <button onClick={async () => {
              const days = Number(window.prompt('Re-auction ke liye kitne days? (max 30)', '7')) || 7;
              if (!window.confirm(`Re-auction = 1000 AT for ${days} days. Proceed?`)) return;
              try { await api.post(`/marketplace/auction/${aid}/reauction`, null, { params: { days } }); toast.success('✅ Re-auctioned'); load(); }
              catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
            }} data-testid="mkt-auction-reauction"
                    className="w-full py-2 rounded-xl bg-amber-500 text-slate-900 text-xs font-black">🔁 Re-Auction · 1000 AT</button>
          )}
        </div>
      </div>
    </div>
  );
}

// ============ Active gate ============
function ActiveGate() {
  return (
    <div className="rounded-2xl bg-purple-500/15 border-2 border-purple-400/60 p-6 text-center space-y-2">
      <Award size={28} className="mx-auto text-purple-300" />
      <div className="text-lg font-black">Plan required</div>
      <div className="text-xs opacity-80">Plans tab me jaakar Basic / Standard / Prime plan lein — phir Store & Listings unlock.</div>
    </div>
  );
}

// ============ Tax Withdraw Form (Super Admin only) ============
function TaxWithdrawForm({ onDone }) {
  const [amount, setAmount] = useState('');
  const [toUser, setToUser] = useState('');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
  useEffect(() => { setToUser(currentUser?.id || ''); }, [currentUser?.id]);
  const submit = async () => {
    if (!Number(amount) || !toUser || !reason.trim()) { toast.error('All fields required'); return; }
    if (!window.confirm(`Withdraw ${amount} AT from Tax Reserve to user ${toUser.slice(0, 12)}…?`)) return;
    setBusy(true);
    try {
      const r = await api.post('/admin/marketplace/tax-wallet/withdraw', { amount: Number(amount), to_user_id: toUser, reason });
      toast.success(`✅ ${amount} AT withdrawn · New balance: ${r.data.new_balance.toFixed(2)} AT`);
      setAmount(''); setReason(''); onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl bg-slate-900/70 border border-slate-700 p-3 space-y-2" data-testid="mkt-admin-tax-withdraw">
      <div className="text-xs font-black text-emerald-300 uppercase">💸 Withdraw from Tax Reserve</div>
      <div className="grid grid-cols-2 gap-2">
        <input type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Amount (AT) *" data-testid="tax-withdraw-amount" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
        <input value={toUser} onChange={(e) => setToUser(e.target.value)} placeholder="Recipient user_id *" data-testid="tax-withdraw-to" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
      </div>
      <input value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Reason (required) *" data-testid="tax-withdraw-reason" maxLength={200} className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
      <button onClick={submit} disabled={busy} data-testid="tax-withdraw-submit"
              className="w-full py-2 rounded bg-gradient-to-r from-emerald-500 to-teal-500 text-slate-900 text-xs font-black disabled:opacity-50">
        {busy ? '⏳' : '💸 Withdraw'}
      </button>
    </div>
  );
}

// ============ ALL SHOPS DIRECTORY (public, guest-friendly) ============
function ShopsTab() {
  const [shops, setShops] = useState([]);
  const [q, setQ] = useState({ search: '', city: '', category: '', kind: '' });
  const [topRatedOnly, setTopRatedOnly] = useState(false);
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/marketplace/shops', { params: { ...q, limit: 120 } }); setShops(r.data.shops || []); }
    finally { setLoading(false); }
  }, [q]);
  useEffect(() => { load(); }, [load]);
  // Client-side "Top Rated" filter — hero-tint qualifying shops and (when toggled) show only them
  const visibleShops = topRatedOnly ? shops.filter((s) => s.is_top_rated) : shops;
  return (
    <div className="space-y-3" data-testid="mkt-shops-tab">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <input value={q.search} onChange={(e) => setQ({ ...q, search: e.target.value })} placeholder="🔍 Search shop name" data-testid="mkt-shops-search"
               className="col-span-2 px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-sm" />
        <input value={q.city} onChange={(e) => setQ({ ...q, city: e.target.value })} placeholder="📍 City" data-testid="mkt-shops-city"
               className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-sm" />
        <select value={q.kind} onChange={(e) => setQ({ ...q, kind: e.target.value })} className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-sm" data-testid="mkt-shops-kind">
          <option value="">All types</option>
          <option value="product">📦 Product shops</option>
          <option value="service">🔧 Service shops</option>
          <option value="showroom">🚘 Showrooms</option>
        </select>
        <input value={q.category} onChange={(e) => setQ({ ...q, category: e.target.value })} placeholder="🏷️ Category" data-testid="mkt-shops-cat"
               className="col-span-2 sm:col-span-4 px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-sm" />
      </div>
      {/* Sort chips */}
      <div className="flex items-center gap-2 flex-wrap">
        <button
          onClick={() => setTopRatedOnly((v) => !v)}
          data-testid="mkt-shops-top-rated-chip"
          className={`px-3 py-1.5 rounded-full text-xs font-black flex items-center gap-1 transition ${
            topRatedOnly
              ? 'bg-gradient-to-r from-yellow-400 to-amber-500 text-slate-900 shadow-lg shadow-yellow-500/40'
              : 'bg-slate-800 text-yellow-300 border border-yellow-400/40 hover:border-yellow-400'
          }`}>
          <Star size={12} className={topRatedOnly ? 'fill-slate-900' : 'fill-yellow-300 text-yellow-300'} />
          Top Rated
          <span className="text-[9px] opacity-80">(4.5★+ · 10+ reviews)</span>
        </button>
        <div className="text-[10px] opacity-60">
          {topRatedOnly
            ? `${visibleShops.length} top-rated shop${visibleShops.length !== 1 ? 's' : ''}`
            : `${shops.length} shops total`}
        </div>
      </div>
      {loading && <div className="text-center opacity-60 py-6 text-xs">⏳ Loading shops…</div>}
      {!loading && visibleShops.length === 0 && (
        <div className="text-center opacity-60 py-8 text-xs">
          {topRatedOnly ? 'No shops qualify for Top Rated yet — encourage buyers to review!' : 'No shops match filters'}
        </div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
        {visibleShops.map((s) => {
          const medals = { 1: { icon: '🥇', bg: 'from-yellow-400 to-amber-500', label: '#1' },
                           2: { icon: '🥈', bg: 'from-slate-300 to-slate-400', label: '#2' },
                           3: { icon: '🥉', bg: 'from-orange-400 to-amber-600', label: '#3' } };
          const rank = s.city_rank ? medals[s.city_rank] : null;
          const heroTint = s.is_top_rated;
          return (
            <a key={s.slug} href={`/marketplace/store/${s.slug}`} data-testid={`mkt-shop-tile-${s.slug}`}
               className={`rounded-2xl overflow-hidden border-2 bg-slate-900/70 hover:border-purple-300 transition relative ${
                 heroTint
                   ? 'border-yellow-400 shadow-lg shadow-yellow-500/30 ring-1 ring-yellow-300/40'
                   : 'border-slate-700'
               }`}>
              <div className="aspect-video bg-slate-800 relative">
                {s.banner_url || s.thumbnail_url || s.logo_url ? <img src={abs(s.banner_url || s.thumbnail_url || s.logo_url)} alt="" className="w-full h-full object-cover" /> : <div className="w-full h-full flex items-center justify-center text-5xl">🏪</div>}
                {s.verified && <span className="absolute top-1 right-1 px-1.5 py-0.5 rounded bg-emerald-500 text-white text-[9px] font-black">✓</span>}
                {s.is_sponsor && <span className="absolute top-1 left-1 px-1.5 py-0.5 rounded bg-amber-400 text-slate-900 text-[9px] font-black">⭐ SPONSORED</span>}
                {heroTint && (
                  <span data-testid={`mkt-shop-top-rated-badge-${s.slug}`}
                        className="absolute top-1 left-1/2 -translate-x-1/2 px-2 py-0.5 rounded-full bg-gradient-to-r from-yellow-400 to-amber-500 text-slate-900 text-[9px] font-black flex items-center gap-0.5 shadow-md">
                    <Star size={9} className="fill-slate-900" /> TOP RATED
                  </span>
                )}
                {rank && (
                  <div data-testid={`mkt-shop-rank-${s.slug}`}
                       className={`absolute bottom-1 left-1 flex items-center gap-1 px-2 py-1 rounded-full bg-gradient-to-r ${rank.bg} text-slate-900 text-[10px] font-black shadow-lg border border-white/40`}>
                    <span className="text-base leading-none">{rank.icon}</span>
                    <span>Top {rank.label} · {s.city_rank_of || s.city}</span>
                  </div>
                )}
              </div>
              <div className="p-2 space-y-0.5">
                <div className="text-xs font-black truncate">{s.shop_name}</div>
                {/* Rating badge — only render when there's ≥1 review */}
                {s.rating_count > 0 && (
                  <div className="flex items-center gap-1" data-testid={`mkt-shop-rating-${s.slug}`}>
                    <Star size={10} className="fill-yellow-300 text-yellow-300" />
                    <span className="text-[10px] font-black text-yellow-300">{Number(s.rating_avg).toFixed(1)}</span>
                    <span className="text-[9px] opacity-60">({s.rating_count})</span>
                  </div>
                )}
                {/* Next available appointment slot — boosts buyer confidence */}
                {s.next_slot && (
                  <div className="flex items-center gap-1" data-testid={`mkt-shop-next-slot-${s.slug}`}>
                    <span className="text-[9px] font-black text-emerald-300 bg-emerald-500/20 border border-emerald-400/40 rounded px-1 py-0.5 flex items-center gap-1">
                      📅 Next: {(() => {
                        const d = new Date(s.next_slot.date);
                        const today = new Date(); today.setHours(0,0,0,0);
                        const dd = new Date(s.next_slot.date); dd.setHours(0,0,0,0);
                        const diff = Math.round((dd - today) / 86400000);
                        return diff === 0 ? 'Today' : diff === 1 ? 'Tomorrow' : d.toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short' });
                      })()}
                      <span className="opacity-70">· {s.next_slot.available} free</span>
                    </span>
                  </div>
                )}
                {s.tagline && <div className="text-[9px] opacity-70 truncate">{s.tagline}</div>}
                <div className="text-[10px] opacity-80 truncate">{s.city && <>📍 {s.city}</>} {s.category && <>· 🏷 {s.category}</>}</div>
                {/* Wave 51 · Social & custom-button chips */}
                <ShopSocialChips store={s} compact />
              </div>
            </a>
          );
        })}
      </div>
    </div>
  );
}

// ============ ALL SHOWROOMS TAB ============
function AllShowroomsTab() {
  const [shops, setShops] = useState([]);
  const [q, setQ] = useState({ search: '', city: '', category: '' });
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try { const r = await api.get('/marketplace/shops', { params: { ...q, kind: 'showroom', limit: 120 } }); setShops(r.data.shops || []); }
    finally { setLoading(false); }
  }, [q]);
  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-3" data-testid="mkt-showrooms-tab">
      {/* Hero */}
      <div className="rounded-2xl p-4 bg-gradient-to-br from-violet-700 via-purple-700 to-indigo-800 text-white relative overflow-hidden shadow-xl">
        <div className="absolute inset-0 opacity-20 bg-[radial-gradient(circle_at_70%_50%,white,transparent)]"></div>
        <div className="relative">
          <div className="text-xs font-black uppercase tracking-widest opacity-80">Anant Smart Marketplace</div>
          <div className="text-2xl font-black mt-1">🚘 All Showrooms</div>
          <div className="text-[11px] opacity-90 mt-1">Cars · Bikes · Electronics · Furniture · Premium showrooms near you</div>
        </div>
      </div>
      {/* Filters */}
      <div className="grid grid-cols-2 gap-2">
        <input value={q.search} onChange={(e) => setQ({ ...q, search: e.target.value })} placeholder="🔍 Showroom name"
               className="col-span-2 px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-sm" data-testid="mkt-showrooms-search" />
        <input value={q.city} onChange={(e) => setQ({ ...q, city: e.target.value })} placeholder="📍 City"
               className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-sm" data-testid="mkt-showrooms-city" />
        <input value={q.category} onChange={(e) => setQ({ ...q, category: e.target.value })} placeholder="🏷️ Category"
               className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-sm" data-testid="mkt-showrooms-cat" />
      </div>
      {loading && <div className="text-center opacity-60 py-6 text-xs">⏳ Loading showrooms…</div>}
      {!loading && shops.length === 0 && (
        <div className="text-center opacity-60 py-10 text-xs">
          Koi showroom abhi listed nahi. Showroom owners ko add karne ka bol!
        </div>
      )}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
        {shops.map((s) => (
          <div key={s.slug} className="rounded-2xl overflow-hidden border-2 border-violet-500/40 bg-slate-900 shadow" data-testid={`mkt-showroom-tile-${s.slug}`}>
            <a href={`/marketplace/store/${s.slug}`}>
              <div className="aspect-video bg-slate-800">
                {s.banner_url || s.thumbnail_url || s.logo_url
                  ? <img src={s.banner_url || s.thumbnail_url || s.logo_url} alt="" className="w-full h-full object-cover" />
                  : <div className="w-full h-full flex items-center justify-center text-4xl">🚘</div>}
              </div>
              <div className="p-2">
                <div className="text-xs font-black truncate">{s.shop_name}</div>
                <div className="text-[10px] opacity-80 truncate">{s.city && <span>📍 {s.city}</span>} {s.category && <span> · {s.category}</span>}</div>
                {s.tagline && <div className="text-[9px] opacity-60 truncate">{s.tagline}</div>}
              </div>
            </a>
            {/* Social + Location + WhatsApp row */}
            <div className="px-2 pb-2 flex gap-1 flex-wrap">
              {s.social_links?.whatsapp && (
                <a href={`https://wa.me/${(s.social_links.whatsapp || '').replace(/\D/g, '')}`} target="_blank" rel="noreferrer"
                   className="flex-1 py-1 rounded-lg bg-green-700/50 text-green-200 text-[10px] font-black text-center" data-testid={`mkt-showroom-wa-${s.slug}`}>
                  💬 WhatsApp
                </a>
              )}
              {(s.lat && s.lng) && (
                <a href={`https://www.google.com/maps/search/?api=1&query=${s.lat},${s.lng}`} target="_blank" rel="noreferrer"
                   className="flex-1 py-1 rounded-lg bg-sky-700/50 text-sky-200 text-[10px] font-black text-center">
                  📍 Map
                </a>
              )}
              {s.social_links?.instagram && (
                <a href={s.social_links.instagram} target="_blank" rel="noreferrer"
                   className="py-1 px-2 rounded-lg bg-gradient-to-br from-purple-600/50 to-pink-600/50 text-pink-200 text-[10px] font-black">
                  📸
                </a>
              )}
              {s.social_links?.facebook && (
                <a href={s.social_links.facebook} target="_blank" rel="noreferrer"
                   className="py-1 px-2 rounded-lg bg-blue-700/50 text-blue-200 text-[10px] font-black">
                  📘
                </a>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============ LEAD 360 CRM TAB — AAPKA MAHA KHAJANA · AAPKI COMMUNITY ============
function Lead360CRMTab({ cfg, isAdmin, isGuest }) {
  const [sub, setSub] = useState(null);
  const [leads, setLeads] = useState([]);
  const [cats, setCats] = useState([]);
  const [rewards, setRewards] = useState({ rewards: [], total_earned: 0 });
  const [panel, setPanel] = useState('leads'); // leads | add | rewards | subscribe | admin
  const [newLead, setNewLead] = useState({ name: '', mobile: '', business_type: '', category_id: '', city: '', notes: '' });
  const [editLead, setEditLead] = useState(null);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [listening, setListening] = useState(false);
  const [busy, setBusy] = useState(false);
  const [adminStats, setAdminStats] = useState(null);
  const [showAddCat, setShowAddCat] = useState(false);
  const [newCatName, setNewCatName] = useState('');
  const [selectedLead, setSelectedLead] = useState(null); // for detail modal
  const recogRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const [catR, subR] = await Promise.all([
        api.get('/lead360/categories').catch(() => ({ data: { categories: [] } })),
        isGuest ? Promise.resolve({ data: { subscription: null } }) : api.get('/lead360/subscription').catch(() => ({ data: { subscription: null } })),
      ]);
      setCats(catR.data.categories || []);
      const freshSub = subR.data.subscription;
      setSub(freshSub);
      // Fix: use freshSub directly — not stale `sub` state
      if (!isGuest && freshSub) {
        try { const r = await api.get('/lead360/leads', { params: { search, status: statusFilter } }); setLeads(r.data.leads || []); } catch {}
        try { const r = await api.get('/lead360/rewards'); setRewards(r.data); } catch {}
      }
    } catch {}
    if (isAdmin) {
      try { const r = await api.get('/admin/lead360/stats'); setAdminStats(r.data); } catch {}
    }
  }, [isGuest, search, statusFilter, isAdmin]);

  useEffect(() => { load(); }, [load]);

  const startVoice = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error('Voice input is not supported in this browser'); return; }
    const r = new SR(); r.lang = 'hi-IN'; r.continuous = false; r.interimResults = false;
    r.onresult = (e) => {
      const t = Array.from(e.results).map((x) => x[0].transcript).join(' ');
      setNewLead((prev) => ({ ...prev, notes: (prev.notes ? prev.notes + ' ' : '') + t }));
      setListening(false);
    };
    r.onerror = () => setListening(false);
    r.onend = () => setListening(false);
    recogRef.current = r;
    r.start(); setListening(true);
  };

  const submitLead = async () => {
    if (!newLead.name.trim()) { toast.error('Name required'); return; }
    setBusy(true);
    try {
      await api.post('/lead360/leads', newLead);
      toast.success('✅ Lead add ho gaya!');
      setNewLead({ name: '', mobile: '', business_type: '', category_id: '', city: '', notes: '' });
      setPanel('leads'); load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
    finally { setBusy(false); }
  };

  const updateStatus = async (lead, status) => {
    try { await api.patch(`/lead360/leads/${lead.id}`, { status }); toast.success('Updated!'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };

  const markMilestone = async (lead, milestone) => {
    try { await api.patch(`/lead360/leads/${lead.id}`, { milestone_done: milestone }); load(); }
    catch {}
  };

  const deleteLead = async (lead) => {
    if (!window.confirm('Delete karna hai?')) return;
    try { await api.delete(`/lead360/leads/${lead.id}`); toast.success('Deleted'); load(); }
    catch {}
  };

  const subscribe = async (tier) => {
    const tiers = { basic: 300, standard: 600 };
    if (!window.confirm(`${tier === 'basic' ? '300' : '600'} AT se Lead360 ${tier} plan lena hai?`)) return;
    setBusy(true);
    try {
      await api.post('/lead360/subscribe', { tier });
      toast.success(`🎉 Lead360 ${tier} plan activated!`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Subscription failed'); }
    finally { setBusy(false); }
  };

  const STATUS_COLORS = { new: 'bg-sky-500/20 text-sky-300', contacted: 'bg-amber-500/20 text-amber-300', interested: 'bg-emerald-500/20 text-emerald-300', converted: 'bg-purple-500/20 text-purple-300', lost: 'bg-rose-500/20 text-rose-300' };
  const STATUSES = ['new', 'contacted', 'interested', 'converted', 'lost'];

  return (
    <div className="space-y-3" data-testid="mkt-lead360-tab">
      {/* Hero */}
      <div className="rounded-2xl p-4 bg-gradient-to-br from-emerald-800 via-teal-800 to-cyan-900 text-white relative overflow-hidden shadow-xl border border-emerald-400/30">
        <div className="absolute inset-0 opacity-10 bg-[radial-gradient(circle_at_30%_80%,gold,transparent)]"></div>
        <div className="relative">
          <div className="text-[11px] font-black uppercase tracking-widest text-emerald-300">🌟 Lead 360 CRM</div>
          <div className="text-2xl font-black mt-1 text-white">AAPKA MAHA KHAJANA</div>
          <div className="text-sm font-black text-emerald-300">AAPKI COMMUNITY</div>
          <div className="text-[11px] mt-1 opacity-80">Smart lead management · 4-level community rewards · Voice notes · Milestone alerts</div>
        </div>
        {sub && (
          <div className="mt-2 inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-400/20 border border-emerald-300/40 text-[10px] font-black text-emerald-200">
            ✓ {sub.label} Plan · expires {(sub.expires_at || '').slice(0, 10)}
          </div>
        )}
      </div>

      {/* Subscription CTA if no sub */}
      {!isGuest && !sub && (
        <div className="rounded-2xl bg-gradient-to-br from-amber-900/60 to-yellow-900/60 border-2 border-amber-400/40 p-4 space-y-3" data-testid="mkt-lead360-subscribe-cta">
          <div className="text-sm font-black text-amber-200">🔐 Lead360 CRM unlock karne ke liye plan lo</div>
          <div className="text-[11px] opacity-80">Community reward engine se earn karo jab aapki referral chain subscribe kare!</div>
          <div className="grid grid-cols-2 gap-2">
            <button onClick={() => subscribe('basic')} disabled={busy}
                    data-testid="mkt-lead360-sub-basic"
                    className="py-3 rounded-xl bg-gradient-to-r from-amber-500 to-yellow-500 text-slate-900 font-black text-sm disabled:opacity-40">
              🥉 Basic · 300 AT<div className="text-[10px] font-normal">Local leads · 30 days</div>
            </button>
            <button onClick={() => subscribe('standard')} disabled={busy}
                    data-testid="mkt-lead360-sub-standard"
                    className="py-3 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 text-white font-black text-sm disabled:opacity-40">
              🥇 Standard · 600 AT<div className="text-[10px] font-normal">Global leads · 30 days</div>
            </button>
          </div>
          <div className="text-[10px] opacity-70 text-center">Community Reward Engine: L1=15% · L2=7% · L3=5% · L4=3% of subscription</div>
        </div>
      )}

      {isGuest && (
        <div className="rounded-xl bg-slate-900 border border-slate-700 p-4 text-center">
          <div className="text-sm font-black text-slate-300">Login karein Lead360 CRM use karne ke liye</div>
          <a href="/login" className="inline-block mt-2 px-4 py-2 rounded-xl bg-emerald-500 text-white text-xs font-black">Login →</a>
        </div>
      )}

      {/* If subscribed: Panels */}
      {!isGuest && sub && (
        <>
          {/* Upcoming Birthdays / Anniversaries / Family Dates — same as Prospects tab */}
          <ProspectsUpcomingEventsBlock />

          {/* Community Mode banner for Standard plan */}
          {sub.tier === 'standard' && (
            <div className="rounded-xl bg-emerald-500/15 border-2 border-emerald-400/60 p-3 flex items-center gap-2" data-testid="mkt-lead360-community-banner">
              <div className="text-2xl">🌐</div>
              <div>
                <div className="text-xs font-black text-emerald-300">Community Mode Active · Standard Plan</div>
                <div className="text-[11px] opacity-80">Aap sabki community leads dekh sakte hain — 600 AT Standard plan ki power!</div>
              </div>
            </div>
          )}

          {/* Sub-tabs */}
          <div className="flex gap-1 flex-wrap">
            {[['leads', '📋 My Leads'], ['add', '➕ Add Lead'], ['rewards', '💰 Community Rewards'], ...(isAdmin ? [['admin', '👑 Admin']] : [])].map(([k, l]) => (
              <button key={k} onClick={() => setPanel(k)}
                      className={`px-3 py-1.5 rounded-lg text-[11px] font-black border transition ${panel === k ? 'bg-emerald-600 border-emerald-400 text-white' : 'bg-slate-800 border-slate-700 text-slate-300'}`}>
                {l}
              </button>
            ))}
          </div>

          {/* LEADS PANEL */}
          {panel === 'leads' && (
            <div className="space-y-2">
              <div className="flex items-center justify-between mb-1">
                <div className="text-xs font-black text-slate-300">
                  {sub.tier === 'standard'
                    ? `🌐 Community Database · ${leads.length} leads`
                    : `📋 My Leads · ${leads.length}`}
                </div>
              </div>
              <div className="flex gap-2 flex-wrap">
                <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="🔍 Search leads"
                       className="flex-1 min-w-0 px-2 py-1.5 rounded-lg bg-slate-800 border border-slate-700 text-sm" />
                <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                        className="px-2 py-1.5 rounded-lg bg-slate-800 border border-slate-700 text-sm">
                  <option value="">All</option>
                  {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
                {/* CSV Export */}
                <button
                  onClick={() => {
                    if (!leads.length) { toast.error('No leads to export'); return; }
                    const headers = ['Name', 'Mobile', 'Email', 'Business Type', 'City', 'State', 'Category', 'Status', 'Notes', 'Source', 'Created At', 'Days Old'];
                    const rows = leads.map((l) => [
                      l.name || '', l.mobile || '', l.email || '',
                      l.business_type || '', l.city || '', l.state || '',
                      (cats.find((c) => c.id === l.category_id) || {}).name || '',
                      l.status || '', (l.notes || '').replace(/,/g, ';').replace(/\n/g, ' '),
                      l.source || '', (l.created_at || '').slice(0, 10), l.days_old ?? 0
                    ]);
                    const csv = [headers.join(','), ...rows.map((r) => r.map((v) => `"${v}"`).join(','))].join('\n');
                    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                    const url = URL.createObjectURL(blob);
                    const a = document.createElement('a'); a.href = url; a.download = `leads_${new Date().toISOString().slice(0,10)}.csv`;
                    a.click(); URL.revokeObjectURL(url);
                    toast.success(`📥 ${leads.length} leads exported!`);
                  }}
                  data-testid="mkt-lead360-csv-export"
                  className="px-3 py-1.5 rounded-lg bg-emerald-600/40 border border-emerald-400/50 text-emerald-200 text-xs font-black whitespace-nowrap">
                  📥 CSV
                </button>
                {/* WhatsApp Share Summary */}
                <button
                  onClick={() => {
                    if (!leads.length) { toast.error('No leads to share'); return; }
                    const today = new Date().toLocaleDateString('hi-IN');
                    const summary = leads.slice(0, 20).map((l, i) =>
                      `${i+1}. *${l.name}* · ${l.mobile || '—'} · ${l.business_type || '—'} · ${l.city || '—'} · _${l.status}_`
                    ).join('\n');
                    const msg = `📋 *Lead360 Summary* · ${today}\n*AAPKA MAHA KHAJANA · AAPKI COMMUNITY*\nTotal: ${leads.length} leads\n\n${summary}${leads.length > 20 ? `\n\n...aur ${leads.length - 20} leads` : ''}`;
                    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, '_blank');
                  }}
                  data-testid="mkt-lead360-wa-share"
                  className="px-3 py-1.5 rounded-lg bg-green-700/40 border border-green-500/50 text-green-200 text-xs font-black whitespace-nowrap">
                  💬 WA
                </button>
              </div>
              {leads.length === 0 && <div className="text-center text-xs opacity-60 py-8">Koi lead nahi. "Add Lead" se start karein.</div>}
              <div className="space-y-2">
                {leads.map((lead) => {
                  const isCRM = !lead.source || lead.source === 'manual';
                  return (
                  <div key={lead.id} className="rounded-2xl bg-slate-900 border border-slate-700 p-3 space-y-2" data-testid={`mkt-lead360-card-${lead.id}`}>
                    {/* Header */}
                    <div className="flex items-start justify-between gap-2">
                      <div className="min-w-0 flex-1">
                        <div className="font-black text-sm">{lead.name}</div>
                        <div className="text-[11px] opacity-70">{lead.business_type || lead.category_name} {lead.city ? `· 📍 ${lead.city}` : ''}</div>
                      </div>
                      <div className="flex flex-col items-end gap-0.5">
                        <span className={`px-2 py-0.5 rounded-full text-[10px] font-black ${STATUS_COLORS[lead.status] || 'bg-slate-700 text-slate-300'}`}>
                          {lead.status || 'new'}
                        </span>
                        {!isCRM && (
                          <span className="px-1.5 py-0.5 rounded text-[8px] font-black bg-cyan-500/20 text-cyan-300 border border-cyan-400/30">
                            📥 {lead.source === 'vyapar_customers' ? 'Vyapar' : lead.source === 'avm_customers' ? 'AVM' : lead.source === 'leadgen_leads' ? 'LeadGen' : lead.source === 'qr_leads' ? 'QR' : 'Prospects'}
                          </span>
                        )}
                      </div>
                    </div>
                    {/* Contact actions */}
                    <div className="flex gap-1 flex-wrap">
                      {lead.mobile && (
                        <>
                          <a href={`tel:${lead.mobile}`} className="px-2 py-1 rounded-lg bg-sky-700/40 text-sky-200 text-[10px] font-black" data-testid={`mkt-lead360-call-${lead.id}`}>📞 Call</a>
                          <a href={`https://wa.me/${lead.mobile.replace(/\D/g, '')}`} target="_blank" rel="noreferrer"
                             className="px-2 py-1 rounded-lg bg-green-700/40 text-green-200 text-[10px] font-black" data-testid={`mkt-lead360-wa-${lead.id}`}>💬 WhatsApp</a>
                        </>
                      )}
                      {lead.voice_note_url && (
                        <button onClick={() => { const a = new Audio(lead.voice_note_url); a.play(); }}
                                className="px-2 py-1 rounded-lg bg-amber-700/40 text-amber-200 text-[10px] font-black">🔊 Note</button>
                      )}
                    </div>
                    {/* Milestone alert (only for CRM leads) */}
                    {isCRM && lead.milestone_due && (
                      <div className="rounded-lg bg-amber-500/20 border border-amber-400/40 px-2 py-1 text-[11px] font-black text-amber-200 flex items-center justify-between" data-testid={`mkt-lead360-milestone-${lead.id}`}>
                        ⏰ {lead.milestone_due} day milestone! Followup karein
                        <button onClick={() => markMilestone(lead, lead.milestone_due)} className="px-2 py-0.5 rounded bg-amber-500 text-slate-900 text-[10px]">✓ Done</button>
                      </div>
                    )}
                    {/* Notes */}
                    {lead.notes && <div className="text-[11px] opacity-70 line-clamp-2">{lead.notes}</div>}
                    {/* Status change — only for CRM leads */}
                    <div className="flex gap-1 flex-wrap">
                      {isCRM && STATUSES.filter((s) => s !== lead.status).map((s) => (
                        <button key={s} onClick={() => updateStatus(lead, s)}
                                className={`px-2 py-0.5 rounded text-[10px] font-black ${STATUS_COLORS[s] || 'bg-slate-700'} border border-current/30`}>
                          → {s}
                        </button>
                      ))}
                      <button onClick={() => setSelectedLead(lead)}
                              data-testid={`mkt-lead360-detail-${lead.id}`}
                              className="px-2 py-0.5 rounded bg-emerald-600/40 border border-emerald-500/50 text-emerald-200 text-[10px] font-black">
                        📋 {isCRM ? 'Full Profile' : 'View'}
                      </button>
                      {isCRM && (
                        <button onClick={() => deleteLead(lead)} className="px-2 py-0.5 rounded bg-rose-900/40 text-rose-300 text-[10px] font-black ml-auto">🗑</button>
                      )}
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* ADD LEAD PANEL */}
          {panel === 'add' && (
            <div className="rounded-2xl bg-slate-900 border border-emerald-500/40 p-4 space-y-2" data-testid="mkt-lead360-add-panel">
              <div className="font-black text-emerald-300">➕ New Lead Add Karein</div>
              <input value={newLead.name} onChange={(e) => setNewLead({ ...newLead, name: e.target.value })}
                     placeholder="Name *" data-testid="mkt-lead360-name"
                     className="w-full px-3 py-2 rounded-xl bg-slate-800 border border-slate-700 text-sm" />
              <input value={newLead.mobile} onChange={(e) => setNewLead({ ...newLead, mobile: e.target.value })}
                     placeholder="Mobile" type="tel"
                     className="w-full px-3 py-2 rounded-xl bg-slate-800 border border-slate-700 text-sm" />
              <div className="grid grid-cols-2 gap-2">
                <input value={newLead.business_type} onChange={(e) => setNewLead({ ...newLead, business_type: e.target.value })}
                       placeholder="Business Type"
                       className="px-3 py-2 rounded-xl bg-slate-800 border border-slate-700 text-sm" />
                <input value={newLead.city} onChange={(e) => setNewLead({ ...newLead, city: e.target.value })}
                       placeholder="City"
                       className="px-3 py-2 rounded-xl bg-slate-800 border border-slate-700 text-sm" />
              </div>
              <select value={newLead.category_id} onChange={(e) => setNewLead({ ...newLead, category_id: e.target.value })}
                      className="w-full px-3 py-2 rounded-xl bg-slate-800 border border-slate-700 text-sm">
                <option value="">Select Category</option>
                {cats.map((c) => <option key={c.id} value={c.id}>{c.icon} {c.name}</option>)}
              </select>
              {/* Inline Add Category */}
              {!showAddCat ? (
                <button onClick={() => setShowAddCat(true)} className="text-[10px] text-emerald-300 underline">+ New Category add karo</button>
              ) : (
                <div className="flex gap-2 mt-1">
                  <input value={newCatName} onChange={(e) => setNewCatName(e.target.value)} placeholder="Category name"
                         className="flex-1 px-2 py-1 rounded-lg bg-slate-800 border border-slate-700 text-xs" />
                  <button onClick={async () => {
                    if (!newCatName.trim()) return;
                    try {
                      const r = await api.post('/lead360/categories', { name: newCatName.trim(), icon: '🏷️' });
                      const cat = r.data.category;
                      setCats((prev) => [...prev, cat]);
                      setNewLead((prev) => ({ ...prev, category_id: cat.id }));
                      setNewCatName(''); setShowAddCat(false);
                      toast.success(`Category "${cat.name}" added!`);
                    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
                  }} className="px-2 py-1 rounded-lg bg-emerald-600 text-white text-xs font-black">+ Add</button>
                  <button onClick={() => setShowAddCat(false)} className="px-2 py-1 rounded-lg bg-slate-700 text-slate-300 text-xs">✕</button>
                </div>
              )}
              <div className="relative">
                <textarea value={newLead.notes} onChange={(e) => setNewLead({ ...newLead, notes: e.target.value })}
                          rows={3} placeholder="Notes (ya 🎤 Voice Note record karo)"
                          className="w-full px-3 py-2 rounded-xl bg-slate-800 border border-slate-700 text-sm pr-12" />
                <button onClick={listening ? (() => { recogRef.current?.stop(); setListening(false); }) : startVoice}
                        className={`absolute right-2 top-2 w-8 h-8 rounded-full flex items-center justify-center text-sm ${listening ? 'bg-rose-500 animate-pulse' : 'bg-slate-700'}`}
                        data-testid="mkt-lead360-voice-btn">
                  {listening ? '⏹' : '🎤'}
                </button>
              </div>
              {listening && <div className="text-[11px] text-rose-300 text-center animate-pulse">🎤 Bol raha hoon… (Hindi ya English)</div>}
              <button onClick={submitLead} disabled={busy}
                      data-testid="mkt-lead360-submit"
                      className="w-full py-3 rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 text-white font-black text-sm disabled:opacity-40">
                {busy ? '…' : '✅ Lead Add Karo'}
              </button>
            </div>
          )}

          {/* COMMUNITY REWARDS PANEL */}
          {panel === 'rewards' && (
            <div className="space-y-3">
              <div className="rounded-2xl bg-gradient-to-br from-amber-900/50 to-yellow-900/50 border border-amber-400/40 p-4 text-center" data-testid="mkt-lead360-rewards-card">
                <div className="text-[11px] uppercase font-black text-amber-300 opacity-80">Community Reward Earned</div>
                <div className="text-4xl font-black text-amber-300 tabular-nums mt-1">{Number(rewards.total_earned || 0).toFixed(2)}</div>
                <div className="text-xs text-amber-200 font-black">Anant Tokens</div>
              </div>
              <div className="rounded-xl bg-slate-900 border border-slate-700 p-3">
                <div className="text-xs font-black text-amber-200 mb-2">Reward Engine: L1=15% · L2=7% · L3=5% · L4=3%</div>
                <div className="space-y-1 max-h-60 overflow-y-auto">
                  {(rewards.rewards || []).length === 0 && <div className="text-xs opacity-60 text-center py-4">Koi reward abhi nahi. Apni community expand karo!</div>}
                  {(rewards.rewards || []).map((r, i) => (
                    <div key={r.id || i} className="flex items-center justify-between py-1 border-b border-slate-800 last:border-0 text-[11px]">
                      <div className="opacity-70">L{r.level} · {(r.created_at || '').slice(0, 10)}</div>
                      <div className="font-black text-amber-300">+{Number(r.amount).toFixed(2)} AT</div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="rounded-xl bg-slate-800 border border-slate-700 p-3 text-[11px] space-y-1 text-slate-300">
                <div className="font-black text-white">Reward Engine kaise kaam karta hai?</div>
                <div>Jab aapki referral chain mein koi Lead360 subscribe kare:</div>
                <div>• L1 (Direct): <b className="text-amber-300">15%</b> of subscription amount</div>
                <div>• L2: <b className="text-amber-300">7%</b> · L3: <b className="text-amber-300">5%</b> · L4: <b className="text-amber-300">3%</b></div>
              </div>
            </div>
          )}

          {/* ADMIN PANEL */}
          {panel === 'admin' && isAdmin && adminStats && (
            <div className="space-y-2" data-testid="mkt-lead360-admin">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {[
                  ['Total Leads', adminStats.total_leads],
                  ['Active Subs', adminStats.active_subscriptions],
                  ['Basic Subs', adminStats.basic_subs],
                  ['Standard Subs', adminStats.standard_subs],
                  ['Rewards Dist.', adminStats.total_rewards_distributed],
                  ['Total Revenue AT', adminStats.total_subscription_revenue_at],
                ].map(([label, val]) => (
                  <div key={label} className="rounded-xl bg-slate-900 border border-emerald-500/30 p-3 text-center">
                    <div className="text-[10px] opacity-70">{label}</div>
                    <div className="font-black text-emerald-300 text-xl">{val ?? 0}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </>
      )}
      {/* Lead Full Profile Modal */}
      {selectedLead && (
        <LeadDetailModal lead={selectedLead} cats={cats} onClose={() => setSelectedLead(null)} onSaved={() => { setSelectedLead(null); load(); }} />
      )}
    </div>
  );
}
function ShopSocialChips({ store, compact = false }) {
  const SOCIAL = [
    { k: 'zomato', icon: '🍽️', bg: 'bg-red-600' },
    { k: 'swiggy', icon: '🥘', bg: 'bg-orange-500' },
    { k: 'blinkit', icon: '⚡', bg: 'bg-yellow-500 text-slate-900' },
    { k: 'zepto', icon: '🚀', bg: 'bg-fuchsia-600' },
    { k: 'instagram', icon: '📸', bg: 'bg-gradient-to-br from-purple-500 to-pink-500' },
    { k: 'facebook', icon: '📘', bg: 'bg-blue-600' },
    { k: 'youtube', icon: '▶️', bg: 'bg-red-500' },
    { k: 'website', icon: '🌍', bg: 'bg-sky-600' },
    { k: 'google_maps', icon: '📍', bg: 'bg-emerald-600' },
    { k: 'justdial', icon: '📒', bg: 'bg-amber-600' },
  ];
  const links = store?.social_links || {};
  const custom = store?.custom_buttons || [];
  const active = SOCIAL.filter((s) => (links[s.k] || '').trim());
  if (active.length === 0 && custom.length === 0) return null;
  const maxShow = compact ? 5 : 999;
  return (
    <div className="flex flex-wrap gap-1 mt-1" data-testid={`mkt-shop-social-chips-${store.slug || 'row'}`}>
      {active.slice(0, maxShow).map((s) => (
        <a key={s.k} href={links[s.k]} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
           data-testid={`mkt-shop-chip-${s.k}`} title={s.k}
           className={`inline-flex items-center justify-center ${compact ? 'w-5 h-5 text-[11px]' : 'px-2 py-1 text-[11px] gap-1 font-black'} rounded-full ${s.bg} text-white shadow`}>
          <span>{s.icon}</span>{!compact && <span className="capitalize">{s.k.replace('_', ' ')}</span>}
        </a>
      ))}
      {compact && active.length > maxShow && (
        <span className="text-[9px] opacity-70">+{active.length - maxShow}</span>
      )}
      {!compact && custom.map((b, i) => (
        b.url ? <a key={`cb-${i}`} href={b.url} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
                   data-testid={`mkt-shop-custom-chip-${i}`}
                   className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-slate-700 text-white text-[11px] font-black">
          <span>{b.icon || '🔘'}</span><span>{b.label || 'Open'}</span>
        </a> : null
      ))}
      {compact && custom.length > 0 && (
        <span className="inline-flex items-center justify-center w-5 h-5 rounded-full bg-slate-700 text-white text-[10px] font-black" title={`${custom.length} custom`}>+{custom.length}</span>
      )}
    </div>
  );
}

// ============ CART TAB ============
function CartTab() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [delivery, setDelivery] = useState(0);
  const [quote, setQuote] = useState(null); // computed delivery quote from backend
  const [note, setNote] = useState('');
  const [buyer, setBuyer] = useState({ name: '', phone: '', city: '', address: '' });
  const [paying, setPaying] = useState(false);
  const [bal, setBal] = useState(null);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, c] = await Promise.all([api.get('/marketplace/cart'), api.get('/marketplace/config')]);
      setItems(r.data.items || []); setBal(c.data.at_balance || 0);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  // Live delivery quote — recomputes whenever items change
  useEffect(() => {
    if (!items.length) { setQuote(null); return; }
    let cancelled = false;
    api.post('/marketplace/delivery-quote', {
      items: items.map((it) => ({ listing_id: it.listing_id, quantity: it.quantity })),
    }).then((r) => { if (!cancelled) { setQuote(r.data); setDelivery(r.data.delivery_at || 0); } })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [items]);
  const qty = async (lid, delta) => {
    try { await api.post('/marketplace/cart/qty', { listing_id: lid, delta }); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed'); }
  };
  const remove = async (lid) => {
    try { await api.post('/marketplace/cart/remove', { listing_id: lid, quantity: 1 }); load(); }
    catch { toast.error('Failed'); }
  };
  const clear = async () => {
    if (!window.confirm('Empty entire cart?')) return;
    try { await api.post('/marketplace/cart/clear'); setItems([]); } catch { toast.error('Failed'); }
  };
  const subtotal = items.reduce((s, it) => s + Number(it.price_at || 0) * Number(it.quantity || 0), 0);
  const total = subtotal + Number(delivery || 0);
  const totalItems = items.length;
  const canPay = totalItems > 0 && (bal || 0) >= total && !paying;

  const placeOrder = async () => {
    if (!canPay) return;
    if (!window.confirm(`Confirm order? ${total.toFixed(0)} AT aapke wallet se debit hoga.`)) return;
    setPaying(true);
    try {
      const r = await api.post('/marketplace/order/place', {
        items: items.map((it) => ({ listing_id: it.listing_id, quantity: it.quantity })),
        delivery_charges_at: Number(delivery || 0),
        note: note || null,
        buyer_name: buyer.name || null, buyer_phone: buyer.phone || null,
        buyer_city: buyer.city || null, buyer_address: buyer.address || null,
      });
      toast.success(`✅ Order placed · ${r.data.at_deducted} AT debit · Wallet: ${r.data.wallet_balance_after.toFixed(0)} AT`);
      setDelivery(0); setNote(''); load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Order failed');
    } finally { setPaying(false); }
  };

  return (
    <div className="space-y-3" data-testid="mkt-cart-tab">
      <div className="rounded-2xl bg-amber-500/10 border-2 border-amber-400/50 p-3 flex items-center gap-3">
        <div className="flex-1 text-xs">
          <div className="font-black text-amber-300 uppercase">🛍️ Your Cart · Pay with AT</div>
          <div className="opacity-80">{totalItems} items · Subtotal <span className="text-yellow-300 font-black">{subtotal.toFixed(0)} AT</span></div>
          <div className="opacity-80">💰 Your AT: <span className="text-emerald-300 font-black">{bal !== null ? bal.toFixed(0) : '…'}</span></div>
        </div>
        {totalItems > 0 && <button onClick={clear} data-testid="mkt-cart-clear" className="px-3 py-1.5 rounded-lg bg-red-600 text-white text-xs font-black">🗑️ Clear</button>}
      </div>
      {loading && <div className="text-center opacity-60 py-6 text-xs">⏳ Loading…</div>}
      {!loading && totalItems === 0 && <div className="text-center opacity-60 py-10 text-xs">Cart khali hai. Feed me products browse karein aur "🛍️ Add to Cart" karein.</div>}

      {/* Items with qty +/- */}
      {items.map((it) => (
        <div key={it.listing_id} className="rounded-xl bg-slate-900/70 border border-slate-700 p-2 flex items-center gap-2" data-testid={`mkt-cart-item-${it.listing_id}`}>
          {it.image ? <img src={abs(it.image)} alt="" className="w-14 h-14 object-cover rounded" /> : <div className="w-14 h-14 bg-slate-800 rounded flex items-center justify-center text-xl">📦</div>}
          <div className="flex-1 min-w-0">
            <div className="text-xs font-black truncate">{it.title}</div>
            <div className="text-[10px] opacity-70 truncate">🏪 {it.shop_name}</div>
            <div className="text-sm font-black text-yellow-300">₹{it.price_at} AT × {it.quantity} = <span className="text-emerald-300">{(it.price_at * it.quantity).toFixed(0)}</span></div>
          </div>
          <div className="flex items-center gap-1">
            <button onClick={() => qty(it.listing_id, -1)} data-testid={`mkt-cart-qty-minus-${it.listing_id}`} className="w-7 h-7 rounded-full bg-slate-700 text-white font-black">−</button>
            <span className="w-7 text-center text-xs font-black">{it.quantity}</span>
            <button onClick={() => qty(it.listing_id, 1)} data-testid={`mkt-cart-qty-plus-${it.listing_id}`} className="w-7 h-7 rounded-full bg-emerald-600 text-white font-black">+</button>
          </div>
          <button onClick={() => remove(it.listing_id)} data-testid={`mkt-cart-remove-${it.listing_id}`} className="p-1.5 rounded bg-red-900/60"><Trash2 size={12} /></button>
        </div>
      ))}

      {/* Order form + AT payment */}
      {totalItems > 0 && (
        <div className="rounded-2xl bg-slate-900/70 border-2 border-emerald-400/60 p-3 space-y-2" data-testid="mkt-cart-order-form">
          <div className="text-xs font-black text-emerald-300 uppercase">📋 Delivery Details + Payment</div>
          <div className="grid grid-cols-2 gap-2">
            <input value={buyer.name} onChange={(e) => setBuyer({ ...buyer, name: e.target.value })} placeholder="Full name" data-testid="mkt-order-name" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
            <input value={buyer.phone} onChange={(e) => setBuyer({ ...buyer, phone: e.target.value })} placeholder="Phone (+91…)" data-testid="mkt-order-phone" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
            <input value={buyer.city} onChange={(e) => setBuyer({ ...buyer, city: e.target.value })} placeholder="City" data-testid="mkt-order-city" className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
            {quote?.fulfillment_mode === 'self_pickup' ? (
              <div className="px-2 py-1.5 rounded bg-amber-500/15 border border-amber-400/50 text-[11px] font-black text-amber-300 flex items-center justify-center" data-testid="mkt-order-pickup-badge">
                📍 Self-Pickup Only
              </div>
            ) : (
              <div className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-[11px] flex items-center justify-between" data-testid="mkt-order-delivery-computed">
                <span className="opacity-80">🚚 Delivery:</span>
                <b className="text-yellow-300">{Number(quote?.delivery_at ?? delivery).toFixed(0)} AT</b>
              </div>
            )}
          </div>
          <textarea value={buyer.address} onChange={(e) => setBuyer({ ...buyer, address: e.target.value })} rows={2} placeholder="Full address (optional)" data-testid="mkt-order-address" className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />
          <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} placeholder="📝 Order tracking notes (optional — status/tracking preferences)" data-testid="mkt-order-note" className="w-full px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-xs" />

          {/* Peak-hours surge banner */}
          {quote?.peak_surge_active && (
            <div className="rounded-lg bg-gradient-to-r from-orange-500/20 to-rose-500/20 border border-orange-400/60 p-2 text-[11px] flex items-center gap-2" data-testid="mkt-peak-surge-banner">
              <span className="text-2xl animate-pulse">🔥</span>
              <div className="flex-1">
                <div className="font-black text-orange-300">Peak Hours Surge · {quote.peak_multiplier}× delivery</div>
                <div className="text-[10px] opacity-80">Busy time ({quote.peak_window_ist}) — order karo abhi ya pichli deals bacha lo</div>
              </div>
            </div>
          )}

          {/* Per-line delivery hints */}
          {quote?.lines?.some((l) => l.unlock_hint) && (
            <div className="rounded-lg bg-emerald-500/10 border border-emerald-400/40 p-2 text-[10px] space-y-1" data-testid="mkt-order-delivery-hints">
              <div className="font-black text-emerald-300 uppercase">💡 Save on delivery:</div>
              {quote.lines.filter((l) => l.unlock_hint).map((l, i) => (
                <div key={i} className="opacity-90"><b>{l.title}</b> — {l.unlock_hint}</div>
              ))}
            </div>
          )}

          <div className="rounded-lg bg-slate-800/60 p-2 text-xs space-y-0.5">
            <div className="flex justify-between"><span className="opacity-80">Subtotal:</span> <b className="text-yellow-300">{subtotal.toFixed(0)} AT</b></div>
            <div className="flex justify-between">
              <span className="opacity-80">🚚 Delivery:</span>
              {quote?.fulfillment_mode === 'self_pickup'
                ? <b className="text-amber-300">📍 Self-Pickup · 0 AT</b>
                : <b className="text-yellow-300">{Number(delivery).toFixed(0)} AT</b>}
            </div>
            <div className="flex justify-between border-t border-slate-700 pt-1 mt-1"><span className="font-black">TOTAL:</span> <b className="text-lg text-emerald-300 font-black">{total.toFixed(0)} AT</b></div>
          </div>
          {bal !== null && bal < total && (
            <div className="text-[10px] text-rose-300 font-black">⚠️ Wallet me kam balance ({bal.toFixed(0)} AT) — {(total - bal).toFixed(0)} AT aur chahiye</div>
          )}
          <button onClick={placeOrder} disabled={!canPay} data-testid="mkt-order-pay"
                  className="w-full py-3 rounded-2xl bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-slate-900 text-sm font-black disabled:opacity-40 flex items-center justify-center gap-2">
            {paying ? '⏳ Processing…' : `💰 Pay ${total.toFixed(0)} AT & Confirm Order`}
          </button>
        </div>
      )}
    </div>
  );
}

// ============ ANALYTICS TAB (seller — 30-day chart) ============
function AnalyticsTab() {
  const [data, setData] = useState(null);
  const [topBy, setTopBy] = useState('views');
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    setLoading(true);
    api.get('/marketplace/analytics', { params: { days: 30 } })
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, []);
  if (loading) return <div className="text-center opacity-60 py-8 text-xs">⏳ Loading analytics…</div>;
  if (!data) return <div className="text-center opacity-60 py-8 text-xs">Setup store first to unlock analytics.</div>;
  const maxLeads = Math.max(1, ...data.series.map((d) => d.leads));
  const maxVisitors = Math.max(1, ...data.series.map((d) => (d.visitors || 0)));
  const topRows = topBy === 'leads' ? (data.top_listings_by_leads || []) : (data.top_listings || []);
  return (
    <div className="space-y-3" data-testid="mkt-analytics-tab">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <div className="rounded-xl bg-green-500/15 border border-green-400/50 p-3 text-center" data-testid="mkt-analytics-wa-conversion">
          <div className="text-[10px] opacity-80 uppercase font-black">📲 WhatsApp Conversion</div>
          <div className="text-2xl font-black text-green-300">{data.wa_conversion_pct ?? 0}%</div>
          <div className="text-[9px] opacity-70">WA leads ÷ views{data.range_views ? ` · last ${data.days}d` : ' · all-time'}</div>
        </div>
        <div className="rounded-xl bg-emerald-500/15 border border-emerald-400/50 p-3 text-center">
          <div className="text-[10px] opacity-80 uppercase font-black">👁 Views (all-time)</div>
          <div className="text-2xl font-black text-emerald-300">{data.total_views}</div>
        </div>
        <div className="rounded-xl bg-purple-500/15 border border-purple-400/50 p-3 text-center">
          <div className="text-[10px] opacity-80 uppercase font-black">💬 Leads (all-time)</div>
          <div className="text-2xl font-black text-purple-300">{data.total_leads}</div>
        </div>
        <div className="rounded-xl bg-amber-500/15 border border-amber-400/50 p-3 text-center col-span-2 sm:col-span-1">
          <div className="text-[10px] opacity-80 uppercase font-black">📅 Range</div>
          <div className="text-lg font-black text-amber-300">{data.days} days</div>
        </div>
      </div>

      <div className="rounded-2xl bg-slate-900/70 border-2 border-slate-700 p-3" data-testid="mkt-analytics-visitors-chart">
        <div className="text-xs font-black text-sky-300 uppercase mb-2">👥 Daily Visitors (last {data.days} days) · {data.range_visitors || 0} total</div>
        <div className="flex items-end gap-0.5 h-32">
          {data.series.map((d) => {
            const v = d.visitors || 0;
            return (
              <div key={d.date} className="flex-1 flex flex-col items-center justify-end" title={`${d.date}: ${v} visitors · ${d.views || 0} views`}>
                <div className="w-full rounded-t bg-gradient-to-t from-sky-600 to-sky-300" style={{ height: `${(v / maxVisitors) * 100}%`, minHeight: v > 0 ? '3px' : '0' }} />
              </div>
            );
          })}
        </div>
        <div className="flex justify-between text-[8px] opacity-60 mt-1">
          <span>{data.series[0]?.date?.slice(5)}</span>
          <span>Today</span>
        </div>
      </div>

      {/* Simple SVG bar chart — leads over 30 days */}
      <div className="rounded-2xl bg-slate-900/70 border-2 border-slate-700 p-3" data-testid="mkt-analytics-chart">
        <div className="text-xs font-black text-purple-300 uppercase mb-2">💬 Daily Leads (last {data.days} days)</div>
        <div className="flex items-end gap-0.5 h-32">
          {data.series.map((d, i) => {
            const h = maxLeads > 0 ? (d.leads / maxLeads) * 100 : 0;
            return (
              <div key={d.date} className="flex-1 flex flex-col items-center justify-end" title={`${d.date}: ${d.leads} leads`}>
                <div className="w-full rounded-t bg-gradient-to-t from-purple-600 to-purple-300" style={{ height: `${h}%`, minHeight: d.leads > 0 ? '3px' : '0' }} />
              </div>
            );
          })}
        </div>
        <div className="flex justify-between text-[8px] opacity-60 mt-1">
          <span>{data.series[0]?.date?.slice(5)}</span>
          <span>Today</span>
        </div>
      </div>

      {/* Top listings */}
      <div className="rounded-2xl bg-slate-900/70 border-2 border-slate-700 p-3">
        <div className="flex items-center justify-between mb-2">
          <div className="text-xs font-black text-purple-300 uppercase">🏆 Top 5 Listings by {topBy === 'leads' ? 'Leads' : 'Views'}</div>
          <div className="flex gap-1">
            {['views', 'leads'].map((k) => (
              <button key={k} onClick={() => setTopBy(k)} data-testid={`mkt-analytics-top-${k}`}
                      className={`px-2 py-0.5 rounded text-[10px] font-black ${topBy === k ? 'bg-purple-500 text-white' : 'bg-slate-800 text-slate-300'}`}>{k === 'views' ? '👁 Views' : '💬 Leads'}</button>
            ))}
          </div>
        </div>
        <div className="space-y-1">
          {topRows.map((t, i) => (
            <div key={t.id} className="flex items-center justify-between text-xs">
              <span className="truncate flex-1"><span className="text-amber-400 font-black">#{i + 1}</span> {t.title}</span>
              <span className="text-emerald-300 font-black">👁 {t.views || 0} · 💬 {t.leads || 0} · 📲 {t.conversion_pct || 0}%</span>
            </div>
          ))}
          {topRows.length === 0 && <div className="text-center opacity-60 py-3 text-[10px]">No data yet</div>}
        </div>
      </div>
    </div>
  );
}

// ============ VYAPAAR PRO TAB (Standard/Prime unlock all AVM features) ============
function VyapaarProTab() {
  const [pro, setPro] = useState(null);
  useEffect(() => { api.get('/marketplace/pro-status').then((r) => setPro(r.data)).catch(() => setPro({ is_pro: false })); }, []);
  if (!pro) return <div className="text-center opacity-60 py-6 text-xs">⏳ Loading…</div>;
  return (
    <div className="space-y-3" data-testid="mkt-vyapaar-tab">
      <div className={`rounded-2xl p-4 border-2 ${pro.is_pro ? 'bg-emerald-500/10 border-emerald-400/60' : 'bg-amber-500/10 border-amber-400/60'}`}>
        <div className="flex items-center gap-2 mb-2">
          <Radio size={22} className={pro.is_pro ? 'text-emerald-300' : 'text-amber-300'} />
          <div>
            <div className="text-lg font-black">🚀 Marketing Suite (Vyapaar Pro)</div>
            <div className="text-[11px] opacity-80">
              {pro.is_pro ? `✅ Unlocked · Plan: ${pro.plan_tier?.toUpperCase()}` : '🔒 Locked · Standard (600 AT) ya Prime (1500 AT) plan lein'}
            </div>
          </div>
        </div>
        {pro.is_pro ? (
          <div className="text-xs opacity-90 space-y-2">
            <div>Aapko in features ka access mil gaya hai — koi extra plan nahi chahiye:</div>
            <ul className="list-disc pl-5 space-y-1">
              <li>📢 <b>Bulk WhatsApp Broadcasts</b> — Text + Voice notes</li>
              <li>👥 <b>Client CRM</b> — Groups, filters, tags, birthdays</li>
              <li>🎯 <b>Campaigns</b> — Festival, launch, offer templates</li>
              <li>📥 <b>LeadGen QR + Forms</b> — Har lead direct aapke CRM me</li>
              <li>🤖 <b>AI Rewrite</b> — Message tone/language auto-improve</li>
            </ul>
          </div>
        ) : (
          <div className="text-xs opacity-80">
            Standard/Prime plan lene ke baad ye tab me sab marketing tools yahin unlock ho jaayenge — aapko koi alag subscribe karne ki zaroorat nahi hai.
          </div>
        )}
      </div>
      {pro.is_pro && (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          <a href="/vyapaar-master" data-testid="mkt-vyapaar-open" className="rounded-2xl bg-gradient-to-br from-purple-600 to-pink-600 p-4 text-white shadow-lg hover:scale-[1.02] transition">
            <div className="text-3xl mb-1">📢</div>
            <div className="text-sm font-black">Open Full Marketing Suite →</div>
            <div className="text-[10px] opacity-90 mt-0.5">Broadcasts · Clients · Campaigns · Leads</div>
          </a>
          <a href="/vyapaar-master?tab=leads" data-testid="mkt-vyapaar-leadgen" className="rounded-2xl bg-gradient-to-br from-emerald-600 to-teal-600 p-4 text-white shadow-lg hover:scale-[1.02] transition">
            <div className="text-3xl mb-1">📥</div>
            <div className="text-sm font-black">LeadGen QR + Forms →</div>
            <div className="text-[10px] opacity-90 mt-0.5">Public forms · Auto-CRM sync</div>
          </a>
        </div>
      )}
    </div>
  );
}


// ============ Wave 48/49 · Prospects & Leads — mobile-first, searchable, bulk WA ============
function ProspectsUpcomingEventsBlock() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(30);
  const [month, setMonth] = useState('');
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (month) params.month = parseInt(month, 10); else params.days = days;
      if (search.trim()) params.search = search.trim();
      const r = await api.get('/marketplace/prospects/family-events', { params });
      setData(r.data);
    } catch (e) { /* paywall or empty */ }
    finally { setLoading(false); }
  }, [days, month, search]);
  useEffect(() => { if (open) load(); }, [open, load]);

  const months = [
    { v: '', l: `All (next ${days}d)` },
    { v: '1', l: 'Jan' }, { v: '2', l: 'Feb' }, { v: '3', l: 'Mar' },
    { v: '4', l: 'Apr' }, { v: '5', l: 'May' }, { v: '6', l: 'Jun' },
    { v: '7', l: 'Jul' }, { v: '8', l: 'Aug' }, { v: '9', l: 'Sep' },
    { v: '10', l: 'Oct' }, { v: '11', l: 'Nov' }, { v: '12', l: 'Dec' },
  ];
  return (
    <div className="rounded-2xl bg-gradient-to-br from-purple-900/40 via-fuchsia-900/30 to-slate-900 border-2 border-fuchsia-400/40 p-3" data-testid="mkt-prospects-family-events">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <div className="text-lg font-black">👨‍👩‍👧 Upcoming Family Events {data ? `· ${data.total}` : ''}</div>
          <div className="text-[11px] opacity-80">Family-member birthdays & anniversaries across CRM + AVM + QR leads · WhatsApp 1-tap wish.</div>
        </div>
        <button onClick={() => setOpen((v) => !v)} data-testid="mkt-prospects-family-toggle"
                className="px-3 py-1.5 rounded-lg bg-fuchsia-500 text-white text-xs font-black">
          {open ? 'Hide' : 'Show →'}
        </button>
      </div>
      {open && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-2">
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="🔎 Search name / relation / phone"
                   data-testid="mkt-prospects-family-search"
                   className="px-2 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm" />
            <select value={month} onChange={(e) => setMonth(e.target.value)}
                    data-testid="mkt-prospects-family-month"
                    className="px-2 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm">
              {months.map((m) => <option key={m.v} value={m.v}>{m.l}</option>)}
            </select>
            <select value={days} onChange={(e) => setDays(Number(e.target.value))}
                    disabled={!!month} data-testid="mkt-prospects-family-days"
                    className="px-2 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm disabled:opacity-40">
              {[7, 15, 30, 60, 90].map((d) => <option key={d} value={d}>Next {d}d</option>)}
            </select>
          </div>
          {loading && <div className="text-xs opacity-60 text-center py-3">Loading…</div>}
          {!loading && (!data || data.events.length === 0) && (
            <div className="text-xs opacity-60 text-center py-5" data-testid="mkt-prospects-family-empty">
              Koi upcoming event nahi mila.
            </div>
          )}
          <div className="space-y-1.5 mt-2 max-h-72 overflow-y-auto">
            {(data?.events || []).map((ev, i) => (
              <div key={i} className="p-2 rounded-lg bg-slate-950/60 border border-fuchsia-400/30 flex items-center gap-2" data-testid={`mkt-prospects-family-row-${i}`}>
                <div className="text-xl">{ev.event_kind === 'birthday' ? '🎂' : '💐'}</div>
                <div className="flex-1 min-w-0">
                  <div className="text-[12px] font-black truncate">
                    {ev.person_name}{ev.relation ? <span className="opacity-70 text-[10px]"> ({ev.relation})</span> : null}
                  </div>
                  <div className="text-[10px] opacity-70 flex flex-wrap gap-1">
                    {ev.is_family && <span className="px-1 rounded bg-fuchsia-500/25 text-fuchsia-200 font-black">👨‍👩‍👧 Family</span>}
                    <span>via {ev.customer_name}</span>
                    <span>· {ev.date}</span>
                    {ev.days_until === 0 ? <span className="text-emerald-300 font-black">· TODAY</span>
                     : <span className="text-amber-300">· in {ev.days_until}d</span>}
                  </div>
                </div>
                {ev.wa_link ? (
                  <a href={ev.wa_link} target="_blank" rel="noreferrer" data-testid={`mkt-prospects-family-wa-${i}`}
                     className="px-2 py-1 rounded bg-green-600 text-white text-[11px] font-black">📱 Wish</a>
                ) : <span className="text-[10px] opacity-50">🔒</span>}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function ProspectsAdminCleanupBlock({ onDone }) {
  const [scan, setScan] = useState(null);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState('empty');
  const runScan = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/admin/marketplace/prospects/scan?filter_type=${filter}`);
      setScan(r.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Scan failed'); }
    finally { setBusy(false); }
  };
  const bulkDelete = async () => {
    if (!window.confirm(`Bulk delete ALL "${filter}" prospects across every source? This cannot be undone.`)) return;
    setBusy(true);
    try {
      const r = await api.post('/admin/marketplace/prospects/bulk-delete', { filter_type: filter });
      toast.success(`🗑 Deleted ${r.data.deleted} prospects`);
      setScan(null); onDone && onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Bulk delete failed'); }
    finally { setBusy(false); }
  };
  const deleteOne = async (row) => {
    if (!window.confirm(`Delete "${row.name || 'unnamed'}" from ${row.source}?`)) return;
    try {
      await api.post('/admin/marketplace/prospects/delete', { source: row.source, id: row.id });
      setScan({ ...scan, rows: scan.rows.filter((r) => r.id !== row.id), total: scan.total - 1 });
      toast.success('🗑 Deleted');
      onDone && onDone();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };
  return (
    <div className="rounded-2xl bg-rose-500/10 border-2 border-rose-400/40 p-3 space-y-2" data-testid="mkt-prospects-admin-cleanup">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-[11px] font-black text-rose-200 uppercase">👑 Admin · Prospects Cleanup (system-wide)</div>
        <div className="flex items-center gap-1.5">
          <select value={filter} onChange={(e) => setFilter(e.target.value)} data-testid="mkt-prospects-admin-filter"
                  className="px-2 py-1 rounded bg-slate-800 border border-slate-700 text-[11px]">
            <option value="empty">Fully empty (no name, phone, email)</option>
            <option value="no_name">Missing name</option>
            <option value="no_contact">Missing phone AND email</option>
            <option value="all">All prospects</option>
          </select>
          <button onClick={runScan} disabled={busy} data-testid="mkt-prospects-admin-scan"
                  className="px-2.5 py-1 rounded bg-amber-500 text-slate-900 text-[11px] font-black disabled:opacity-50">🔎 Scan</button>
          {scan && scan.rows.length > 0 && filter !== 'all' && (
            <button onClick={bulkDelete} disabled={busy} data-testid="mkt-prospects-admin-bulk-del"
                    className="px-2.5 py-1 rounded bg-rose-600 text-white text-[11px] font-black disabled:opacity-50">🗑 Bulk Delete ({scan.total})</button>
          )}
        </div>
      </div>
      {scan && (
        <div className="max-h-64 overflow-y-auto space-y-1 mt-1" data-testid="mkt-prospects-admin-results">
          {scan.rows.length === 0 && <div className="text-center text-[10px] opacity-60 py-2">✅ Nothing matches — clean!</div>}
          {scan.rows.slice(0, 200).map((r) => (
            <div key={`${r.source}-${r.id}`} className="flex items-center justify-between gap-2 p-1.5 rounded bg-slate-900 border border-slate-800 text-[11px]">
              <div className="min-w-0 flex-1">
                <div className="truncate"><b>{r.name || '—'}</b> <span className="opacity-60">· {r.source}</span></div>
                <div className="opacity-70 text-[10px]">{r.phone || '—'} · {r.email || '—'} · {r.city || '—'}</div>
              </div>
              <button onClick={() => deleteOne(r)} data-testid={`mkt-prospects-admin-del-one-${(r.id || '').slice(0, 8)}`}
                      className="px-2 py-1 rounded bg-rose-700 text-white text-[10px] font-black">🗑</button>
            </div>
          ))}
          {scan.rows.length > 200 && <div className="text-[10px] opacity-70 text-center pt-1">…showing first 200 of {scan.total}. Use Bulk Delete to clean all.</div>}
        </div>
      )}
    </div>
  );
}

function ProspectsTab({ isAdmin }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [showAdd, setShowAdd] = useState(false);
  const [addForm, setAddForm] = useState({ name: '', phone: '', email: '', category: '', group: '', city: '', birthday: '', anniversary: '', family_members: '', notes: '' });
  const [family360, setFamily360] = useState([]);   // [{name, relation, birthday}]
  const [search, setSearch] = useState('');
  const [filterSource, setFilterSource] = useState('all');
  const [filterCity, setFilterCity] = useState('');
  const [filterCategory, setFilterCategory] = useState('');
  const [filterGroup, setFilterGroup] = useState('');
  const [dedupe, setDedupe] = useState(false);
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [showBulk, setShowBulk] = useState(false);
  const [bulkTemplate, setBulkTemplate] = useState("Namaste {name}! 👋 Aapke liye special offer aaya hai — reply karein details ke liye. 🎁");
  const [templates, setTemplates] = useState([]);
  const [quota, setQuota] = useState(null);
  const [wishes, setWishes] = useState(null);
  const [showWishes, setShowWishes] = useState(false);
  const [saveTplName, setSaveTplName] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [dupInfo, setDupInfo] = useState(null);
  const [communityLeads, setCommunityLeads] = useState(null); // Lead360 community DB for standard plan

  const load = async () => {
    setBusy(true);
    try {
      const r = await api.get('/marketplace/prospects'); setData(r.data);
      // Wave 50 · parallel fetch templates + quota + wishes (best-effort)
      Promise.all([
        api.get('/marketplace/prospects/templates').then((x) => setTemplates(x.data.templates || [])).catch(() => {}),
        api.get('/marketplace/prospects/quota').then((x) => setQuota(x.data)).catch(() => {}),
        r.data.unlocked ? api.get('/marketplace/prospects/wishes-today').then((x) => setWishes(x.data)).catch(() => {}) : null,
        // Fetch Lead360 community leads for standard plan
        api.get('/lead360/leads').then((x) => {
          if (x.data && x.data.subscription && x.data.subscription.tier === 'standard') {
            setCommunityLeads({ leads: x.data.leads || [], sub: x.data.subscription });
          }
        }).catch(() => {}),
      ]);
    }
    catch (e) { toast.error(e?.response?.data?.detail || 'Failed to load prospects'); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, []);

  const saveNew = async () => {
    if (!addForm.name.trim()) { toast.error('Name required'); return; }
    try {
      const payload = { ...addForm };
      const cleanFam = family360.filter((m) => (m.name || '').trim());
      if (cleanFam.length) payload.family_360 = cleanFam;
      await api.post('/marketplace/prospects/self-add', payload);
      toast.success('✅ Prospect saved'); setShowAdd(false);
      setAddForm({ name: '', phone: '', email: '', category: '', group: '', city: '', birthday: '', anniversary: '', family_members: '', notes: '' });
      setFamily360([]);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };

  const wa = (r) => {
    const phone = String(r.phone || '').replace(/[^0-9]/g, '');
    if (!phone || phone.length < 10) { toast.error('Phone locked or missing'); return; }
    const msg = `Namaste ${r.name}! 👋`;
    window.open(`https://wa.me/91${phone.slice(-10)}?text=${encodeURIComponent(msg)}`, '_blank');
  };
  const call = (r) => {
    const phone = String(r.phone || '').replace(/[^0-9]/g, '');
    if (!phone || phone.length < 10) { toast.error('Phone locked or missing'); return; }
    window.location.href = `tel:${phone}`;
  };
  const wish = (r, kind) => {
    const phone = String(r.phone || '').replace(/[^0-9]/g, '');
    if (!phone || phone.length < 10) { toast.error('Phone locked or missing'); return; }
    const msg = kind === 'birthday'
      ? `🎂 Happy Birthday ${r.name}! Aapko aur aapke parivaar ko bahut bahut mubarak ho. Special offer aapke liye — reply karein details ke liye. 🎁`
      : `💍 Happy Anniversary ${r.name}! Aapke saath ke saal aur bhi khushiyaan laayein. Special offer — reply karein. 🎁`;
    window.open(`https://wa.me/91${phone.slice(-10)}?text=${encodeURIComponent(msg)}`, '_blank');
  };

  // Wave 49 · Filter + search + dedupe
  const filtered = useMemo(() => {
    if (!data) return [];
    let rows = data.rows || [];
    const q = search.trim().toLowerCase();
    if (q) rows = rows.filter((r) => [r.name, r.phone, r.email, r.category, r.group, r.city, r.notes, r.added_by].some((v) => String(v || '').toLowerCase().includes(q)));
    if (filterSource !== 'all') rows = rows.filter((r) => r.source === filterSource);
    if (filterCity) rows = rows.filter((r) => String(r.city || '').toLowerCase().includes(filterCity.toLowerCase()));
    if (filterCategory) rows = rows.filter((r) => String(r.category || '').toLowerCase().includes(filterCategory.toLowerCase()));
    if (filterGroup) rows = rows.filter((r) => String(r.group || '').toLowerCase().includes(filterGroup.toLowerCase()));
    if (dedupe) {
      const seen = new Set();
      rows = rows.filter((r) => {
        const key = String(r.phone || '').replace(/\D/g, '').slice(-10);
        if (!key) return true;
        if (seen.has(key)) return false;
        seen.add(key); return true;
      });
    }
    return rows;
  }, [data, search, filterSource, filterCity, filterCategory, filterGroup, dedupe]);

  const allSources = useMemo(() => {
    if (!data) return [];
    return [...new Set((data.rows || []).map((r) => r.source))].sort();
  }, [data]);

  const toggleSel = (id) => setSelectedIds((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const selectAll = () => setSelectedIds(new Set(filtered.map((r) => r.id).filter(Boolean)));
  const clearSel = () => setSelectedIds(new Set());

  const runBulkWa = async () => {
    if (!data?.unlocked) { toast.error('Upgrade to Standard/Prime for bulk WA'); return; }
    const phones = filtered.filter((r) => selectedIds.has(r.id)).map((r) => r.phone).filter(Boolean);
    if (!phones.length) { toast.error('Select at least one prospect with a phone'); return; }
    // Wave 50 · safety: enforce daily quota client-side + 200 burst limit
    if (quota && phones.length > quota.remaining) {
      toast.error(`Only ${quota.remaining} left in today's quota (${quota.tier} plan, cap ${quota.cap}/day)`);
      return;
    }
    if (phones.length > 200) { toast.error('Max 200 messages per burst (browser safety)'); return; }
    try {
      const r = await api.post('/marketplace/prospects/bulk-wa-prep', { phones, template: bulkTemplate });
      toast.success(`✅ Prep ready — ${r.data.count} messages. Opening WhatsApp tabs…`);
      r.data.links.forEach((lnk, i) => setTimeout(() => window.open(lnk, '_blank'), i * 900));
      // Log usage to update quota display
      api.post('/marketplace/prospects/wa-log', { count: r.data.count }).then(() =>
        api.get('/marketplace/prospects/quota').then((x) => setQuota(x.data)).catch(() => {})
      ).catch(() => {});
      setShowBulk(false);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Bulk WA failed'); }
  };

  // Wave 50 · save current bulk template
  const saveCurrentTpl = async () => {
    if (!saveTplName.trim()) { toast.error('Give the template a name'); return; }
    try {
      await api.post('/marketplace/prospects/templates', { name: saveTplName, body: bulkTemplate, category: 'generic' });
      const r = await api.get('/marketplace/prospects/templates');
      setTemplates(r.data.templates || []);
      setSaveTplName('');
      toast.success('✅ Template saved');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };

  const deleteTpl = async (id) => {
    if (!window.confirm('Delete this template?')) return;
    try { await api.delete(`/marketplace/prospects/templates/${id}`); setTemplates(templates.filter((t) => t.id !== id)); }
    catch (e) { toast.error('Delete failed'); }
  };

  const sendWish = (w) => {
    window.open(w.wa_link, '_blank');
    api.post('/marketplace/prospects/wa-log', { count: 1 }).catch(() => {});
  };

  const daysAgo = (iso) => {
    if (!iso) return null;
    try {
      const t = new Date(iso).getTime();
      return Math.floor((Date.now() - t) / 86400000);
    } catch { return null; }
  };

  const runDupScan = async () => {
    try { const r = await api.post('/marketplace/prospects/merge-duplicates'); setDupInfo(r.data); toast.success(`🔎 ${r.data.duplicate_phones} duplicate phone groups found`); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Scan failed'); }
  };

  const downloadCsv = async () => {
    try {
      const token = localStorage.getItem('token');
      const url = `${process.env.REACT_APP_BACKEND_URL}/api/marketplace/prospects/export.csv`;
      const resp = await fetch(url, { headers: { Authorization: `Bearer ${token}` } });
      const blob = await resp.blob();
      const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
      a.download = `prospects_${Date.now()}.csv`; a.click(); URL.revokeObjectURL(a.href);
    } catch (e) { toast.error('CSV download failed'); }
  };

  if (busy || !data) return <div className="p-6 text-center opacity-70">⏳ Loading prospects…</div>;
  return (
    <div className="space-y-3" data-testid="mkt-prospects-tab">
      {/* Header */}
      <div className="rounded-2xl bg-slate-900 border border-purple-400/40 p-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="min-w-0">
            <div className="text-lg font-black">🎯 Prospects &amp; Leads</div>
            <div className="text-[11px] opacity-70">
              {data.unlocked
                ? `Full access · ${data.total} contacts · showing ${filtered.length} · ${isAdmin ? 'Admin' : `${data.plan_tier?.toUpperCase()} plan`}`
                : `${data.total} contacts platform-wide · contact info locked`}
            </div>
          </div>
          <div className="flex gap-1.5 flex-wrap">
            {data.unlocked && (<>
              <button onClick={() => setShowAdd((v) => !v)} data-testid="mkt-prospects-add-btn"
                      className="px-2.5 py-1.5 rounded-lg bg-emerald-500 text-slate-900 text-[11px] font-black">➕ Add</button>
              <button onClick={downloadCsv} data-testid="mkt-prospects-csv"
                      className="px-2.5 py-1.5 rounded-lg bg-sky-500 text-white text-[11px] font-black">⬇ CSV</button>
              <button onClick={runDupScan} data-testid="mkt-prospects-dupscan"
                      className="px-2.5 py-1.5 rounded-lg bg-amber-500 text-slate-900 text-[11px] font-black">🔎 Dup Scan</button>
              <button onClick={() => setShowBulk(true)} disabled={selectedIds.size === 0} data-testid="mkt-prospects-bulk-wa"
                      className="px-2.5 py-1.5 rounded-lg bg-green-600 text-white text-[11px] font-black disabled:opacity-40">📱 Bulk WA ({selectedIds.size})</button>
            </>)}
          </div>
        </div>
        {!data.unlocked && (
          <div className="mt-3 p-3 rounded-xl bg-gradient-to-r from-amber-500/20 to-yellow-500/10 border border-amber-400/50" data-testid="mkt-prospects-paywall">
            <div className="text-sm font-black text-amber-200">🔒 {data.upgrade_message}</div>
            <div className="mt-2 flex flex-wrap gap-2">
              <button onClick={async () => { try { await api.post('/marketplace/plan/upgrade', { target_plan: 'standard' }); toast.success('✅ Upgraded to Standard'); load(); } catch (e) { toast.error(e?.response?.data?.detail || 'Upgrade failed'); } }}
                      data-testid="mkt-prospects-upgrade-standard"
                      className="px-3 py-1.5 rounded-lg bg-emerald-500 text-slate-900 text-xs font-black">Upgrade → Standard · 600 AT/mo</button>
              <button onClick={async () => { try { await api.post('/marketplace/plan/upgrade', { target_plan: 'prime' }); toast.success('✅ Upgraded to Prime VIP'); load(); } catch (e) { toast.error(e?.response?.data?.detail || 'Upgrade failed'); } }}
                      data-testid="mkt-prospects-upgrade-prime"
                      className="px-3 py-1.5 rounded-lg bg-fuchsia-500 text-white text-xs font-black">Upgrade → Prime VIP · 1500 AT/mo</button>
            </div>
          </div>
        )}
      </div>

      {/* Wave 50 · Quota banner (safety-first) */}
      {quota && data.unlocked && (
        <div className={`rounded-xl p-2 text-[11px] flex items-center justify-between flex-wrap gap-2 ${
          quota.remaining < 20 ? 'bg-rose-500/20 border border-rose-400/50' : 'bg-emerald-500/10 border border-emerald-400/40'
        }`} data-testid="mkt-prospects-quota">
          <div>
            📱 <b>{quota.tier?.toUpperCase()}</b> plan · Bulk WA today: <b className="text-cyan-300">{quota.used}/{quota.cap}</b> ·
            <b className={quota.remaining < 20 ? 'text-rose-300' : 'text-emerald-300'}> {quota.remaining} left</b>
          </div>
          <details className="cursor-pointer">
            <summary className="text-[10px] opacity-70">🛡 Safety rules</summary>
            <ul className="mt-1 pl-4 list-disc text-[10px] opacity-80">
              {quota.safety_rules.map((s, i) => <li key={i}>{s}</li>)}
            </ul>
          </details>
        </div>
      )}

      {/* Wave 50 · 🎂 Today's Wishes Outbox (Auto-Wisher) */}
      {wishes && wishes.count > 0 && data.unlocked && (
        <div className="rounded-2xl bg-gradient-to-br from-pink-900/40 via-rose-900/30 to-slate-900 border-2 border-pink-400/50 p-3" data-testid="mkt-prospects-wishes">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="text-lg font-black">🎂 Today's Wishes Outbox · {wishes.count} people</div>
              <div className="text-[11px] opacity-80">Auto-composed messages ready — one tap sends via WhatsApp. Never forget a customer birthday again.</div>
            </div>
            <button onClick={() => setShowWishes((v) => !v)} data-testid="mkt-prospects-wishes-toggle"
                    className="px-3 py-1.5 rounded-lg bg-pink-500 text-white text-xs font-black">
              {showWishes ? 'Hide' : 'Show all →'}
            </button>
          </div>
          {showWishes && (
            <div className="mt-2 space-y-1.5 max-h-60 overflow-y-auto" data-testid="mkt-prospects-wishes-list">
              {wishes.wishes.map((w, i) => (
                <div key={i} className="p-2 rounded-lg bg-slate-950/60 border border-pink-400/30 flex items-start gap-2" data-testid={`mkt-prospects-wish-${i}`}>
                  <div className="flex-1 min-w-0">
                    <div className="text-[12px] font-black flex items-center gap-1 flex-wrap">
                      {w.kind === 'birthday' && '🎂'} {w.kind === 'anniversary' && '💍'} {w.kind === 'family_birthday' && '👨‍👩‍👧 🎂'}
                      <span className="truncate">{w.name}</span>
                      {w.relation !== 'self' && <span className="text-[10px] opacity-70">· {w.relation}: {w.member_name}</span>}
                    </div>
                    <div className="text-[10px] opacity-70 mt-0.5 line-clamp-2">{w.message}</div>
                  </div>
                  <button onClick={() => sendWish(w)} data-testid={`mkt-prospects-wish-send-${i}`}
                          className="flex-shrink-0 px-2 py-1 rounded bg-green-600 text-white text-[11px] font-black">📱 Send</button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Wave 51 · Upcoming Family Events (next 30d + month filter + name search) */}
      {data.unlocked && <ProspectsUpcomingEventsBlock />}

      {/* Lead360 Community Database — visible for Standard (600 AT) plan */}
      {communityLeads && communityLeads.leads.length > 0 && (
        <div className="rounded-2xl bg-emerald-500/10 border-2 border-emerald-400/50 p-3 space-y-2" data-testid="mkt-prospects-community-leads">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <div>
              <div className="text-sm font-black text-emerald-300">🌐 Lead360 Community Database · {communityLeads.leads.length} leads</div>
              <div className="text-[11px] opacity-80">Standard Plan ka faida — sab community members ki leads yahan dikhti hain</div>
            </div>
            <span className="px-2 py-0.5 rounded-full text-[9px] font-black bg-emerald-500/30 text-emerald-200 border border-emerald-400/40">600 AT STANDARD</span>
          </div>
          <div className="space-y-1.5 max-h-80 overflow-y-auto">
            {communityLeads.leads.slice(0, 100).map((lead) => (
              <div key={lead.id} className="flex items-center gap-2 p-2 rounded-lg bg-slate-900/70 border border-emerald-400/20" data-testid={`mkt-prospects-comm-lead-${lead.id?.slice(0, 8)}`}>
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-black truncate">{lead.name}</div>
                  <div className="text-[10px] opacity-70 flex flex-wrap gap-2">
                    {lead.city && <span>📍 {lead.city}</span>}
                    {lead.business_type && <span>🏢 {lead.business_type}</span>}
                    {lead.status && <span className="px-1 rounded bg-slate-700 text-amber-200">{lead.status}</span>}
                  </div>
                </div>
                {lead.mobile && (
                  <a href={`https://wa.me/91${lead.mobile.replace(/[^0-9]/g, '').slice(-10)}?text=${encodeURIComponent(`Namaste ${lead.name}! 👋`)}`}
                     target="_blank" rel="noreferrer"
                     className="px-2 py-1 rounded bg-green-700/60 text-green-200 text-[10px] font-black">💬 WA</a>
                )}
              </div>
            ))}
            {communityLeads.leads.length > 100 && (
              <div className="text-center text-[10px] opacity-60 py-2">+ {communityLeads.leads.length - 100} aur leads · Lead360 CRM tab mein full access ke saath dekho</div>
            )}
          </div>
        </div>
      )}

      {/* Search + filters (mobile-first) */}
      <div className="rounded-xl bg-slate-900 border border-slate-800 p-2 space-y-2" data-testid="mkt-prospects-filters">
        <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="🔍 Search name / phone / email / city / notes…"
               data-testid="mkt-prospects-search"
               className="w-full px-3 py-2 rounded-lg bg-slate-800 border border-slate-700 text-sm" />
        <div className="grid grid-cols-2 md:grid-cols-4 gap-1.5">
          <select value={filterSource} onChange={(e) => setFilterSource(e.target.value)} data-testid="mkt-prospects-filter-source"
                  className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-[11px]">
            <option value="all">All sources</option>
            {allSources.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <input value={filterCity} onChange={(e) => setFilterCity(e.target.value)} placeholder="City" data-testid="mkt-prospects-filter-city"
                 className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-[11px]" />
          <input value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)} placeholder="Category"
                 className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-[11px]" />
          <input value={filterGroup} onChange={(e) => setFilterGroup(e.target.value)} placeholder="Group"
                 className="px-2 py-1.5 rounded bg-slate-800 border border-slate-700 text-[11px]" />
        </div>
        <div className="flex items-center justify-between gap-2 flex-wrap text-[11px]">
          <label className="flex items-center gap-1.5"><input type="checkbox" checked={dedupe} onChange={(e) => setDedupe(e.target.checked)} data-testid="mkt-prospects-dedupe" /> Merge duplicates by phone</label>
          {data.unlocked && <div className="flex gap-1.5">
            <button onClick={selectAll} className="px-2 py-1 rounded bg-slate-700 text-[10px] font-black">Select all filtered</button>
            <button onClick={clearSel} className="px-2 py-1 rounded bg-slate-700 text-[10px] font-black">Clear</button>
          </div>}
        </div>
      </div>

      {/* Admin cleanup panel — visible only to admin */}
      {isAdmin && <ProspectsAdminCleanupBlock onDone={load} />}

      {/* Dup scan result summary */}
      {dupInfo && (
        <div className="rounded-xl bg-amber-500/10 border border-amber-400/40 p-2 text-[11px]" data-testid="mkt-prospects-dupinfo">
          🔎 {dupInfo.unique_phones} unique phones · <b>{dupInfo.duplicate_phones}</b> duplicate groups. Toggle "Merge duplicates" above to hide extra copies.
        </div>
      )}

      {/* Groups summary */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
        {['by_category', 'by_group', 'by_city'].map((k) => (
          <div key={k} className="rounded-xl bg-slate-900 border border-slate-700 p-2">
            <div className="text-[10px] font-black opacity-70 uppercase mb-1">{k.replace('by_', '')}</div>
            {(data.groups[k] || []).slice(0, 6).map((g) => (
              <div key={g.key} className="flex justify-between text-[11px] py-0.5 border-b border-slate-800 last:border-0">
                <span className="truncate">{g.key}</span>
                <span className="text-purple-300 font-black">{g.count}</span>
              </div>
            ))}
          </div>
        ))}
      </div>

      {/* Add form with Family 360 */}
      {showAdd && data.unlocked && (
        <div className="rounded-xl bg-slate-900 border border-emerald-400/40 p-3 space-y-2" data-testid="mkt-prospects-add-form">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
            <input value={addForm.name} onChange={(e) => setAddForm({ ...addForm, name: e.target.value })} placeholder="Name *" className="px-2 py-2 rounded bg-slate-800 text-sm" data-testid="mkt-prospects-add-name" />
            <input value={addForm.phone} onChange={(e) => setAddForm({ ...addForm, phone: e.target.value })} placeholder="Phone" className="px-2 py-2 rounded bg-slate-800 text-sm" data-testid="mkt-prospects-add-phone" />
            <input value={addForm.email} onChange={(e) => setAddForm({ ...addForm, email: e.target.value })} placeholder="Email" className="px-2 py-2 rounded bg-slate-800 text-sm" />
            <input value={addForm.category} onChange={(e) => setAddForm({ ...addForm, category: e.target.value })} placeholder="Category" className="px-2 py-2 rounded bg-slate-800 text-sm" />
            <input value={addForm.group} onChange={(e) => setAddForm({ ...addForm, group: e.target.value })} placeholder="Group" className="px-2 py-2 rounded bg-slate-800 text-sm" />
            <input value={addForm.city} onChange={(e) => setAddForm({ ...addForm, city: e.target.value })} placeholder="City" className="px-2 py-2 rounded bg-slate-800 text-sm" />
            <input type="date" value={addForm.birthday} onChange={(e) => setAddForm({ ...addForm, birthday: e.target.value })} className="px-2 py-2 rounded bg-slate-800 text-sm" title="Birthday" />
            <input type="date" value={addForm.anniversary} onChange={(e) => setAddForm({ ...addForm, anniversary: e.target.value })} className="px-2 py-2 rounded bg-slate-800 text-sm" title="Anniversary" />
          </div>
          <textarea value={addForm.notes} onChange={(e) => setAddForm({ ...addForm, notes: e.target.value })} placeholder="Notes" className="w-full px-2 py-2 rounded bg-slate-800 text-sm" rows={2} />
          {/* Family 360 dynamic list */}
          <div className="rounded-lg bg-slate-800/50 border border-purple-400/30 p-2" data-testid="mkt-prospects-family360">
            <div className="flex items-center justify-between mb-1"><div className="text-[11px] font-black text-purple-300">👨‍👩‍👧 Family 360 · Relatives with relation + DOB</div>
              <button onClick={() => setFamily360([...family360, { name: '', relation: '', birthday: '' }])} className="px-2 py-0.5 rounded bg-emerald-500 text-slate-900 text-[10px] font-black" data-testid="mkt-prospects-family360-add">+ Add member</button>
            </div>
            {family360.map((m, i) => (
              <div key={i} className="grid grid-cols-1 md:grid-cols-4 gap-1 mb-1" data-testid={`mkt-prospects-family360-row-${i}`}>
                <input value={m.name} onChange={(e) => setFamily360(family360.map((x, j) => j === i ? { ...x, name: e.target.value } : x))} placeholder="Name" className="px-2 py-1.5 rounded bg-slate-900 text-xs" />
                <input value={m.relation} onChange={(e) => setFamily360(family360.map((x, j) => j === i ? { ...x, relation: e.target.value } : x))} placeholder="Relation (spouse/son/daughter…)" className="px-2 py-1.5 rounded bg-slate-900 text-xs" />
                <input type="date" value={m.birthday} onChange={(e) => setFamily360(family360.map((x, j) => j === i ? { ...x, birthday: e.target.value } : x))} className="px-2 py-1.5 rounded bg-slate-900 text-xs" />
                <button onClick={() => setFamily360(family360.filter((_, j) => j !== i))} className="px-2 py-1 rounded bg-rose-600 text-white text-[10px] font-black">✕ Remove</button>
              </div>
            ))}
          </div>
          <button onClick={saveNew} data-testid="mkt-prospects-add-save" className="w-full py-2 rounded bg-emerald-500 text-slate-900 text-sm font-black">💾 Save Prospect</button>
        </div>
      )}

      {/* Bulk WA modal */}
      {showBulk && (
        <div className="fixed inset-0 z-[100] bg-black/70 flex items-end md:items-center justify-center p-2" onClick={() => setShowBulk(false)} data-testid="mkt-prospects-bulk-modal">
          <div className="w-full md:max-w-lg rounded-2xl bg-slate-950 border-2 border-green-400/50 p-4 space-y-2" onClick={(e) => e.stopPropagation()}>
            <div className="text-lg font-black">📱 Bulk WhatsApp · {selectedIds.size} selected</div>
            <div className="text-[11px] opacity-70">
              {quota ? <>Today's quota: <b>{quota.remaining}/{quota.cap}</b> left · </> : ''}
              Messages will open sequentially. Use <code>{'{name}'}</code> as placeholder.
            </div>
            {templates.length > 0 && (
              <div>
                <div className="text-[10px] opacity-70 mb-1">📋 Saved templates</div>
                <div className="flex gap-1 flex-wrap max-h-24 overflow-y-auto" data-testid="mkt-prospects-templates-list">
                  {templates.map((t) => (
                    <div key={t.id} className="flex items-center gap-0.5">
                      <button onClick={() => setBulkTemplate(t.body)} data-testid={`mkt-prospects-tpl-${t.id.slice(0, 8)}`}
                              className="px-2 py-1 rounded bg-slate-800 hover:bg-slate-700 text-[10px] font-black" title={t.body}>
                        {t.name}
                      </button>
                      <button onClick={() => deleteTpl(t.id)} className="px-1 rounded bg-rose-800 text-white text-[9px]" title="Delete template">✕</button>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <textarea value={bulkTemplate} onChange={(e) => setBulkTemplate(e.target.value)} rows={4} className="w-full px-2 py-2 rounded bg-slate-800 text-sm" data-testid="mkt-prospects-bulk-template" />
            <div className="flex gap-1 items-center">
              <input value={saveTplName} onChange={(e) => setSaveTplName(e.target.value)} placeholder="Name to save as…"
                     data-testid="mkt-prospects-save-tpl-name"
                     className="flex-1 px-2 py-1.5 rounded bg-slate-800 text-xs" />
              <button onClick={saveCurrentTpl} data-testid="mkt-prospects-save-tpl"
                      className="px-3 py-1.5 rounded bg-sky-500 text-white text-xs font-black">💾 Save</button>
            </div>
            <div className="flex gap-2">
              <button onClick={runBulkWa} className="flex-1 py-2 rounded bg-green-600 text-white font-black" data-testid="mkt-prospects-bulk-send">🚀 Blast now</button>
              <button onClick={() => setShowBulk(false)} className="px-3 py-2 rounded bg-slate-700 text-white font-black">Cancel</button>
            </div>
          </div>
        </div>
      )}

      {/* Scrollable card list — mobile first */}
      <div className="rounded-xl bg-slate-900 border border-slate-800 overflow-hidden" data-testid="mkt-prospects-list">
        <div className="max-h-[70vh] overflow-y-auto overflow-x-hidden divide-y divide-slate-800">
          {filtered.length === 0 && <div className="p-6 text-center opacity-60 text-sm">No prospects match your filters.</div>}
          {filtered.map((r, i) => {
            const sel = selectedIds.has(r.id);
            const expanded = expandedId === r.id;
            return (
              <div key={r.id || i} className={`p-2.5 ${sel ? 'bg-green-900/20' : 'hover:bg-slate-800/30'}`} data-testid={`mkt-prospects-row-${(r.id || '').slice(0, 8)}`}>
                <div className="flex items-start gap-2">
                  {data.unlocked && <input type="checkbox" checked={sel} onChange={() => toggleSel(r.id)} className="mt-1 flex-shrink-0" data-testid={`mkt-prospects-sel-${(r.id || '').slice(0, 8)}`} />}
                  <div className="flex-1 min-w-0" onClick={() => setExpandedId(expanded ? null : r.id)}>
                    <div className="flex items-center gap-2 flex-wrap">
                      <div className="font-black text-sm truncate">{r.name || 'Unnamed'}</div>
                      <span className={`px-1.5 py-0.5 rounded text-[9px] font-black ${r.source === 'lead360_crm' ? 'bg-emerald-500/30 text-emerald-300 border border-emerald-400/30' : 'bg-slate-700 text-slate-400'}`}>
                        {r.source === 'lead360_crm' ? '🌟 CRM' : r.source}
                      </span>
                      {r.city && <span className="text-[10px] opacity-70">📍 {r.city}</span>}
                      {(() => {
                        const d = daysAgo(r.created_at);
                        return d != null && d > 30 ? (
                          <span className="px-1.5 py-0.5 rounded bg-amber-500/30 text-amber-200 text-[9px] font-black" data-testid={`mkt-prospects-followup-${(r.id || '').slice(0, 8)}`}
                                title={`Added ${d} days ago — follow-up recommended`}>
                            🔔 Follow-up ({d}d)
                          </span>
                        ) : null;
                      })()}
                    </div>
                    <div className="text-[11px] mt-0.5 flex items-center gap-2 flex-wrap">
                      {r.phone && <span className="text-cyan-300 font-mono">{r.phone}</span>}
                      {r.email && <span className="opacity-70 truncate">{r.email}</span>}
                    </div>
                    <div className="text-[10px] opacity-60 mt-0.5">
                      {r.category && <>🏷 {r.category} · </>}
                      {r.group && <>👥 {r.group} · </>}
                      {r.added_by && <>by @{String(r.added_by).slice(0, 14)}</>}
                    </div>
                    {expanded && (r.notes || r.birthday || r.anniversary || (r.family_360 || []).length > 0) && (
                      <div className="mt-1.5 p-1.5 rounded bg-slate-800/60 text-[11px] space-y-1">
                        {r.birthday && <div>🎂 Birthday: {r.birthday}</div>}
                        {r.anniversary && <div>💍 Anniversary: {r.anniversary}</div>}
                        {r.notes && <div className="opacity-80">📝 {r.notes}</div>}
                        {(r.family_360 || []).length > 0 && (
                          <div>
                            <div className="text-[10px] font-black text-purple-300">👨‍👩‍👧 Family 360:</div>
                            {r.family_360.map((m, j) => (
                              <div key={j} className="text-[10px] opacity-80">· {m.name} ({m.relation || '—'}){m.birthday ? ` · 🎂 ${m.birthday}` : ''}</div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                  <div className="flex-shrink-0">
                    {data.unlocked ? (
                      <div className="flex flex-col gap-1">
                        <div className="flex gap-1">
                          <button onClick={() => wa(r)} data-testid={`mkt-prospects-wa-${(r.id || '').slice(0, 8)}`}
                                  className="px-2 py-1 rounded bg-green-600 text-white text-[10px] font-black">📱</button>
                          <button onClick={() => call(r)} className="px-2 py-1 rounded bg-sky-600 text-white text-[10px] font-black">📞</button>
                        </div>
                        <div className="flex gap-1">
                          <button onClick={() => wish(r, 'birthday')} className="px-2 py-1 rounded bg-pink-600 text-white text-[10px] font-black">🎂</button>
                          <button onClick={() => wish(r, 'anniversary')} className="px-2 py-1 rounded bg-rose-600 text-white text-[10px] font-black">💍</button>
                        </div>
                        {isAdmin && r.id && (
                          <button
                            onClick={async (e) => {
                              e.stopPropagation();
                              if (!window.confirm(`Delete "${r.name || 'unnamed'}" (${r.source})? This removes it for all users.`)) return;
                              try {
                                await api.post('/admin/marketplace/prospects/delete', { source: r.source, id: r.id });
                                toast.success('🗑 Deleted');
                                load();
                              } catch (err) { toast.error(err?.response?.data?.detail || 'Delete failed'); }
                            }}
                            data-testid={`mkt-prospects-admin-del-${(r.id || '').slice(0, 8)}`}
                            className="px-2 py-1 rounded bg-rose-700 text-white text-[10px] font-black">🗑 Admin</button>
                        )}
                      </div>
                    ) : <span className="text-[10px] opacity-50">🔒</span>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <div className="p-1.5 text-[10px] opacity-60 bg-slate-950 text-center border-t border-slate-800">
          Showing {filtered.length} of {data.total} · scroll to load more · tap a row to see notes / Family 360
        </div>
      </div>
    </div>
  );
}

// ============ Wave 48 · Citywise Deals News tab ============
function CityDealsTab({ isAdmin }) {
  const [deals, setDeals] = useState([]);
  const [city, setCity] = useState('');
  const [category, setCategory] = useState('');
  const [summary, setSummary] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ title: '', body: '', city: '', category: '', image_url: '', video_url: '', link_url: '', active: true });

  const load = async () => {
    try {
      if (isAdmin) {
        const r = await api.get('/admin/marketplace/city-deals'); setDeals(r.data.deals || []); setSummary(r.data.summary || null);
      } else {
        const r = await api.get('/marketplace/city-deals', { params: { city: city || undefined, category: category || undefined } });
        setDeals(r.data.deals || []); setSummary(null);
      }
    } catch (e) { toast.error(e?.response?.data?.detail || 'Load failed'); }
  };
  useEffect(() => { load(); }, [city, category, isAdmin]);

  const save = async () => {
    if (!form.title || !form.city) { toast.error('Title + City required'); return; }
    try {
      if (editing) { await api.patch(`/admin/marketplace/city-deals/${editing}`, form); toast.success('✅ Updated'); }
      else { await api.post('/admin/marketplace/city-deals', form); toast.success('✅ Created'); }
      setShowForm(false); setEditing(null);
      setForm({ title: '', body: '', city: '', category: '', image_url: '', video_url: '', link_url: '', active: true });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Save failed'); }
  };
  const del = async (id) => {
    if (!window.confirm('Delete this deal post?')) return;
    try { await api.delete(`/admin/marketplace/city-deals/${id}`); toast.success('Deleted'); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Delete failed'); }
  };
  const track = (id, event, url) => {
    api.post(`/marketplace/city-deals/${id}/track`, null, { params: { event } }).catch(() => {});
    if (url) window.open(url, '_blank');
  };

  return (
    <div className="space-y-3" data-testid="mkt-citydeals-tab">
      <div className="rounded-2xl bg-slate-900 border border-cyan-400/40 p-3">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div>
            <div className="text-lg font-black">📰 Citywise Deals News</div>
            <div className="text-[11px] opacity-70">Local deals + promos curated by Anant</div>
          </div>
          {isAdmin && (
            <button onClick={() => { setEditing(null); setForm({ title: '', body: '', city: '', category: '', image_url: '', video_url: '', link_url: '', active: true }); setShowForm(true); }}
                    data-testid="mkt-citydeals-add-btn"
                    className="px-3 py-1.5 rounded-lg bg-emerald-500 text-slate-900 text-xs font-black">➕ Add Deal</button>
          )}
        </div>
        <div className="flex gap-2 mt-2 flex-wrap">
          <input value={city} onChange={(e) => setCity(e.target.value)} placeholder="Filter city" className="px-2 py-1 rounded bg-slate-800 text-xs" data-testid="mkt-citydeals-city-filter" />
          <input value={category} onChange={(e) => setCategory(e.target.value)} placeholder="Filter category" className="px-2 py-1 rounded bg-slate-800 text-xs" />
        </div>
      </div>

      {isAdmin && summary && (
        <div className="grid grid-cols-4 gap-2" data-testid="mkt-citydeals-analytics">
          <div className="rounded-xl bg-slate-900 border border-slate-800 p-2"><div className="text-[10px] opacity-70">POSTS</div><div className="text-lg font-black">{summary.total}</div></div>
          <div className="rounded-xl bg-slate-900 border border-slate-800 p-2"><div className="text-[10px] opacity-70">VIEWS</div><div className="text-lg font-black text-cyan-300">{summary.views}</div></div>
          <div className="rounded-xl bg-slate-900 border border-slate-800 p-2"><div className="text-[10px] opacity-70">CLICKS</div><div className="text-lg font-black text-emerald-300">{summary.clicks}</div></div>
          <div className="rounded-xl bg-slate-900 border border-slate-800 p-2"><div className="text-[10px] opacity-70">SALES</div><div className="text-lg font-black text-fuchsia-300">{summary.sales}</div></div>
        </div>
      )}

      {showForm && (
        <div className="rounded-xl bg-slate-900 border border-emerald-400/40 p-3 space-y-2" data-testid="mkt-citydeals-form">
          <div className="grid grid-cols-2 gap-2">
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Title *" className="px-2 py-1.5 rounded bg-slate-800 text-xs" />
            <input value={form.city} onChange={(e) => setForm({ ...form, city: e.target.value })} placeholder="City *" className="px-2 py-1.5 rounded bg-slate-800 text-xs" />
            <input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="Category" className="px-2 py-1.5 rounded bg-slate-800 text-xs" />
            <input value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })} placeholder="Image URL" className="px-2 py-1.5 rounded bg-slate-800 text-xs" />
            <input value={form.video_url} onChange={(e) => setForm({ ...form, video_url: e.target.value })} placeholder="Video URL" className="px-2 py-1.5 rounded bg-slate-800 text-xs" />
            <input value={form.link_url} onChange={(e) => setForm({ ...form, link_url: e.target.value })} placeholder="Link URL" className="px-2 py-1.5 rounded bg-slate-800 text-xs" />
          </div>
          <textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} placeholder="Body / description" rows={3} className="w-full px-2 py-1.5 rounded bg-slate-800 text-xs" />
          <label className="flex items-center gap-2 text-[11px]"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} /> Active</label>
          <div className="flex gap-2"><button onClick={save} className="flex-1 py-1.5 rounded bg-emerald-500 text-slate-900 text-xs font-black">💾 Save</button>
            <button onClick={() => { setShowForm(false); setEditing(null); }} className="px-3 py-1.5 rounded bg-slate-700 text-white text-xs font-black">Cancel</button></div>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {deals.length === 0 && <div className="col-span-2 p-8 text-center opacity-60 text-sm">No deals yet.</div>}
        {deals.map((d) => (
          <div key={d.id} className="rounded-xl bg-slate-900 border border-slate-700 overflow-hidden"
               data-testid={`mkt-citydeals-card-${d.id.slice(0, 8)}`}
               onMouseEnter={() => !isAdmin && track(d.id, 'view')}>
            {d.image_url && <img src={d.image_url} alt="" className="w-full h-32 object-contain bg-slate-800/60" />}
            <div className="p-2 space-y-1">
              <div className="flex items-center justify-between gap-2">
                <div className="font-black text-sm">{d.title}</div>
                <span className="text-[9px] px-1.5 py-0.5 rounded bg-cyan-500/20 text-cyan-300 font-black">📍 {d.city}</span>
              </div>
              {d.category && <div className="text-[10px] opacity-70">🏷 {d.category}</div>}
              {d.body && <div className="text-[11px] opacity-80 whitespace-pre-wrap">{d.body}</div>}
              <div className="flex items-center gap-2 text-[10px] opacity-70">
                👁 {d.views || 0} · 🔗 {d.clicks || 0} · 💰 {d.sales || 0}
              </div>
              <div className="flex gap-1.5 flex-wrap">
                {d.link_url && <button onClick={() => track(d.id, 'click', d.link_url)} data-testid={`mkt-citydeals-open-${d.id.slice(0, 8)}`}
                                        className="px-2 py-0.5 rounded bg-emerald-500 text-slate-900 text-[10px] font-black">↗ Open</button>}
                {d.video_url && <a href={d.video_url} target="_blank" rel="noreferrer" onClick={() => api.post(`/marketplace/city-deals/${d.id}/track`, null, { params: { event: 'click' } }).catch(()=>{})}
                                   className="px-2 py-0.5 rounded bg-fuchsia-600 text-white text-[10px] font-black">▶ Video</a>}
                {isAdmin && <>
                  <button onClick={() => { setEditing(d.id); setForm({ title: d.title, body: d.body || '', city: d.city, category: d.category || '', image_url: d.image_url || '', video_url: d.video_url || '', link_url: d.link_url || '', active: d.active !== false }); setShowForm(true); }}
                          className="px-2 py-0.5 rounded bg-sky-600 text-white text-[10px] font-black">✏ Edit</button>
                  <button onClick={() => del(d.id)} className="px-2 py-0.5 rounded bg-rose-600 text-white text-[10px] font-black">🗑 Del</button>
                </>}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ============ Wave 48 · Demand Pulse tab ============
function DemandPulseTab() {
  const [rows, setRows] = useState([]);
  const [city, setCity] = useState('');
  const [hours, setHours] = useState(24);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    setBusy(true);
    try {
      const r = await api.get('/marketplace/pulse', { params: { city: city || undefined, hours, limit: 20 } });
      setRows(r.data.hot_demands || []);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Load failed'); }
    finally { setBusy(false); }
  };
  useEffect(() => { load(); }, [city, hours]);

  return (
    <div className="space-y-3" data-testid="mkt-pulse-tab">
      <div className="rounded-2xl bg-gradient-to-br from-orange-900/40 via-rose-900/40 to-slate-900 border border-orange-400/50 p-3">
        <div className="text-lg font-black">🔥 Demand Pulse</div>
        <div className="text-[11px] opacity-80">Real-time buyer intent — kya log dhundh rahe hain aapke ilaake mein. Stock laao sirf jo bikega.</div>
        <div className="flex gap-2 mt-2 flex-wrap">
          <input value={city} onChange={(e) => setCity(e.target.value)} placeholder="City filter" className="px-2 py-1 rounded bg-slate-800 text-xs" data-testid="mkt-pulse-city" />
          <select value={hours} onChange={(e) => setHours(Number(e.target.value))} className="px-2 py-1 rounded bg-slate-800 text-xs" data-testid="mkt-pulse-hours">
            <option value={1}>Last 1 hour</option>
            <option value={24}>Last 24 hours</option>
            <option value={72}>Last 3 days</option>
            <option value={168}>Last 7 days</option>
          </select>
          <button onClick={load} className="px-3 py-1 rounded bg-orange-500 text-slate-900 text-xs font-black">🔄 Refresh</button>
        </div>
      </div>
      {busy && <div className="p-4 text-center opacity-60">⏳ Aggregating…</div>}
      <div className="space-y-2">
        {rows.length === 0 && !busy && <div className="p-8 text-center opacity-60 text-sm">No demand signals in this window yet. Buyers ke search + wishlist events aggregate hongey yahan.</div>}
        {rows.map((r, i) => (
          <div key={r.query || i} className="rounded-xl bg-slate-900 border border-orange-400/30 p-3 flex items-center justify-between gap-2"
               data-testid={`mkt-pulse-row-${i}`}>
            <div className="min-w-0">
              <div className="text-sm font-black">🔍 {r.query}</div>
              <div className="text-[10px] opacity-70">{r.city ? `📍 ${r.city} · ` : ''}{r.category ? `🏷 ${r.category}` : ''}</div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-black text-orange-300" data-testid={`mkt-pulse-count-${i}`}>{r.unique_seekers}</div>
              <div className="text-[9px] opacity-70">unique seekers · {r.total_searches} searches</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
